import {
  escapeHtml as sharedEscapeHtml,
  escapeAttribute as sharedEscapeAttribute,
  safeOption as sharedSafeOption,
  localDateValue as sharedLocalDateValue,
  localDateFromValue as sharedLocalDateFromValue,
  addDays as sharedAddDays,
  formatDate as sharedFormatDate,
  number as sharedNumber,
  pct as sharedPct,
  normalizeHeader as sharedNormalizeHeader,
  subjectMatchesName as sharedSubjectMatchesName,
  isPlannerGroup as sharedIsPlannerGroup,
  plannerCountableItems as sharedPlannerCountableItems,
  isQuestionGroup as sharedIsQuestionGroup,
  questionLeafRecords as sharedQuestionLeafRecords,
  plannerGroupChildren as sharedPlannerGroupChildren,
  plannerTopLevelItems as sharedPlannerTopLevelItems,
  questionGroupChildren as sharedQuestionGroupChildren,
  questionGroupMetrics as sharedQuestionGroupMetrics,
  questionTopLevelItems as sharedQuestionTopLevelItems,
  groupBy as sharedGroupBy,
  renderEmptyState as sharedRenderEmptyState,
  EMPTY_STATE_SVG as sharedEmptyStateSvg,
} from "../../src/app/api.js";

import {
  migratePlannerDerivedFields,
  movePlannerItemToColumn as domainMovePlannerItemToColumn,
  reschedulePlannerItem as domainReschedulePlannerItem,
  togglePlannerItemDone as domainTogglePlannerItemDone,
} from "../../src/features/planner/domain.js";
import {
  applyProtectedPlannerFields,
  originUpdatedAt,
} from "../../src/features/planner/sync.js";
import {
  plannerGroupDate as sharedPlannerGroupDate,
  plannerOverdue as sharedPlannerOverdue,
  plannerGroupStatus as sharedPlannerGroupStatus,
  isPlannerReviewDayPhase as sharedPlannerReviewDayPhase,
} from "../../src/features/planner/helpers.js";
import { setupFocusController, renderFocusController, updateFocusControllerTimer } from "./ui/focus-controller.js";

/* =====================================================
   CONFIGURACOES GLOBAIS
   Constantes de dominio, estado em memoria e referencias de DOM.
   ===================================================== */

// Limite do buffer de undo/redo. Ajustavel conforme perfil de uso.
// Cada snapshot eh um JSON.stringify(state); em bases grandes (~600KB)
// 80 snapshots ~= 48MB de strings apenas para historico.
// Reduza se a aba travar em dispositivos moveis antigos.
const HISTORY_MAX_SNAPSHOTS = 80;
const WAL_QUEUE_MAX_ENTRIES = 50;
const WAL_MAX_DRAIN_FAILURES = 3;
const HEAVY_ARRAY_TRIM_THRESHOLD = 1000;
const ARCHIVE_MAX_ENTRIES = 250;
const SYNC_MAX_ARCHIVE_RECORDS = 1000;
const SYNC_TOMBSTONE_RETENTION_MS = 90 * 24 * 60 * 60 * 1000;
const SYNC_MAX_RETRIES = 5;
const SYNC_BASE_DELAY_MS = 1000;
const SYNC_RETRY_CAP_MS = 16000;
const SYNC_RETRY_WINDOW_MS = 300000;
const SYNC_PAGE_SIZE = 512000; // 512 KB por segmento JSON (limite de body da API ~4MB)
const SYNC_POLL_INTERVAL_MS = 60000;
const SYNC_INACTIVITY_DEBOUNCE_MS = 300000;
const PENDING_LESSON_DONE_RETENTION_MS = 24 * 60 * 60 * 1000;
const ENEM_DATA = { year: 2026, month: 10, day: 4 };
const subjects = [
  "Matemática",
  "Física",
  "Química",
  "Biologia",
  "Linguagens",
  "Redação",
  "História",
  "Geografia",
  "Filosofia",
  "Sociologia",
];

const colors = {
  "Matemática": "#70c69a",
  "Física": "#f2bc54",
  "Química": "#b48be6",
  Biologia: "#5aa7ff",
  Linguagens: "#e88977",
  "Português": "#ff9f7a",
  "Redação": "#f6df7f",
  "História": "#91d5d2",
  Geografia: "#9bc27a",
  Filosofia: "#dca3ce",
  Sociologia: "#a8a29e",
};

const TEST_USER_PROFILE = {
  displayName: "Teste",
  login: "teste@exemplo.com",
};
const SANDBOX_USER_PROFILE = {
  displayName: "Sandbox local",
  login: "sandbox@local.test",
};

const MERGE_ARRAY_DOMAINS = ["planner", "questions", "errors", "mocks", "essays", "studySessions", "focusTodos", "customSubjects", "flashcards", "images", "pendingLessons"];
const MERGE_CONTENT_MAP_DOMAINS = ["subjectNotes", "subjectContents", "subjectCycles", "cycleLessons", "customText", "smartFormMemory", "subjectCovers"];
const MERGE_LAYOUT_MAP_DOMAINS = ["dashboardOrder", "dashboardBlockSizes", "collapsedLessonGroups", "collapsedSections", "cycleContentOrder"];
const MERGE_SCALAR_DOMAINS = ["weeklyGoal", "studyWeeklyGoalHours", "plannerTimeEnabled"];

const state = stripUnusedQuestionListNames(repairEncodedData(loadState()));
if (!state.metadata._encodingVersion) {
  state.metadata._encodingVersion = 1;
}
const historyPast = [];
const historyFuture = [];
let remoteSyncEnabled = false;
let cloudSessionActive = false;
let remoteSaveTimer = null;
let localSaveTimer = null;
let syncStatusKey = "offline";
let localPersistRevision = 0;
let syncDisconnectedPromptShown = false;
let syncDisconnectedPreviousFocus = null;
let manualCloudLogout = false;
let localAuthSessionActive = false;
let pendingMergeUpload = false;
let pendingMergeSnapshot = null;
let conflictPending = false;
let syncPollingTimer = null;
let reconnectInFlight = false;
let lastUserActivityAt = Date.now();

let isBootstrapping = true;
let isApplyingHistory = false;
let lastAuthModalOpen = true;
let lastHistorySnapshot = "";
let calendarDate = new Date();
let plannerMode = document.body.classList.contains("layout-mobile") ? "day" : "week";
let plannerActiveView = "calendar";
let selectedErrorSubject = "";
let selectedErrorContent = "";
let selectedStudySubject = "";
let selectedStudyCycle = "";
let selectedQuestionSubject = "";
let questionViewMode = "cards";
let editMode = false;
let layoutEditSnapshot = null;
let activeStudyTimer = null;
let studyTimerTick = null;
let studyTimerTickLastUpdate = 0;
const STUDY_TIMER_TICK_MS = 1000;
let focusScreenOpen = false;
let focusPanelSignature = "";
let selectedStudyTimelineDate = "";
let plannerBuilderOpen = false;
let plannerBuilderType = "";
let plannerBuilderDate = localDateValue();
let plannerBuilderTime = "";
let plannerSelectedMaterialRefs = [];
let plannerSelectedQuestionRefs = [];
let plannerSelectedTaskIds = [];
let plannerSelectionAnchorId = "";
let plannerSelectedMaterialAnchor = "";
let plannerSelectedQuestionAnchor = "";
let activePlannerPointerDrag = null;
let plannerPointerSuppressClickUntil = 0;
let activeGenericPointerDrag = null;
let genericPointerSuppressClickUntil = 0;
let questionSelectedRowIds = [];
let activeQuestionDragIds = [];
let taskCompletionFlashTimer = null;
let focusStudyPanelCollapsed = false;
let flashcardTxtImportInput = null;
let activeDashboardDragBlockId = "";
let subjectNoteSaveTimer = null;
let pendingDeleteAction = null;
let pendingPlannerCreationAction = null;
let pendingQuestionGroupAction = null;
let editingQuestionId = "";
let pendingQuestionGroupId = "";
let questionGroupBuilderState = null;
let activeQuestionGroupViewId = "";
let questionGroupViewSearch = "";
let pendingPlannerLaterMove = null;
const REVIEW_DAY_CUTOFF_HOUR = 2;
const LOCAL_SAVE_DEBOUNCE_MS = 450;
const DASHBOARD_GRID_COLUMNS = 14;
const DASHBOARD_HEIGHT_GRID = 40;
const DASHBOARD_BLOCK_MIN_HEIGHT = 120;
const DASHBOARD_BLOCK_MAX_HEIGHT = 820;
const TOPBAR_NAV_REVEAL_MARGIN = 12;
const TOPBAR_NAV_WHEEL_FACTOR = 0.9; // atenua a velocidade do wheel na topbar
const TOPBAR_NAV_SCROLL_EASING = 0.18; // easing exponencial do scroll suave (0..1)
const TOPBAR_MENU_ANIM_MS = 200; // duração da animação do painel do menu de conta (== --ds-transition-popover)
const CHART_MIN_DRAWABLE_WIDTH = 220;
const CHART_COMPACT_BREAKPOINT = 420;
const CHART_LEGEND_HORIZONTAL_PADDING = 76;
const CHART_X_LABEL_HEIGHT = 34;
const CHART_MIN_VISIBLE_X_LABELS = 2;
const CHART_MAX_VISIBLE_X_LABELS = 8;
const CHART_MIN_X_LABEL_SPACING = 72;
const SUBJECT_FILTER_EXCLUSIONS = [
  "Ciências da Natureza",
  "Filosofia",
  "Geografia",
  "História",
  "Linguagens",
  "Redação",
  "Sociologia",
  "Flash-cards",
];

const filters = {
  dashboardSubject: "all",
  planner: "all",
  questions: "all",
  questionsDone: "all",
  questionsDate: "",
  errors: "all",
  mocks: "all",
  essays: "all",
};

const taskColumns = [
  { id: "todo", label: "A fazer" },
  { id: "doing", label: "Fazendo" },
  { id: "done", label: "Feito" },
];

const plannerBlockTypes = [
  { id: "Teoria", icon: "assets/teoria.png" },
  { id: "Flash-card", icon: "assets/flash-cards.png" },
  { id: "Revisão", icon: "assets/revisão.png" },
  { id: "Simulado", icon: "assets/simulado.png" },
  { id: "Questões", icon: "assets/questoes.png" },
  { id: "Redação", icon: "" },
  { id: "Outro", icon: "" },
];

const views = document.querySelectorAll(".view");
const navItems = document.querySelectorAll(".nav-item");
const pageTitle = document.querySelector("#pageTitle");
const pageSubtitle = document.querySelector("#pageSubtitle");
const pageHeaderContextActions = document.querySelector("#pageHeaderContextActions");
const pageHeaderActionPlaceholders = new Map();
const pageHeaderContextActionSelectors = {
  dashboard: [],
  materias: [".subjects-home-add", "#materias .subject-batch-trigger", "#materias .import-inline", "#materias .ghost-button[data-action='close-study-subject']", "#materias .ghost-button[data-action='close-study-cycle']"],
  tempo: [".timer-subject-add-button"],
  planner: [],
  questoes: ["#questoes .subject-back", "#questoes .questions-add-button", "#questoes .question-group-create-button"],
  erros: ["#erros .subject-back", "#erros .questions-add-button"],
  simulados: ["#simulados .questions-add-button"],
  redacoes: ["#redacoes .questions-add-button"],
};
const viewRoutes = {
  dashboard: "/",
  materias: "/materias",
  tempo: "/tempo",
  planner: "/planner",
  questoes: "/questoes",
  erros: "/caderno-de-erros",
  simulados: "/simulados",
  redacoes: "/redacoes",
};
const routeViews = {
  ...Object.fromEntries(Object.entries(viewRoutes).map(([viewId, route]) => [route, viewId])),
  "/cronometro": "tempo",
};
const sectionModuleLoaders = {
  dashboard: () => import("./sections/dashboard.js"),
  materias: () => import("./sections/materias.js"),
  tempo: () => import("./sections/tempo.js"),
  planner: () => import("./sections/planner.js"),
  questoes: () => import("./sections/questoes.js"),
  erros: () => import("./sections/erros.js"),
  simulados: () => import("./sections/simulados.js"),
  redacoes: () => import("./sections/redacoes.js"),
};
const loadedSectionModules = new Map();
const loadingSectionModules = new Map();
const failedSectionModules = new Set();
const sectionRenderCache = new Map();
const sectionChartCache = new Map();
const domainVersionKeys = [
  "planner",
  "questions",
  "errors",
  "essays",
  "mocks",
  "reviews",
  "subjects",
  "pendingLessons",
  "studySessions",
  "timer",
  "focus",
  "profile",
  "ui",
];
const domainVersions = Object.fromEntries(domainVersionKeys.map((key) => [key, 0]));
const domainVersionSnapshots = new Map();
const loadedDataDomains = new Set();
const loadingDataDomains = new Map();
const sectionDataDomains = {
  dashboard: ["profile", "metrics", "planner", "questions", "errors", "mocks", "essays", "studySessions"],
  materias: ["subjects", "planner", "questions", "errors", "mocks", "essays", "studySessions"],
  tempo: ["timer", "studySessions", "planner", "focus", "pendingLessons"],
  planner: ["planner", "subjects", "questions"],
  questoes: ["questions", "planner", "subjects"],
  erros: ["errors", "subjects"],
  simulados: ["mocks", "subjects"],
  redacoes: ["essays"],
};
const sectionRenderDomains = {
  dashboard: ["ui", "planner", "questions", "errors", "mocks", "essays", "studySessions"],
  materias: ["subjects", "pendingLessons", "planner", "questions", "errors", "mocks", "essays", "studySessions", "ui"],
  tempo: ["timer", "studySessions", "focus", "planner", "pendingLessons", "ui"],
  planner: ["planner", "questions", "subjects", "studySessions", "ui"],
  questoes: ["questions", "planner", "subjects", "ui"],
  erros: ["errors", "subjects", "ui"],
  simulados: ["mocks", "subjects", "ui"],
  redacoes: ["essays", "ui"],
};
const sectionChartDomains = {
  dashboard: ["questions"],
  redacoes: ["essays"],
};
let lastDragOverAt = 0;
let lastPlannerAutoScrollAt = 0;
let toastSequence = 0;
const virtualListStep = 80;
const virtualListMinimum = 80;
const virtualListLimits = {
  questoes: virtualListMinimum,
  planner: virtualListMinimum,
  erros: virtualListMinimum,
};
const nativeAlert = window.alert.bind(window);
const bootLoadingUI = window.bootLoadingUI || {
  hide() {},
  setStatus() {},
  show() {},
};

/* =====================================================
   BACKUP / WAL IMPORTS (dynamic — app.js is a plain script)
   ===================================================== */
let _backupModules = null;
let _walDrainTargetConfigured = false;

async function _loadBackupModules() {
  if (_backupModules) return _backupModules;
  if (window.__backupRing && window.__backupWal && window.__backupPersistence && window.__backupSanitize) {
    _backupModules = {
      ring: { ring: window.__backupRing, IDB_NAME: window.__backupRing.IDB_NAME, IDB_STORE: window.__backupRing.IDB_STORE, IDB_VERSION: window.__backupRing.IDB_VERSION },
      wal: { ring: window.__backupWal },
      persistence: { ring: window.__backupPersistence },
      sanitize: window.__backupSanitize,
    };
    return _backupModules;
  }
  const [ring, wal, persistence, sanitize] = await Promise.all([
    import("./backups/ring.js"),
    import("./backups/wal.js"),
    import("./backups/persistence.js"),
    import("./backups/sanitize.js"),
  ]);
  _backupModules = { ring, wal, persistence, sanitize };
  return _backupModules;
}

function publishWalRuntimeConfig() {
  globalThis.ENEM_WAL_QUEUE_MAX_ENTRIES = WAL_QUEUE_MAX_ENTRIES;
  globalThis.ENEM_WAL_MAX_DRAIN_FAILURES = WAL_MAX_DRAIN_FAILURES;
  globalThis.ENEM_HEAVY_ARRAY_TRIM_THRESHOLD = HEAVY_ARRAY_TRIM_THRESHOLD;
  globalThis.ENEM_ARCHIVE_MAX_ENTRIES = ARCHIVE_MAX_ENTRIES;
  globalThis.ENEM_SYNC_MAX_ARCHIVE_RECORDS = SYNC_MAX_ARCHIVE_RECORDS;
  globalThis.ENEM_SYNC_TOMBSTONE_RETENTION_MS = SYNC_TOMBSTONE_RETENTION_MS;
  globalThis.ENEM_SYNC_MAX_RETRIES = SYNC_MAX_RETRIES;
  globalThis.ENEM_SYNC_BASE_DELAY_MS = SYNC_BASE_DELAY_MS;
  globalThis.ENEM_SYNC_RETRY_CAP_MS = SYNC_RETRY_CAP_MS;
  globalThis.ENEM_SYNC_RETRY_WINDOW_MS = SYNC_RETRY_WINDOW_MS;
  globalThis.ENEM_SYNC_PAGE_SIZE = SYNC_PAGE_SIZE;
  globalThis.ENEM_SYNC_POLL_INTERVAL_MS = SYNC_POLL_INTERVAL_MS;
  globalThis.ENEM_SYNC_INACTIVITY_DEBOUNCE_MS = SYNC_INACTIVITY_DEBOUNCE_MS;
  window.ENEM_HEAVY_ARRAY_TRIM_THRESHOLD = HEAVY_ARRAY_TRIM_THRESHOLD;
  window.ENEM_ARCHIVE_MAX_ENTRIES = ARCHIVE_MAX_ENTRIES;
  window.ENEM_SYNC_MAX_ARCHIVE_RECORDS = SYNC_MAX_ARCHIVE_RECORDS;
  window.ENEM_SYNC_TOMBSTONE_RETENTION_MS = SYNC_TOMBSTONE_RETENTION_MS;
  window.ENEM_SYNC_MAX_RETRIES = SYNC_MAX_RETRIES;
  window.ENEM_SYNC_BASE_DELAY_MS = SYNC_BASE_DELAY_MS;
  window.ENEM_SYNC_RETRY_CAP_MS = SYNC_RETRY_CAP_MS;
  window.ENEM_SYNC_RETRY_WINDOW_MS = SYNC_RETRY_WINDOW_MS;
  window.ENEM_SYNC_PAGE_SIZE = SYNC_PAGE_SIZE;
  window.ENEM_SYNC_POLL_INTERVAL_MS = SYNC_POLL_INTERVAL_MS;
  window.ENEM_SYNC_INACTIVITY_DEBOUNCE_MS = SYNC_INACTIVITY_DEBOUNCE_MS;
  globalThis.__enemWalNotify = (message, type) => notify(message, type);
  globalThis.__enemArchiveNotify = (message, type) => notify(message, type);
  window.__enemArchiveNotify = (message, type) => notify(message, type);
  globalThis.__enemWalSetPendingStatus = () => setSyncStatus("pendingLocal");
  globalThis.__enemPersistSyncRetry = () => persistSyncRetry();
  globalThis.__enemGetPendingMergeSnapshot = () => (pendingMergeUpload ? cloneStudyState(pendingMergeSnapshot || state) : null);
  globalThis.__enemClearPendingMergeSnapshot = () => {
    pendingMergeUpload = false;
    pendingMergeSnapshot = null;
  };
}

function configureWalDrainTarget(modules) {
  publishWalRuntimeConfig();
  if (_walDrainTargetConfigured) return;
  const walRing = modules?.wal?.ring;
  if (!walRing || typeof walRing.setOnDrain !== "function") return;
  walRing.setOnDrain(async (snapshot) => {
    await _persistStateNow(snapshot);
  });
  _walDrainTargetConfigured = true;
}

async function enqueueWalSnapshot(snapshot) {
  try {
    const modules = await _loadBackupModules();
    configureWalDrainTarget(modules);
    await modules.wal.ring.enqueueWrite(cloneStudyState(snapshot));
  } catch (err) {
    console.error("[enqueueWalSnapshot] falhou ao registrar snapshot WAL:", err);
    setSyncStatus("pendingLocal");
    notify("Nao foi possivel registrar a fila local. O salvamento principal continua ativo.", "error");
  }
}

async function drainWalWrites() {
  try {
    const modules = await _loadBackupModules();
    configureWalDrainTarget(modules);
    const stats = await modules.wal.ring.drainWrites();
    if (stats?.failed > 0) {
      setSyncStatus("pendingLocal");
    }
    return stats;
  } catch (err) {
    console.error("[drainWalWrites] falhou ao drenar WAL:", err);
    setSyncStatus("pendingLocal");
    notify("A sincronizacao local ficou pendente neste dispositivo.", "error");
    return { processed: 0, failed: 1, pending: 1 };
  }
}

async function pendingWalWriteCount() {
  try {
    const modules = await _loadBackupModules();
    configureWalDrainTarget(modules);
    const walRing = modules?.wal?.ring;
    if (walRing && typeof walRing.pendingCount === "function") {
      return await walRing.pendingCount();
    }
  } catch (err) {
    console.error("[pendingWalWriteCount]", err);
  }
  return 0;
}

function backupSanitizer() {
  if (_backupModules?.sanitize) return _backupModules.sanitize;
  if (window.__backupSanitize) return window.__backupSanitize;
  return null;
}

// Aplica a whitelist de dados de negocio a um objeto de estado.
// Se o modulo sanitize ainda nao estiver disponivel, devolve o objeto
// intacto (comportamento legado) — no browser o script tag ja o registra.
function sanitizeStateForBackup(source) {
  const sanitizer = backupSanitizer();
  if (sanitizer && typeof sanitizer.sanitizeStateForBackup === "function") {
    return sanitizer.sanitizeStateForBackup(source);
  }
  return source;
}

function getLatestBackup() {
  return _loadBackupModules().then((m) => m.ring.ring.getLatestBackup());
}

function _persistStateNow(state) {
  return _loadBackupModules().then((m) => m.persistence.ring.persistStateNow(state));
}

function resetStorageWarning() {
  return _loadBackupModules().then((m) => m.persistence.ring.resetStorageWarning());
}

async function clearEphemeralSyncArtifacts(options = {}) {
  const preserveStateCache = options.preserveStateCache !== false;
  const nextState = options.nextState || null;
  const m = await _loadBackupModules();

  // D-05: sync nunca apaga o local — preserva backups, cache, temp backup e imagens.
  try {
    await m.persistence.ring.clearEphemeralStateCache?.({ preserveStateCache, nextState });
  } catch (err) {
    console.error("[clearEphemeralSyncArtifacts] falhou ao limpar cache local:", err);
  }
}

async function finalizeRemoteSync() {
  markLastSyncedAt();
  purgeExpiredTombstones(state);
  state._cacheStale = false;
  await clearEphemeralSyncArtifacts({ preserveStateCache: true, nextState: state });
}

_loadBackupModules();

/* =====================================================
   NOTIFICACOES
   ===================================================== */
function toastContainer() {
  let container = document.querySelector("#toastStack");
  if (container) return container;
  container = document.createElement("div");
  container.id = "toastStack";
  container.className = "toast-stack";
  container.setAttribute("role", "region");
  container.setAttribute("aria-live", "polite");
  container.setAttribute("aria-label", "Notificações");
  document.body.appendChild(container);
  return container;
}

function showToast(message, options = {}) {
  const text = String(message || "").trim();
  if (!text) return null;
  if (!document.body) {
    nativeAlert(text);
    return null;
  }
  const container = toastContainer();
  const toast = document.createElement("div");
  const type = options.type || "info";
  const id = `toast-${++toastSequence}`;
  toast.id = id;
  toast.className = `app-toast ${type}`;
  toast.setAttribute("role", type === "error" ? "alert" : "status");
  toast.innerHTML = `
    <div class="app-toast-content">
      <strong>${type === "error" ? "Atenção" : type === "success" ? "Pronto" : "Aviso"}</strong>
      <span></span>
    </div>
    <button class="app-toast-close" type="button" aria-label="Fechar aviso">×</button>
  `;
  toast.querySelector("span").textContent = text;
  const close = () => {
    toast.classList.add("is-leaving");
    setTimeout(() => toast.remove(), 180);
  };
  toast.querySelector("button")?.addEventListener("click", close);
  container.appendChild(toast);
  if (type !== "error") {
    const duration = Number.isFinite(options.duration) ? options.duration : Math.min(7000, Math.max(3200, text.length * 45));
    setTimeout(close, duration);
  }
  return toast;
}

function showUndoToast(message = "Ação concluída.") {
  const toast = showToast(message, { type: "success", duration: 9000 });
  if (!toast || historyPast.length <= 1) return toast;
  const actions = document.createElement("div");
  actions.className = "app-toast-actions";
  const undoButton = document.createElement("button");
  undoButton.className = "app-toast-action";
  undoButton.type = "button";
  undoButton.textContent = "Desfazer";
  undoButton.addEventListener("click", () => {
    undoState();
    toast.classList.add("is-leaving");
    setTimeout(() => toast.remove(), 180);
  });
  actions.appendChild(undoButton);
  toast.appendChild(actions);
  return toast;
}

function notify(message, type = "info", options = {}) {
  return showToast(message, { ...options, type });
}

function notifyConflictCount(count = 0) {
  if (!count) return;
  const message = count === 1
    ? "Mesclado 1 item da nuvem"
    : `Mesclados ${count} itens da nuvem`;
  notify(message, "info");
}

window.alert = (message) => {
  const text = String(message || "");
  const errorTone = /erro|falh|não consegui|nao consegui|inválid|invalid|finalize|permita|selecione|digite/i.test(text);
  notify(text, errorTone ? "error" : "info");
};

function syncDetailsPanelAccessibility(details) {
  if (!details?.matches?.(".action-menu, .filter-menu")) return;
  const panel = details.querySelector(":scope > .action-menu-panel, :scope > .filter-menu-panel");
  const trigger = details.querySelector(":scope > summary");
  if (!panel) return;
  const open = details.hasAttribute("open");
  if (trigger) trigger.setAttribute("aria-expanded", String(open));
  panel.toggleAttribute("inert", !open);
  panel.setAttribute("aria-hidden", String(!open));
}

function syncAllDetailsPanelAccessibility() {
  document.querySelectorAll(".action-menu, .filter-menu").forEach(syncDetailsPanelAccessibility);
}

function resetVirtualListLimit(key = "") {
  if (Object.hasOwn(virtualListLimits, key)) virtualListLimits[key] = virtualListMinimum;
}

function increaseVirtualListLimit(key = "") {
  if (!Object.hasOwn(virtualListLimits, key)) return;
  virtualListLimits[key] += virtualListStep;
}

function virtualListLimit(key = "") {
  return virtualListLimits[key] || virtualListMinimum;
}

function virtualListButton(key = "", visibleCount = 0, totalCount = 0, label = "itens") {
  if (!Object.hasOwn(virtualListLimits, key) || visibleCount >= totalCount) return "";
  return `
    <tr class="virtual-list-row">
      <td colspan="12">
        <button class="ghost-button virtual-list-more" data-action="show-more-list" data-list="${key}" type="button">
          Mostrar mais ${label} (${visibleCount}/${totalCount})
        </button>
      </td>
    </tr>
  `;
}

function closeActionMenu() {
  document.querySelector(".action-menu[open]")?.removeAttribute("open");
  syncActionMenuState();
}

function closeFilterMenus() {
  document.querySelectorAll(".filter-menu[open]").forEach((element) => element.removeAttribute("open"));
  syncFilterMenuState();
}

function closeAllOverlays({ except } = {}) {
  except = except || [];
  if (!except.includes("plannerCardMenu")) closePlannerCardMenu();
  if (!except.includes("timerSubjectMenu")) closeTimerSubjectMenu();
  if (!except.includes("timerSubjectModal")) closeTimerSubjectModal();
  if (!except.includes("subjectModal")) closeSubjectModal();
  if (!except.includes("studySessionMenu")) closeStudySessionMenu();
  if (!except.includes("studySessionModal")) closeStudySessionModal();
  if (!except.includes("actionMenu")) closeActionMenu();
  if (!except.includes("filterMenus")) closeFilterMenus();
  if (!except.includes("topbarMobileMenu")) closeTopbarMobileMenu();
  const shortcutOvr = document.querySelector("#shortcutOverlay");
  if (!except.includes("shortcutOverlay") && shortcutOvr && !shortcutOvr.classList.contains("hidden")) shortcutOvr.classList.add("hidden");
  if (!except.includes("questionModal")) closeQuestionModal();
  if (!except.includes("questionGroupModal")) closeQuestionGroupModal();
  if (!except.includes("questionGroupView")) closeQuestionGroupView();
  if (!except.includes("deleteConfirmModal")) closeDeleteConfirmModal();
  if (!except.includes("plannerCreationModal")) closePlannerCreationModal();
  if (!except.includes("plannerLaterModal")) closePlannerLaterModal();
  if (!except.includes("plannerCompletionModal")) closePlannerCompletionModal();
  if (!except.includes("subjectCycleModal")) closeSubjectCycleModal();
  if (!except.includes("cycleLessonModal")) closeCycleLessonModal();
  if (!except.includes("layoutEditExitModal")) closeLayoutEditExitModal();
  if (!except.includes("errorModal")) closeEntryModal("errorModal");
  if (!except.includes("mockModal")) closeEntryModal("mockModal");
  if (!except.includes("essayModal")) closeEntryModal("essayModal");
  if (!except.includes("syncDisconnectedModal")) closeSyncDisconnectedModal();
  if (!except.includes("imageModal")) closeImageModal();
  if (!except.includes("focusScreen") && focusScreenOpen) { focusScreenOpen = false; render(); }
}

function anchorRectFromPoint(x, y) {
  const left = Number.isFinite(x) ? x : 0;
  const top = Number.isFinite(y) ? y : 0;
  return { left, right: left, top, bottom: top, width: 0, height: 0 };
}

function floatingAnchorRect(anchor) {
  if (!anchor) return anchorRectFromPoint(window.innerWidth / 2, window.innerHeight / 2);
  if (anchor instanceof Event) return anchorRectFromPoint(anchor.clientX, anchor.clientY);
  if (anchor.nodeType === Node.ELEMENT_NODE) return anchor.getBoundingClientRect();
  if (Number.isFinite(anchor.clientX) || Number.isFinite(anchor.clientY)) return anchorRectFromPoint(anchor.clientX, anchor.clientY);
  if (Number.isFinite(anchor.x) || Number.isFinite(anchor.y)) return anchorRectFromPoint(anchor.x, anchor.y);
  return anchorRectFromPoint(window.innerWidth / 2, window.innerHeight / 2);
}

function keepPanelInsideViewport(panel, position = {}, options = {}) {
  if (!panel) return { left: 0, top: 0 };
  const padding = Number.isFinite(options.padding) ? options.padding : 8;
  const rect = panel.getBoundingClientRect();
  const maxLeft = Math.max(padding, window.innerWidth - rect.width - padding);
  const maxTop = Math.max(padding, window.innerHeight - rect.height - padding);
  const left = Math.min(Math.max(padding, position.left || padding), maxLeft);
  const top = Math.min(Math.max(padding, position.top || padding), maxTop);
  panel.style.left = `${left}px`;
  panel.style.top = `${top}px`;
  const actualRect = panel.getBoundingClientRect();
  let adjustedLeft = left;
  let adjustedTop = top;
  if (actualRect.left < padding) adjustedLeft += padding - actualRect.left;
  if (actualRect.right > window.innerWidth - padding) adjustedLeft -= actualRect.right - (window.innerWidth - padding);
  if (actualRect.top < padding) adjustedTop += padding - actualRect.top;
  if (actualRect.bottom > window.innerHeight - padding) adjustedTop -= actualRect.bottom - (window.innerHeight - padding);
  adjustedLeft = Math.max(padding, adjustedLeft);
  adjustedTop = Math.max(padding, adjustedTop);
  if (adjustedLeft !== left) panel.style.left = `${adjustedLeft}px`;
  if (adjustedTop !== top) panel.style.top = `${adjustedTop}px`;
  return { left: adjustedLeft, top: adjustedTop };
}

function positionFloatingPanel(anchor, panel, options = {}) {
  if (!panel) return null;
  const anchorRect = floatingAnchorRect(anchor);
  const gap = Number.isFinite(options.gap) ? options.gap : 8;
  const padding = Number.isFinite(options.padding) ? options.padding : 8;
  const align = options.align || "start";
  const panelRect = panel.getBoundingClientRect();
  const preferPoint = options.mode === "point";
  const preferBottomAnchor = options.anchor === "bottom";

  let top;
  if (preferBottomAnchor) {
    const bottomAlignedTop = anchorRect.bottom - panelRect.height;
    const fitsAbove = bottomAlignedTop >= padding;
    const fitsBelow = anchorRect.bottom + gap + panelRect.height <= window.innerHeight - padding;
    top = fitsAbove || !fitsBelow
      ? bottomAlignedTop
      : anchorRect.bottom + gap;
  } else {
    const canOpenBelow = anchorRect.bottom + gap + panelRect.height <= window.innerHeight - padding;
    const canOpenAbove = anchorRect.top - gap - panelRect.height >= padding;
    top = canOpenBelow || !canOpenAbove
      ? anchorRect.bottom + gap
      : anchorRect.top - gap - panelRect.height;
  }

  let left = align === "end"
    ? anchorRect.right - panelRect.width
    : anchorRect.left;
  if (preferPoint) left = anchorRect.left;

  const overflowsRight = left + panelRect.width > window.innerWidth - padding;
  const fallbackLeft = preferPoint
    ? anchorRect.left - panelRect.width
    : anchorRect.right - panelRect.width;
  if (overflowsRight) left = fallbackLeft;

  const result = keepPanelInsideViewport(panel, { left, top }, { padding });
  panel.dataset.floatingPlacement = result.top < anchorRect.top ? "top" : "bottom";
  panel.dataset.floatingAlign = result.left <= anchorRect.left ? "start" : "end";
  return result;
}

function registerFloatingPanel(panel, anchor, options = {}) {
  if (!panel) return;
  panel.__floatingAnchor = anchor || null;
  panel.__floatingOptions = options || {};
  positionFloatingPanel(anchor, panel, options);
}

function refreshFloatingPanels() {
  document.querySelectorAll("[data-floating-panel='true']:not([inert])").forEach((panel) => {
    positionFloatingPanel(panel.__floatingAnchor, panel, panel.__floatingOptions || {});
  });
  syncActionMenuState();
  syncFilterMenuState();
}

function updateActionMenuPlacement(menu) {
  if (!menu) return;
  const trigger = menu.querySelector("summary");
  const panel = menu.querySelector(".action-menu-panel");
  if (!trigger || !panel) return;
  panel.dataset.floatingPanel = "true";
  registerFloatingPanel(panel, trigger, { align: "end", gap: 8, padding: 12 });
}

function syncActionMenuState() {
  document.querySelectorAll(".action-menu").forEach(syncDetailsPanelAccessibility);
  document.querySelectorAll(".action-menu[open]").forEach((menu) => updateActionMenuPlacement(menu));
  syncTopbarBackdropState();
}

function updateFilterMenuPlacement(menu) {
  if (!menu) return;
}

function syncFilterMenuState() {
  document.querySelectorAll(".filter-menu").forEach(syncDetailsPanelAccessibility);
  const openMenus = [...document.querySelectorAll(".filter-menu[open]")];
  document.body.classList.toggle("filter-menu-open", openMenus.length > 0);
  openMenus.forEach((menu) => updateFilterMenuPlacement(menu));
  syncTopbarBackdropState();
}

const OVERLAY_SCROLL_LOCK_SELECTOR = [
  ".image-editor-modal",
  ".image-modal",
  ".auth-overlay",
  ".question-group-view-modal",
  ".timer-subject-modal",
  ".subject-modal",
  ".study-session-modal",
].join(", ");

function isVisibleOverlayElement(element) {
  return Boolean(element)
    && !element.classList.contains("hidden")
    && element.getAttribute("aria-hidden") !== "true";
}

function hasVisibleOverlayOpen() {
  return [...document.querySelectorAll(OVERLAY_SCROLL_LOCK_SELECTOR)].some(isVisibleOverlayElement);
}

function lockBodyScroll() {
  if (document.body.dataset.scrollLock === "true") return;
  document.body.dataset.scrollLock = "true";
  document.body.classList.add("modal-open");
}

function unlockBodyScroll() {
  if (document.body.dataset.scrollLock !== "true") return;
  document.body.classList.remove("modal-open");
  delete document.body.dataset.scrollLock;
}

function syncBodyScrollLock() {
  if (hasVisibleOverlayOpen()) {
    lockBodyScroll();
    return;
  }
  unlockBodyScroll();
}

window.syncBodyScrollLock = syncBodyScrollLock;

function toggleModalById(id = "", open = false) {
  const modal = document.querySelector(`#${id}`);
  if (!modal) return;
  modal.classList.toggle("hidden", !open);
  modal.setAttribute("aria-hidden", String(!open));
  syncBodyScrollLock();
}

function closeModalById(id = "", { cleanup = null } = {}) {
  toggleModalById(id, false);
  if (typeof cleanup === "function") cleanup();
}

function questionModalElement() {
  return document.querySelector("#questionModal");
}

function deleteConfirmModalElement() {
  return document.querySelector("#deleteConfirmModal");
}

function plannerCreationModalElement() {
  return document.querySelector("#plannerCreationModal");
}

function plannerLaterModalElement() {
  return document.querySelector("#plannerLaterModal");
}

function questionGroupModalElement() {
  return document.querySelector("#questionGroupModal");
}

function questionGroupBuilderModalElement() {
  return document.querySelector("#questionGroupBuilderModal");
}

function closeQuestionModal() {
  editingQuestionId = "";
  pendingQuestionGroupId = "";
  closeModalById("questionModal");
}
function closeDeleteConfirmModal() { closeModalById("deleteConfirmModal", { cleanup: () => { pendingDeleteAction = null; } }); }
function closePlannerCreationModal() { closeModalById("plannerCreationModal", { cleanup: () => { pendingPlannerCreationAction = null; } }); }
function closePlannerLaterModal() { closeModalById("plannerLaterModal", { cleanup: () => { pendingPlannerLaterMove = null; } }); }
function closeQuestionGroupModal() { closeModalById("questionGroupModal", { cleanup: () => { pendingQuestionGroupAction = null; } }); }

function plannerCompletionModalElement() {
  return document.querySelector("#plannerCompletionModal");
}

function closePlannerCompletionModal() {
  plannerCompletionModalElement()?.remove();
  syncBodyScrollLock();
}

function openPlannerCompletionModal(item) {
  const questionId = item?.questionData?.questionId;
  const question = activeRecords(state.questions || []).find((entry) => entry.id === questionId);
  if (!question) return false;

  closeAllOverlays();
  closePlannerCompletionModal();
  const modal = document.createElement("div");
  modal.id = "plannerCompletionModal";
  modal.className = "image-editor-modal planner-completion-modal";
  modal.setAttribute("aria-hidden", "false");
  modal.innerHTML = `
    <div class="modal timer-subject-dialog planner-completion-dialog" role="dialog" aria-modal="true" aria-labelledby="plannerCompletionTitle">
      <div class="modal-content timer-subject-content planner-completion-content">
        <div class="planner-builder-head">
          <div>
            <strong id="plannerCompletionTitle">Finalizar lista de questões</strong>
            <small>Registre seus acertos para concluir a tarefa.</small>
          </div>
          <button class="icon-button" data-action="close-planner-completion-modal" type="button" aria-label="Fechar">×</button>
        </div>
        <form class="planner-builder-form planner-completion-form" data-action="submit-planner-completion" data-id="${escapeAttribute(item.id)}">
          <label><span>Data</span><input name="date" type="date" value="${escapeAttribute(item.date || localDateValue())}" required></label>
          ${state.plannerTimeEnabled || item.time ? renderPlannerTimeFields(item.time || "05:00", item.endTime || defaultPlannerEndTime(item.time || "05:00")) : ""}
          <label><span>Tarefa</span><input name="title" value="${escapeAttribute(item.title || "Lista de questões")}" required></label>
          <label><span>Conteúdo</span><input name="content" value="${escapeAttribute(question.content || item.questionData?.content || "")}" required></label>
          <div class="planner-completion-question-summary">
            <span>Total de questões</span>
            <strong>${Math.max(1, number(question.questions || item.questionData?.totalQuestions))}</strong>
          </div>
          <label class="planner-completion-correct-field"><span>Acertos</span><input name="correct" type="number" min="0" max="${Math.max(1, number(question.questions || item.questionData?.totalQuestions))}" value="${Math.max(0, number(question.correct))}" required autofocus></label>
          <div class="question-modal-actions">
            <button class="ghost-button" data-action="close-planner-completion-modal" type="button">Cancelar</button>
            <button type="submit">Finalizar tarefa</button>
          </div>
        </form>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  syncBodyScrollLock();
  modal.querySelector('input[name="correct"]')?.focus();
  return true;
}

function submitPlannerCompletion(form) {
  const item = state.planner.find((entry) => entry.id === form.dataset.id);
  const questionId = item?.questionData?.questionId;
  const question = activeRecords(state.questions || []).find((entry) => entry.id === questionId);
  if (!item || !question) return false;
  const totalQuestions = Math.max(1, number(question.questions || item.questionData?.totalQuestions));
  const correct = number(form.elements.correct.value);
  if (!Number.isFinite(correct) || correct < 0 || correct > totalQuestions) {
    alert(`Informe acertos entre 0 e ${totalQuestions}.`);
    return false;
  }
  item.date = form.elements.date.value || item.date;
  item.title = form.elements.title.value.trim() || item.title;
  if (form.elements.time) item.time = form.elements.time.value;
  if (form.elements.endTime) item.endTime = form.elements.endTime.value;
  question.date = item.date;
  question.content = form.elements.content.value.trim() || question.content;
  question.correct = correct;
  question.done = true;
  question.plannerDone = true;
  touchRecord("questions", question.id);
  setPlannerDone(item, true, { completedAt: reviewDayValue() });
  closePlannerCompletionModal();
  renderPlannerLight();
  saveState();
  commitHistory();
  showUndoToast(`Tarefa concluída: ${correct}/${totalQuestions} acertos.`);
  return true;
}

function closeQuestionGroupBuilderModal() {
  questionGroupBuilderState = null;
  questionGroupBuilderModalElement()?.remove();
  syncBodyScrollLock();
}

function openQuestionModal({ item = null, groupId = "" } = {}) {
  const modal = questionModalElement();
  if (!modal) return;
  editingQuestionId = item?.id || "";
  pendingQuestionGroupId = editingQuestionId ? "" : String(groupId || "").trim();
  closeAllOverlays({ except: pendingQuestionGroupId ? ["questionGroupView"] : [] });
  const form = modal.querySelector("#questionForm");
  const title = modal.querySelector(".planner-builder-head strong");
  const subtitle = modal.querySelector(".planner-builder-head small");
  const submit = form?.querySelector('button[type="submit"]');
  form?.reset();
  if (title) title.textContent = editingQuestionId ? "Editar lista de questões" : "Nova lista de questões";
  if (subtitle) subtitle.textContent = editingQuestionId
    ? "Atualize matéria, conteúdo, quantidade e acertos"
    : "Registre matéria, conteúdo, quantidade e acertos";
  if (submit) submit.textContent = editingQuestionId ? "Salvar alterações" : "Salvar lista";
  modal.classList.remove("hidden");
  modal.classList.toggle("question-modal-over-group", Boolean(pendingQuestionGroupId));
  modal.setAttribute("aria-hidden", "false");
  syncBodyScrollLock();
  if (item && form) {
    form.elements.date.value = String(item.date || "");
    form.elements.subject.value = String(item.subject || "Geral");
    form.elements.content.value = String(item.content || "");
    form.elements.subcontent.value = String(item.subcontent || "");
    form.elements.questions.value = String(item.questions ?? "");
    form.elements.correct.value = String(item.correct ?? "");
  }
  syncQuestionFormDateDefault();
  renderContentOptions();
  if (!item) applySmartFieldMemory(modal);
  setTimeout(() => modal.querySelector('select[name="subject"], input[name="date"]')?.focus(), 0);
}

function openQuestionGroupModal({ itemCount = 2, defaultName = "", defaultSubject = "", onConfirm = null } = {}) {
  const modal = questionGroupModalElement();
  if (!modal) return false;
  closeAllOverlays();
  pendingQuestionGroupAction = typeof onConfirm === "function" ? onConfirm : null;
  modal.classList.remove("hidden");
  modal.setAttribute("aria-hidden", "false");
  syncBodyScrollLock();
  const title = modal.querySelector("#questionGroupModalTitle");
  const subtitle = modal.querySelector("#questionGroupModalSubtitle");
  const input = modal.querySelector('input[name="groupName"]');
  const subjectInput = modal.querySelector('select[name="groupSubject"]');
  if (title) title.textContent = "Novo grupo de listas";
  if (subtitle) subtitle.textContent = `${itemCount} listas serão agrupadas`;
  if (input) input.value = defaultName || `Grupo com ${itemCount} listas`;
  if (subjectInput) {
    subjectInput.innerHTML = questionGroupSubjectOptions(defaultSubject);
    subjectInput.value = defaultSubject || "Geral";
  }
  setTimeout(() => input?.focus(), 0);
  return true;
}

function confirmQuestionGroupModal(name = "", subject = "") {
  const action = pendingQuestionGroupAction;
  closeQuestionGroupModal();
  if (typeof action === "function") action(name, subject);
}

function questionGroupSubjectOptions(selectedSubject = "") {
  const regularSubjects = [...new Set(["Geral", ...getSubjects()])];
  const options = selectedSubject === "__mixed__"
    ? [{ value: "__mixed__", label: "Várias matérias" }, ...regularSubjects.map((subject) => ({ value: subject, label: subject }))]
    : regularSubjects.map((subject) => ({ value: subject, label: subject }));
  if (selectedSubject && selectedSubject !== "__mixed__" && !options.some((option) => option.value === selectedSubject)) {
    options.unshift({ value: selectedSubject, label: selectedSubject });
  }
  return options
    .map((option) => `<option value="${escapeAttribute(option.value)}"${option.value === selectedSubject ? " selected" : ""}>${escapeHtml(option.label)}</option>`)
    .join("");
}

function emptyQuestionGroupBuilderRow() {
  return {
    recordId: "",
    date: "",
    content: "",
    subcontent: "",
    questions: "",
    correct: "",
  };
}

function questionGroupBuilderRows() {
  return [emptyQuestionGroupBuilderRow(), emptyQuestionGroupBuilderRow()];
}

function questionGroupBuilderSubjectOptions(subject) {
  return [...new Set([subject, ...getSubjects()])]
    .filter(Boolean)
    .map((entry) => `<option value="${escapeAttribute(entry)}"${entry === subject ? " selected" : ""}>${escapeHtml(entry)}</option>`)
    .join("");
}

function renderQuestionGroupBuilderModal() {
  const builder = questionGroupBuilderState;
  if (!builder) return;
  let modal = questionGroupBuilderModalElement();
  if (!modal) {
    modal = document.createElement("div");
    modal.id = "questionGroupBuilderModal";
    document.body.appendChild(modal);
  }
  modal.className = "image-editor-modal question-group-builder-modal";
  modal.setAttribute("aria-hidden", "false");
  modal.innerHTML = `
    <div class="modal question-group-builder-dialog">
      <div class="modal-content question-group-builder-content">
        <div class="planner-builder-head">
          <div>
            <strong>Adicionar grupo de listas</strong>
            <small>As listas deste grupo pertencem à mesma matéria.</small>
          </div>
          <button class="icon-button" type="button" data-action="close-question-group-builder" aria-label="Fechar">×</button>
        </div>
        <form class="question-group-builder-form" data-action="submit-question-group-builder">
          <div class="question-group-builder-fields">
            <label>
              <span>Matéria</span>
              <select name="subject" required>${questionGroupBuilderSubjectOptions(builder.subject)}</select>
            </label>
            <label>
              <span>Nome do grupo</span>
              <input name="groupName" value="${escapeAttribute(builder.name)}" placeholder="Nome do grupo" required>
            </label>
          </div>
          <div class="question-group-builder-list">
            ${builder.rows.map((row, index) => `
              <fieldset class="question-group-builder-row" data-question-group-builder-row data-row-index="${index}" data-record-id="${escapeAttribute(row.recordId)}">
                <legend>Lista ${index + 1}</legend>
                <div class="question-group-builder-row-fields">
                  <label><span>Data</span><input type="date" data-field="date" value="${escapeAttribute(row.date)}"></label>
                  <label class="question-group-builder-content-field"><span>Conteúdo</span><input type="text" data-field="content" value="${escapeAttribute(row.content)}" required></label>
                  <label><span>Subconteúdo</span><input type="text" data-field="subcontent" value="${escapeAttribute(row.subcontent)}"></label>
                  <label><span>Questões</span><input type="number" data-field="questions" min="1" value="${escapeAttribute(row.questions)}" required></label>
                  <label><span>Acertos</span><input type="number" data-field="correct" min="0" value="${escapeAttribute(row.correct)}"></label>
                  <button class="question-group-builder-remove" type="button" data-action="remove-question-group-builder-row" data-row-index="${index}" aria-label="Remover lista ${index + 1}"><span aria-hidden="true">×</span></button>
                </div>
              </fieldset>
            `).join("")}
          </div>
          <button class="question-group-builder-add" type="button" data-action="add-question-group-builder-row">+ Adicionar lista vazia</button>
          <div class="question-modal-actions">
            <button class="ghost-button" type="button" data-action="close-question-group-builder">Cancelar</button>
            <button type="submit">Criar grupo</button>
          </div>
        </form>
      </div>
    </div>
  `;
  syncBodyScrollLock();
}

function openQuestionGroupBuilder(subject = selectedQuestionSubject) {
  const normalizedSubject = canonicalSubject(subject || "Geral");
  questionGroupBuilderState = {
    subject: normalizedSubject,
    name: `Grupo de ${normalizedSubject}`,
    rows: questionGroupBuilderRows(),
  };
  closeAllOverlays({ except: ["questionGroupBuilderModal"] });
  renderQuestionGroupBuilderModal();
  setTimeout(() => questionGroupBuilderModalElement()?.querySelector('select[name="subject"]')?.focus(), 0);
}

function syncQuestionGroupBuilderStateFromModal() {
  const builder = questionGroupBuilderState;
  const modal = questionGroupBuilderModalElement();
  const form = modal?.querySelector("form");
  if (!builder || !form) return;
  builder.subject = canonicalSubject(String(form.elements.subject?.value || builder.subject || "Geral").trim() || "Geral");
  builder.name = String(form.elements.groupName?.value || builder.name || "").trim();
  builder.rows = [...form.querySelectorAll("[data-question-group-builder-row]")].map((row) => ({
    recordId: row.dataset.recordId || "",
    date: String(row.querySelector('[data-field="date"]')?.value || ""),
    content: String(row.querySelector('[data-field="content"]')?.value || ""),
    subcontent: String(row.querySelector('[data-field="subcontent"]')?.value || ""),
    questions: String(row.querySelector('[data-field="questions"]')?.value || ""),
    correct: String(row.querySelector('[data-field="correct"]')?.value || ""),
  }));
}

function addQuestionGroupBuilderRow() {
  if (!questionGroupBuilderState) return;
  syncQuestionGroupBuilderStateFromModal();
  questionGroupBuilderState.rows.push(emptyQuestionGroupBuilderRow());
  renderQuestionGroupBuilderModal();
}

function removeQuestionGroupBuilderRow(index) {
  if (!questionGroupBuilderState) return;
  syncQuestionGroupBuilderStateFromModal();
  questionGroupBuilderState.rows.splice(index, 1);
  renderQuestionGroupBuilderModal();
}

function saveQuestionGroupBuilder(form) {
  if (!questionGroupBuilderState) return false;
  const subject = canonicalSubject(String(form.elements.subject?.value || "").trim() || "Geral");
  const name = String(form.elements.groupName?.value || "").trim();
  const rows = [...form.querySelectorAll("[data-question-group-builder-row]")];
  if (rows.length < 2) {
    notify("Adicione pelo menos duas listas ao grupo.", "error");
    return false;
  }
  if (!name) {
    notify("Digite um nome para o grupo.", "error");
    return false;
  }
  const records = [];
  for (const row of rows) {
    const content = String(row.querySelector('[data-field="content"]')?.value || "").trim();
    const questionsValue = String(row.querySelector('[data-field="questions"]')?.value || "").trim();
    if (!content || !questionsValue) {
      notify("Preencha conteúdo e quantidade de questões em todas as listas.", "error");
      return false;
    }
    const data = {
      date: String(row.querySelector('[data-field="date"]')?.value || "").trim() || questionTodayValue(),
      subject,
      content,
      subcontent: String(row.querySelector('[data-field="subcontent"]')?.value || "").trim(),
      questions: Math.max(1, number(questionsValue)),
      correct: number(row.querySelector('[data-field="correct"]')?.value),
    };
    data.correct = Math.min(data.questions, Math.max(0, data.correct));
    const existing = row.dataset.recordId
      ? (state.questions || []).find((item) => item.id === row.dataset.recordId && !isQuestionGroup(item))
      : null;
    const record = existing || addQuestionRecord(data);
    if (existing) {
      Object.assign(existing, data);
      syncQuestionPerformance(existing);
      touchRecord("questions", existing.id);
    }
    if (subject !== "Geral") ensureSubjectExists(subject);
    rememberContent(subject, content);
    records.push(record);
  }

  const group = {
    id: uid(),
    name,
    isGroup: true,
    collapsed: false,
    subject,
    content: "",
    questions: 0,
    correct: 0,
    difficulty: "-",
    totalLists: records.length,
    date: records[0]?.date || questionTodayValue(),
  };
  state.questions = [...(state.questions || []), group];
  touchRecord("questions", group.id);
  records.forEach((record) => {
    record.questionGroupId = group.id;
    touchRecord("questions", record.id);
  });
  syncQuestionGroupState(group.id, { removeEmpty: false });
  questionSelectedRowIds = [group.id];
  closeQuestionGroupBuilderModal();
  renderSectionNow("questoes", { save: true, commit: true });
  openQuestionGroupView(group.id);
  return true;
}

function subjectCycleModalElement() {
  return document.querySelector("#subjectCycleModal");
}

function closeSubjectCycleModal() { closeModalById("subjectCycleModal"); }

function openSubjectCycleModal() {
  if (!selectedStudySubject) return;
  const modal = subjectCycleModalElement();
  if (!modal) return;
  closeAllOverlays();
  modal.classList.remove("hidden");
  modal.setAttribute("aria-hidden", "false");
  syncBodyScrollLock();
  const subjectInput = modal.querySelector("#subjectCycleModalSubject");
  const textarea = modal.querySelector('textarea[name="cycleBatch"]');
  if (subjectInput) subjectInput.value = selectedStudySubject;
  if (textarea) textarea.value = "";
  setTimeout(() => textarea?.focus(), 0);
}

function cycleLessonModalElement() {
  return document.querySelector("#cycleLessonModal");
}

function closeCycleLessonModal() { closeModalById("cycleLessonModal"); }

function openCycleLessonModal(group = "") {
  if (!selectedStudySubject || !selectedStudyCycle) return;
  const modal = cycleLessonModalElement();
  if (!modal) return;
  closeAllOverlays();
  modal.classList.remove("hidden");
  modal.setAttribute("aria-hidden", "false");
  syncBodyScrollLock();
  const subjectInput = modal.querySelector("#cycleLessonModalSubject");
  const cycleInput = modal.querySelector("#cycleLessonModalCycle");
  const groupInput = modal.querySelector("#cycleLessonModalGroup");
  const textarea = modal.querySelector('textarea[name="topicBatch"]');
  if (subjectInput) subjectInput.value = selectedStudySubject;
  if (cycleInput) cycleInput.value = selectedStudyCycle;
  if (groupInput) groupInput.value = decodeURIComponent(String(group || ""));
  if (textarea) textarea.value = "";
  setTimeout(() => (groupInput?.value ? textarea : groupInput)?.focus(), 0);
}

function pendingLessonModalElement() {
  return document.querySelector("#pendingLessonModal");
}

function closePendingLessonModal() {
  closeModalById("pendingLessonModal");
}

function openPendingLessonModal(subject = "", item = null) {
  const modal = pendingLessonModalElement();
  if (!modal) return;
  closeAllOverlays();
  modal.classList.remove("hidden");
  modal.setAttribute("aria-hidden", "false");
  syncBodyScrollLock();
  const subjectInput = modal.querySelector("#pendingLessonModalSubject");
  const titleInput = modal.querySelector("#pendingLessonModalTitle");
  const idInput = modal.querySelector("#pendingLessonModalId");
  const titleElement = modal.querySelector(".planner-builder-head strong");
  const submitButton = modal.querySelector('[data-action="submit-pending-lesson-modal"] button[type="submit"]');
  const record = item || null;
  const editing = Boolean(record);
  if (subjectInput) subjectInput.value = String(subject || record?.subject || selectedStudySubject || "");
  if (titleInput) titleInput.value = record?.title || "";
  if (idInput) idInput.value = record?.id || "";
  if (titleElement) titleElement.textContent = editing ? "Editar aula pendente" : "Nova aula pendente";
  if (submitButton) submitButton.textContent = editing ? "Salvar" : "Adicionar";
  setTimeout(() => titleInput?.focus(), 0);
}

function openEntryModal(id = "", focusSelector = "") {
  if (!id) return;
  closeAllOverlays();
  toggleModalById(id, true);
  if (id === "errorModal") {
    syncErrorFormDateDefault();
    renderContentOptions();
  }
  const modal = document.querySelector(`#${id}`);
  if (modal) applySmartFieldMemory(modal);
  setTimeout(() => document.querySelector(`#${id} ${focusSelector}`)?.focus(), 0);
}

function closeEntryModal(id = "") {
  if (!id) return;
  toggleModalById(id, false);
}

function questionDeleteMessage(item) {
  if (!item) return "Excluir esta lista de questões?";
  if (isQuestionGroup(item)) {
    return "Excluir esta lista agrupada e todas as listas internas?";
  }
  const linkedPlannerId = item.plannerId || (state.planner || []).find((entry) => entry.questionData?.questionId === item.id)?.id || "";
  const linkedPlannerItem = linkedPlannerId
    ? (state.planner || []).find((entry) => entry.id === linkedPlannerId)
    : null;
  return linkedPlannerItem
    ? `Excluir esta lista de questões também removerá o bloco vinculado do Planner "${linkedPlannerItem.title}". Continuar?`
    : "Excluir esta lista de questões?";
}

function openDeleteConfirmModal({
  title = "Confirmar exclusão",
  subtitle = "Revise a ação antes de continuar",
  message = "Excluir este item?",
  confirmLabel = "Excluir",
  onConfirm = null,
} = {}) {
  const modal = deleteConfirmModalElement();
  if (!modal) return false;
  const titleElement = modal.querySelector(".planner-builder-head strong");
  const subtitleElement = modal.querySelector(".planner-builder-head small");
  const messageElement = document.querySelector("#deleteConfirmMessage");
  const confirmButton = modal.querySelector('[data-action="confirm-delete-confirm-modal"]');
  if (!messageElement || !confirmButton) return false;
  closeAllOverlays();
  pendingDeleteAction = typeof onConfirm === "function" ? onConfirm : null;
  if (titleElement) titleElement.textContent = title;
  if (subtitleElement) subtitleElement.textContent = subtitle;
  messageElement.textContent = message;
  confirmButton.textContent = confirmLabel;
  modal.classList.remove("hidden");
  modal.setAttribute("aria-hidden", "false");
  syncBodyScrollLock();
  setTimeout(() => confirmButton.focus(), 0);
  return true;
}

function openPlannerCreationModal({ itemCount = 2, onSimple = null, onGroup = null } = {}) {
  const modal = plannerCreationModalElement();
  if (!modal) return false;
  const titleElement = modal.querySelector(".planner-builder-head strong");
  const subtitleElement = modal.querySelector(".planner-builder-head small");
  const messageElement = modal.querySelector("#plannerCreationMessage");
  if (!messageElement) return false;
  closeAllOverlays();
  pendingPlannerCreationAction = {
    simple: typeof onSimple === "function" ? onSimple : null,
    group: typeof onGroup === "function" ? onGroup : null,
  };
  if (titleElement) titleElement.textContent = "Como deseja criar as tarefas?";
  if (subtitleElement) subtitleElement.textContent = `${itemCount} tarefas prontas para adicionar ao Planner`;
  messageElement.textContent = "Escolha se deseja criar tarefas simples separadas ou um grupo com subtarefas.";
  modal.classList.remove("hidden");
  modal.setAttribute("aria-hidden", "false");
  syncBodyScrollLock();
  setTimeout(() => modal.querySelector('[data-action="create-planner-as-simple"]')?.focus(), 0);
  return true;
}

function confirmPlannerCreationMode(mode = "simple") {
  const action = pendingPlannerCreationAction;
  closePlannerCreationModal();
  const handler = mode === "group" ? action?.group : action?.simple;
  if (typeof handler === "function") handler();
}

function openPlannerLaterModal({ ids = [], defaultDate = localDateValue(), destination = null, selectedIds = [] } = {}) {
  const modal = plannerLaterModalElement();
  if (!modal) return false;
  const input = modal.querySelector('input[name="date"]');
  closeAllOverlays();
  pendingPlannerLaterMove = { ids: [...ids], destination, selectedIds: [...selectedIds] };
  if (input) input.value = defaultDate || localDateValue();
  modal.classList.remove("hidden");
  modal.setAttribute("aria-hidden", "false");
  syncBodyScrollLock();
  setTimeout(() => input?.focus(), 0);
  return true;
}

function submitPlannerLaterModal(dateValue = "") {
  const move = pendingPlannerLaterMove;
  const parsedDate = dateValue || localDateValue();
  if (!move?.ids?.length || !parsedDate) return false;
  const items = move.ids.map((entryId) => state.planner.find((entry) => entry.id === entryId)).filter(Boolean);
  if (!items.length) {
    closePlannerLaterModal();
    return false;
  }
  items.forEach((item) => {
    if (isPlannerGroup(item)) return;
    const result = domainReschedulePlannerItem(state.planner || [], item.id, { date: parsedDate });
    state.planner = result.planner;
    const updated = state.planner.find((entry) => entry.id === item.id);
    if (updated) {
      markPlannerUserEdited(updated);
      syncPlannerItemToSource(updated);
    }
  });
  if (move.destination) {
    rewritePlannerOrderForDestination(
      { ...move.destination, scopeDate: parsedDate },
      move.selectedIds?.length ? move.selectedIds : move.ids,
    );
  }
  syncPlannerGroupsForDraggedIds(move.ids);
  plannerSelectedTaskIds = move.selectedIds?.length ? move.selectedIds : move.ids;
  plannerSelectionAnchorId = "";
  closePlannerLaterModal();
  renderSectionNow("planner", { save: true, commit: true });
  showUndoToast("Item do Planner reagendado.");
  return true;
}

function confirmDeleteModalAction() {
  const action = pendingDeleteAction;
  closeDeleteConfirmModal();
  if (typeof action === "function") action();
}

function openQuestionDeleteModal(id = "") {
  const item = (state.questions || []).find((entry) => entry.id === String(id || ""));
  if (!item) return false;
  return openDeleteConfirmModal({
    title: isQuestionGroup(item) ? "Excluir lista agrupada" : "Excluir lista de questões",
    subtitle: "Essa ação pode remover vínculos do Planner",
    message: questionDeleteMessage(item),
    onConfirm: () => {
      if (!removeQuestionRecord(item.id, { confirm: false })) return;
      renderSectionNow("questoes", { save: true, commit: true });
      showUndoToast(isQuestionGroup(item) ? "Grupo de questões excluído." : "Lista de questões excluída.");
    },
  });
}

/* =====================================================
   TOPBAR
===================================================== */
let topbarMenuOpen = false;

const topbarNav = document.querySelector("#topbarNav");
const topbarMobileToggle = document.querySelector("#topbarMobileToggle");
const topbarBackdrop = document.querySelector("#topbarBackdrop");
const topbarSettingsMenu = document.querySelector("#topbarSettingsMenu");

// Animação de fechamento do painel do menu de conta da topbar.
// O <details> nativo esconde o conteúdo imediatamente ao remover [open], então
// usamos o beforetoggle cancelável (Chrome 131+) para adiar o fechamento até o
// fim da animação de saída (classe .closing). Sem suporte (não cancelável),
// cai no comportamento nativo instantâneo.
let topbarMenuClosing = false;
let topbarMenuForceClose = false;

topbarSettingsMenu?.addEventListener("beforetoggle", (e) => {
  if (e.newState !== "closed") return; // só intercepta o fechamento
  if (!e.cancelable) return; // navegador antigo: fecha nativo, sem animação
  if (topbarMenuForceClose) return; // fechamento real já no fim da animação
  if (topbarMenuClosing) return; // já está fechando
  e.preventDefault();
  topbarMenuClosing = true;
  topbarSettingsMenu.classList.add("closing");
  const finish = () => {
    topbarMenuForceClose = true;
    topbarSettingsMenu.removeAttribute("open"); // dispara beforetoggle; forceClose permite
    topbarMenuForceClose = false;
    topbarMenuClosing = false;
    topbarSettingsMenu.classList.remove("closing");
  };
  const panel = topbarSettingsMenu.querySelector(".action-menu-panel");
  panel?.addEventListener("animationend", finish, { once: true });
  setTimeout(finish, TOPBAR_MENU_ANIM_MS + 60); // fallback de segurança
});

function syncTopbarBackdropState() {
  const menuOpen = topbarMenuOpen;
  const compactTopbarMenuOpen = document.body.classList.contains("layout-mobile")
    && Boolean(document.querySelector(".action-menu[open], .filter-menu[open]"));
  if (topbarBackdrop) topbarBackdrop.classList.toggle("visible", menuOpen || compactTopbarMenuOpen);
}

function desktopTopbarUsesHorizontalScroll() {
  if (!topbarNav || !topbarMobileToggle) return false;
  return window.getComputedStyle(topbarMobileToggle).display === "none";
}

function revealActiveTopbarNavItem(options = {}) {
  if (!desktopTopbarUsesHorizontalScroll()) return;
  if (!topbarNav || topbarNav.scrollWidth <= topbarNav.clientWidth) return;
  const activeItem = topbarNav.querySelector(".nav-item.active");
  if (!activeItem) return;

  const behavior = options.behavior === "smooth" ? "smooth" : "auto";
  const currentLeft = topbarNav.scrollLeft;
  const visibleLeft = currentLeft;
  const visibleRight = currentLeft + topbarNav.clientWidth;
  const itemLeft = activeItem.offsetLeft - TOPBAR_NAV_REVEAL_MARGIN;
  const itemRight = activeItem.offsetLeft + activeItem.offsetWidth + TOPBAR_NAV_REVEAL_MARGIN;

  if (itemLeft < visibleLeft) {
    topbarNav.scrollTo({ left: Math.max(0, itemLeft), behavior });
    return;
  }

  if (itemRight > visibleRight) {
    topbarNav.scrollTo({ left: Math.max(0, itemRight - topbarNav.clientWidth), behavior });
  }
}

let topbarNavRevealFrame = 0;
function scheduleActiveTopbarNavReveal(options = {}) {
  if (topbarNavRevealFrame) window.cancelAnimationFrame(topbarNavRevealFrame);
  topbarNavRevealFrame = window.requestAnimationFrame(() => {
    topbarNavRevealFrame = 0;
    revealActiveTopbarNavItem(options);
  });
}

function toggleTopbarMobileMenu(open) {
  const expanded = open !== undefined ? open : !topbarMenuOpen;
  topbarMenuOpen = expanded;
  if (topbarNav) topbarNav.classList.toggle("open", expanded);
  if (topbarMobileToggle) topbarMobileToggle.setAttribute("aria-expanded", String(expanded));
  document.body.classList.toggle("topbar-menu-open", expanded);
  if (expanded) {
    closeActionMenu();
    closeFilterMenus();
  }
  syncTopbarBackdropState();
}

function closeTopbarMobileMenu() {
  toggleTopbarMobileMenu(false);
}

// Mobile toggle
topbarMobileToggle?.addEventListener("click", () => toggleTopbarMobileMenu());

// Roda do mouse: converte o wheel vertical em scroll horizontal do nav,
// com inércia (easing) para evitar saltos bruscos. Sem isso o scrollLeft é
// aplicado na hora em cada evento do wheel (desktop usa scrollbar oculta,
// então o wheel nativo não rola sozinho).
let topbarNavScrollRaf = 0;
let topbarNavScrollTarget = 0;

function animateTopbarNavScroll(el) {
  const maxScroll = el.scrollWidth - el.clientWidth;
  topbarNavScrollTarget = Math.min(maxScroll, Math.max(0, topbarNavScrollTarget));
  const diff = topbarNavScrollTarget - el.scrollLeft;
  if (Math.abs(diff) < 0.5) {
    el.scrollLeft = topbarNavScrollTarget;
    topbarNavScrollRaf = 0;
    return;
  }
  // Easing exponencial: o movimento começa rápido e desacelera até o alvo.
  el.scrollLeft += diff * TOPBAR_NAV_SCROLL_EASING;
  topbarNavScrollRaf = window.requestAnimationFrame(() => animateTopbarNavScroll(el));
}

topbarNav?.addEventListener(
  "wheel",
  (e) => {
    if (!desktopTopbarUsesHorizontalScroll()) return; // mobile: dropdown rola vertical
    const el = topbarNav;
    if (!el || el.scrollWidth <= el.clientWidth) return; // sem overflow: deixa o wheel normal
    let delta = e.deltaY * TOPBAR_NAV_WHEEL_FACTOR;
    if (e.deltaMode === 1) delta *= 40; // linhas -> px
    if (!delta) return;

    // Acessibilidade: sem animação quando "reduzir movimento" está ativo
    // (consistente com @media (prefers-reduced-motion: reduce) em styles.css).
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const before = el.scrollLeft;
      el.scrollLeft += delta;
      if (el.scrollLeft !== before) e.preventDefault();
      return;
    }

    // Sincroniza o alvo com scrolls externos (ex: reveal ativo via scrollTo)
    // quando nenhuma animação nossa está rodando.
    if (!topbarNavScrollRaf) topbarNavScrollTarget = el.scrollLeft;

    const maxScroll = el.scrollWidth - el.clientWidth;
    const newTarget = Math.min(maxScroll, Math.max(0, topbarNavScrollTarget + delta));
    if (newTarget === topbarNavScrollTarget) return; // já no limite: deixa a página rolar
    topbarNavScrollTarget = newTarget;
    if (topbarNavScrollRaf) window.cancelAnimationFrame(topbarNavScrollRaf);
    topbarNavScrollRaf = window.requestAnimationFrame(() => animateTopbarNavScroll(el));
    e.preventDefault();
  },
  { passive: false }
);

// Backdrop clicks
topbarBackdrop?.addEventListener("click", () => {
  closeTopbarMobileMenu();
  closeActionMenu();
  closeFilterMenus();
});

// Escape key
document.getElementById("shortcutOverlayClose")?.addEventListener("click", () => {
  const overlay = document.querySelector("#shortcutOverlay");
  if (overlay) overlay.classList.add("hidden");
});
document.getElementById("shortcutOverlay")?.addEventListener("click", (e) => {
  if (e.target.id === "shortcutOverlay") {
    const overlay = document.querySelector("#shortcutOverlay");
    if (overlay) overlay.classList.add("hidden");
  }
});

document.addEventListener("keydown", (e) => {
  const inEditableField = Boolean(e.target.closest("input, textarea, select, [contenteditable='true']"));
  if (e.key === "Escape") {
    closeTopbarMobileMenu();
    closeActionMenu();
    closeFilterMenus();
  }
  if (e.key === "?" && !e.ctrlKey && !e.metaKey && !e.altKey && !inEditableField) {
    e.preventDefault();
    const overlay = document.querySelector("#shortcutOverlay");
    if (overlay) overlay.classList.toggle("hidden");
  }
});

function defaultState() {
  return {
    weeklyGoal: 300,
    studyWeeklyGoalHours: 20,
    activeView: "dashboard",
    customSubjects: [],
    hiddenSubjects: [],
    customText: {},
    dashboardOrder: {},
    dashboardBlockSizes: {},
    mergeStamps: {},
    subjectNotes: {},
    subjectCovers: {},
    subjectContents: {},
    smartFormMemory: {},
    subjectCycles: {},
    cycleLessons: {},
    cycleContentOrder: {},
    collapsedLessonGroups: {},
    collapsedSections: {},
    plannerTimeEnabled: false,
    timerSubjects: null,
    hiddenTimerSubjects: [],
    activeStudyTimer: null,
    userProfile: null,
    keepConnected: true,
    planner: [],
    focusTodos: [],
    pendingLessons: [],
    questions: [],
    errors: [],
    mocks: [],
    essays: [],
    studySessions: [],
    syncMeta: {
      lastSyncedAt: "",
    },
    metadata: {
      updatedAt: 0,
      schemaVersion: 1,
    },
  };
}

function ensureMergeStampRoot(target, domain) {
  target.mergeStamps ||= {};
  if (typeof target.mergeStamps[domain] !== "number") {
    target.mergeStamps[domain] = Date.now();
  }
}

function ensureMergeStampKey(target, domain, key) {
  target.mergeStamps ||= {};
  target.mergeStamps[domain] ||= {};
  if (typeof target.mergeStamps[domain][key] !== "number") {
    target.mergeStamps[domain][key] = Date.now();
  }
  return target.mergeStamps[domain][key];
}

function focusTodoRecordId(ref = "") {
  const cleanRef = String(ref || "").trim();
  return cleanRef ? `focus:${cleanRef}` : "";
}

function backfillMergeRecord(record, stamp) {
  if (!record || typeof record !== "object" || Array.isArray(record)) return;
  if (typeof record.updatedAt !== "number" || !Number.isFinite(record.updatedAt)) {
    record.updatedAt = stamp || Date.now();
  }
  record.deletedAt ??= null;
}

function backfillFocusTodoRecord(record, stamp) {
  if (!record || typeof record !== "object" || Array.isArray(record)) return;
  if (typeof record.id !== "string" || !record.id.trim()) {
    const id = focusTodoRecordId(record.ref);
    if (id) record.id = id;
  }
  backfillMergeRecord(record, stamp);
}

function backfillMergeRecordsInValue(value, stamp) {
  if (Array.isArray(value)) {
    value.forEach((item) => backfillMergeRecord(item, stamp));
    return;
  }
  backfillMergeRecord(value, stamp);
}

function arrayDomainNeedsRootStamp(items) {
  if (!Array.isArray(items) || !items.length) return false;
  return !items.some((item) => (
    item
    && typeof item === "object"
    && !Array.isArray(item)
    && typeof item.id === "string"
    && item.id.trim()
  ));
}

function backfillMergeTimestamps(target) {
  if (!target || typeof target !== "object") return target;
  target.mergeStamps ||= {};

  MERGE_ARRAY_DOMAINS.forEach((domain) => {
    const items = target[domain];
    if (!Array.isArray(items)) return;
    if (arrayDomainNeedsRootStamp(items)) ensureMergeStampRoot(target, domain);
    if (domain === "focusTodos") {
      items.forEach((item) => backfillFocusTodoRecord(item));
      return;
    }
    items.forEach((item) => backfillMergeRecord(item));
  });

  MERGE_CONTENT_MAP_DOMAINS.forEach((domain) => {
    const map = target[domain];
    if (!map || typeof map !== "object" || Array.isArray(map)) return;
    Object.keys(map).forEach((key) => {
      const stamp = ensureMergeStampKey(target, domain, key);
      backfillMergeRecordsInValue(map[key], stamp);
    });
  });

  MERGE_LAYOUT_MAP_DOMAINS.forEach((domain) => {
    if (Object.prototype.hasOwnProperty.call(target, domain)) ensureMergeStampRoot(target, domain);
  });

  MERGE_SCALAR_DOMAINS.forEach((domain) => {
    if (Object.prototype.hasOwnProperty.call(target, domain)) ensureMergeStampRoot(target, domain);
  });

  backfillHistoryTimestamps(target);
  return target;
}

function backfillHistoryTimestamps(target) {
  if (!target || typeof target !== "object") return target;
  const stamp = Date.now();
  ["essays", "cycles", "cycleLessons", "mocks"].forEach((domain) => {
    const records = target[domain];
    if (Array.isArray(records)) {
      records.forEach((record) => backfillMergeRecord(record, stamp));
      return;
    }
    if (records && typeof records === "object") {
      Object.values(records).forEach((value) => backfillMergeRecordsInValue(value, stamp));
    }
  });
  return target;
}

function ensureMergeStampsState() {
  state.mergeStamps ||= {};
  return state.mergeStamps;
}

function activeRecords(items = []) {
  return (Array.isArray(items) ? items : []).filter((item) => !item?.deletedAt);
}

function findRecordLocationById(domain, id) {
  const cleanId = String(id || "");
  const source = state[domain];
  if (Array.isArray(source)) {
    const record = source.find((entry) => entry && typeof entry === "object" && entry.id === cleanId) || null;
    return record ? { record, key: null } : null;
  }
  if (source && typeof source === "object") {
    for (const [key, records] of Object.entries(source)) {
      if (!Array.isArray(records)) continue;
      const record = records.find((entry) => entry && typeof entry === "object" && entry.id === cleanId) || null;
      if (record) return { record, key };
    }
  }
  return null;
}

function findRecordById(domain, id) {
  return findRecordLocationById(domain, id)?.record || null;
}

function touchDomainRoot(domain) {
  ensureMergeStampsState()[domain] = Date.now();
}

function touchRecord(domain, id) {
  const location = findRecordLocationById(domain, id);
  if (!location?.record) return null;
  const { record, key } = location;
  record.updatedAt = Date.now();
  if (record.deletedAt === undefined) record.deletedAt = null;
  if (key && MERGE_CONTENT_MAP_DOMAINS.includes(domain)) {
    ensureMergeStampsState()[domain] ||= {};
    state.mergeStamps[domain][key] = record.updatedAt;
  } else {
    touchDomainRoot(domain);
  }
  return record;
}

function markDeleted(domain, id) {
  const location = findRecordLocationById(domain, id);
  if (!location?.record) return null;
  const { record, key } = location;
  record.updatedAt = Date.now();
  record.deletedAt = record.updatedAt;
  if (key && MERGE_CONTENT_MAP_DOMAINS.includes(domain)) {
    ensureMergeStampsState()[domain] ||= {};
    state.mergeStamps[domain][key] = record.updatedAt;
  } else {
    touchDomainRoot(domain);
  }
  return record;
}

function updateRecord(domain, id, patch = {}) {
  const record = findRecordById(domain, id);
  if (!record) return null;
  const stableId = record.id;
  Object.assign(record, patch, { id: stableId });
  return touchRecord(domain, stableId);
}

function touchMapKey(domain, key, value) {
  if (!key) return value;
  state[domain] ||= {};
  state[domain][key] = value;
  ensureMergeStampsState()[domain] ||= {};
  state.mergeStamps[domain][key] = Date.now();
  return value;
}

function touchScalar(domain, value) {
  state[domain] = value;
  touchDomainRoot(domain);
  return value;
}

function removeRecord(domain, id) {
  try {
    const record = findRecordById(domain, id);
    if (!record) return null;
    if (domain === "planner" && isPlannerGroup(record)) {
      plannerGroupChildren(record.id).forEach((child) => markDeleted("planner", child.id));
    }
    if (domain === "questions" && isQuestionGroup(record)) {
      questionGroupChildren(record.id, { subjectFilter: "all" }).forEach((child) => markDeleted("questions", child.id));
    }
    const removed = markDeleted(domain, id);
    saveState();
    return removed;
  } catch (err) {
    console.error("[removeRecord]", err);
    return null;
  }
}

function purgeExpiredTombstones(target = state, now = Date.now()) {
  try {
    if (!target || typeof target !== "object") return target;
    const cutoff = now - SYNC_TOMBSTONE_RETENTION_MS;
    const purgeList = (records) => {
      if (!Array.isArray(records)) return records;
      return records.filter((record) => {
        if (!record || typeof record !== "object" || record.deletedAt === null || record.deletedAt === undefined) return true;
        return !(isFiniteTimestamp(record.deletedAt) && record.deletedAt < cutoff);
      });
    };

    Array.from(new Set(MERGE_ARRAY_DOMAINS.concat(["cycles"]))).forEach((domain) => {
      if (Array.isArray(target[domain])) target[domain] = purgeList(target[domain]);
    });
    MERGE_CONTENT_MAP_DOMAINS.forEach((domain) => {
      const map = target[domain];
      if (!map || typeof map !== "object" || Array.isArray(map)) return;
      Object.keys(map).forEach((key) => {
        if (Array.isArray(map[key])) map[key] = purgeList(map[key]);
      });
    });
    return target;
  } catch (err) {
    console.error("[purgeExpiredTombstones]", err);
    return target;
  }
}

/* =====================================================
   PERSISTENCIA E HISTORICO
   Carregamento, migracoes, salvamento local/remoto e undo/redo.
===================================================== */
function loadState() {
  const stored = localStorage.getItem(stateStorageKey());
  if (!stored) return applyLocalTestAccount(defaultState());

  const payload = parsePersistedPayload(stored);
  expirePendingLessons(payload);
  return applyLocalTestAccount(payload);
}

function isLocalSandbox() {
  return Boolean(window.studyRuntime?.isLocalSandbox);
}

function runtimeStorageKey(baseKey) {
  return window.studyRuntime?.storageKey
    ? window.studyRuntime.storageKey(baseKey)
    : baseKey;
}

function stateStorageKey() {
  return runtimeStorageKey("enemStudyApp");
}

function temporaryBackupStorageKey() {
  return runtimeStorageKey("enemTempBackup");
}

function sandboxCacheExists() {
  if (!isLocalSandbox()) return false;
  try {
    return Boolean(localStorage.getItem(stateStorageKey()));
  } catch (err) {
    console.error("[sandboxCacheExists] falhou ao ler cache sandbox:", err);
    return false;
  }
}

function parsePersistedPayload(stored) {
  // Payload prefixado como "LZ1:" foi comprimido por LZString.compressToUTF16
  // antes do persistStateNow. Sem prefixo = cru legado (compatibilidade).
  if (typeof stored === "string" && stored.startsWith("LZ1:")) {
    try {
      const raw = window.LZString?.decompressFromUTF16(stored.slice(4));
      if (!raw) throw new Error("LZString retornou vazio");
      const parsed = JSON.parse(raw);
      return migrateLoadedState(parsed);
    } catch (err) {
      console.error("[loadState] payload comprimido corrompido:", err);
      return applyLocalTestAccount(defaultState());
    }
  }

  try {
    const parsed = JSON.parse(stored);
    return migrateLoadedState(parsed);
  } catch (err) {
    console.error("[loadState] falha ao parsear estado salvo:", err);
    return applyLocalTestAccount(defaultState());
  }
}

function migrateLoadedState(parsed) {
  if (!parsed || typeof parsed !== "object") {
    return applyLocalTestAccount(defaultState());
  }
  const merged = migratePlannerDerivedFields({
    ...defaultState(),
    ...parsed,
    userProfile: parsed.userProfile || null,
    metadata: { ...defaultState().metadata, ...parsed.metadata },
  });
  delete merged.metadata.payloadHash;
  backfillMergeTimestamps(merged);
  purgeExpiredTombstones(merged);
  return applyLocalTestAccount(merged);
}

async function verifyLoadedStateIntegrity() {
  var hash = state?.metadata?.payloadHash;
  if (!hash || !window.studyIntegrity) return;
  try {
    var ok = await window.studyIntegrity.verifyPayload(state, hash);
    if (!ok) {
      console.log("[integrity] hash mismatch (expected during migration), recalculating...");
      state.metadata.payloadHash = await window.studyIntegrity.sha256(state);
      persistStateNow();
    }
  } catch (e) {
    console.warn("[integrity] verification error:", e);
  }
}

function applyLocalTestAccount(nextState = defaultState()) {
  return nextState;
}

function fixMojibakeText(value) {
  if (typeof value !== "string") return value;
  let text = value;
  const mojibake = (...points) => String.fromCodePoint(...points);
  const symbolFixes = [
    [mojibake(226, 8226, 162), "•"],
    [mojibake(226, 8364, 186), "›"],
    [mojibake(226, 8364, 190), "v"],
    [mojibake(226, 8211, 184), "›"],
    [mojibake(226, 8211, 190), "v"],
    [mojibake(226, 8364, 8212), "-"],
    [mojibake(195, 8220), "Ó"],
    [mojibake(195, 8212), "x"],
  ];

  symbolFixes.forEach(([from, to]) => {
    text = text.split(from).join(to);
  });

  for (let index = 0; index < 3; index += 1) {
    if (!new RegExp(`[${mojibake(194)}${mojibake(195)}${mojibake(226)}]`).test(text)) break;
    try {
      const bytes = new Uint8Array(text.length);
      for (let i = 0; i < text.length; i++) bytes[i] = text.charCodeAt(i) & 0xFF;
      const decoded = new TextDecoder("utf-8").decode(bytes);
      if (decoded === text || decoded.includes("\uFFFD")) break;
      text = decoded;
    } catch (err) {
      console.error("[fixMojibakeText]", err);
      break;
    }
  }

  return text;
}

function repairEncodedData(value) {
  if (typeof value === "string") return fixMojibakeText(value);
  if (Array.isArray(value)) return value.map(repairEncodedData);
  if (!value || typeof value !== "object") return value;

  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [
      fixMojibakeText(key),
      repairEncodedData(entry),
    ]),
  );
}

function migrateRenamedLabels() {
  state.customText ||= {};
  if (state.userProfile?.password) delete state.userProfile.password;
  if (state.customText["nav:tempo"] === "Tempo") state.customText["nav:tempo"] = "Cronômetro";
  if (state.customText.timeTitle === "Tempo de estudo") state.customText.timeTitle = "Cronômetro";
  if (/^Feito\s*🙌$/.test(state.customText["taskColumn:done"] || "")) state.customText["taskColumn:done"] = "Feito";
  if (/^Outro dia(\.\.\.|…)$/.test(state.customText["taskColumn:later"] || "")) state.customText["taskColumn:later"] = "Outro dia";
}

function stripUnusedQuestionListNames(target = state) {
  if (!target || typeof target !== "object") return target;
  (target.questions || []).forEach((item) => {
    if (!item || typeof item !== "object") return;
    if (!item.isGroup) delete item.name;
    delete item.listName;
  });
  (target.planner || []).forEach((item) => {
    if (!item?.questionData || typeof item.questionData !== "object") return;
    delete item.questionData.listName;
  });
  if (target.smartFormMemory && typeof target.smartFormMemory === "object") {
    delete target.smartFormMemory["question-form::name"];
    delete target.smartFormMemory["focus-question-form::name"];
  }
  return target;
}

function cleanProfileText(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

function isLegacyTestProfile(profile = state.userProfile) {
  return cleanProfileText(profile?.displayName) === TEST_USER_PROFILE.displayName
    && cleanProfileText(profile?.login).toLowerCase() === TEST_USER_PROFILE.login;
}

function fallbackProfileFromEmail(email = "") {
  const login = cleanProfileText(email).toLowerCase();
  if (!login) return null;
  return {
    displayName: login.split("@")[0] || "Estudante",
    login,
  };
}

function localAuthSessionReady() {
  if (localAuthSessionActive || cloudSessionActive) return true;
  if (state.keepConnected !== false) return true;
  return sessionStorage.getItem("studyKeepConnectedSession") === "temporary";
}

function profileReady() {
  const profileComplete = Boolean(state.userProfile?.displayName && state.userProfile?.login && !isLegacyTestProfile(state.userProfile));
  return profileComplete && localAuthSessionReady();
}

function applyUserProfile() {
  const avatar = document.querySelector("#topbarAvatar");
  const username = document.querySelector("#topbarUsername");
  const profile = state.userProfile || {};
  const displayName = cleanProfileText(profile.displayName);

  if (avatar) avatar.textContent = (displayName || "E").charAt(0).toUpperCase();
  if (username) username.textContent = displayName || "Usuário";
}

function setKeepConnected(value) {
  state.keepConnected = Boolean(value);
  if (state.keepConnected) {
    sessionStorage.setItem("studyKeepConnectedSession", "1");
  } else {
    sessionStorage.setItem("studyKeepConnectedSession", "temporary");
  }
}

async function applyConnectionPreference() {
  if (state.keepConnected !== false) return;
  if (sessionStorage.getItem("studyKeepConnectedSession") === "temporary") return;
  await window.studyCloud?.signOut?.();
  remoteSyncEnabled = false;
  cloudSessionActive = false;
}

function syncKeepConnectedInputs() {
  document.querySelectorAll("#firstLoginForm [name='keepConnected']").forEach((input) => {
    input.checked = state.keepConnected !== false;
  });
}

function firstLoginFormReady(form = document.querySelector("#firstLoginForm")) {
  if (!form) return false;
  const isSignup = form.elements.authAction?.value === "signUp";
  const login = cleanProfileText(form.elements.login?.value).toLowerCase();
  const password = String(form.elements.password?.value || "").trim();
  const displayName = cleanProfileText(form.elements.displayName?.value);
  return Boolean(login && password && (!isSignup || displayName));
}

function setFirstLoginStatus(type = "", message = "") {
  const form = document.querySelector("#firstLoginForm");
  const status = document.querySelector("#firstLoginStatus");
  if (!form || !status) return;
  const normalizedType = ["error", "success", "loading", "info"].includes(type) ? type : "";
  form.dataset.authStatus = normalizedType;
  status.textContent = message;
  status.hidden = !message;
}

function setFirstLoginLoading(loading) {
  const form = document.querySelector("#firstLoginForm");
  if (!form) return;
  const submit = form.querySelector("#firstLoginSubmit");
  form.classList.toggle("auth-loading", Boolean(loading));
  form.querySelectorAll("input, button").forEach((control) => {
    if (control.matches(".auth-secondary-button, .auth-link-button, [data-auth-mode]")) {
      control.disabled = Boolean(loading);
      return;
    }
    if (control.type !== "hidden") control.disabled = Boolean(loading);
  });
  if (submit) submit.textContent = loading
    ? (form.elements.authAction?.value === "signUp" ? "Criando conta..." : "Entrando...")
    : (form.elements.authAction?.value === "signUp" ? "Criar conta" : "Entrar");
  if (!loading) {
    const isSignup = form.elements.authAction?.value === "signUp";
    if (form.elements.displayName) {
      form.elements.displayName.disabled = !isSignup;
      form.elements.displayName.required = isSignup;
    }
    syncFirstLoginSubmitState(form);
  }
}

function syncFirstLoginSubmitState(form = document.querySelector("#firstLoginForm")) {
  if (!form) return;
  if (isLocalSandbox()) {
    syncSandboxEntryControls(form);
    return;
  }
  const submit = form.querySelector("#firstLoginSubmit");
  if (submit && !form.classList.contains("auth-loading")) {
    submit.disabled = !firstLoginFormReady(form);
  }
}

function syncSandboxEntryControls(form = document.querySelector("#firstLoginForm")) {
  if (!form) return;
  const title = form.querySelector("#firstLoginTitle");
  const description = form.querySelector("#firstLoginDescription");
  const sandboxActions = form.querySelector("#sandboxEntryActions");
  const authActions = form.querySelector(".auth-card-actions");
  const authMode = form.querySelector(".auth-mode-selector");
  const continueButton = form.querySelector("#sandboxContinueButton");
  const submit = form.querySelector("#firstLoginSubmit");
  const authFormRow = form.querySelector(".auth-form-row");
  const hasCache = sandboxCacheExists();

  form.classList.add("auth-mode-sandbox");
  form.classList.remove("auth-mode-signin", "auth-mode-signup");
  if (form.elements.authAction) form.elements.authAction.value = "sandbox";
  if (title) title.textContent = "Modo local de testes";
  if (description) description.textContent = "Escolha como iniciar. Nada aqui altera seus dados oficiais.";
  if (sandboxActions) sandboxActions.hidden = false;
  if (authActions) authActions.hidden = true;
  if (authMode) authMode.hidden = true;
  if (authFormRow) authFormRow.hidden = true;
  if (continueButton) {
    continueButton.disabled = !hasCache;
    continueButton.title = hasCache ? "" : "Nao ha cache local de testes neste navegador.";
  }
  if (submit) submit.disabled = true;

  [form.elements.displayName, form.elements.login, form.elements.password].forEach((input) => {
    if (!input) return;
    input.disabled = true;
    input.required = false;
    const label = input.closest("label");
    if (label) label.hidden = true;
  });

  setFirstLoginStatus(
    "info",
    hasCache
      ? "Cache sandbox encontrado neste navegador."
      : "Sem cache sandbox ainda. Use exemplos ou importe um backup.",
  );
}

function setFirstLoginAuthMode(mode = "signIn") {
  const form = document.querySelector("#firstLoginForm");
  if (!form) return;
  if (isLocalSandbox()) {
    syncSandboxEntryControls(form);
    return;
  }

  const normalizedMode = mode === "signUp" ? "signUp" : "signIn";
  const isSignup = normalizedMode === "signUp";
  const title = form.querySelector("#firstLoginTitle");
  const description = form.querySelector("#firstLoginDescription");
  const submit = form.querySelector("#firstLoginSubmit");
  const secondary = form.querySelector(".auth-secondary-button");
  const actionInput = form.elements.authAction;
  const nameField = form.querySelector(".auth-register-field");
  const nameInput = form.elements.displayName;

  form.classList.toggle("auth-mode-signup", isSignup);
  form.classList.toggle("auth-mode-signin", !isSignup);
  if (actionInput) actionInput.value = normalizedMode;
  if (title) title.textContent = isSignup ? "Criar sua conta" : "Entrar no painel";
  if (description) {
    description.textContent = isSignup
      ? "Informe seu nome, e-mail e senha para começar a sincronizar seus estudos."
      : "Use seu e-mail e senha para continuar de onde parou.";
  }
  if (submit) submit.textContent = isSignup ? "Criar conta" : "Entrar";
  if (secondary) {
    secondary.textContent = isSignup ? "Entrar" : "Criar conta";
    secondary.setAttribute("aria-label", isSignup ? "Voltar para entrar" : "Criar conta");
  }
  if (nameField) nameField.hidden = !isSignup;
  if (nameInput) {
    nameInput.required = isSignup;
    nameInput.disabled = !isSignup;
    if (!isSignup) nameInput.value = "";
    nameInput.tabIndex = isSignup ? 0 : -1;
  }

  form.querySelectorAll("[data-auth-mode]").forEach((button) => {
    const active = button.dataset.authMode === normalizedMode;
    button.classList.toggle("active", active);
    button.setAttribute("aria-selected", String(active));
  });

  setFirstLoginStatus("", "");
  syncFirstLoginSubmitState(form);
}

function updateFirstLoginModal() {
  const modal = document.querySelector("#firstLoginModal");
  if (!modal) return;
  const temporarySessionExpired =
    state.keepConnected === false && sessionStorage.getItem("studyKeepConnectedSession") !== "temporary";
  const disconnected = syncStatusKey === "disconnected";
  const open = isLocalSandbox()
    ? !profileReady()
    : (!profileReady() || temporarySessionExpired || disconnected);
  modal.classList.toggle("hidden", !open);
  modal.setAttribute("aria-hidden", String(!open));
  document.body.classList.toggle("auth-gated", open);
  document.body.classList.toggle("auth-ready", !open);
  syncBodyScrollLock();

  const wasOpen = lastAuthModalOpen;
  lastAuthModalOpen = open;

  if (open) {
    const form = modal.querySelector("#firstLoginForm");
    if (isLocalSandbox()) {
      syncSandboxEntryControls(form);
      return;
    }
    if (form && state.userProfile) {
      const profile = isLegacyTestProfile(state.userProfile) ? null : state.userProfile;
      form.elements.displayName.value = profile?.displayName || "";
      form.elements.login.value = profile?.login || "";
      form.elements.password.value = "";
    }
    syncKeepConnectedInputs();
    setFirstLoginAuthMode("signIn");
    syncFirstLoginSubmitState(form);
    setTimeout(() => form?.querySelector('input:not([type="hidden"]):not([disabled])')?.focus(), 0);
  } else if (wasOpen && !open) {
    setTimeout(() => drawChartsForSection(activeViewId(), { force: true }), 100);
  }
}

function setupFirstLogin() {
  const form = document.querySelector("#firstLoginForm");
  if (!form) return;
  setFirstLoginAuthMode(form.elements.authAction?.value || "signIn");

  form.querySelector("#sandboxExampleButton")?.addEventListener("click", () => {
    enterSandboxWithExamples();
  });

  form.querySelector("#sandboxContinueButton")?.addEventListener("click", () => {
    continueSandboxCache();
  });

  form.querySelector("#sandboxImportButton")?.addEventListener("click", () => {
    openBackupImportPicker();
  });

  form.querySelectorAll("[data-auth-mode]").forEach((button) => {
    button.addEventListener("click", () => setFirstLoginAuthMode(button.dataset.authMode));
  });

  form.querySelector("[data-auth-action='toggle-create-account']")?.addEventListener("click", () => {
    const nextMode = form.elements.authAction?.value === "signUp" ? "signIn" : "signUp";
    setFirstLoginAuthMode(nextMode);
  });

  form.querySelector("[data-auth-action='forgot-password']")?.addEventListener("click", () => {
    setFirstLoginStatus("info", "Recuperação de senha pronta para integração futura.");
  });

  form.addEventListener("input", () => {
    setFirstLoginStatus("", "");
    syncFirstLoginSubmitState(form);
  });

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (isLocalSandbox()) {
      syncSandboxEntryControls(form);
      return;
    }
    const authAction = form.elements.authAction?.value === "signUp" ? "signUp" : "signIn";
    const data = Object.fromEntries(new FormData(form).entries());
    const displayName = cleanProfileText(data.displayName);
    const login = cleanProfileText(data.login).toLowerCase();
    const password = String(data.password || "").trim();
    const keepConnected = Boolean(data.keepConnected);

    if (!login || !password || (authAction === "signUp" && !displayName)) {
      setFirstLoginStatus("error", authAction === "signUp" ? "Preencha nome, e-mail e senha." : "Preencha e-mail e senha.");
      syncFirstLoginSubmitState(form);
      return;
    }

    const profile = authAction === "signUp" ? { displayName, login } : fallbackProfileFromEmail(login);
    setKeepConnected(keepConnected);
    setFirstLoginLoading(true);
    setFirstLoginStatus("loading", authAction === "signUp" ? "Criando sua conta..." : "Entrando no painel...");

    try {
      if (window.studyCloud?.enabled) {
        console.log("[login] studyCloud habilitado, signIn method existe?", typeof window.studyCloud.signIn);
        const connected = await authenticateCloudAccount(login, password, authAction, {
          skipRender: true,
          profile: authAction === "signUp" ? profile : null,
          localState: JSON.parse(JSON.stringify({
            ...state,
            userProfile: authAction === "signUp" ? profile : state.userProfile,
          })),
        });
        if (!connected) {
          setFirstLoginStatus("error", authAction === "signUp"
            ? "Não foi possível criar a conta agora."
            : "Não foi possível entrar. Confira e-mail e senha.");
          return;
        }
        if (!profileReady()) {
          state.userProfile = fallbackProfileFromEmail(login);
          saveState();
        }
      } else {
        console.log("[login] studyCloud desabilitado, modo local");
        state.userProfile = authAction === "signUp" ? profile : fallbackProfileFromEmail(login);
        saveState();
      }

      setFirstLoginStatus("success", authAction === "signUp" ? "Conta pronta." : "Entrada concluída.");
      applyUserProfile();
      updateFirstLoginModal();
      updateCloudStatus();
    } finally {
      setFirstLoginLoading(false);
    }
  });

  syncFirstLoginSubmitState(form);
}

function ensureSandboxProfile() {
  if (!isLocalSandbox()) return;
  const profile = state.userProfile || {};
  if (!profile.displayName || !profile.login || isLegacyTestProfile(profile)) {
    state.userProfile = { ...SANDBOX_USER_PROFILE };
  }
  state.keepConnected = true;
  localAuthSessionActive = true;
  sessionStorage.setItem("studyKeepConnectedSession", "1");
}

function finishSandboxEntry() {
  ensureSandboxProfile();
  saveState({ immediate: true });
  applyUserProfile();
  setSyncStatus("localSandbox");
  updateFirstLoginModal();
  updateCloudStatus();
  render();
}

function enterSandboxWithExamples() {
  seedExampleData();
  finishSandboxEntry();
}

function continueSandboxCache() {
  if (!sandboxCacheExists()) {
    setFirstLoginStatus("info", "Nao ha cache local de testes neste navegador.");
    return;
  }
  replaceState(loadState());
  finishSandboxEntry();
}

function setupLocalSandboxBanner() {
  const sandbox = isLocalSandbox();
  const banner = document.querySelector("#localSandboxBanner");
  const dismissed = sandbox && sessionStorage.getItem("enemSandboxBannerDismissed") === "1";

  document.body.classList.toggle("local-sandbox", sandbox && !dismissed);
  if (banner) banner.hidden = !sandbox || dismissed;

  const close = document.querySelector("#localSandboxBannerClose");
  if (close) {
    close.addEventListener("click", () => {
      sessionStorage.setItem("enemSandboxBannerDismissed", "1");
      document.body.classList.remove("local-sandbox");
      if (banner) banner.hidden = true;
    });
  }
}

async function persistStateNow() {
  const persistRevision = ++localPersistRevision;
  syncActiveStudyTimerState();
  expirePendingLessons();
  stripUnusedQuestionListNames(state);
  if (window.studyIntegrity) {
    try {
      state.metadata.payloadHash = await window.studyIntegrity.sha256(state);
    } catch (e) {
      console.warn("[persistStateNow] sha256 failed, skipping hash:", e);
    }
  }
  const snapshot = cloneStudyState(state);
  await enqueueWalSnapshot(snapshot);
  if (persistRevision !== localPersistRevision) return false;
  return _persistStateNow(snapshot);
}

function persistStateCache() {
  localPersistRevision += 1;
  syncActiveStudyTimerState();
  stripUnusedQuestionListNames(state);
  localStorage.setItem(stateStorageKey(), JSON.stringify(state));
  resetStorageWarning();
}

function scheduleRemoteSave() {
  if (!remoteSyncEnabled || isBootstrapping || !window.studyCloud?.enabled) return;
  clearTimeout(remoteSaveTimer);
  remoteSaveTimer = setTimeout(async () => {
    setSyncStatus("syncing");
    try {
      publishWalRuntimeConfig();
      await window.studyCloud.saveState(state);
      await finalizeRemoteSync();
      setSyncStatus("synced");
    } catch (error) {
      await handleRemoteSaveError(error);
    }
  }, 250);
}

async function handleRemoteSaveError(error) {
  remoteSyncEnabled = false;
  const user = await window.studyCloud?.getUser?.().catch(() => null);
  if (!user) {
    setSyncStatus("disconnected");
    openSyncDisconnectedModal();
    return;
  }
    setSyncStatus("error");
    console.warn("[handleRemoteSaveError] erro ao sincronizar dados:", error);
}

function saveState(options = {}) {
  const immediate = Boolean(options.immediate);
  if (immediate) {
    clearTimeout(localSaveTimer);
    localSaveTimer = null;
    persistStateNow();
    if (window.studyCloud?.enabled) scheduleRemoteSave();
    return;
  }

  clearTimeout(localSaveTimer);
  localSaveTimer = setTimeout(() => {
    localSaveTimer = null;
    persistStateNow();
  }, LOCAL_SAVE_DEBOUNCE_MS);
  if (window.studyCloud?.enabled) scheduleRemoteSave();
}

function flushStateSave() {
  saveState({ immediate: true });
}

function replaceState(nextState) {
  resetDataDomainVersions();
  Object.keys(state).forEach((key) => delete state[key]);
  Object.assign(state, { ...defaultState(), ...nextState });
  stripUnusedQuestionListNames(state);
  state.timerSubjects = normalizeTimerSubjectsList(state.timerSubjects || []);
  state.hiddenTimerSubjects = normalizeTimerSubjectsList(state.hiddenTimerSubjects || []);
  state.activeStudyTimer = normalizeActiveStudyTimer(state.activeStudyTimer);
}

function cloneStudyState(source = state) {
  return JSON.parse(JSON.stringify(source || {}));
}

function isFiniteTimestamp(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function tombstoneMarkerFor(record) {
  if (
    !record
    || typeof record !== "object"
    || Array.isArray(record)
    || typeof record.id !== "string"
    || !record.id.trim()
    || !isFiniteTimestamp(record.updatedAt)
    || !isFiniteTimestamp(record.deletedAt)
  ) {
    return null;
  }
  return {
    id: record.id,
    updatedAt: record.updatedAt,
    deletedAt: record.deletedAt,
    __tombstone: true,
  };
}

function sanitizeArchiveRecordsForMerge(records, maxRecords) {
  if (!Array.isArray(records) || records.length <= maxRecords) {
    return { records: Array.isArray(records) ? cloneStudyState(records) : records, droppedCount: 0 };
  }

  const ranked = records
    .map((record, index) => ({
      index,
      stamp: isFiniteTimestamp(record?.updatedAt) ? record.updatedAt : Number.NEGATIVE_INFINITY,
    }))
    .sort((left, right) => right.stamp - left.stamp || left.index - right.index);
  const keptIndexes = new Set(ranked.slice(0, maxRecords).map((entry) => entry.index));
  const sanitized = [];
  const markers = [];
  let droppedCount = 0;

  records.forEach((record, index) => {
    if (keptIndexes.has(index)) {
      sanitized.push(cloneStudyState(record));
      return;
    }
    droppedCount++;
    const marker = tombstoneMarkerFor(record);
    if (marker) markers.push(marker);
  });

  return { records: sanitized.concat(markers), droppedCount };
}

function purgeArchiveFromMergeInput(localState, cloudState) {
  try {
    const local = cloneStudyState(localState || {});
    const cloud = cloneStudyState(cloudState || {});
    const domains = Array.from(new Set(MERGE_ARRAY_DOMAINS.concat(["cycles"])));
    let droppedCount = 0;

    [local, cloud].forEach((target) => {
      domains.forEach((domain) => {
        const result = sanitizeArchiveRecordsForMerge(target[domain], SYNC_MAX_ARCHIVE_RECORDS);
        if (Array.isArray(result.records)) target[domain] = result.records;
        droppedCount += result.droppedCount;
      });
    });

    return { local, cloud, droppedCount };
  } catch (err) {
    console.error("[purgeArchiveFromMergeInput]", err);
    return {
      local: cloneStudyState(localState || {}),
      cloud: cloneStudyState(cloudState || {}),
      droppedCount: 0,
    };
  }
}

function setPendingMergeUpload(snapshot) {
  pendingMergeSnapshot = cloneStudyState(snapshot || state);
  pendingMergeUpload = true;
}

async function persistSyncRetry(snapshot = state) {
  try {
    const modules = await _loadBackupModules();
    configureWalDrainTarget(modules);
    const walRing = modules?.wal?.ring;
    if (!walRing || typeof walRing.rewriteQueue !== "function") return false;
    await walRing.rewriteQueue(cloneStudyState(snapshot || state));
    setSyncStatus("pendingLocal");
    return true;
  } catch (err) {
    console.error("[persistSyncRetry]", err);
    notify("Nao foi possivel preservar a fila de sincronizacao. Exporte um backup antes de fechar a aba.", "error");
    return false;
  }
}

function createTemporaryBackup(reason = "replace") {
  const payload = {
    createdAt: new Date().toISOString(),
    reason,
    app: "estudos-enem",
    version: 1,
    state: cloneStudyState(state),
  };
  try {
    localStorage.setItem(temporaryBackupStorageKey(), JSON.stringify(payload));
  } catch (err) {
    // localStorage cheio — WAL do replaceStateSafely serve como fallback
    console.error("[createTemporaryBackup] falha ao salvar backup temporario:", err);
  }
  return payload;
}

function restoreTemporaryBackup() {
  const previousState = getLatestBackup();
  if (!previousState || typeof previousState !== "object") return false;
  replaceState(previousState);
  persistStateNow();
  return true;
}

async function replaceStateSafely(nextState, reason = "replace") {
  const backup = createTemporaryBackup(reason);
  try {
    replaceState(nextState);
    persistStateCache();
    return true;
  } catch (error) {
    restoreTemporaryBackup(backup);
    throw error;
  }
}

function extractBackupState(packageData) {
  const imported = packageData?.state || packageData;
  if (!imported || typeof imported !== "object" || Array.isArray(imported)) {
    throw new Error("O arquivo não parece ser um backup válido.");
  }

  // Só conta como backup valido se tiver ao menos uma chave de dados de negocio.
  const sanitizer = backupSanitizer();
  const businessKeys = sanitizer?.BACKUP_BUSINESS_KEYS || Object.keys(defaultState());
  const hasBusinessKey = businessKeys.some((key) =>
    Object.prototype.hasOwnProperty.call(imported, key),
  );
  if (!hasBusinessKey) {
    throw new Error("O arquivo não contém dados reconhecidos deste app.");
  }

  const arrayKeys = ["planner", "questions", "errors", "mocks", "essays", "studySessions", "focusTodos"];
  const invalidArrayKey = arrayKeys.find((key) => imported[key] !== undefined && !Array.isArray(imported[key]));
  if (invalidArrayKey) {
    throw new Error(`O campo "${invalidArrayKey}" está inválido.`);
  }

  // Descarta chaves de UI/sessao/tecnicas vindas do arquivo.
  // O spread de defaultState() preserva as preferencias LOCAIS do dispositivo.
  const businessData = sanitizer?.sanitizeStateForBackup
    ? sanitizer.sanitizeStateForBackup(imported)
    : imported;
  return { ...defaultState(), ...businessData };
}

function stateHasUserData(source = {}) {
  const data = { ...defaultState(), ...(source || {}) };
  const arrayKeys = ["planner", "questions", "errors", "mocks", "essays", "studySessions", "focusTodos", "customSubjects"];
  if (arrayKeys.some((key) => Array.isArray(data[key]) && data[key].length > 0)) return true;

  const objectKeys = [
    "customText",
    "dashboardOrder",
    "dashboardBlockSizes",
    "subjectNotes",
    "subjectCovers",
    "subjectContents",
    "smartFormMemory",
    "subjectCycles",
    "cycleLessons",
    "cycleContentOrder",
  ];
  if (objectKeys.some((key) => data[key] && typeof data[key] === "object" && Object.keys(data[key]).length > 0)) return true;

  return Boolean(
    data.weeklyGoal !== defaultState().weeklyGoal ||
    data.studyWeeklyGoalHours !== defaultState().studyWeeklyGoalHours
  );
}

function markLastSyncedAt() {
  state.syncMeta ||= {};
  state.syncMeta.lastSyncedAt = new Date().toISOString();
  try {
    persistStateNow();
  } catch (err) {
    console.warn("[markLastSyncedAt] persistStateNow falhou:", err);
  }
}

function stateSnapshot() {
  return JSON.stringify(state);
}

function commitHistory() {
  if (isBootstrapping || isApplyingHistory) return false;

  const snapshot = stateSnapshot();
  if (snapshot === lastHistorySnapshot) return false;

  historyPast.push(snapshot);
  if (historyPast.length > HISTORY_MAX_SNAPSHOTS) historyPast.shift();
  historyFuture.length = 0;
  lastHistorySnapshot = snapshot;
  return true;
}

function applyHistorySnapshot(snapshot) {
  isApplyingHistory = true;
  replaceState(JSON.parse(snapshot));
  lastHistorySnapshot = snapshot;
  saveState({ immediate: true });
  render();
  isApplyingHistory = false;
}

function undoState() {
  if (historyPast.length <= 1) return;

  const current = historyPast.pop();
  historyFuture.push(current);
  applyHistorySnapshot(historyPast[historyPast.length - 1]);
}

async function downloadStateBackup() {
  flushStateSave();
  await _loadBackupModules();
  // Backup carrega apenas dados de negocio (whitelist). UI/sessao/timestamps
  // de sync e flags tecnicas ficam de fora.
  const backupState = sanitizeStateForBackup(state);
  const passphrase = window.prompt("Frase secreta (opcional — deixe vazio para sem criptografia):");
  if (passphrase && window.studyIntegrity) {
    const result = await window.studyIntegrity.hmacEnvelop({ state: backupState, exportedAt: new Date().toISOString(), app: "estudos-enem", version: 1 }, passphrase);
    const blob = new Blob([JSON.stringify(result.envelop, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `backup-estudos-enem-${localDateValue()}.hmac.json`;
    document.body.appendChild(link);
    link.click();
    const href = link.href;
    link.remove();
    setTimeout(() => URL.revokeObjectURL(href), 0);
    return;
  }
  const payload = {
    exportedAt: new Date().toISOString(),
    app: "estudos-enem",
    version: 1,
    state: backupState,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `backup-estudos-enem-${localDateValue()}.json`;
  document.body.appendChild(link);
  link.click();
  const href = link.href;
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), 0);
}

function openBackupImportPicker() {
  const input = document.querySelector("#backupImportInput");
  if (!input) return;
  input.value = "";
  input.click();
}

async function handleBackupImport(event) {
  const input = event.target;
  const file = input.files?.[0];
  if (!file) return;

  try {
    await _loadBackupModules();
    const text = await readFileText(file);
    const parsed = JSON.parse(text);

    // Detecta HMAC envelop (exportado com frase secreta)
    if (parsed?.sig && parsed?.data && parsed?.salt && window.studyIntegrity) {
      const passphrase = window.prompt("Este backup esta protegido por frase secreta. Digite-a:");
      if (!passphrase) { input.value = ""; return; }
      const recovered = await window.studyIntegrity.verifyEnvelop(parsed, passphrase);
      const importedState = extractBackupState(recovered?.state || recovered);
      const exportedAt = recovered?.exportedAt ? localDateTimeLabel(recovered.exportedAt) : "data nao informada";
      openDeleteConfirmModal({
        title: "Importar backup criptografado",
        subtitle: "Backup protegido verificado com sucesso",
        message: `Backup de ${exportedAt}. Os dados atuais serao preservados automaticamente antes da troca. Continuar?`,
        confirmLabel: "Importar backup",
        onConfirm: async () => {
          try {
            await replaceStateSafely(importedState, "backup-import");
            ensureSandboxProfile();
            saveState({ immediate: true });
            render();
            updateFirstLoginModal();
            alert("Backup importado com seguranca.");
          } catch (error) {
            alert(`Nao consegui importar o backup. Seus dados anteriores foram restaurados. ${error.message || ""}`);
          }
        },
      });
      return;
    }

    const importedState = extractBackupState(parsed);
    const exportedAt = parsed?.exportedAt ? localDateTimeLabel(parsed.exportedAt) : "data nao informada";

    openDeleteConfirmModal({
      title: "Importar backup",
      subtitle: "Confirme antes de substituir os dados",
      message: `Esse backup foi exportado em ${exportedAt}. Os dados atuais serao guardados em um backup temporario automatico antes da troca. Continuar?`,
      confirmLabel: "Importar backup",
      onConfirm: async () => {
        try {
          await replaceStateSafely(importedState, "backup-import");
          ensureSandboxProfile();
          saveState({ immediate: true });
          render();
          updateFirstLoginModal();
          alert("Backup importado com seguranca.");
        } catch (error) {
          alert(`Nao consegui importar o backup. Seus dados anteriores foram restaurados. ${error.message || ""}`);
        }
      },
    });
  } catch (error) {
    alert(`Arquivo de backup invalido: ${error.message}`);
  } finally {
    input.value = "";
  }
}

function redoState() {
  const next = historyFuture.pop();
  if (!next) return;

  historyPast.push(next);
  applyHistorySnapshot(next);
}

async function hydrateRemoteState(source = "boot") {
  if (isLocalSandbox()) {
    remoteSyncEnabled = false;
    cloudSessionActive = false;
    setSyncStatus("localSandbox");
    return;
  }
  if (!window.studyCloud?.enabled && window.SUPABASE_CONFIG_PROMISE) {
    await window.SUPABASE_CONFIG_PROMISE;
  }
  if (window.studyCloud?.enabled) {
    try {
      const user = await window.studyCloud.getUser();
      if (!user) {
        remoteSyncEnabled = false;
        cloudSessionActive = false;
        setSyncStatus("disconnected");
        return;
      }
      cloudSessionActive = true;
      const localState = cloneStudyState(state);
      const versionResult = await Promise.race([
        window.studyCloud.loadLatestVersion(),
        timeoutPromise(10000),
      ]);
      const remoteState = versionResult?.data || null;
      const versionDate = versionResult?.created_at || null;
      const resolved = await Promise.race([
        resolveCloudStateAfterAuth({
          cloudState: remoteState,
          localState,
          profile: localState.userProfile,
          source: "boot",
          versionDate,
        }),
        timeoutPromise(20000),
      ]);
      if (!resolved) return;
        if (source === "auth" && versionDate && stateHasUserData(remoteState)) {
            notify("Restaurado de " + localDateTimeLabel(versionDate), "info");
        }
      remoteSyncEnabled = true;
      console.log("[hydrateRemoteState] estado remoto aplicado com sucesso — cache local substituido");
      await clearEphemeralSyncArtifacts({ preserveStateCache: true, nextState: state });
      setSyncStatus("synced");
      return;
    } catch (err) {
      if (err?.message === "DB_TIMEOUT") {
        console.warn("[hydrateRemoteState] estudoCloud timeout, mantendo apenas cache temporario e abrindo reconexao");
        remoteSyncEnabled = false;
        setSyncStatus("error");
        openSyncDisconnectedModal();
        return;
      }
      console.error("[hydrateRemoteState] caminho studyCloud falhou:", err);
      remoteSyncEnabled = false;
      setSyncStatus("error");
      openSyncDisconnectedModal();
    }
    return;
  }

  remoteSyncEnabled = false;
  setSyncStatus("offline");
}

/* =====================================================
   UTILITARIOS
   Datas, numeros, CSV, filtros e normalizacoes compartilhadas.
===================================================== */
function uid() {
  return globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : String(Date.now() + Math.random());
}

function today() {
  return localDateValue();
}

function timerOperationalDate(date = new Date()) {
  return reviewDayDate(date);
}

function timerOperationalValue(date = new Date()) {
  return localDateValue(timerOperationalDate(date));
}

function reviewDayDate(date = new Date()) {
  const current = new Date(date);
  if (current.getHours() < REVIEW_DAY_CUTOFF_HOUR) current.setDate(current.getDate() - 1);
  return current;
}

function reviewDayValue(date = new Date()) {
  return localDateValue(reviewDayDate(date));
}

function localDateValue(date = new Date()) {
  return sharedLocalDateValue(date);
}

function localDateFromValue(value) {
  return sharedLocalDateFromValue(value);
}

function addDays(dateValue, days) {
  return sharedAddDays(dateValue, days);
}

function normalizeTimerSubjectsList(items = []) {
  return [...new Set((items || [])
    .map((item) => String(item || "").trim())
    .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b, "pt-BR"));
}

function normalizeActiveStudyTimer(timer) {
  if (!timer || typeof timer !== "object") return null;
  const subject = String(timer.subject || "").trim();
  const startTime = String(timer.startTime || "").trim();
  if (!subject || !startTime) return null;
  const running = Boolean(timer.running);
  return {
    id: String(timer.id || uid()),
    subject,
    startTime,
    elapsedMs: Math.max(0, number(timer.elapsedMs)),
    runningSince: running ? Math.max(0, number(timer.runningSince) || Date.now()) : 0,
    running,
  };
}

function syncActiveStudyTimerState() {
  state.activeStudyTimer = activeStudyTimer ? normalizeActiveStudyTimer(activeStudyTimer) : null;
}

function restoreActiveStudyTimerState() {
  activeStudyTimer = normalizeActiveStudyTimer(state.activeStudyTimer);
  if (activeStudyTimer?.running) startStudyTimerTick();
}

function formatDate(date) {
  return sharedFormatDate(date);
}

function localDateTimeLabel(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "data não informada";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function timelineDateLabel(dateValue) {
  const selected = localDateFromValue(dateValue);
  const todayValue = localDateValue();
  const yesterdayValue = addDays(todayValue, -1);
  const tomorrowValue = addDays(todayValue, 1);
  const dateSuffix = new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  }).format(selected);
  if (dateValue === todayValue) return `Hoje, ${dateSuffix}`;
  if (dateValue === yesterdayValue) return `Ontem, ${dateSuffix}`;
  if (dateValue === tomorrowValue) return `Amanhã, ${dateSuffix}`;
  const label = new Intl.DateTimeFormat("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
  }).format(selected);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function timeValueFromDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function formatTime(value) { return timeValueFromDate(value); }

function localDateTimeValue(dateValue, timeValue = "00:00") {
  const safeDate = String(dateValue || localDateValue()).trim() || localDateValue();
  const safeTime = String(timeValue || "00:00").trim() || "00:00";
  return `${safeDate}T${safeTime}:00`;
}

function formatDuration(minutes) {
  const total = Math.max(0, Math.round(number(minutes)));
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours && mins) return `${hours}h ${mins}min`;
  if (hours) return `${hours}h`;
  return `${mins}min`;
}

function parseBrazilianDate(date) {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date.trim());
  if (!match) return "";
  const [, day, month, year] = match;
  const parsed = new Date(`${year}-${month}-${day}T00:00:00`);
  if (
    parsed.getFullYear() !== Number(year) ||
    parsed.getMonth() + 1 !== Number(month) ||
    parsed.getDate() !== Number(day)
  ) {
    return "";
  }
  return `${year}-${month}-${day}`;
}

function normalizeHeader(value) {
  return sharedNormalizeHeader(value);
}
function parseFlexibleDate(value) {
  const clean = String(value || "").trim();
  if (!clean) return today();
  if (/^\d{4}-\d{2}-\d{2}/.test(clean)) return clean.slice(0, 10);
  const datePart = clean.split(/\s+/)[0];
  const parsed = parseBrazilianDate(datePart);
  return parsed || parseNotionDate(clean) || today();
}

function parseNotionDate(value) {
  const clean = String(value || "").toLowerCase();
  const months = {
    "jan": 1,
    "fev": 2,
    "mar": 3,
    "abr": 4,
    "mai": 5,
    "jun": 6,
    "jul": 7,
    "ago": 8,
    "set": 9,
    "out": 10,
    "nov": 11,
    "dez": 12,
  };
  const match = /(jan|fev|mar|abr|mai|jun|jul|ago|set|out|nov|dez)\.?\s+(\d{1,2})(?:,?\s+(\d{4}))?/.exec(clean);
  if (!match) return "";
  const month = String(months[match[1]]).padStart(2, "0");
  const day = String(Number(match[2])).padStart(2, "0");
  const year = match[3] || String(new Date().getFullYear());
  return `${year}-${month}-${day}`;
}

function parseCsvNumber(value) {
  const clean = String(value || "")
    .replace("%", "")
    .replace(/\s+/g, "")
    .replace(",", ".");
  const parsed = Number(clean);
  return Number.isFinite(parsed) ? parsed : 0;
}

async function readFileText(file) {
  const buf = await file.arrayBuffer();
  const b = new Uint8Array(buf, 0, 4);
  if (b[0] === 0xEF && b[1] === 0xBB && b[2] === 0xBF)
    return new TextDecoder().decode(buf.slice(3));
  if (b[0] === 0xFF && b[1] === 0xFE)
    return new TextDecoder("utf-16le").decode(buf);
  if (b[0] === 0xFE && b[1] === 0xFF)
    return new TextDecoder("utf-16be").decode(buf);
  const utf8 = new TextDecoder("utf-8", { fatal: false }).decode(buf);
  if (!utf8.includes("\uFFFD")) return utf8;
  return new TextDecoder("windows-1252").decode(buf);
}

function parseCsv(text) {
  const rows = [];
  let row = [];
  let value = "";
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];

    if (char === '"' && inQuotes && next === '"') {
      value += '"';
      index += 1;
      continue;
    }

    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }

    if (!inQuotes && (char === "," || char === ";")) {
      row.push(value);
      value = "";
      continue;
    }

    if (!inQuotes && (char === "\n" || char === "\r")) {
      if (char === "\r" && next === "\n") index += 1;
      row.push(value);
      if (row.some((cell) => cell.trim() !== "")) rows.push(row);
      row = [];
      value = "";
      continue;
    }

    value += char;
  }

  row.push(value);
  if (row.some((cell) => cell.trim() !== "")) rows.push(row);
  if (!rows.length) return [];

  const headers = rows[0].map(normalizeHeader);
  return rows.slice(1).map((cells) => {
    const record = {};
    headers.forEach((header, index) => {
      record[header] = (cells[index] || "").trim();
    });
    return record;
  });
}

function pick(record, names) {
  for (const name of names) {
    const key = normalizeHeader(name);
    if (record[key] !== undefined && record[key] !== "") return record[key];
  }
  return "";
}

function number(value) {
  return sharedNumber(value);
}

function optionalNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function essayScoreTotal(item = {}) {
  return ["c1", "c2", "c3", "c4", "c5"].reduce((sum, field) => sum + number(item[field]), 0);
}

function essayIsUngraded(item = {}) {
  return essayScoreTotal(item) === 0;
}

function plannerPendingEssayOptions() {
  const scheduledEssayIds = new Set(
    activeRecords(state.planner || [])
      .map((item) => item.essayData?.essayId)
      .filter(Boolean),
  );
  return activeRecords(state.essays || [])
    .filter(essayIsUngraded)
    .filter((essay) => !scheduledEssayIds.has(essay.id))
    .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")) || String(a.theme || "").localeCompare(String(b.theme || ""), "pt-BR"));
}

function syncEssayScores() {
  activeRecords(state.essays).forEach((item) => {
    item.score = essayScoreTotal(item);
  });
}

function visibleEssays() {
  return activeRecords(state.essays || [])
    .filter((item) => matchesSubject(item, filters.essays, "Redação"));
}

function pct(correct, total) {
  return sharedPct(correct, total);
}

function questionPerformanceLabel(correct, total) {
  const parsedCorrect = Number(correct);
  const parsedTotal = Number(total);
  if (!Number.isFinite(parsedCorrect) || !Number.isFinite(parsedTotal) || parsedTotal === 0) return "-";
  const ratio = parsedCorrect / parsedTotal;
  if (ratio >= 0.8) return "Ótimo";
  if (ratio >= 0.6) return "Bom";
  return "Ruim";
}

function syncQuestionPerformance(item) {
  if (!item) return item;
  item.difficulty = questionPerformanceLabel(item.correct, item.questions);
  return item;
}

function syncAllQuestionPerformance() {
  (state.questions || []).forEach(syncQuestionPerformance);
}

function isQuestionGroup(item = {}) {
  return sharedIsQuestionGroup(item);
}

function questionLeafRecords(items = state.questions || []) {
  return sharedQuestionLeafRecords(activeRecords(items))
    .filter((item) => !item.essayId && canonicalSubject(item.subject || "") !== "Redação");
}

function setTodayDefaults() {
  document.querySelectorAll('input[type="date"]').forEach((input) => {
    if (!input.value) input.value = today();
  });
  syncQuestionFormDateDefault();
  syncErrorFormDateDefault();
  syncMockFormDateDefault();
}

function syncQuestionFormDateDefault() {
  const input = document.querySelector('#questionForm input[name="date"]');
  if (!input) return;
  if (!input.value || input.value === today()) input.value = questionTodayValue();
}

function syncErrorFormDateDefault() {
  const input = document.querySelector('#errorForm input[name="date"]');
  if (!input) return;
  if (!input.value || input.value === today()) input.value = errorTodayValue();
}

function syncMockFormDateDefault() {
  const input = document.querySelector('#mockForm input[name="date"]');
  if (!input) return;
  if (!input.value || input.value === today()) input.value = mockTodayValue();
}

function fillSubjectSelects() {
  document.querySelectorAll('select[name="subject"]').forEach((select) => {
    if (select.id === "mockSubjectFilter") return;
    const current = select.value;
    const options = select.dataset.includeGeneral === "true" ? ["Geral", ...getSubjects()] : getSubjects();
    const supportsNewSubject = Boolean(select.closest('.planner-builder-form[data-type="Teoria"], .planner-builder-form[data-type="Revisão"]'));
    const createOption = supportsNewSubject ? '<option value="__new__">+ Criar nova matéria</option>' : "";
    select.innerHTML = options.map((subject) => `<option>${subject}</option>`).join("") + createOption;
    if ([...select.options].some((option) => option.value === current)) select.value = current;
  });

  document.querySelectorAll("[data-filter-select]").forEach((select) => {
    if (select.id === "essaySubjectFilter") return;
    const current = select.value;
    const options = select.dataset.includeGeneral === "true" ? ["Geral", ...getSubjects()] : getSubjects();
    select.innerHTML = [
      '<option value="all">Todas</option>',
      ...options.map((subject) => `<option value="${subject}">${subject}</option>`),
    ].join("");
    if ([...select.options].some((option) => option.value === current)) select.value = current;
  });

  const mockTarget = document.querySelector("#mockTargetSelect");
  if (mockTarget) {
    const current = mockTarget.value;
    mockTarget.innerHTML = [
      '<option value="day1">Dia 1</option>',
      '<option value="day2">Dia 2</option>',
      ...getSubjects().map((subject) => `<option value="${subject}">${subject}</option>`),
    ].join("");
    if ([...mockTarget.options].some((option) => option.value === current)) mockTarget.value = current;
  }
}

function matchesSubject(item, filter, fallback = "") {
  if (filter === "all") return true;
  if (Array.isArray(item.subjects) && item.subjects.includes(filter)) return true;
  return (item.subject || fallback) === filter;
}

function dashboardSubjectFilter() {
  return filters.dashboardSubject || "all";
}

function dashboardTodayValue(date = new Date()) {
  return reviewDayValue(date);
}

function plannerTodayValue(date = new Date()) {
  return reviewDayValue(date);
}

function questionTodayValue(date = new Date()) {
  return reviewDayValue(date);
}

function questionsSubjectFilter() {
  return filters.questions || "all";
}

function questionsDoneFilter() {
  return filters.questionsDone || "all";
}

function questionsDateFilter() {
  return filters.questionsDate || "";
}

function sanitizeQuestionDoneFilter(value) {
  return ["done", "pending"].includes(value) ? value : "all";
}

function sanitizeQuestionDateFilter(value) {
  const clean = String(value || "").trim();
  return ["recent", "old"].includes(clean) ? clean : "";
}

function errorsSubjectFilter() {
  return filters.errors || "all";
}

function isExcludedFromSubjectFilters(subject) {
  return SUBJECT_FILTER_EXCLUSIONS.some((item) => normalizeHeader(item) === normalizeHeader(subject));
}

function sanitizeSubjectFilterValue(value) {
  if (!value || value === "all") return "all";
  return isExcludedFromSubjectFilters(value) ? "all" : value;
}

function subjectFilterOptions() {
  const fromStudyContent = Object.entries(state.subjectContents || {})
    .filter(([, contents]) => Array.isArray(contents) && contents.length)
    .map(([subject]) => subject);
  const fromStudyCycles = Object.entries(state.subjectCycles || {})
    .filter(([, cycles]) => Array.isArray(cycles) && cycles.length)
    .map(([subject]) => subject);
  const fromQuestions = questionLeafRecords(state.questions || []).map((item) => item.subject);
  const fromErrors = (state.errors || []).map((item) => item.subject);
  const fromPlannerReviews = (state.planner || [])

  return [
    ...fromStudyContent,
    ...fromStudyCycles,
    ...fromQuestions,
    ...fromErrors,
    ...fromPlannerReviews,
  ]
    .map((subject) => canonicalSubject(subject))
    .filter((subject) => subject && subject !== "Geral")
    .filter((subject) => !isExcludedFromSubjectFilters(subject))
    .filter((subject, index, list) => list.findIndex((item) => normalizeHeader(item) === normalizeHeader(subject)) === index)
    .sort((a, b) => a.localeCompare(b, "pt-BR"));
}

function questionFilterSubjects() {
  return subjectFilterOptions();
}

function errorFilterSubjects() {
  return (state.errors || [])
    .map((item) => canonicalSubject(item.subject))
    .filter((subject) => subject && subject !== "Geral")
    .filter((subject, index, list) => list.findIndex((item) => normalizeHeader(item) === normalizeHeader(subject)) === index)
    .sort((a, b) => a.localeCompare(b, "pt-BR"));
}

function syncErrorSubjectFilter() {
  const select = document.querySelector("#errorSubjectFilter");
  const resetButton = document.querySelector("#errorFilterReset");
  if (!select) return;
  const subjects = errorFilterSubjects();
  const valid = errorsSubjectFilter() === "all" || subjects.some(
    (subject) => normalizeHeader(subject) === normalizeHeader(errorsSubjectFilter()),
  );
  if (!valid) filters.errors = "all";
  select.innerHTML = [
    '<option value="all">Todas</option>',
    ...subjects.map((subject) => `<option value="${escapeAttribute(subject)}">${escapeHtml(subject)}</option>`),
  ].join("");
  select.value = errorsSubjectFilter();
  if (resetButton) resetButton.disabled = errorsSubjectFilter() === "all";
}

function linkedPlannerQuestionItem(question) {
  if (!question) return null;
  const plannerId = String(question.plannerId || "").trim();
  if (plannerId) {
    const linked = (state.planner || []).find((item) => item.id === plannerId);
    if (linked) return linked;
  }
  return (state.planner || []).find((item) => item.questionData?.questionId === question.id) || null;
}

function plannerQuestionDisplayLabel(question = {}) {
  const content = String(question.content || question.questionData?.content || "").trim();
  const subcontent = String(question.subcontent || question.questionData?.subcontent || "").trim();
  return [content, subcontent].filter(Boolean).join(" - ") || "Sem conteúdo";
}

function questionRecordDone(question) {
  if (!question || isQuestionGroup(question)) return false;
  const linkedPlanner = linkedPlannerQuestionItem(question);
  if (linkedPlanner) return Boolean(linkedPlanner.done || question.done || question.plannerDone);
  return Boolean(question.done || question.plannerDone);
}

function matchesQuestionDoneFilter(question, filter = questionsDoneFilter()) {
  if (filter === "all") return true;
  const done = questionRecordDone(question);
  return filter === "done" ? done : !done;
}

function matchesQuestionDateFilter(question, dateFilter = questionsDateFilter()) {
  const clean = sanitizeQuestionDateFilter(dateFilter);
  return clean ? Boolean(question) : true;
}

function matchesQuestionFilters(
  question,
  subjectFilter = questionsSubjectFilter(),
  doneFilter = questionsDoneFilter(),
  dateFilter = questionsDateFilter(),
) {
  return matchesSubject(question, subjectFilter)
    && matchesQuestionDoneFilter(question, doneFilter)
    && matchesQuestionDateFilter(question, dateFilter);
}

function shouldCountQuestionRecord(question) {
  return questionRecordDone(question);
}

function questionsForMetrics(items = state.questions || []) {
  return activeRecords(items).filter((item) => shouldCountQuestionRecord(item));
}

function syncQuestionSubjectFilter() {
  const select = document.querySelector("#questionSubjectFilter");
  const doneSelect = document.querySelector("#questionDoneFilter");
  const dateInput = document.querySelector("#questionDateFilter");
  const subjects = questionFilterSubjects();
  const valid = questionsSubjectFilter() === "all" || subjects.some(
    (subject) => normalizeHeader(subject) === normalizeHeader(questionsSubjectFilter()),
  );
  if (!valid) filters.questions = "all";
  filters.questionsDone = sanitizeQuestionDoneFilter(filters.questionsDone);
  filters.questionsDate = sanitizeQuestionDateFilter(filters.questionsDate);
  if (select) {
    select.innerHTML = [
      '<option value="all">Todas</option>',
      ...subjects.map((subject) => `<option value="${escapeAttribute(subject)}">${escapeHtml(subject)}</option>`),
    ].join("");
    select.value = questionsSubjectFilter();
  }
  if (doneSelect) doneSelect.value = questionsDoneFilter();
  if (dateInput) dateInput.value = questionsDateFilter();
  const resetButton = document.querySelector("#questionFilterReset");
  if (resetButton) resetButton.disabled = questionsSubjectFilter() === "all" && questionsDoneFilter() === "all" && !questionsDateFilter();
}

function startOfReviewWeekDate(date = new Date()) {
  const start = reviewDayDate(date);
  const dayIndex = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - dayIndex);
  start.setHours(0, 0, 0, 0);
  return start;
}

function hasFilterForSection(viewId) {
  return ["dashboard", "planner", "questoes", "erros", "simulados", "redacoes"].includes(viewId);
}

function buildFilterContent(viewId) {
  switch (viewId) {
    case "dashboard":
      return `
        <label class="filter-control">
          <span>Matéria</span>
          <select id="dashboardSubjectFilter">
            <option value="all">Todas</option>
          </select>
        </label>`;
    case "planner":
      return `
        <label class="filter-control">
          <span>Matéria</span>
          <select id="plannerSubjectFilter" data-filter-select></select>
        </label>`;
    case "questoes":
      return `
        <label class="filter-control">
          <span>Matéria</span>
          <select id="questionSubjectFilter"></select>
        </label>
        <label class="filter-control">
          <span>Status</span>
          <select id="questionDoneFilter">
            <option value="all">Todas</option>
            <option value="done">Feitas</option>
            <option value="pending">Não feitas</option>
          </select>
        </label>
        <label class="filter-control">
          <span>Data</span>
          <select id="questionDateFilter">
            <option value="">Ordem manual</option>
            <option value="recent">Mais recentes</option>
            <option value="old">Mais antigas</option>
          </select>
        </label>
        <button class="ghost-button" id="questionFilterReset" type="button">Limpar filtro</button>`;
    case "erros":
      return `
        <label class="filter-control">
          <span>Matéria</span>
          <select id="errorSubjectFilter"></select>
        </label>
        <button class="ghost-button" id="errorFilterReset" type="button">Limpar filtro</button>`;
    case "simulados":
      return `
        <label class="filter-control">
          <span>Área foco</span>
          <select id="mockSubjectFilter" data-filter-select data-include-general="true"></select>
        </label>`;
    case "redacoes":
      return `
        <label class="filter-control">
          <span>Matéria</span>
          <select id="essaySubjectFilter" data-filter-select>
            <option value="all">Todas</option>
            <option value="Redação">Redação</option>
          </select>
        </label>`;
    default:
      return "";
  }
}

function syncTopbarFilterOptions(viewId) {
  switch (viewId) {
    case "dashboard":
      syncDashboardSubjectOptions();
      break;
    case "questoes":
      syncQuestionSubjectFilter();
      break;
    case "erros":
      syncErrorSubjectFilter();
      break;
    case "planner":
    case "simulados":
      fillSubjectSelects();
      break;
  }
}

function updateTopbarSectionFilter(viewId) {
  const filterMenu = document.querySelector("#topbarSectionFilter");
  const panel = document.querySelector("#topbarSectionFilterPanel");
  if (!filterMenu || !panel) return;
  const hasFilter = hasFilterForSection(viewId);
  filterMenu.removeAttribute("open");
  filterMenu.hidden = false;
  filterMenu.classList.toggle("filter-menu-empty", !hasFilter);
  filterMenu.toggleAttribute("inert", !hasFilter);
  filterMenu.setAttribute("aria-hidden", String(!hasFilter));
  panel.innerHTML = hasFilter ? buildFilterContent(viewId) : "";
  const trigger = filterMenu.querySelector(".filter-menu-trigger");
  if (trigger) {
    const label = viewId === "simulados" ? "Filtrar simulados" : "Filtrar";
    trigger.setAttribute("aria-label", label);
    trigger.setAttribute("title", label);
    trigger.setAttribute("aria-expanded", "false");
    trigger.setAttribute("aria-disabled", String(!hasFilter));
  }
  syncTopbarFilterOptions(viewId);
}

function handleTopbarFilterChange(event) {
  const target = event.target;

  if (target.id === "dashboardSubjectFilter") {
    const nextFilter = sanitizeSubjectFilterValue(target.value);
    filters.dashboardSubject = nextFilter;
    filters.questions = nextFilter;
    selectedErrorSubject = "";
    selectedErrorContent = "";
    renderSectionNow("dashboard");
    return;
  }
  if (target.id === "questionSubjectFilter") {
    filters.questions = sanitizeSubjectFilterValue(target.value);
    resetVirtualListLimit("questoes");
    renderSectionNow("questoes");
    return;
  }
  if (target.id === "questionDoneFilter") {
    filters.questionsDone = sanitizeQuestionDoneFilter(target.value);
    resetVirtualListLimit("questoes");
    renderSectionNow("questoes");
    return;
  }
  if (target.id === "questionDateFilter") {
    filters.questionsDate = sanitizeQuestionDateFilter(target.value);
    resetVirtualListLimit("questoes");
    renderSectionNow("questoes");
    return;
  }
  if (target.id === "questionFilterReset") {
    filters.questions = "all";
    filters.questionsDone = "all";
    filters.questionsDate = "";
    resetVirtualListLimit("questoes");
    renderSectionNow("questoes");
    return;
  }
  if (target.id === "errorSubjectFilter") {
    filters.errors = sanitizeSubjectFilterValue(target.value);
    selectedErrorSubject = "";
    selectedErrorContent = "";
    resetVirtualListLimit("erros");
    closeFilterMenus();
    renderSectionNow("erros");
    return;
  }
  if (target.id === "errorFilterReset") {
    filters.errors = "all";
    selectedErrorSubject = "";
    selectedErrorContent = "";
    resetVirtualListLimit("erros");
    closeFilterMenus();
    renderSectionNow("erros");
    return;
  }
  if (target.id === "plannerSubjectFilter") {
    filters.planner = target.value;
    resetVirtualListLimit("planner");
    renderSectionNow("planner");
    return;
  }
  if (target.id === "mockSubjectFilter") {
    filters.mocks = target.value;
    renderSectionNow("simulados");
    return;
  }
  if (target.id === "essaySubjectFilter") {
    filters.essays = target.value;
    renderSectionNow("redacoes");
    return;
  }
}

function dashboardSubjects() {
  return subjectFilterOptions();
}

function syncDashboardSubjectOptions() {
  const select = document.querySelector("#dashboardSubjectFilter");
  if (!select) return;
  const subjectsForDashboard = dashboardSubjects();
  const validFilter = dashboardSubjectFilter() === "all" || subjectsForDashboard.some(
    (subject) => normalizeHeader(subject) === normalizeHeader(dashboardSubjectFilter()),
  );
  if (!validFilter) filters.dashboardSubject = "all";

  select.innerHTML = [
    '<option value="all">Todas</option>',
    ...subjectsForDashboard.map((subject) => `<option value="${escapeAttribute(subject)}">${escapeHtml(subject)}</option>`),
  ].join("");
  select.value = dashboardSubjectFilter();
}

function dashboardQuestions() {
  return questionsForMetrics(state.questions).filter((item) => matchesSubject(item, dashboardSubjectFilter()));
}

function dashboardErrors() {
  return activeRecords(state.errors).filter((item) => matchesSubject(item, dashboardSubjectFilter()));
}

function plannerItemVisible(item = {}) {
  if (!item || item.deletedAt) return false;
  if (isPlannerGroup(item)) return plannerGroupChildren(item.id).some((child) => plannerItemVisible(child));
  return true;
}

function canonicalSubject(value) {
  const raw = String(value || "").trim();
  if (!raw) return "Geral";
  const normalized = normalizeHeader(raw);
  return getSubjects().find((subject) => normalizeHeader(subject) === normalized) || raw;
}

function mockTodayValue(date = new Date()) {
  return reviewDayValue(date);
}

function errorTodayValue(date = new Date()) {
  return reviewDayValue(date);
}

function mockTypeOf(item = {}) {
  if (item.mockType) return item.mockType;
  if (item.examDay === "day1" || item.examDay === "day2") return item.examDay;
  return "subject";
}

function mockTotalQuestions(item = {}) {
  const type = mockTypeOf(item);
  if (type === "day1") return 90;
  if (type === "day2") return 90;
  return Math.max(0, number(item.totalQuestions));
}

function mockCorrectAnswers(item = {}) {
  const type = mockTypeOf(item);
  if (type === "day1") return Math.max(0, number(item.humanities) + number(item.languages));
  if (type === "day2") return Math.max(0, number(item.math) + number(item.nature));
  return Math.max(0, number(item.correct));
}

function mockAccuracyPercent(item = {}) {
  const total = mockTotalQuestions(item);
  const correct = mockCorrectAnswers(item);
  return total > 0 ? Math.round((correct / total) * 100) : null;
}

function normalizeMockRecord(item = {}) {
  const type = mockTypeOf(item);
  const normalized = {
    ...item,
    id: item.id || uid(),
    date: String(item.date || "").trim() || mockTodayValue(),
    name: String(item.name || "").trim() || "Simulado",
    mockType: type,
    examDay: type === "day1" || type === "day2" ? type : "",
    score: optionalNumber(item.score),
    correct: optionalNumber(item.correct),
    totalQuestions: optionalNumber(item.totalQuestions),
    languages: Math.max(0, number(item.languages)),
    humanities: Math.max(0, number(item.humanities)),
    nature: Math.max(0, number(item.nature)),
    math: Math.max(0, number(item.math)),
    essayScore: optionalNumber(item.essayScore),
  };
  if (type === "subject") {
    normalized.subject = canonicalSubject(item.subject || "Geral");
    if (normalized.subject && normalized.subject !== "Geral") ensureSubjectExists(normalized.subject);
    normalized.correct = normalized.correct === null ? null : Math.max(0, normalized.correct);
    normalized.totalQuestions = normalized.totalQuestions === null ? null : Math.max(1, normalized.totalQuestions);
    if (normalized.correct !== null && normalized.totalQuestions !== null) {
      normalized.correct = Math.min(normalized.totalQuestions, normalized.correct);
    }
  } else {
    normalized.subject = "Geral";
    normalized.correct = mockCorrectAnswers(normalized);
    normalized.totalQuestions = mockTotalQuestions(normalized);
  }
  return normalized;
}

function syncAllMocks() {
  state.mocks = (state.mocks || []).map((item) => normalizeMockRecord(item));
}

function subjectMatchesName(subject, target) {
  return sharedSubjectMatchesName(subject, target);
}

function isSubjectHidden(subject) {
  return (state.hiddenSubjects || []).some((item) => subjectMatchesName(item, subject));
}

function ensureSubjectState(subject) {
  const clean = String(subject || "").trim();
  if (!clean) return;
  state.subjectNotes ||= {};
  state.subjectContents ||= {};
  state.subjectCycles ||= {};
  state.cycleLessons ||= {};
  state.cycleContentOrder ||= {};
  state.subjectCovers ||= {};
  state.subjectNotes[clean] ??= "";
  state.subjectContents[clean] ||= [];
  state.subjectCycles[clean] ||= [];
}

function getSubjects() {
  const custom = state.customSubjects || [];
  return [...subjects, ...custom]
    .filter((subject, index, list) => list.findIndex((item) => normalizeHeader(item) === normalizeHeader(subject)) === index)
    .filter((subject) => !isSubjectHidden(subject));
}

function getTimerSubjects() {
  const timerDefaults = ["Português"];
  const hidden = normalizeTimerSubjectsList(state.hiddenTimerSubjects || []);
  const hiddenSet = new Set(hidden.map((item) => normalizeHeader(item)));
  const custom = Array.isArray(state.timerSubjects) ? normalizeTimerSubjectsList(state.timerSubjects) : [];
  return normalizeTimerSubjectsList([...getSubjects(), ...timerDefaults, ...custom]).filter(
    (subject) => !hiddenSet.has(normalizeHeader(subject)),
  );
}

function ensureTimerSubjectsInitialized() {
  state.hiddenTimerSubjects = normalizeTimerSubjectsList(state.hiddenTimerSubjects || []);
  if (Array.isArray(state.timerSubjects)) {
    state.timerSubjects = normalizeTimerSubjectsList(state.timerSubjects);
    return state.timerSubjects;
  }
  state.timerSubjects = [];
  return state.timerSubjects;
}

function addTimerSubject(name) {
  const subject = String(name || "").trim();
  if (!subject) {
    notify("Digite um nome para a matéria.", "error");
    return false;
  }
  const list = ensureTimerSubjectsInitialized();
  state.hiddenTimerSubjects = normalizeTimerSubjectsList(state.hiddenTimerSubjects || []).filter(
    (item) => normalizeHeader(item) !== normalizeHeader(subject),
  );
  if (getTimerSubjects().some((item) => normalizeHeader(item) === normalizeHeader(subject))) {
    notify("Essa matéria já existe no Cronômetro.", "error");
    return false;
  }
  list.push(subject);
  state.timerSubjects = normalizeTimerSubjectsList(list);
  return true;
}

function renameTimerSubject(currentName, nextName) {
  const current = String(currentName || "").trim();
  const next = String(nextName || "").trim();
  if (!current || !next) {
    notify("Digite um nome para a matéria.", "error");
    return false;
  }
  const list = ensureTimerSubjectsInitialized();
  const index = list.findIndex((item) => normalizeHeader(item) === normalizeHeader(current));
  if (index === -1) return false;
  const duplicate = getTimerSubjects().find(
    (item) => normalizeHeader(item) === normalizeHeader(next) && normalizeHeader(item) !== normalizeHeader(current),
  );
  if (duplicate) {
    notify("Já existe uma matéria com esse nome no Cronômetro.", "error");
    return false;
  }
  list[index] = next;
  state.timerSubjects = normalizeTimerSubjectsList(list);
  state.hiddenTimerSubjects = normalizeTimerSubjectsList(state.hiddenTimerSubjects || []).map((item) =>
    normalizeHeader(item) === normalizeHeader(current) ? next : item,
  );
  (state.studySessions || []).forEach((item) => {
    if (normalizeHeader(item.subject) === normalizeHeader(current)) item.subject = next;
  });
  (state.focusTodos || []).forEach((item) => {
    if (normalizeHeader(item.subject) === normalizeHeader(current)) item.subject = next;
  });
  if (activeStudyTimer && normalizeHeader(activeStudyTimer.subject) === normalizeHeader(current)) {
    activeStudyTimer.subject = next;
  }
  return true;
}

function deleteTimerSubject(subject, options = {}) {
  const current = String(subject || "").trim();
  if (!current) return false;
  if (activeStudyTimer && normalizeHeader(activeStudyTimer.subject) === normalizeHeader(current)) {
    notify("Finalize ou pause a sessão ativa antes de excluir essa matéria do Cronômetro.", "error");
    return false;
  }
  if (options.confirm !== false) {
    openDeleteConfirmModal({
      title: "Remover matéria do Cronômetro",
      subtitle: "O histórico salvo será preservado",
      message: `Remover "${current}" da seção Cronômetro?`,
      onConfirm: () => {
        if (!deleteTimerSubject(current, { confirm: false })) return;
        closeTimerSubjectMenu();
        renderSectionNow("tempo", { save: true, commit: true });
        showUndoToast("Matéria do cronômetro excluída.");
      },
    });
    return false;
  }
  state.timerSubjects = ensureTimerSubjectsInitialized().filter(
    (item) => normalizeHeader(item) !== normalizeHeader(current),
  );
  state.hiddenTimerSubjects = normalizeTimerSubjectsList([...(state.hiddenTimerSubjects || []), current]);
  return true;
}

function addSubject(name) {
  const subject = String(name || "").trim();
  if (!subject) return;

  const normalized = normalizeHeader(subject);
  const existingBase = subjects.find((item) => normalizeHeader(item) === normalized);
  const existingCustom = (state.customSubjects || []).find((item) => normalizeHeader(item) === normalized);

  state.hiddenSubjects = (state.hiddenSubjects || []).filter(
    (item) => normalizeHeader(item) !== normalized,
  );

  if (!existingBase && !existingCustom) {
    state.customSubjects ||= [];
    state.customSubjects = [...state.customSubjects, subject];
    state.customSubjects.sort((a, b) => a.localeCompare(b, "pt-BR"));
    touchDomainRoot("customSubjects");
  }

  ensureSubjectState(existingBase || existingCustom || subject);
}

function subjectUsageSummary(subject) {
  const match = (value) => subjectMatchesName(value, subject);
  const planner = (state.planner || []).filter((item) => match(item.subject) || (item.subjects || []).some(match));
  const questions = questionLeafRecords(state.questions || []).filter((item) => match(item.subject));
  const errors = (state.errors || []).filter((item) => match(item.subject));
  const mocks = (state.mocks || []).filter((item) => match(item.subject) || (item.subjects || []).some(match));
  const essays = (state.essays || []).filter((item) => match(item.subject));
  const studySessions = (state.studySessions || []).filter((item) => match(item.subject));
  const focusItems = (state.focusTodos || []).filter((item) => match(item.subject));
  const timerVisible = ensureTimerSubjectsInitialized().some(match);
  return {
    planner: planner.length,
    questions: questions.length,
    errors: errors.length,
    mocks: mocks.length,
    essays: essays.length,
    studySessions: studySessions.length,
    focusItems: focusItems.length,
    timerVisible,
  };
}

function deleteSubject(subject, options = {}) {
  const current = String(subject || "").trim();
  if (!current) return false;
  if (activeStudyTimer && subjectMatchesName(activeStudyTimer.subject, current)) {
    alert("Finalize ou pause a sessão ativa antes de ocultar essa matéria.");
    return false;
  }
  const usage = subjectUsageSummary(current);
  const linkedRecords = usage.planner + usage.questions + usage.errors + usage.mocks + usage.essays + usage.studySessions + usage.focusItems;
  const hasMaterials =
    Boolean(state.subjectNotes?.[current]) ||
    Boolean(state.subjectContents?.[current]?.length) ||
    Boolean(state.subjectCycles?.[current]?.length);
  const detail = [
    linkedRecords ? `${linkedRecords} registro(s) ligado(s)` : "",
    usage.timerVisible ? "visível no Cronômetro" : "",
    hasMaterials ? "conteúdos/ciclos salvos" : "",
  ].filter(Boolean).join(" · ");
  if (options.confirm !== false) {
    openDeleteConfirmModal({
      title: "Ocultar matéria",
      subtitle: "Os dados ligados serão preservados",
      message: `Ocultar "${current}" das galerias e menus? ${detail || "Os dados serão preservados."}`,
      onConfirm: () => {
        if (!deleteSubject(current, { confirm: false })) return;
        renderSectionNow("materias", { save: true, commit: true });
        showUndoToast("Matéria ocultada.");
      },
    });
    return false;
  }

  state.customSubjects = (state.customSubjects || []).filter((item) => !subjectMatchesName(item, current));
  if (subjects.some((item) => subjectMatchesName(item, current))) {
    state.hiddenSubjects ||= [];
    if (!state.hiddenSubjects.some((item) => subjectMatchesName(item, current))) state.hiddenSubjects.push(current);
  }
  if (Array.isArray(state.timerSubjects)) {
    state.timerSubjects = state.timerSubjects.filter((item) => !subjectMatchesName(item, current));
  }

  if (selectedStudySubject === current) {
    selectedStudySubject = "";
    selectedStudyCycle = "";
  }
  if (selectedErrorSubject === current) {
    selectedErrorSubject = "";
    selectedErrorContent = "";
  }
  if (filters.errors === current) filters.errors = "all";
  if (filters.questions === current) filters.questions = "all";
  if (filters.planner === current) filters.planner = "all";
  if (filters.mocks === current) filters.mocks = "all";
  if (filters.dashboardSubject === current) filters.dashboardSubject = "all";
  return true;
}

function addStudyCycle(cycleName) {
  const cycle = String(cycleName || "").trim();
  if (!cycle || !selectedStudySubject) return;

  const cycles = getSubjectCycles(selectedStudySubject);
  if (!cycles.some((item) => item.toLowerCase() === cycle.toLowerCase())) {
    cycles.push(cycle);
    cycles.sort((a, b) => a.localeCompare(b, "pt-BR"));
    touchMapKey("subjectCycles", selectedStudySubject, cycles);
  }
}

function cycleDependencySummary(subject, cycle) {
  const lessons = activeRecords(getCycleLessons(subject, cycle));
  const lessonIds = new Set(lessons.map((item) => item.id));
  return {
    planner: activeRecords(state.planner || []).filter(
      (item) =>
        subjectMatchesName(item.materialRef?.subject, subject) &&
        String(item.materialRef?.cycle || "") === String(cycle || ""),
    ).length,
    focusItems: (state.focusTodos || []).filter(
      (item) =>
        String(item.ref || "").includes(`lesson:${encodeURIComponent(subject)}:${encodeURIComponent(cycle)}:`) ||
        (subjectMatchesName(item.subject, subject) && String(item.cycle || "") === String(cycle || "")),
    ).length,
    lessonIds,
  };
}

function deleteStudyCycle(cycle, options = {}) {
  if (!selectedStudySubject || !cycle) return false;
  const dependencies = cycleDependencySummary(selectedStudySubject, cycle);
  if (dependencies.planner || dependencies.focusItems) {
    alert("Esse ciclo ainda está vinculado a Planner ou tarefas de foco. Remova os vínculos antes de excluir.");
    return false;
  }
  if (options.confirm !== false) {
    openDeleteConfirmModal({
      title: "Remover ciclo",
      subtitle: "Esse ciclo será removido da matéria atual",
      message: `Remover o ciclo "${cycle}"?`,
      onConfirm: () => {
        if (!deleteStudyCycle(cycle, { confirm: false })) return;
        renderSectionNow("materias", { save: true, commit: true });
        showUndoToast("Ciclo removido.");
      },
    });
    return false;
  }

  const filteredCycles = getSubjectCycles(selectedStudySubject).filter(
    (item) => item !== cycle,
  );
  touchMapKey("subjectCycles", selectedStudySubject, filteredCycles);
  touchMapKey("cycleLessons", cycleKey(selectedStudySubject, cycle), []);
  const nextOrderMap = { ...(state.cycleContentOrder || {}) };
  delete nextOrderMap[cycleKey(selectedStudySubject, cycle)];
  touchScalar("cycleContentOrder", nextOrderMap);
  if (selectedStudyCycle === cycle) selectedStudyCycle = "";
  return true;
}

function moveStudyCycleToPosition(fromCycle, beforeCycle = "") {
  if (!selectedStudySubject || !fromCycle) return false;
  const cycles = [...getSubjectCycles(selectedStudySubject)];
  const fromIndex = cycles.indexOf(fromCycle);
  if (fromIndex < 0) return false;
  const [moved] = cycles.splice(fromIndex, 1);
  const beforeIndex = beforeCycle ? cycles.indexOf(beforeCycle) : -1;
  if (beforeIndex >= 0) cycles.splice(beforeIndex, 0, moved);
  else cycles.push(moved);
  touchMapKey("subjectCycles", selectedStudySubject, cycles);
  return true;
}

function dndPlaceholder(kind = "block") {
  const selector = `[data-dnd-placeholder="${kind}"]`;
  let placeholder = document.querySelector(selector);
  if (!placeholder) {
    placeholder = document.createElement("div");
    placeholder.className = `dnd-drop-placeholder ${kind}-drop-placeholder`;
    placeholder.dataset.dndPlaceholder = kind;
    placeholder.setAttribute("aria-hidden", "true");
  }
  return placeholder;
}

function clearGenericDndState(kind = "") {
  const suffix = kind ? `[data-dnd-placeholder="${kind}"]` : "[data-dnd-placeholder]";
  document.querySelectorAll(suffix).forEach((element) => element.remove());
  document.querySelectorAll(".drop-before, .drop-after, .drag-over").forEach((element) => {
    if (element.matches("[data-edit-block], [data-task-id], [data-calendar-date]")) return;
    element.classList.remove("drop-before", "drop-after", "drag-over");
  });
}

function insertionBeforeDataValue(container, selector, dataKey, clientY, draggedValue = "") {
  const items = [...(container?.querySelectorAll(selector) || [])]
    .filter((item) => item.dataset[dataKey] !== draggedValue)
    .filter((item) => !item.matches("[data-dnd-placeholder]"));
  let candidate = null;
  let closestOffset = Number.NEGATIVE_INFINITY;
  items.forEach((item) => {
    const rect = item.getBoundingClientRect();
    const offset = clientY - rect.top - rect.height / 2;
    if (offset < 0 && offset > closestOffset) {
      closestOffset = offset;
      candidate = item;
    }
  });
  return candidate?.dataset[dataKey] || "";
}

function renderBlockPlaceholder(container, beforeElement, kind, draggedElement = null) {
  if (!container) return;
  const placeholder = dndPlaceholder(kind);
  const height = draggedElement?.getBoundingClientRect().height || beforeElement?.getBoundingClientRect().height || 72;
  placeholder.style.minHeight = `${Math.max(44, Math.min(180, height))}px`;
  if (placeholder.parentElement !== container) container.appendChild(placeholder);
  if (beforeElement && beforeElement.parentElement === container) container.insertBefore(placeholder, beforeElement);
  else container.appendChild(placeholder);
}

function updateStudyCycleDropTarget(event, draggedCycle = "") {
  const container = event.target.closest(".cycle-gallery");
  if (!container) return null;
  const beforeValue = insertionBeforeDataValue(container, "[data-study-cycle-card]", "studyCycleCard", event.clientY, encodeURIComponent(draggedCycle));
  const beforeElement = beforeValue ? container.querySelector(`[data-study-cycle-card="${CSS.escape(beforeValue)}"]`) : null;
  clearGenericDndState("cycle");
  container.classList.add("drag-over");
  renderBlockPlaceholder(container, beforeElement, "cycle", document.querySelector("[data-study-cycle-card].dragging"));
  return { beforeCycle: beforeValue ? decodeURIComponent(beforeValue) : "" };
}

function validViewId(viewId) {
  return Boolean(viewId && document.getElementById(viewId) && document.querySelector(`[data-view="${viewId}"]`));
}

function normalizeRoutePath(pathname = location.pathname) {
  const clean = `/${String(pathname || "/").replace(/^\/+/, "")}`;
  return clean.length > 1 ? clean.replace(/\/+$/, "") : "/";
}

function routeForView(viewId = "dashboard") {
  return viewRoutes[viewId] || viewRoutes.dashboard;
}

// --- Deep-linking: query param infrastructure ---

function buildQueryParams(viewId = activeViewId()) {
  const params = new URLSearchParams();
  if (viewId === "materias") {
    if (selectedStudySubject) params.set("subject", selectedStudySubject);
    if (selectedStudyCycle) params.set("cycle", selectedStudyCycle);
  }
  if (viewId === "questoes") {
    if (selectedQuestionSubject) params.set("subject", selectedQuestionSubject);
    if (questionViewMode && questionViewMode !== "cards") params.set("viewMode", questionViewMode);
    if (activeQuestionGroupViewId) params.set("group", activeQuestionGroupViewId);
  }
  if (viewId === "erros") {
    if (selectedErrorSubject) params.set("subject", selectedErrorSubject);
    if (selectedErrorContent) params.set("content", selectedErrorContent);
  }
  if (viewId === "planner") {
    const dateStr = localDateValue(calendarDate);
    const todayStr = localDateValue(new Date());
    if (dateStr && dateStr !== todayStr) params.set("date", dateStr);
    if (plannerMode) params.set("mode", plannerMode);
    if (plannerActiveView && plannerActiveView !== "calendar") params.set("tab", plannerActiveView);
  }
  if (viewId === "tempo") {
    if (selectedStudyTimelineDate) params.set("timelineDate", selectedStudyTimelineDate);
  }
  return params;
}

function applyQueryParams(viewId, searchParams) {
  if (!searchParams || !(searchParams instanceof URLSearchParams)) return;
  if (viewId === "materias") {
    const subject = searchParams.get("subject");
    if (subject) {
      const subjects = getSubjects();
      const match = subjects.find((s) => normalizeHeader(s) === normalizeHeader(subject));
      if (match) selectedStudySubject = match;
    }
    const cycle = searchParams.get("cycle");
    if (cycle && selectedStudySubject) {
      const cycles = getSubjectCycles(selectedStudySubject);
      const cycleMatch = cycles.find((c) => normalizeHeader(c) === normalizeHeader(cycle));
      if (cycleMatch) selectedStudyCycle = cycleMatch;
    }
  }
  if (viewId === "questoes") {
    const subject = searchParams.get("subject");
    if (subject) {
      const subjects = getSubjects();
      const match = subjects.find((s) => normalizeHeader(s) === normalizeHeader(subject));
      if (match) selectedQuestionSubject = match;
    }
    const viewMode = searchParams.get("viewMode");
    if (viewMode && (viewMode === "cards" || viewMode === "table")) {
      questionViewMode = viewMode;
    }
    const group = searchParams.get("group");
    if (group) activeQuestionGroupViewId = group;
  }
  if (viewId === "erros") {
    const subject = searchParams.get("subject");
    if (subject) {
      const errSubjects = errorFilterSubjects();
      const match = errSubjects.find((s) => normalizeHeader(s) === normalizeHeader(subject));
      if (match) selectedErrorSubject = match;
    }
    const content = searchParams.get("content");
    if (content && selectedErrorSubject) {
      const contentMatch = (state.errors || [])
        .filter((item) => subjectMatchesName(item.subject, selectedErrorSubject))
        .find((item) => normalizeHeader(item.content || "Sem conteúdo") === normalizeHeader(content));
      if (contentMatch) selectedErrorContent = contentMatch.content || "Sem conteúdo";
    }
  }
  if (viewId === "planner") {
    const date = searchParams.get("date");
    if (date) {
      const parsed = new Date(date + "T12:00:00");
      if (!isNaN(parsed.getTime())) calendarDate = parsed;
    }
    const mode = searchParams.get("mode");
    if (mode && (mode === "day" || mode === "week" || mode === "month")) plannerMode = mode;
    const tab = searchParams.get("tab");
    if (tab) plannerActiveView = tab;
  }
  if (viewId === "tempo") {
    const timelineDate = searchParams.get("timelineDate");
    if (timelineDate) selectedStudyTimelineDate = timelineDate;
  }
}

function syncURLParams() {
  const viewId = activeViewId();
  const params = buildQueryParams(viewId);
  const route = routeForView(viewId);
  const qs = params.toString();
  const url = qs ? `${route}?${qs}` : route;
  const currentURL = location.pathname + location.search;
  if (currentURL !== url) {
    history.replaceState(history.state, "", url);
  }
}

function pushSubStateToHistory() {
  syncBrowserHistory(activeViewId());
}

// --- End deep-linking infrastructure ---

function viewFromRoutePath(pathname = location.pathname) {
  return routeViews[normalizeRoutePath(pathname)] || "";
}

function syncBrowserHistory(viewId = activeViewId(), options = {}) {
  if (location.protocol === "file:" || !window.history?.pushState) return;
  const route = routeForView(viewId);
  const params = buildQueryParams(viewId);
  const qs = params.toString();
  const url = qs ? `${route}?${qs}` : route;
  const currentURL = location.pathname + location.search;
  const method = options.replace || currentURL === url ? "replaceState" : "pushState";
  window.history[method]({ viewId }, "", url);
}

async function applyViewFromBrowserHistory() {
  const nextView = viewFromRoutePath() || "dashboard";
  if (!validViewId(nextView)) return;
  state.activeView = nextView;
  const params = new URLSearchParams(location.search);
  applyQueryParams(nextView, params);
  saveState();
  await ensureSectionDataLoaded(nextView);
  await ensureSectionModuleLoaded(nextView);
  render();
}

function initBrowserHistory() {
  const routeView = viewFromRoutePath();
  if (routeView && validViewId(routeView)) state.activeView = routeView;
  if (!validViewId(state.activeView)) state.activeView = "dashboard";
  const params = new URLSearchParams(location.search);
  applyQueryParams(state.activeView, params);
  syncBrowserHistory(activeViewId(), { replace: true });
  window.addEventListener("popstate", applyViewFromBrowserHistory);
}

function activeViewId() {
  return validViewId(state.activeView) ? state.activeView : "dashboard";
}

function dataDomainInitializers() {
  return {
    profile: () => {},
    metrics: () => {},
    subjects: () => {
      state.customSubjects ||= [];
      state.hiddenSubjects ||= [];
      state.subjectNotes ||= {};
      state.subjectCovers ||= {};
      state.subjectContents ||= {};
      state.subjectCycles ||= {};
      state.cycleLessons ||= {};
      state.cycleContentOrder ||= {};
    },
    timer: () => {
      state.timerSubjects = normalizeTimerSubjectsList(state.timerSubjects || []);
      state.hiddenTimerSubjects = normalizeTimerSubjectsList(state.hiddenTimerSubjects || []);
      state.activeStudyTimer = normalizeActiveStudyTimer(state.activeStudyTimer);
    },
    planner: () => {
      state.planner ||= [];
    },
    questions: () => {
      state.questions ||= [];
    },
    errors: () => {
      state.errors ||= [];
    },
    mocks: () => {
      state.mocks ||= [];
    },
    essays: () => {
      state.essays ||= [];
    },
    studySessions: () => {
      state.studySessions ||= [];
    },
    focus: () => {
      state.focusTodos ||= [];
    },
  };
}

function loadDataDomain(domain = "") {
  if (!domain || loadedDataDomains.has(domain)) return Promise.resolve();
  if (loadingDataDomains.has(domain)) return loadingDataDomains.get(domain);
  const loading = Promise.resolve()
    .then(() => {
      const initializer = dataDomainInitializers()[domain];
      if (typeof initializer === "function") initializer();
      loadedDataDomains.add(domain);
    })
    .finally(() => {
      loadingDataDomains.delete(domain);
    });
  loadingDataDomains.set(domain, loading);
  return loading;
}

function dataDomainsForSection(viewId = activeViewId()) {
  return sectionDataDomains[viewId] || sectionDataDomains.dashboard;
}

function preloadSectionData(viewId = activeViewId()) {
  dataDomainsForSection(viewId).forEach((domain) => {
    loadDataDomain(domain).catch((err) => {
      console.error("[preloadSectionData]", err);
    });
  });
}

async function ensureSectionDataLoaded(viewId = activeViewId()) {
  await Promise.all(dataDomainsForSection(viewId).map(loadDataDomain));
}

const sectionModuleApiKeys = {
  dashboard: [
    "escapeHtml",
    "escapeAttribute",
    "safeOption",
    "formatDate",
    "localDateValue",
    "renderMetrics",
    "applyDashboardOrder",
    "applyDashboardBlockSizes",
    "plannerCardHeading",
    "plannerSubjectsLabel",
    "dashboardUpcomingTasks",
    "dashboardConfiguredSubjectCount",
    "dashboardBreakdownSnapshot",
    "heatMapSnapshot",
    "heatMapTooltipText",
    "dashboardQuestionChartSnapshot",
  ],
  materias: [
    "escapeHtml",
    "escapeAttribute",
    "formatDuration",
    "getSubjects",
    "getSubjectCycles",
    "studySubjectCardStats",
    "getCycleLessons",
    "getOrderedLessonGroups",
    "isLessonGroupCollapsed",
    "studySubjectsHomeSummary",
    "pendingLessonsFor",
    "pendingLessonsPending",
    "pendingLessonsRecentDone",
    "pendingLessonsCount",
    "subjectClass",
    "studySubjectIconAsset",
    "studySubjectAreaLabel",
    "renderEmptyState",
    "emptyStateSvg",
  ],
  tempo: [
    "renderStudyTime",
  ],
  planner: [
    "escapeHtml",
    "escapeAttribute",
    "safeOption",
    "formatDate",
    "localDateValue",
    "matchesSubject",
    "renderPlannerBuilderPanel",
    "plannerTimeSlots",
    "plannerTimeToMinutes",
    "defaultPlannerEndTime",
    "taskColumns",
    "plannerTodayValue",
    "taskStatus",
    "isPlannerGroup",
    "plannerSelectedCardClass",
    "plannerDoneClass",
    "plannerStatusClass",
    "plannerGroupCompleteClass",
    "plannerStatusLabel",
    "plannerDateLabel",
    "plannerDateValue",
    "plannerSubjectsLabel",
    "plannerGroupChildren",
    "plannerGroupSummaryText",
    "plannerCardHeading",
    "plannerTypeClass",
    "plannerTopLevelItems",
    "plannerItemVisible",
    "comparePlannerVisualOrder",
    "reviewDayValue",
    "isPlannerReviewDayPhase",
    "applyPlannerDropDestination",
    "renderEmptyState",
    "emptyStateSvg",
  ],
  questoes: [
    "escapeHtml",
    "escapeAttribute",
    "safeOption",
    "formatDate",
    "number",
    "pct",
    "normalizeHeader",
    "canonicalSubject",
    "subjectClass",
    "studySubjectIconAsset",
    "studySubjectAreaLabel",
    "questionsSubjectFilter",
    "questionsDoneFilter",
    "questionsDateFilter",
    "syncQuestionSubjectFilter",
    "matchesQuestionDoneFilter",
    "matchesQuestionDateFilter",
    "matchesQuestionFilters",
    "questionRecordDone",
    "questionPerformanceLabel",
    "questionsForMetrics",
    "isQuestionGroup",
    "questionLeafRecords",
    "questionTopLevelItems",
    "compareQuestionVisualOrder",
    "compareQuestionDateOrder",
    "questionGroupChildren",
    "questionGroupMetrics",
    "questionGroupSubjectLabel",
    "questionGroupContentLabel",
    "questionGroupSummaryText",
    "performanceTone",
    "renderEmptyState",
    "emptyStateSvg",
  ],
  erros: [
    "escapeHtml",
    "escapeAttribute",
    "errorsSubjectFilter",
    "syncErrorSubjectFilter",
    "matchesSubject",
    "canonicalSubject",
    "subjectMatchesName",
    "groupBy",
    "subjectClass",
    "studySubjectIconAsset",
    "studySubjectAreaLabel",
    "renderQuestionHeaderContent",
    "renderRichTextContent",
    "renderEmptyState",
    "emptyStateSvg",
  ],
  simulados: [
    "escapeHtml",
    "formatDate",
    "matchesSubject",
    "mockTypeOf",
    "mockTotalQuestions",
    "mockCorrectAnswers",
    "mockAccuracyPercent",
    "performanceLabel",
    "performanceTone",
    "renderEmptyState",
    "emptyStateSvg",
  ],
  redacoes: [
    "escapeHtml",
    "formatDate",
    "matchesSubject",
    "essayScoreTotal",
    "renderThumb",
    "essayChartSnapshot",
    "renderEmptyState",
    "emptyStateSvg",
  ],
};

function sectionModuleApiRegistry() {
  return {
    escapeHtml,
    escapeAttribute,
    safeOption,
    formatDate,
    localDateValue,
    number,
    addDays,
    localDateFromValue,
    pct,
    normalizeHeader,
    canonicalSubject,
    errorsSubjectFilter,
    syncErrorSubjectFilter,
    questionsSubjectFilter,
    questionsDoneFilter,
    questionsDateFilter,
    syncQuestionSubjectFilter,
    matchesSubject,
    matchesQuestionDoneFilter,
    matchesQuestionDateFilter,
    matchesQuestionFilters,
    questionRecordDone,
    questionPerformanceLabel,
    questionsForMetrics,
    isQuestionGroup,
    questionLeafRecords,
    questionTopLevelItems,
    compareQuestionVisualOrder,
    compareQuestionDateOrder,
    questionGroupChildren,
    questionGroupMetrics,
    questionGroupSubjectLabel,
    questionGroupContentLabel,
    questionGroupSummaryText,
    subjectMatchesName,
    groupBy,
    subjectClass,
    studySubjectIconAsset,
    studySubjectAreaLabel,
    renderQuestionHeaderContent,
    renderRichTextContent,
    renderMetrics,
    applyDashboardOrder,
    applyDashboardBlockSizes,
    dashboardUpcomingTasks,
    dashboardConfiguredSubjectCount,
    dashboardBreakdownSnapshot,
    heatMapSnapshot,
    heatMapTooltipText,
    dashboardQuestionChartSnapshot,
    studySubjectsHomeSummary,
    pendingLessonsFor,
    pendingLessonsPending,
    pendingLessonsRecentDone,
    pendingLessonsCount,
    formatDuration,
    getSubjects,
    getSubjectCycles,
    studySubjectCardStats,
    getCycleLessons,
    getOrderedLessonGroups,
    isLessonGroupCollapsed,
    renderStudyTime,
    renderPlannerBuilderPanel,
    plannerTimeSlots,
    plannerTimeToMinutes,
    defaultPlannerEndTime,
    taskColumns,
    plannerTodayValue,
    taskStatus,
    isPlannerGroup,
    plannerSelectedCardClass,
    plannerDoneClass,
    plannerStatusClass,
    plannerGroupCompleteClass,
    plannerStatusLabel,
    plannerDateLabel,
    plannerDateValue,
    plannerSubjectsLabel,
    plannerGroupChildren,
    plannerGroupSummaryText,
    plannerCardHeading,
    plannerTypeClass,
    plannerTopLevelItems,
    plannerItemVisible,
    comparePlannerVisualOrder,
    reviewDayValue,
    isPlannerReviewDayPhase: (now = "") => sharedPlannerReviewDayPhase(now, REVIEW_DAY_CUTOFF_HOUR),
    applyPlannerDropDestination,
    essayScoreTotal,
    mockTypeOf,
    mockTotalQuestions,
    mockCorrectAnswers,
    mockAccuracyPercent,
    performanceLabel,
    performanceTone,
    renderThumb,
    essayChartSnapshot,
    renderEmptyState: sharedRenderEmptyState,
    emptyStateSvg: sharedEmptyStateSvg,
  };
}

function pickSectionModuleApi(keys = []) {
  const registry = sectionModuleApiRegistry();
  return keys.reduce((api, key) => {
    if (key in registry) api[key] = registry[key];
    else console.warn(`API de modulo de secao nao encontrada: ${key}`);
    return api;
  }, {});
}

function sectionModuleApi(viewId = activeViewId()) {
  return pickSectionModuleApi(sectionModuleApiKeys[viewId] || []);
}

function sectionModuleUi(viewId = activeViewId()) {
  if (viewId === "materias") {
    return {
      get selectedStudySubject() {
        return selectedStudySubject;
      },
      set selectedStudySubject(value) {
        selectedStudySubject = value;
      },
      get selectedStudyCycle() {
        return selectedStudyCycle;
      },
      set selectedStudyCycle(value) {
        selectedStudyCycle = value;
      },
    };
  }
  if (viewId === "planner") {
    return {
      get calendarDate() {
        return calendarDate;
      },
      set calendarDate(value) {
        calendarDate = value instanceof Date ? value : new Date(value || Date.now());
      },
      get plannerMode() {
        return plannerMode;
      },
      set plannerMode(value) {
        plannerMode = value === "month" ? "month" : value === "day" ? "day" : "week";
      },
      get plannerActiveView() {
        return plannerActiveView;
      },
      set plannerActiveView(value) {
        plannerActiveView = value;
      },
      get plannerBuilderOpen() {
        return plannerBuilderOpen;
      },
      set plannerBuilderOpen(value) {
        plannerBuilderOpen = Boolean(value);
      },
      get plannerBuilderDate() {
        return plannerBuilderDate;
      },
      set plannerBuilderDate(value) {
        plannerBuilderDate = String(value || "").trim() || localDateValue();
      },
      get plannerBuilderTime() {
        return plannerBuilderTime;
      },
      set plannerBuilderTime(value) {
        plannerBuilderTime = String(value || "").trim();
      },
    };
  }
  if (viewId === "questoes") {
    return {
      get questionSelectedRowIds() {
        return questionSelectedRowIds;
      },
      set questionSelectedRowIds(value) {
        questionSelectedRowIds = Array.isArray(value) ? value : [];
      },
      get activeQuestionGroupViewId() {
        return activeQuestionGroupViewId;
      },
      set activeQuestionGroupViewId(value) {
        activeQuestionGroupViewId = value;
      },
      get questionGroupViewSearch() {
        return questionGroupViewSearch;
      },
      set questionGroupViewSearch(value) {
        questionGroupViewSearch = value;
      },
      get selectedQuestionSubject() {
        return selectedQuestionSubject;
      },
      set selectedQuestionSubject(value) {
        selectedQuestionSubject = value;
      },
      get questionViewMode() {
        return questionViewMode;
      },
      set questionViewMode(value) {
        questionViewMode = value === "table" ? "table" : "cards";
      },
      setQuestionGroupViewModalOpen,
    };
  }
  if (viewId === "erros") {
    return {
      get selectedErrorSubject() {
        return selectedErrorSubject;
      },
      set selectedErrorSubject(value) {
        selectedErrorSubject = value;
      },
      get selectedErrorContent() {
        return selectedErrorContent;
      },
      set selectedErrorContent(value) {
        selectedErrorContent = value;
      },
    };
  }
  return {};
}

function sectionModuleContext(viewId = activeViewId()) {
  return {
    viewId,
    active: activeViewId() === viewId,
    root: document.getElementById(viewId),
    state,
    filters,
    api: sectionModuleApi(viewId),
    ui: sectionModuleUi(viewId),
    ensureData: () => ensureSectionDataLoaded(viewId),
    renderSection: (options = {}) => renderSectionNow(viewId, options),
    renderApp: () => render(),
    virtualListLimit,
    virtualListButton,
    syncURLParams,
    pushSubStateToHistory,
  };
}

function runSectionModuleHook(module, hookName, viewId = activeViewId()) {
  const hook = module?.[hookName];
  if (typeof hook !== "function") return;
  hook(sectionModuleContext(viewId));
}

function runSectionModuleAction(viewId = activeViewId(), payload = {}) {
  const module = loadedSectionModules.get(viewId);
  const handler = module?.handleAction;
  if (typeof handler !== "function") return false;
  return Boolean(handler(sectionModuleContext(viewId), payload));
}

function queueSectionModuleLoad(viewId = activeViewId()) {
  if (!validViewId(viewId)) return Promise.resolve(null);
  if (loadedSectionModules.has(viewId)) return Promise.resolve(loadedSectionModules.get(viewId));
  if (loadingSectionModules.has(viewId)) return loadingSectionModules.get(viewId);
  const loader = sectionModuleLoaders[viewId];
  if (!loader) return Promise.resolve(null);

  const loading = loader()
    .then((module) => {
      loadedSectionModules.set(viewId, module);
      failedSectionModules.delete(viewId);
      loadingSectionModules.delete(viewId);
      runSectionModuleHook(module, "setup", viewId);
      if (activeViewId() === viewId) runSectionModuleHook(module, "afterRender", viewId);
      return module;
    })
    .catch((error) => {
      loadingSectionModules.delete(viewId);
      failedSectionModules.add(viewId);
      console.warn(`Nao foi possivel carregar o modulo da secao ${viewId}.`, error);
      return null;
    });
  loadingSectionModules.set(viewId, loading);
  return loading;
}

function preloadSectionModule(viewId = activeViewId()) {
  queueSectionModuleLoad(viewId);
}

async function ensureSectionModuleLoaded(viewId = activeViewId()) {
  return queueSectionModuleLoad(viewId);
}

function hasSectionModuleLoader(viewId = activeViewId()) {
  return typeof sectionModuleLoaders[viewId] === "function";
}

function shouldWaitForSectionModule(viewId = activeViewId()) {
  return hasSectionModuleLoader(viewId)
    && !loadedSectionModules.has(viewId)
    && !failedSectionModules.has(viewId);
}

function scheduleSectionRenderAfterModuleLoad(viewId = activeViewId()) {
  queueSectionModuleLoad(viewId).then((module) => {
    if (!module || activeViewId() !== viewId) return;
    renderSectionNow(viewId, { force: true });
  });
}

function navItemLabelText(item) {
  return (item?.querySelector(".nav-item-label")?.textContent || item?.textContent || "").trim();
}

function sectionSubtitleFallback(viewId = "") {
  const subtitles = {
    dashboard: "Visão geral dos estudos",
    materias: "Gerencie suas matérias e acompanhe seu progresso",
    tempo: "Registre sessões por disciplina e acompanhe sua semana",
    planner: "Organize aulas, listas, revisões e simulados",
    questoes: "Registre listas, conteúdo, quantidade e acertos",
    erros: "Transforme erro em revisão objetiva",
    simulados: "Acompanhe nota, acertos e áreas do ENEM",
    redacoes: "Registre tema, nota total e competências",
  };
  return subtitles[viewId] || "";
}

function sectionSubtitleText(viewId = activeViewId()) {
  const view = document.getElementById(viewId);
  const subtitle = view?.querySelector(
    ':scope > .panel > .panel-head:first-child p, :scope .subjects-home-hero p, :scope .questions-home-hero p, :scope .errors-home-hero p',
  );
  return (subtitle?.textContent || sectionSubtitleFallback(viewId)).trim();
}

function restorePageHeaderContextActions() {
  if (!pageHeaderContextActions) return;
  [...pageHeaderActionPlaceholders.entries()].forEach(([node, placeholder]) => {
    if (!placeholder.parentNode) {
      if (node.parentNode === pageHeaderContextActions) node.remove();
      pageHeaderActionPlaceholders.delete(node);
      return;
    }
    if (node.parentNode === pageHeaderContextActions) placeholder.parentNode.insertBefore(node, placeholder);
  });
  pageHeaderContextActions.innerHTML = "";
}

function movePageHeaderContextAction(node) {
  if (!pageHeaderContextActions || !node || node === pageHeaderContextActions || pageHeaderContextActions.contains(node)) return;
  if (!pageHeaderActionPlaceholders.has(node)) {
    const placeholder = document.createComment("page-header-context-action");
    node.parentNode?.insertBefore(placeholder, node);
    pageHeaderActionPlaceholders.set(node, placeholder);
  }
  pageHeaderContextActions.appendChild(node);
}

function syncPageHeaderContextActions() {
  if (!pageHeaderContextActions) return;
  restorePageHeaderContextActions();
  const selectors = pageHeaderContextActionSelectors[activeViewId()] || [];
  selectors
    .map((selector) => document.querySelector(selector))
    .filter(Boolean)
    .forEach(movePageHeaderContextAction);
  pageHeaderContextActions.hidden = pageHeaderContextActions.children.length === 0;
}

function applyActiveView() {
  const viewId = activeViewId();
  views.forEach((view) => view.classList.toggle("active", view.id === viewId));
  navItems.forEach((item) => item.classList.toggle("active", item.dataset.view === viewId));
  const activeNav = document.querySelector(`[data-view="${viewId}"]`);
  if (activeNav) pageTitle.textContent = navItemLabelText(activeNav);
  if (pageSubtitle) {
    const subtitle = sectionSubtitleText(viewId);
    pageSubtitle.textContent = subtitle;
    pageSubtitle.hidden = !subtitle;
  }
  syncPageHeaderContextActions();
  updateTopbarSectionFilter(viewId);
  scheduleActiveTopbarNavReveal({ behavior: isBootstrapping ? "auto" : "smooth" });
}

async function switchView(viewId, options = {}) {
  if (editMode) return;
  if (!validViewId(viewId)) return;
  closeTopbarMobileMenu();
  closeActionMenu();
  closeFilterMenus();
  state.activeView = viewId;
  saveState();
  syncBrowserHistory(viewId, { replace: Boolean(options.replace) });
  await ensureSectionDataLoaded(viewId);
  await ensureSectionModuleLoaded(viewId);
  render();
}

/* =====================================================
   RENDERIZACAO GERAL E DASHBOARD
   Orquestra render principal, metricas, heatmap e blocos editaveis.
===================================================== */
function isNavigationAction(action) {
  return [
    "open-study-subject",
    "close-study-subject",
    "open-study-cycle",
    "close-study-cycle",
    "select-error-subject",
    "close-error-subject",
    "open-error-content",
    "close-error-content",
    "toggle-error-open",
  ].includes(action);
}

function safeRenderSignature(value) {
  try {
    return JSON.stringify(value);
  } catch (err) {
    console.error("[safeRenderSignature]", err);
    return `${Date.now()}`;
  }
}

function dataDomainSources() {
  return {
    planner: () => [state.planner, state.plannerTimeEnabled],
    questions: () => [state.questions],
    errors: () => [state.errors],
    essays: () => [state.essays],
    mocks: () => [state.mocks],
    subjects: () => [
      state.customSubjects,
      state.hiddenSubjects,
      state.subjectNotes,
      state.subjectCovers,
      state.subjectContents,
      state.subjectCycles,
      state.cycleLessons,
      state.cycleContentOrder,
      state.collapsedLessonGroups,
    ],
    pendingLessons: () => [state.pendingLessons],
    studySessions: () => [state.studySessions],
    timer: () => [state.timerSubjects, state.hiddenTimerSubjects, state.activeStudyTimer],
    focus: () => [state.focusTodos, state.pendingLessons, focusScreenOpen, focusStudyPanelCollapsed],
    profile: () => [state.userProfile, state.keepConnected],
    ui: () => [
      state.weeklyGoal,
      state.studyWeeklyGoalHours,
      state.customText,
      state.dashboardOrder,
      state.dashboardBlockSizes,
      state.collapsedSections,
    ],
  };
}

function domainVersionValue(domain = "") {
  return domainVersions[domain] || 0;
}

function domainVersionSignature(...domains) {
  return domains.map((domain) => `${domain}:${domainVersionValue(domain)}`).join("|");
}

function sectionsDependingOnDomain(domain = "") {
  return Object.entries(sectionRenderDomains)
    .filter(([, domains]) => domains.includes(domain))
    .map(([viewId]) => viewId);
}

function chartSectionsDependingOnDomain(domain = "") {
  return Object.entries(sectionChartDomains)
    .filter(([, domains]) => domains.includes(domain))
    .map(([viewId]) => viewId);
}

function invalidateCachesForDomains(domains = []) {
  const renderViews = new Set();
  const chartViews = new Set();
  domains.forEach((domain) => {
    sectionsDependingOnDomain(domain).forEach((viewId) => renderViews.add(viewId));
    chartSectionsDependingOnDomain(domain).forEach((viewId) => chartViews.add(viewId));
  });
  renderViews.forEach((viewId) => sectionRenderCache.delete(viewId));
  chartViews.forEach((viewId) => sectionChartCache.delete(viewId));
}

function refreshDataDomainVersions(options = {}) {
  const force = Boolean(options.force);
  const sources = dataDomainSources();
  const changedDomains = [];
  Object.entries(sources).forEach(([domain, source]) => {
    const nextSnapshot = safeRenderSignature(source());
    const previousSnapshot = domainVersionSnapshots.get(domain);
    if (force || previousSnapshot === undefined || previousSnapshot !== nextSnapshot) {
      domainVersions[domain] = domainVersionValue(domain) + 1;
      domainVersionSnapshots.set(domain, nextSnapshot);
      changedDomains.push(domain);
    }
  });
  if (changedDomains.length) invalidateCachesForDomains(changedDomains);
  return changedDomains;
}

function resetDataDomainVersions() {
  domainVersionKeys.forEach((domain) => {
    domainVersions[domain] = domainVersionValue(domain) + 1;
    domainVersionSnapshots.delete(domain);
  });
  invalidateSectionCache();
}

function sectionRenderSignature(viewId = activeViewId()) {
  const signatures = {
    dashboard: () => [
      domainVersionSignature(...sectionRenderDomains.dashboard),
      filters.dashboardSubject,
    ],
    materias: () => [
      domainVersionSignature(...sectionRenderDomains.materias),
      selectedStudySubject,
      selectedStudyCycle,
    ],
    tempo: () => [
      domainVersionSignature(...sectionRenderDomains.tempo),
      selectedStudyTimelineDate,
      focusScreenOpen,
      focusStudyPanelCollapsed,
    ],
    planner: () => [
      domainVersionSignature(...sectionRenderDomains.planner),
      filters.planner,
      calendarDate.toISOString(),
      plannerMode,
      plannerActiveView,
      plannerBuilderOpen,
      plannerBuilderType,
      plannerBuilderDate,
      plannerBuilderTime,
      plannerSelectedMaterialRefs,
      plannerSelectedQuestionRefs,
      plannerSelectedTaskIds,
    ],
    questoes: () => [
      domainVersionSignature(...sectionRenderDomains.questoes),
      filters.questions,
      filters.questionsDone,
      questionSelectedRowIds,
      activeQuestionGroupViewId,
      questionGroupViewSearch,
    ],
    erros: () => [
      domainVersionSignature(...sectionRenderDomains.erros),
      filters.errors,
      selectedErrorSubject,
      selectedErrorContent,
    ],
    simulados: () => [
      domainVersionSignature(...sectionRenderDomains.simulados),
      filters.mocks,
    ],
    redacoes: () => [
      domainVersionSignature(...sectionRenderDomains.redacoes),
      filters.essays,
    ],
  };
  return safeRenderSignature((signatures[viewId] || signatures.dashboard)());
}

function chartSignatureForSection(viewId = activeViewId()) {
  const signatures = {
    dashboard: () => [filters.dashboardSubject, document.querySelector("#chartMode")?.value || "", domainVersionSignature(...sectionChartDomains.dashboard)],
    redacoes: () => [filters.essays, domainVersionSignature(...sectionChartDomains.redacoes)],
  };
  const signature = signatures[viewId]?.();
  return signature ? safeRenderSignature(signature) : "";
}

function invalidateSectionCache(viewId = "") {
  if (!viewId) {
    sectionRenderCache.clear();
    sectionChartCache.clear();
    return;
  }
  sectionRenderCache.delete(viewId);
  sectionChartCache.delete(viewId);
}

function renderSectionFromModule(viewId = activeViewId()) {
  const module = loadedSectionModules.get(viewId);
  if (typeof module?.render !== "function") return false;
  module.render(sectionModuleContext(viewId));
  return true;
}

function renderSection(viewId = activeViewId(), options = {}) {
  refreshDataDomainVersions();
  preloadSectionData(viewId);
  preloadSectionModule(viewId);
  if (shouldWaitForSectionModule(viewId)) {
    scheduleSectionRenderAfterModuleLoad(viewId);
    return false;
  }
  runSectionModuleHook(loadedSectionModules.get(viewId), "beforeRender", viewId);
  const signature = sectionRenderSignature(viewId);
  if (!options.force && sectionRenderCache.get(viewId) === signature) return false;

  if (!renderSectionFromModule(viewId)) {
    console.warn(`Renderizador modular indisponivel para a secao ${viewId}.`);
    return false;
  }

  sectionRenderCache.set(viewId, sectionRenderSignature(viewId));
  return true;
}

function viewForEditableRecord(collection = "") {
  const viewsByCollection = {
    planner: "planner",
    questions: "questoes",
    errors: "erros",
    mocks: "simulados",
    essays: "redacoes",
    studySessions: "tempo",
  };
  return viewsByCollection[collection] || activeViewId();
}

function renderSectionUi(viewId = activeViewId()) {
  applyCustomText();
  applyCollapsedSections();
  applyActiveView();
  updateEditModeUI();
  applySmartFieldMemory();
  syncPlannerMaterialFormSelectors();
  syncSectionChrome();
  runSectionModuleHook(loadedSectionModules.get(viewId), "afterRender", viewId);
  drawChartsForSection(viewId);
}

function renderSectionNow(viewId = activeViewId(), options = {}) {
  const targetViewId = validViewId(viewId) ? viewId : activeViewId();
  if (options.save) saveState();
  if (options.commit) commitHistory();
  if (activeViewId() !== "dashboard") renderMetrics({ includeDashboard: false });
  if (targetViewId !== activeViewId()) {
    renderSection(activeViewId());
    renderSectionUi(activeViewId());
    return;
  }
  renderSection(targetViewId, { force: Boolean(options.force) });
  renderSectionUi(targetViewId);
}

function prepareRenderState() {
  syncEssayScores();
  normalizeQuestionGroups();
  refreshDataDomainVersions();
  saveState();
  commitHistory();
  renderContentOptions();
  renderFocusController();
  renderFocusScreen();
  applyUserProfile();
  syncKeepConnectedInputs();
  updateFirstLoginModal();
}

function render() {
  prepareRenderState();
  if (activeViewId() !== "dashboard") renderMetrics({ includeDashboard: false });
  renderSection(activeViewId());
  renderSectionUi(activeViewId());
  renderBackupStatus();
}

function syncSectionChrome() {
  const questionsHead = document.querySelector("#questoes .panel-head > div");
  if (questionsHead) {
    const title = questionsHead.querySelector('[data-edit-key="questionsTitle"]');
    if (title) title.textContent = "Questões";
    const subtitle = questionsHead.querySelector('[data-edit-key="questionsSubtitle"]');
    if (subtitle) subtitle.classList.add("subjects-home-subtitle");
    if (!questionsHead.querySelector('[data-edit-key="questionsEyebrow"]')) {
      const eyebrow = document.createElement("p");
      eyebrow.className = "eyebrow";
      eyebrow.dataset.editKey = "questionsEyebrow";
      eyebrow.textContent = "Organização total";
      questionsHead.prepend(eyebrow);
    }
  }
}

function applyCollapsedSections() {
  document.querySelectorAll("[data-section-key]").forEach((section) => {
    const key = section.dataset.sectionKey;
    const collapsed = Boolean(state.collapsedSections?.[key]);
    section.classList.toggle("collapsed", collapsed);
    const button = section.querySelector("[data-action='toggle-section']");
    const caret = section.querySelector(".collapse-caret");
    if (button) button.setAttribute("aria-expanded", String(!collapsed));
    if (caret) caret.textContent = collapsed ? "›" : "v";
  });
}

function toggleSection(key) {
  state.collapsedSections ||= {};
  touchScalar("collapsedSections", {
    ...state.collapsedSections,
    [key]: !state.collapsedSections[key],
  });
}

function applyCustomText() {
  document.querySelectorAll("[data-edit-key]").forEach((element) => {
    const value = state.customText[element.dataset.editKey];
    if (value !== undefined) {
      const key = element.dataset.editKey || "";
      const cleaned = /^(studySubjectCard:|studySubjectEyebrow:|studySubjectTitle:|cycleEyebrow:|errorSubjectCard:|errorSubjectEyebrow:|errorSubjectTitle:|errorContentCard:|errorContentTitle:)/.test(key)
        ? String(value).replace(/^[^\p{L}\p{N}]+/u, "").trim()
        : value;
      element.textContent = cleaned;
    }
  });

  navItems.forEach((item) => {
    const key = `nav:${item.dataset.view}`;
    const label = item.querySelector(".nav-item-label");
    if (state.customText[key] && label) label.textContent = state.customText[key];
  });
  restoreMetricLabelIcons();
}

const metricLabelIcons = {
  metricQuestionsLabel: "assets/metric-questions.png",
  metricEssaysLabel: "assets/metric-essays.png",
  metricErrorsLabel: "assets/metric-errors.png",
  metricGoalLabel: "assets/metric-goal.png",
  metricStudyHoursLabel: "assets/metric-study-hours.png",
  metricFlashcardsLabel: "assets/metric-flashcards.png",
};

function restoreMetricLabelIcons() {
  Object.entries(metricLabelIcons).forEach(([key, src]) => {
    const element = document.querySelector(`[data-edit-key="${key}"]`);
    if (!element) return;
    const label = element.textContent.trim();
    element.innerHTML = `<img class="metric-label-icon" src="${src}" alt="" />${escapeHtml(label)}`;
  });
}

function editableGridConfigs() {
  return [
    {
      selector: ".metrics-grid",
      filter: (id) => String(id || "").startsWith("metric-"),
    },
    {
      selector: ".dashboard-grid",
      filter: (id) => !String(id || "").startsWith("metric-"),
    },
  ];
}

function applyDashboardOrder(tabId) {
  const order = Array.isArray(state.dashboardOrder) ? migrateDashboardOrder() : state.dashboardOrder;
  const tabs = tabId ? [tabId] : Object.keys(order);
  tabs.forEach((id) => {
    const ids = order[id] || [];
    editableGridConfigs().forEach(({ selector, filter }) => {
      const container = document.querySelector(`[data-dashboard-tab-content="${id}"]`);
      const grid = container?.querySelector(selector);
      if (grid) applyOrder(grid, ids.filter(filter));
    });
  });
}

function migrateDashboardOrder() {
  const old = state.dashboardOrder;
  if (!Array.isArray(old)) return old;
  state.dashboardOrder = {
    resumo: old.filter((id) => id.startsWith("metric-") && id !== "metric-goal" && id !== "metric-study-hours"),
    materias: old.filter((id) => id === "subjects" || id === "chart"),
    progresso: old.filter((id) => id === "heatmap" || id === "tasks"),
    metas: ["metric-goal", "metric-study-hours"],
  };
  return state.dashboardOrder;
}

const DASHBOARD_BLOCK_SPAN_LIMITS = {
  metric: { min: 1, max: DASHBOARD_GRID_COLUMNS, fallback: 2 },
  panel: { min: 1, max: DASHBOARD_GRID_COLUMNS, fallback: DASHBOARD_GRID_COLUMNS },
};

function dashboardBlockType(blockId = "") {
  return String(blockId).startsWith("metric-") ? "metric" : "panel";
}

function defaultDashboardBlockSpan(blockId = "") {
  const defaults = {
    chart: DASHBOARD_GRID_COLUMNS,
    subjects: DASHBOARD_GRID_COLUMNS,
    tasks: DASHBOARD_GRID_COLUMNS,
    heatmap: DASHBOARD_GRID_COLUMNS,

  };
  if (defaults[blockId]) return defaults[blockId];
  return DASHBOARD_BLOCK_SPAN_LIMITS[dashboardBlockType(blockId)]?.fallback || 6;
}

function dashboardBlockSpanLimits(blockId = "") {
  return DASHBOARD_BLOCK_SPAN_LIMITS[dashboardBlockType(blockId)] || DASHBOARD_BLOCK_SPAN_LIMITS.panel;
}

function legacyDashboardBlockSpan(blockId = "", legacySize = "") {
  const blockType = dashboardBlockType(blockId);
  const presets = {
    metric: { compact: 2, default: 3, wide: 4 },
    panel: { narrow: 4, default: 6, wide: 8, full: 12 },
  };
  return presets[blockType]?.[legacySize] ?? null;
}

function normalizeDashboardBlockSpan(blockId = "", spanValue = null) {
  const limits = dashboardBlockSpanLimits(blockId);
  const fallback = defaultDashboardBlockSpan(blockId);
  const numericValue =
    typeof spanValue === "string" && Number.isNaN(Number(spanValue))
      ? legacyDashboardBlockSpan(blockId, spanValue)
      : number(spanValue);
  const resolved = numericValue || fallback;
  return Math.min(limits.max, Math.max(limits.min, Math.round(resolved)));
}

function dashboardBlockHeightLimits(blockId = "") {
  return dashboardBlockType(blockId) === "metric"
    ? { min: 96, max: 320 }
    : { min: DASHBOARD_BLOCK_MIN_HEIGHT, max: DASHBOARD_BLOCK_MAX_HEIGHT };
}

function normalizeDashboardBlockHeight(blockId = "", heightValue = null) {
  const value = Math.round(number(heightValue) || 0);
  if (!value) return 0;
  const limits = dashboardBlockHeightLimits(blockId);
  return Math.min(limits.max, Math.max(limits.min, value));
}

function normalizeDashboardBlockSize(blockId = "", value = null) {
  const stored = value && typeof value === "object" ? value : { span: value };
  return {
    span: normalizeDashboardBlockSpan(blockId, stored.span),
    height: normalizeDashboardBlockHeight(blockId, stored.height),
  };
}

function getDashboardBlockSize(blockId = "") {
  state.dashboardBlockSizes ||= {};
  return normalizeDashboardBlockSize(blockId, state.dashboardBlockSizes[blockId]);
}

function getDashboardBlockSpan(blockId = "") {
  return getDashboardBlockSize(blockId).span;
}

function setDashboardBlockSize(blockId = "", nextSize = {}, persist = false) {
  if (!blockId) return;
  touchScalar("dashboardBlockSizes", {
    ...(state.dashboardBlockSizes || {}),
    [blockId]: normalizeDashboardBlockSize(blockId, nextSize),
  });
  if (persist) saveState();
}

function applyDashboardBlockSizes() {
  document.querySelectorAll(".dashboard-tab-content").forEach((container) => {
    container.querySelectorAll("[data-edit-block]").forEach((element) => {
      const blockId = element.dataset.editBlock || "";
      const size = getDashboardBlockSize(blockId);
      element.dataset.dashboardSpan = String(size.span);
      element.style.gridColumn = `span ${size.span}`;
      if (size.height) element.style.minHeight = `${size.height}px`;
      else element.style.removeProperty("min-height");
    });
  });
}

function syncDashboardResizeControls() {
  document.querySelectorAll(".dashboard-size-presets").forEach((element) => element.remove());
  document.querySelectorAll(".dashboard-span-resizer").forEach((button) => button.remove());
  if (!editMode) return;
  document.querySelectorAll("#dashboard .dashboard-tab-content").forEach((container) => {
    container.querySelectorAll("[data-edit-block]").forEach((element) => {
      const blockId = element.dataset.editBlock || "";
      const limits = dashboardBlockSpanLimits(blockId);
      const handle = document.createElement("button");
      handle.type = "button";
      handle.className = "dashboard-span-resizer";
      handle.dataset.block = blockId;
      handle.dataset.min = String(limits.min);
      handle.dataset.max = String(limits.max);
      handle.setAttribute("aria-label", `Redimensionar bloco (${limits.min}-${limits.max})`);
      handle.title = "Arraste para redimensionar";
      element.appendChild(handle);
    });
  });
}

function applyOrder(selector, order) {
  const container = typeof selector === "string" ? document.querySelector(selector) : selector;
  if (!container || !order.length) return;
  order.forEach((id) => {
    const block = container.querySelector(`[data-edit-block="${id}"]`);
    if (block) container.appendChild(block);
  });
}

function saveDashboardOrder() {
  const order = {};
  document.querySelectorAll(".dashboard-tab-content").forEach((container) => {
    const tabId = container.dataset.dashboardTabContent;
    if (tabId) order[tabId] = [...container.querySelectorAll("[data-edit-block]")].map((el) => el.dataset.editBlock);
  });
  state.dashboardOrder = order;
}

function currentCustomTextFromDom() {
  const customText = { ...(state.customText || {}) };
  document.querySelectorAll("[data-edit-key]").forEach((element) => {
    customText[element.dataset.editKey] = element.textContent.trim();
  });
  navItems.forEach((item) => {
    customText[`nav:${item.dataset.view}`] = navItemLabelText(item);
  });
  return customText;
}

function currentDashboardOrderFromDom() {
  const order = {};
  document.querySelectorAll(".dashboard-tab-content").forEach((container) => {
    const tabId = container.dataset.dashboardTabContent;
    if (tabId) order[tabId] = [...container.querySelectorAll("[data-edit-block]")].map((el) => el.dataset.editBlock);
  });
  return order;
}

function currentLayoutEditState({ commit = false } = {}) {
  const next = {
    customText: currentCustomTextFromDom(),
    dashboardOrder: currentDashboardOrderFromDom(),
    dashboardBlockSizes: state.dashboardBlockSizes || {},
  };
  if (commit) {
    state.customText = JSON.parse(JSON.stringify(next.customText));
    state.dashboardOrder = JSON.parse(JSON.stringify(next.dashboardOrder));
  }
  return JSON.parse(JSON.stringify(next));
}

function hasLayoutEditChanges() {
  if (!layoutEditSnapshot) return false;
  return JSON.stringify(currentLayoutEditState()) !== JSON.stringify(layoutEditSnapshot);
}

function restoreLayoutEditSnapshot() {
  if (!layoutEditSnapshot) return;
  state.customText = JSON.parse(JSON.stringify(layoutEditSnapshot.customText || {}));
  state.dashboardOrder = JSON.parse(JSON.stringify(layoutEditSnapshot.dashboardOrder || {}));
  state.dashboardBlockSizes = JSON.parse(JSON.stringify(layoutEditSnapshot.dashboardBlockSizes || {}));
}

function enterLayoutEditMode() {
  layoutEditSnapshot = currentLayoutEditState();
  editMode = true;
  updateEditModeUI();
}

function saveLayoutEdits() {
  currentLayoutEditState({ commit: true });
  saveState();
  commitHistory();
  layoutEditSnapshot = null;
  editMode = false;
  updateEditModeUI();
  closeLayoutEditExitModal();
  renderSectionNow(activeViewId());
}

function discardLayoutEdits() {
  restoreLayoutEditSnapshot();
  layoutEditSnapshot = null;
  editMode = false;
  closeLayoutEditExitModal();
  saveState();
  renderSectionNow(activeViewId(), { force: true });
}

function requestExitLayoutEditMode() {
  if (!hasLayoutEditChanges()) {
    layoutEditSnapshot = null;
    editMode = false;
    updateEditModeUI();
    renderSectionNow(activeViewId());
    return;
  }
  openLayoutEditExitModal();
}

function layoutEditExitModalElement() {
  let modal = document.querySelector("#layoutEditExitModal");
  if (modal) return modal;
  modal = document.createElement("div");
  modal.id = "layoutEditExitModal";
  modal.className = "image-editor-modal question-delete-modal hidden";
  modal.setAttribute("aria-hidden", "true");
  modal.innerHTML = `
    <div class="modal timer-subject-dialog question-delete-dialog">
      <div class="modal-content timer-subject-content question-delete-content layout-edit-exit-content">
        <div class="planner-builder-head">
          <div>
            <strong>Alterações de layout</strong>
            <small>Salvar ou descartar antes de sair</small>
          </div>
          <button class="icon-button" data-action="close-layout-edit-exit-modal" type="button" aria-label="Fechar">×</button>
        </div>
        <p>Você alterou o layout. Deseja salvar essas alterações ou descartar tudo que mudou nesta edição?</p>
        <div class="question-modal-actions">
          <button class="danger-button" data-action="discard-layout-edit" type="button">Descartar</button>
          <button class="success-button" data-action="save-layout-edit" type="button">Salvar alterações</button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  return modal;
}

function openLayoutEditExitModal() {
  const modal = layoutEditExitModalElement();
  closeAllOverlays();
  modal.classList.remove("hidden");
  modal.setAttribute("aria-hidden", "false");
  syncBodyScrollLock();
  setTimeout(() => modal.querySelector('[data-action="save-layout-edit"]')?.focus(), 0);
}

function closeLayoutEditExitModal() {
  const modal = document.querySelector("#layoutEditExitModal");
  if (!modal) return;
  modal.classList.add("hidden");
  modal.setAttribute("aria-hidden", "true");
  syncBodyScrollLock();
}

function syncDisconnectedModalElement() {
  let modal = document.querySelector("#syncDisconnectedModal");
  if (modal) return modal;
  modal = document.createElement("div");
  modal.id = "syncDisconnectedModal";
  modal.className = "image-editor-modal question-delete-modal hidden sync-decision-modal";
  modal.setAttribute("aria-hidden", "true");
  modal.innerHTML = `
    <div class="modal timer-subject-dialog question-delete-dialog" role="dialog" aria-modal="true" aria-labelledby="syncDisconnectedModalTitle">
      <div class="modal-content timer-subject-content question-delete-content sync-decision-content">
        <div class="planner-builder-head">
          <div>
            <strong id="syncDisconnectedModalTitle">Sem conexão com a nuvem</strong>
            <small>Alterações não serão salvas entre dispositivos</small>
          </div>
          <button class="icon-button" data-action="continue-sync-offline" type="button" aria-label="Fechar">×</button>
        </div>
        <p>Dados só ficam seguros quando salvos na nuvem. Offline, as alterações ficam apenas neste navegador e podem ser perdidas.</p>
        <div class="question-modal-actions">
          <button class="ghost-button" data-action="continue-sync-offline" type="button">Continuar offline</button>
          <button class="ghost-button" data-action="export-sync-backup" type="button">Exportar backup</button>
          <button class="success-button" data-action="focus-sync-login" type="button">Reconectar</button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  return modal;
}

function openSyncDisconnectedModal() {
  if (syncDisconnectedPromptShown) return;
  syncDisconnectedPromptShown = true;
  const modal = syncDisconnectedModalElement();
  syncDisconnectedPreviousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  // except: evita que closeAllOverlays() chame closeSyncDisconnectedModal() e
  // consuma o foco salvo acima antes do modal ser efetivamente aberto.
  closeAllOverlays({ except: ["syncDisconnectedModal"] });
  modal.classList.remove("hidden");
  modal.setAttribute("aria-hidden", "false");
  syncBodyScrollLock();
  setTimeout(() => modal.querySelector('[data-action="focus-sync-login"]')?.focus(), 0);
}

function closeSyncDisconnectedModal() {
  const modal = document.querySelector("#syncDisconnectedModal");
  if (!modal) return;
  modal.classList.add("hidden");
  modal.setAttribute("aria-hidden", "true");
  syncBodyScrollLock();
  // Devolve o foco para o elemento que abriu o modal. Sem isso, o foco ficaria
  // preso num botao dentro de um ancestral aria-hidden -> warning do navegador
  // e quebra de navegacao por teclado. Mesmo padrao do openSyncConflictModal.
  const previousFocus = syncDisconnectedPreviousFocus;
  syncDisconnectedPreviousFocus = null;
  if (previousFocus && previousFocus.isConnected && typeof previousFocus.focus === "function") {
    previousFocus.focus();
  }
}

function syncConflictModalElement() {
  let modal = document.querySelector("#syncConflictModal");
  if (modal) return modal;
  modal = document.createElement("div");
  modal.id = "syncConflictModal";
  modal.className = "image-editor-modal question-delete-modal hidden sync-decision-modal sync-conflict-modal";
  modal.setAttribute("aria-hidden", "true");

  const dialog = document.createElement("div");
  dialog.className = "modal timer-subject-dialog question-delete-dialog";
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");
  dialog.setAttribute("aria-labelledby", "syncConflictModalTitle");

  const content = document.createElement("div");
  content.className = "modal-content timer-subject-content question-delete-content sync-decision-content";

  const header = document.createElement("div");
  header.className = "planner-builder-head";
  const titleWrap = document.createElement("div");
  const title = document.createElement("strong");
  title.id = "syncConflictModalTitle";
  title.textContent = "Conflitos de sincronização";
  const subtitle = document.createElement("small");
  subtitle.textContent = "Escolha como resolver as diferenças entre este dispositivo e a nuvem";
  titleWrap.append(title, subtitle);
  const closeButton = document.createElement("button");
  closeButton.className = "icon-button";
  closeButton.type = "button";
  closeButton.dataset.action = "close-sync-conflict-modal";
  closeButton.setAttribute("aria-label", "Fechar");
  closeButton.textContent = "×";
  header.append(titleWrap, closeButton);

  const intro = document.createElement("p");
  intro.textContent = "Alguns dados mudaram nos dois lugares. O app já manteve uma versão mesclada para evitar perda silenciosa.";

  const list = document.createElement("ul");
  list.dataset.conflictBreakdown = "true";

  const actions = document.createElement("div");
  actions.className = "question-modal-actions sync-conflict-actions";
  [
    ["local", "Manter local", "ghost-button"],
    ["remote", "Manter nuvem", "ghost-button"],
    ["merge", "Mesclar", "success-button"],
  ].forEach(([choice, label, className]) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = className;
    button.dataset.choice = choice;
    button.textContent = label;
    actions.appendChild(button);
  });

  content.append(header, intro, list, actions);
  dialog.appendChild(content);
  modal.appendChild(dialog);
  document.body.appendChild(modal);
  return modal;
}

function conflictDomainBreakdown(conflicts = []) {
  return conflicts.reduce((acc, conflict) => {
    const domain = conflict?.domain || "state";
    acc[domain] = (acc[domain] || 0) + 1;
    return acc;
  }, {});
}

function openSyncConflictModal({ localState, cloudState, profile, source, conflicts = [] } = {}) {
  return new Promise((resolve) => {
    try {
      const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const modal = syncConflictModalElement();
      const list = modal.querySelector("[data-conflict-breakdown]");
      if (list) {
        list.textContent = "";
        const breakdown = conflictDomainBreakdown(conflicts);
        Object.entries(breakdown).forEach(([domain, count]) => {
          const item = document.createElement("li");
          item.textContent = `${domain}: ${count} ${count === 1 ? "item em conflito" : "itens em conflito"}`;
          list.appendChild(item);
        });
      }

      const restoreFocus = () => {
        if (previousFocus && typeof previousFocus.focus === "function") previousFocus.focus();
      };

      const dismiss = () => {
        closeSyncConflictModal();
        restoreFocus();
        resolve(null);
      };

      const finish = (choice) => {
        closeSyncConflictModal({ resolved: true });
        restoreFocus();
        resolve(choice);
      };

      const focusablesInModal = () => Array.from(
        modal.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => element.offsetParent !== null);

      modal.onclick = (event) => {
        const choiceButton = event.target.closest("[data-choice]");
        if (choiceButton) {
          finish(choiceButton.dataset.choice || "merge");
          return;
        }
        if (event.target.closest('[data-action="close-sync-conflict-modal"]')) {
          dismiss();
        }
      };
      modal.onkeydown = (event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          dismiss();
          return;
        }
        if (event.key === "Tab") {
          const focusables = focusablesInModal();
          if (focusables.length === 0) return;
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      };
      modal.dataset.source = source || "sync";
      modal.dataset.profile = profile?.login || profile?.displayName || "";
      modal.dataset.localHasData = String(stateHasUserData(localState));
      modal.dataset.cloudHasData = String(stateHasUserData(cloudState));
      closeAllOverlays();
      modal.classList.remove("hidden");
      modal.setAttribute("aria-hidden", "false");
      syncBodyScrollLock();
      setTimeout(() => modal.querySelector('[data-choice="local"]')?.focus(), 0);
    } catch (err) {
      console.error("[openSyncConflictModal]", err);
      resolve(null);
    }
  });
}

function closeSyncConflictModal({ resolved = false } = {}) {
  const modal = document.querySelector("#syncConflictModal");
  if (!modal) return;
  modal.classList.add("hidden");
  modal.setAttribute("aria-hidden", "true");
  syncBodyScrollLock();
  if (resolved) conflictPending = false;
}

async function applySyncConflictChoice(choice, { cloudState, localState, profile } = {}) {
  if (!choice) return cloneStudyState(state);
  let resultingState;
  if (choice === "local") {
    resultingState = {
      ...cloneStudyState(localState || state),
      userProfile: profile || localState?.userProfile || state.userProfile,
    };
    await replaceStateSafely(resultingState, "merge");
  } else if (choice === "remote") {
    resultingState = {
      ...cloneStudyState(cloudState || state),
      userProfile: profile || cloudState?.userProfile || state.userProfile,
    };
    await replaceStateSafely(resultingState, "cloud-to-local");
  } else if (choice === "merge") {
    const mergeResult = window.syncMerge.mergeStates(localState || state, cloudState || {}, {
      mergeStampsLocal: localState?.mergeStamps,
      mergeStampsCloud: cloudState?.mergeStamps,
    });
    const mergedState = {
      ...mergeResult.state,
      userProfile: profile || mergeResult.state?.userProfile || state.userProfile,
    };
    resultingState = mergedState;
    await replaceStateSafely(mergedState, "merge");
  } else {
    throw new Error(`Escolha de conflito invalida: ${choice}`);
  }
  conflictPending = false;
  setPendingMergeUpload(resultingState);
  saveState({ immediate: true });
  setSyncStatus("syncing");
  return cloneStudyState(resultingState);
}

function clearDashboardDropState() {
  document.querySelectorAll("[data-edit-block].drag-over, [data-edit-block].drag-magnet-before, [data-edit-block].drag-magnet-after").forEach((element) => {
    element.classList.remove("drag-over", "drag-magnet-before", "drag-magnet-after");
  });
  document.querySelector("[data-dashboard-drop-placeholder]")?.remove();
}

function ensureDashboardDropPlaceholder(container, blockId = "") {
  let placeholder = document.querySelector("[data-dashboard-drop-placeholder]");
  if (!placeholder) {
    placeholder = document.createElement("div");
    placeholder.className = "dashboard-drop-placeholder";
    placeholder.dataset.dashboardDropPlaceholder = "true";
  }
  if (blockId) {
    const span = getDashboardBlockSpan(blockId);
    placeholder.style.gridColumn = `span ${span}`;
  }
  if (placeholder.parentElement !== container) container.appendChild(placeholder);
  return placeholder;
}

function updateDashboardDropTarget(container, draggedId, clientX, clientY) {
  if (!container || !draggedId) return;
  clearDashboardDropState();
  const dragged = container.querySelector(`[data-edit-block="${draggedId}"]`);
  if (!dragged) return;
  const placeholder = ensureDashboardDropPlaceholder(container, draggedId);
  const blocks = [...container.querySelectorAll("[data-edit-block]")].filter((element) => element !== dragged);
  if (!blocks.length) {
    container.appendChild(placeholder);
    return;
  }

  const target = blocks.reduce((best, element) => {
    const rect = element.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const distance = Math.hypot(clientX - centerX, clientY - centerY);
    if (!best || distance < best.distance) {
      return { element, rect, distance };
    }
    return best;
  }, null);
  if (!target) {
    container.appendChild(placeholder);
    return;
  }

  const before = clientX < target.rect.left + target.rect.width / 2;
  target.element.classList.add("drag-over", before ? "drag-magnet-before" : "drag-magnet-after");
  if (before) {
    container.insertBefore(placeholder, target.element);
    return;
  }
  container.insertBefore(placeholder, target.element.nextElementSibling);
}

function updateEditModeUI() {
  document.body.classList.toggle("edit-mode", editMode);
  document.querySelector("#editModeButton").textContent = editMode ? "Sair da edição" : "Editar layout";
  document.querySelector("#saveLayoutButton").classList.toggle("hidden", !editMode);

  document.querySelectorAll("[data-edit-block]").forEach((element) => {
    element.draggable = editMode;
  });
  syncDashboardResizeControls();
}

const INLINE_EDITABLE_KEY_PREFIXES = [
  "studySubjectCard:",
  "studySubjectEyebrow:",
  "studySubjectTitle:",
  "cyclePreview:",
  "cycleCard:",
  "cycleEyebrow:",
  "cycleTitle:",
  "lessonGroup:",
  "errorSubjectCard:",
  "errorSubjectEyebrow:",
  "errorSubjectTitle:",
  "errorSubjectContentTitle:",
  "errorContentCard:",
  "errorContentTitle:",
  "errorItemTitle:",
];

function isInlineEditableKey(key) {
  return INLINE_EDITABLE_KEY_PREFIXES.some((prefix) => String(key || "").startsWith(prefix));
}

function startCustomInlineEdit(element) {
  if (!element || element.dataset.editing === "true") return;
  const key = element.dataset.editKey;
  if (!isInlineEditableKey(key)) return;

  element.dataset.editing = "true";
  element.contentEditable = "true";
  element.classList.add("inline-editing");
  element.focus();
  showInlineEditToolbar(element);
  selectEditableText(element);
}

function saveCustomInlineEdit(element) {
  if (!element) return;
  const key = element.dataset.editKey;
  if (!isInlineEditableKey(key)) return;

  touchMapKey("customText", key, element.textContent.trim());
  element.dataset.editing = "false";
  element.contentEditable = "false";
  element.classList.remove("inline-editing");
  removeInlineEditToolbar(element);
  if (editMode) {
    requestAnimationFrame(() => renderSectionNow(activeViewId()));
    return;
  }
  saveState();
  requestAnimationFrame(() => renderSectionNow(activeViewId(), { commit: true }));
}

function cancelCustomInlineEdit(element) {
  if (!element) return;
  const key = element.dataset.editKey;
  if (!isInlineEditableKey(key)) return;

  element.dataset.editing = "false";
  element.contentEditable = "false";
  element.classList.remove("inline-editing");
  removeInlineEditToolbar(element);
  renderSectionNow(activeViewId());
}

function selectEditableText(element) {
  if (!element) return;
  const range = document.createRange();
  range.selectNodeContents(element);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
}

function showInlineEditToolbar(element) {
  if (!element || element.querySelector(".inline-edit-toolbar")) return;
  const toolbar = document.createElement("div");
  toolbar.className = "inline-edit-toolbar";
  toolbar.innerHTML =
    '<button type="button" class="inline-edit-save" title="Salvar (Enter)">✓</button>' +
    '<button type="button" class="inline-edit-cancel" title="Cancelar (Esc)">✗</button>';
  toolbar.querySelector(".inline-edit-save").addEventListener("click", () => {
    const el = document.querySelector("[data-editing='true']");
    if (el) saveInlineEdit(el);
  });
  toolbar.querySelector(".inline-edit-cancel").addEventListener("click", () => {
    const el = document.querySelector("[data-editing='true']");
    if (el) cancelInlineEdit(el);
  });
  document.body.appendChild(toolbar);
  const rect = element.getBoundingClientRect();
  const scrollX = window.scrollX || window.pageXOffset;
  const scrollY = window.scrollY || window.pageYOffset;
  toolbar.style.top = Math.max(4, rect.top + scrollY - toolbar.offsetHeight - 4) + "px";
  toolbar.style.left = Math.max(4, rect.left + scrollX) + "px";
}

function removeInlineEditToolbar() {
  const toolbar = document.body.querySelector(".inline-edit-toolbar");
  if (toolbar) toolbar.remove();
}

function renderProgressBar(containerEl, { current, target }) {
  if (!containerEl) return;
  const pct = Math.min(100, Math.round((current / Math.max(1, target)) * 100));
  const complete = pct >= 100;
  containerEl.innerHTML = `
    <div class="metric-progress-track">
      <span class="metric-progress-fill${complete ? ' complete' : ''}" style="width:${pct}%"></span>
    </div>
    <span class="metric-progress-label">${pct}%</span>
  `;
}

function renderMetrics(options = {}) {
  const includeDashboard = options.includeDashboard !== false;
  const filteredQuestions = dashboardQuestions();
  const filteredErrors = dashboardErrors();
  const filteredMocks = activeRecords(state.mocks || []).filter((item) => matchesSubject(item, dashboardSubjectFilter(), "Geral"));
  const filteredEssays = activeRecords(state.essays || [])
    .filter((item) => matchesSubject(item, dashboardSubjectFilter(), "Geral"))
    .filter((item) => essayScoreTotal(item) > 0);
  const todayFlashcards = plannerCountableItems(state.planner || [])
    .filter((item) => item.type === "Flash-card")
    .filter((item) => matchesSubject(item, dashboardSubjectFilter(), "Geral"))
    .filter((item) => item.date === dashboardTodayValue())
    .filter((item) => !item.done);
  const totalQuestions = filteredQuestions.reduce((sum, item) => sum + item.questions, 0);
  const totalCorrect = filteredQuestions.reduce((sum, item) => sum + item.correct, 0);
  const mockScores = filteredMocks
    .map((item) => mockAccuracyPercent(item))
    .filter((score) => score !== null && score !== undefined);
  const bestMock = mockScores.length ? Math.max(...mockScores) : 0;
  const essayAverage = filteredEssays.length
    ? Math.round(filteredEssays.reduce((sum, item) => sum + number(item.score), 0) / filteredEssays.length)
    : 0;
  const openErrors = filteredErrors.filter((item) => !item.done).length;
  const weeklyQuestions = questionsThisWeek(state.questions || []);
  const progress = Math.min(100, Math.round((weeklyQuestions / Math.max(1, number(state.weeklyGoal))) * 100));
  const weeklyStudyMinutes = studyMinutesThisWeek(activeRecords(state.studySessions || []));
  const weeklyStudyGoalMinutes = Math.max(1, number(state.studyWeeklyGoalHours) * 60);
  const studyProgress = Math.min(100, Math.round((weeklyStudyMinutes / weeklyStudyGoalMinutes) * 100));
  const todayFlashcardStats = todayFlashcards.reduce((acc, item) => {
    const counts = plannerFlashcardCounts(item);
    acc.totalCards += counts.totalCards;
    acc.reviewCards += counts.reviewCards;
    acc.newCards += counts.newCards;
    return acc;
  }, { totalCards: 0, reviewCards: 0, newCards: 0 });

  if (includeDashboard) {
    syncDashboardSubjectOptions();
    this.nextActionData = {
      metrics: { weeklyQuestions, progress, weeklyStudyMinutes, studyProgress, todayFlashcardStats, filteredErrors },
      helpers: {
        upcomingTask: dashboardUpcomingTasks(1)[0] || null,
        hasSubjects: dashboardConfiguredSubjectCount() > 0,
        todayTaskStats: dashboardTodayTasksStats(),
        formatDate,
        plannerSubjectsLabel,
        plannerCardHeading,
      },
    };

    const emptyState = document.querySelector("#dashboardEmptyState");
    const hasData = totalQuestions > 0 || filteredMocks.length > 0 || filteredEssays.length > 0 || openErrors > 0 || weeklyQuestions > 0 || weeklyStudyMinutes > 0 || todayFlashcardStats.totalCards > 0;
    document.querySelectorAll(".metrics-grid").forEach((g) => g.classList.toggle("loading", !hasData));
    if (emptyState) emptyState.classList.toggle("hidden", hasData);

    const el = (sel) => document.querySelector(sel);
    if (el("#totalQuestions")) el("#totalQuestions").textContent = totalQuestions;
    if (el("#questionAccuracy")) el("#questionAccuracy").textContent = `${pct(totalCorrect, totalQuestions)}% de acerto`;
    if (el("#totalMocks")) el("#totalMocks").textContent = filteredMocks.length;
    if (el("#bestMock")) el("#bestMock").textContent = `melhor: ${bestMock}%`;
    if (el("#totalEssays")) el("#totalEssays").textContent = filteredEssays.length;
    if (el("#essayAverage")) el("#essayAverage").textContent = `média: ${essayAverage}`;
    if (el("#openErrors")) el("#openErrors").textContent = openErrors;
    if (el("#totalMocksCompact")) el("#totalMocksCompact").textContent = filteredMocks.length;
    if (el("#totalEssaysCompact")) el("#totalEssaysCompact").textContent = filteredEssays.length;
    if (el("#bestMockCompact")) el("#bestMockCompact").textContent = bestMock;
    if (el("#essayAverageCompact")) el("#essayAverageCompact").textContent = essayAverage;
    if (el("#weeklyGoalInput")) el("#weeklyGoalInput").value = state.weeklyGoal;

const goalCard = document.querySelector("#dashboard .metric[data-edit-block=\"metric-goal\"]");
const goalWasSet = number(state.weeklyGoal) !== 300;
if (goalCard) {
goalCard.classList.toggle("goal-set", goalWasSet);
}


    if (el("#weeklyGoalMetric")) el("#weeklyGoalMetric").textContent = weeklyQuestions;
    renderProgressBar(document.getElementById("weeklyGoalProgress"), {
      current: weeklyQuestions,
      target: number(state.weeklyGoal),
    });
    if (el("#studyWeeklyGoalInput")) el("#studyWeeklyGoalInput").value = state.studyWeeklyGoalHours;
    if (el("#weeklyStudyHoursMetric")) el("#weeklyStudyHoursMetric").textContent = formatDuration(weeklyStudyMinutes);
    renderProgressBar(document.getElementById("weeklyStudyProgress"), {
      current: Math.round((weeklyStudyMinutes / 60) * 10) / 10,
      target: number(state.studyWeeklyGoalHours),
    });
    if (el("#todayFlashcardTotal")) el("#todayFlashcardTotal").textContent = todayFlashcardStats.totalCards;
    if (el("#todayFlashcardBreakdown")) el("#todayFlashcardBreakdown").textContent = todayFlashcardStats.totalCards
      ? `R: ${todayFlashcardStats.reviewCards} | N: ${todayFlashcardStats.newCards}`
      : "Sem flash-cards pendentes hoje";
  }

}

function dashboardUpcomingTasks(limit = 5) {
  const todayValue = dashboardTodayValue();
  return [...state.planner]
    .filter((item) => plannerItemVisible(item))
    .filter((item) => matchesSubject(item, dashboardSubjectFilter(), "Geral"))
    .filter((item) => taskStatus(item) !== "done")
    .filter((item) => plannerDateValue(item) >= todayValue)
    .sort((a, b) => {
      const dateCompare = plannerDateValue(a).localeCompare(plannerDateValue(b));
      if (dateCompare !== 0) return dateCompare;
      return String(a.time || "").localeCompare(String(b.time || ""));
    })
    .slice(0, limit);
}

function dashboardConfiguredSubjectCount() {
  return subjectFilterOptions().length;
}

function dashboardTodayTasksStats() {
  const todayStr = localDateValue();
  const todayTasks = plannerCountableItems(state.planner || []).filter((item) => item.date === todayStr);
  return {
    done: todayTasks.filter((item) => item.done).length,
    total: todayTasks.length,
  };
}

function questionsThisWeek(items = state.questions, referenceDate = new Date()) {
  const start = startOfReviewWeekDate(referenceDate);
  const startValue = localDateValue(start);

  return questionsForMetrics(items).reduce((sum, item) => {
    return String(item.date || "") >= startValue ? sum + item.questions : sum;
  }, 0);
}

function studyMinutesThisWeek(items = state.studySessions || [], referenceDate = new Date()) {
  const start = startOfReviewWeekDate(referenceDate);
  const startValue = localDateValue(start);

  return items.reduce((sum, item) => {
    return String(item.date || "") >= startValue ? sum + studySessionMinutes(item) : sum;
  }, 0);
}

function formatTimer(ms) {
  const totalSeconds = Math.max(0, Math.floor(number(ms) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [String(hours), String(minutes).padStart(2, "0"), String(seconds).padStart(2, "0")].join(":");
}

function studySessionMs(item) {
  if (item.durationMs) return number(item.durationMs);
  return number(item.duration) * 60000;
}

function studySessionMinutes(item) {
  return studySessionMs(item) / 60000;
}

function studyMinutesBySubject(subject) {
  return activeRecords(state.studySessions || [])
    .filter((item) => item.subject === subject)
    .reduce((sum, item) => sum + studySessionMinutes(item), 0);
}

function plannerCompletedDate(item) {
  return item.done ? item.date : "";
}

function errorReviewedDate(item) {
  return item.done ? item.date : "";
}

function getDailyMetrics(date) {
  const questionsDone = questionsForMetrics(state.questions || [])
    .filter((item) => item.date === date)
    .reduce((sum, item) => sum + Math.max(0, number(item.questions)), 0);
  const tasksCompleted = plannerCountableItems(state.planner || [])
    .filter((item) => item.done)
    .filter((item) => plannerCompletedDate(item) === date)
    .length;
  const studyMinutes = activeRecords(state.studySessions || [])
    .filter((item) => item.date === date)
    .reduce((sum, item) => sum + Math.max(0, studySessionMinutes(item)), 0);
  const flashcardItems = plannerCountableItems(state.planner || [])
    .filter((item) => item.type === "Flash-card" && item.done)
    .filter((item) => plannerCompletedDate(item) === date);
  const cardsReviewed = flashcardItems.reduce((sum, item) => sum + plannerFlashcardCounts(item).totalCards, 0);
  const reviewCards = flashcardItems.reduce((sum, item) => sum + plannerFlashcardCounts(item).reviewCards, 0);
  const newCards = flashcardItems.reduce((sum, item) => sum + plannerFlashcardCounts(item).newCards, 0);
  const errorsReviewed = activeRecords(state.errors || [])
    .filter((item) => item.done)
    .filter((item) => errorReviewedDate(item) === date)
    .length;
  const essaysWritten = activeRecords(state.essays || []).filter((item) => item.date === date).length;
  const mocksTaken = activeRecords(state.mocks || []).filter((item) => item.date === date).length;
  const reviewWorkload = cardsReviewed;

  return {
    date,
    questionsDone,
    tasksCompleted,
    studyMinutes,
    studyHours: studyMinutes / 60,
    cardsReviewed,
    reviewCards,
    newCards,
    errorsReviewed,
    essaysWritten,
    mocksTaken,
    reviewWorkload,
  };
}

function calculateDailyScore(metrics, maxScore = 0) {
  const score =
    metrics.questionsDone * 1 +
    metrics.tasksCompleted * 2 +
    metrics.studyHours * 5 +
    metrics.cardsReviewed * 0.5 +
    metrics.errorsReviewed * 1.5 +
    metrics.essaysWritten * 4 +
    metrics.mocksTaken * 6;
  if (score <= 0) return { score: 0, level: 0 };
  const normalizedMax = Math.max(maxScore || score, score);
  const ratio = normalizedMax > 0 ? score / normalizedMax : 0;
  if (ratio <= 0.25) return { score, level: 1 };
  if (ratio <= 0.5) return { score, level: 2 };
  if (ratio <= 0.75) return { score, level: 3 };
  return { score, level: 4 };
}

function startOfMonthDate(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonthDate(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function heatMapDateRange() {
  const activityDates = [
    ...questionsForMetrics(state.questions || []).map((item) => item.date),
    ...(state.studySessions || []).map((item) => item.date),
    ...(state.essays || []).map((item) => item.date),
    ...(state.mocks || []).map((item) => item.date),
    ...(state.errors || []).map((item) => errorReviewedDate(item)).filter(Boolean),
    ...(state.planner || []).map((item) => plannerCompletedDate(item)).filter(Boolean),
  ]
    .filter(Boolean)
    .sort();
  const start = activityDates.length
    ? startOfMonthDate(localDateFromValue(activityDates[0]))
    : startOfMonthDate(reviewDayDate());
  const end = endOfMonthDate(new Date());
  return { start, end };
}

function heatMapMonthData(start, end) {
  const months = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const monthStart = startOfMonthDate(cursor);
    const monthEnd = endOfMonthDate(cursor);
    const monthDays = [];
    const dayCursor = new Date(monthStart);
    while (dayCursor <= monthEnd) {
      monthDays.push(localDateValue(dayCursor));
      dayCursor.setDate(dayCursor.getDate() + 1);
    }
    const firstWeekday = mondayIndex(monthStart);
    const weekCount = Math.ceil((firstWeekday + monthDays.length) / 7);
    months.push({
      label: monthStart.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" }),
      firstWeekday,
      weekCount,
      dates: monthDays,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

function mondayIndex(date) {
  return (date.getDay() + 6) % 7;
}

function heatMapTooltipText(day) {
  const minutes = Math.round(day.metrics.studyMinutes);
  const hoursText = minutes ? formatDuration(minutes) : "0min";
  return [
    formatDate(day.date),
    `Questões: ${day.metrics.questionsDone}`,
    `Tarefas concluídas: ${day.metrics.tasksCompleted}`,
    `Horas estudadas: ${hoursText}`,
    `Cards/revisões: ${day.metrics.reviewWorkload}`,
    `Pontuação total: ${day.score.toFixed(1)}`,
  ].join("\n");
}

function heatMapStats(days) {
  const activeDays = days.filter((day) => day.score > 0);
  const totalScore = days.reduce((sum, day) => sum + day.score, 0);
  const average = activeDays.length ? totalScore / activeDays.length : 0;
  const longestStreak = longestActiveStreak(days);
  const currentStreak = currentActiveStreak(days);
  return {
    average,
    studiedDays: activeDays.length,
    longestStreak,
    currentStreak,
  };
}

function longestActiveStreak(days) {
  let best = 0;
  let current = 0;
  days.forEach((day) => {
    if (day.score > 0) {
      current += 1;
      best = Math.max(best, current);
      return;
    }
    current = 0;
  });
  return best;
}

function currentActiveStreak(days) {
  const today = timerOperationalValue();
  const relevantDays = days.filter((day) => day.date <= today);
  let current = 0;
  for (let index = relevantDays.length - 1; index >= 0; index -= 1) {
    if (relevantDays[index].score <= 0) break;
    current += 1;
  }
  return current;
}

function heatMapSnapshot() {
  const { start, end } = heatMapDateRange();
  const months = heatMapMonthData(start, end);
  const dates = months.flatMap((month) => month.dates);
  const todayValue = timerOperationalValue();
  const metricsByDate = dates.map((date) => ({ date, metrics: getDailyMetrics(date) }));
  const maxScore = metricsByDate.reduce((best, entry) => {
    if (entry.date > todayValue) return best;
    const rawScore =
      entry.metrics.questionsDone * 1 +
      entry.metrics.tasksCompleted * 2 +
      entry.metrics.studyHours * 5 +
      entry.metrics.cardsReviewed * 0.2 +
      entry.metrics.errorsReviewed * 1.5 +
      entry.metrics.essaysWritten * 4 +
      entry.metrics.mocksTaken * 6;
    return Math.max(best, rawScore);
  }, 0);
  const days = metricsByDate.map((entry) => {
    const future = entry.date > todayValue;
    const result = future ? { score: 0, level: 0 } : calculateDailyScore(entry.metrics, maxScore);
    return { ...entry, future, score: result.score, level: result.level };
  });
  const dayMap = new Map(days.map((day) => [day.date, day]));
  const stats = heatMapStats(days.filter((day) => !day.future));
  return { months, days, dayMap, stats };
}

function renderHeatMap() {
  const container = document.querySelector("#dashboardHeatMap");
  const { months, dayMap, stats } = heatMapSnapshot();
  if (!container) return;

  container.innerHTML = `
    <div class="heatmap-shell">
      <div class="heatmap-weekdays">
        ${["S", "T", "Q", "Q", "S", "S", "D"].map((label) => `<span>${label}</span>`).join("")}
      </div>
      <div class="heatmap-months">
        ${months.map((month) => {
          const weekCount = month.weekCount || 6;
          const blanks = Array.from({ length: month.firstWeekday }, () => `<span class="heatmap-cell empty"></span>`).join("");
          const cells = month.dates.map((date) => {
            const day = dayMap.get(date);
            const futureClass = day.future ? " future" : "";
            const ariaLabel = day.future ? `${formatDate(date)}\nDia futuro` : heatMapTooltipText(day);
            return `
              <button
                class="heatmap-cell level-${day.level}${futureClass}"
                data-heatmap-day="true"
                data-date="${date}"
                data-level="${day.level}"
                data-future="${day.future ? "true" : "false"}"
                data-questions="${day.metrics.questionsDone}"
                data-tasks="${day.metrics.tasksCompleted}"
                data-hours="${Math.round(day.metrics.studyMinutes)}"
                data-cards="${day.metrics.reviewWorkload}"
                data-score="${day.score.toFixed(1)}"
                type="button"
                aria-label="${escapeAttribute(ariaLabel)}"
              ></button>
            `;
          }).join("");
          return `
            <div class="heatmap-month">
              <div class="heatmap-grid" style="--heatmap-columns:${weekCount};">
                ${blanks}${cells}
              </div>
              <div class="heatmap-month-label">${escapeHtml(month.label)}</div>
            </div>
          `;
        }).join("")}
      </div>
    </div>
    <div class="heatmap-stats">
      <div class="heatmap-stat"><span>Média diária</span><strong>${stats.average.toFixed(1)} pts</strong></div>
      <div class="heatmap-stat"><span>Dias estudados</span><strong>${stats.studiedDays} dias</strong></div>
      <div class="heatmap-stat"><span>Maior sequência</span><strong>${stats.longestStreak} dias</strong></div>
      <div class="heatmap-stat"><span>Sequência atual</span><strong>${stats.currentStreak} dias</strong></div>
    </div>
    <div class="heatmap-legend">
      <span class="heatmap-legend-label">Menos</span>
      <span class="heatmap-cell legend-cell level-0"></span>
      <span class="heatmap-cell legend-cell level-1"></span>
      <span class="heatmap-cell legend-cell level-2"></span>
      <span class="heatmap-cell legend-cell level-3"></span>
      <span class="heatmap-cell legend-cell level-4"></span>
      <span class="heatmap-legend-label">Mais</span>
      <span class="heatmap-legend-note">Pontos = questões + tarefas + horas</span>
    </div>
    <div class="heatmap-tooltip" id="heatMapTooltip" hidden></div>
  `;
}

function updateHeatMap() {
  renderHeatMap();
}

/* =====================================================
   CRONOMETRO, FOCO E LINHA DO TEMPO
   Timer de estudo, sessoes, painel de foco e historico diario.
===================================================== */
function studyMinutesBySubjectForDate(subject, date) {
  return activeRecords(state.studySessions || [])
    .filter((item) => item.subject === subject && item.date === date)
    .reduce((sum, item) => sum + studySessionMinutes(item), 0);
}

function studyMsBySubjectForDate(subject, date) {
  return activeRecords(state.studySessions || [])
    .filter((item) => item.subject === subject && item.date === date)
    .reduce((sum, item) => sum + studySessionMs(item), 0);
}

function studyMsToday() {
  const date = timerOperationalValue();
  return studyMsForDate(date);
}

function studyMsForDate(date) {
  return activeRecords(state.studySessions || [])
    .filter((item) => item.date === date)
    .reduce((sum, item) => sum + studySessionMs(item), 0);
}

function studyMsForWeek() {
  const today = new Date();
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - today.getDay());
  const weekStartStr = timerOperationalValue(weekStart);
  return activeRecords(state.studySessions || [])
    .filter((item) => item.date >= weekStartStr)
    .reduce((sum, item) => sum + studySessionMs(item), 0);
}

function enemDaysLeft() {
  const now = new Date();
  const enem = new Date(ENEM_DATA.year, ENEM_DATA.month - 1, ENEM_DATA.day);
  const diff = enem.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function elapsedStudyTimerMs() {
  if (!activeStudyTimer) return 0;
  const runningMs = activeStudyTimer.running ? Date.now() - activeStudyTimer.runningSince : 0;
  return activeStudyTimer.elapsedMs + runningMs;
}

function renderStudyTimerSummary() {
  const summary = document.querySelector("#studyTimerSummary");
  if (!summary) return;
  const todayValue = timerOperationalValue();
  const activeMs = activeStudyTimer ? elapsedStudyTimerMs() : 0;
  const totalTodayMs = studyMsForDate(todayValue) + activeMs;
  summary.innerHTML = `
    <article class="metric study-timer-summary-card">
      <span class="study-timer-summary-icon" aria-hidden="true">
        <img src="assets/cronometro.png" alt="" />
      </span>
      <div class="study-timer-summary-copy">
        <strong>${formatTimer(totalTodayMs)}</strong>
        <small>tempo estudado hoje</small>
      </div>
    </article>
  `;
}

function renderStudyTime() {
  const list = document.querySelector("#studyTimerList");
  const timeline = document.querySelector("#studyTimeline");
  if (!list || !timeline) return;

  const todayValue = timerOperationalValue();
  const timerSubjects = getTimerSubjects();
  renderStudyTimerSummary();
  list.innerHTML = timerSubjects.length
    ? timerSubjects
    .map((subject) => {
      const isActive = activeStudyTimer?.subject === subject;
      const elapsedMs = isActive ? elapsedStudyTimerMs() : 0;
      const status = isActive ? (activeStudyTimer.running ? "em andamento" : "pausado") : "";
      const timerValue = isActive ? formatTimer(elapsedMs) : formatDuration(studyMinutesBySubjectForDate(subject, todayValue));
      return `
        <article class="study-timer-card ${isActive ? "active" : ""}" data-timer-subject="${escapeAttribute(subject)}" role="region" aria-label="${escapeAttribute(subject)} — ${timerValue}">
          <div class="study-timer-main">
            <span class="subject-dot" style="background:${colors[subject] || "var(--green)"}"></span>
            <div>
              <strong>${subject}</strong>
              ${status ? `<small>${status}</small>` : ""}
            </div>
          </div>
          <div class="study-timer-time">
            <strong>${timerValue}</strong>
            <small>${isActive ? "sessão atual" : "acumulado hoje"}</small>
          </div>
          <div class="study-timer-actions">
            <button class="ghost-button" data-action="${isActive && activeStudyTimer.running ? "pause-study-timer" : "start-study-timer"}" data-subject="${escapeAttribute(subject)}" type="button">
              ${isActive && activeStudyTimer.running ? "Pausar" : isActive ? "Continuar" : "Iniciar"}
            </button>
            <button class="ghost-button" data-action="finish-study-timer" data-subject="${escapeAttribute(subject)}" type="button" ${isActive ? "" : "disabled"}>
              Finalizar
            </button>
            <button class="icon-button timer-card-menu-btn" data-action="open-timer-subject-menu" data-subject="${escapeAttribute(subject)}" type="button" aria-haspopup="true" aria-label="Opções de ${escapeAttribute(subject)}" title="Opções">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
            </button>
          </div>
        </article>
      `;
    })
    .join("")
    : sharedRenderEmptyState("Nenhuma matéria no Cronômetro. Use o botão + para adicionar.");

  renderStudyTimeline(timeline);
}

function updateStudyTimerDisplay() {
  if (!activeStudyTimer && !document.querySelector("#tempo.active")) return;

  if (activeStudyTimer) {
    updateStudyTimerTextOnly();
    return;
  }

  if (document.querySelector("#tempo.active")) {
    const sig = sectionRenderSignature("tempo");
    if (sectionRenderCache.get("tempo") !== sig) {
      renderStudyTime();
    }
  }
}

function updateStudyTimerTextOnly() {
  if (!activeStudyTimer) return false;
  const elapsed = elapsedStudyTimerMs();
  const subject = activeStudyTimer.subject;
  const todayValue = timerOperationalValue();
  const totalTodayMs = studyMsForDate(todayValue) + elapsed;

  const summaryValue = document.querySelector("#studyTimerSummary .study-timer-summary-copy strong");
  if (summaryValue) summaryValue.textContent = formatTimer(totalTodayMs);

  const subjectCard = document.querySelector(`[data-timer-subject="${CSS.escape(subject)}"]`);
  const subjectTime = subjectCard?.querySelector(".study-timer-time strong");
  if (subjectTime) subjectTime.textContent = formatTimer(elapsed);

  updateFocusControllerTimer();

  const focusScreen = document.querySelector("#focusScreen");
  if (focusScreen && !focusScreen.classList.contains("hidden")) {
    document.querySelector("#focusTimer").textContent = formatTimer(elapsed);
    document.querySelector("#focusSubjectTotal").textContent = formatTimer(studyMsBySubjectForDate(subject, todayValue) + elapsed);
    document.querySelector("#focusTodayTotal").textContent = formatTimer(studyMsToday() + elapsed);
    document.querySelector("#focusWeekTotal").textContent = formatTimer(studyMsForWeek() + elapsed);
    const countdownEl = document.querySelector("#focusEnemCountdown");
    if (countdownEl) {
      const daysLeft = enemDaysLeft();
      countdownEl.textContent = daysLeft > 0 ? `ENEM: ${daysLeft}d` : "ENEM hoje!";
    }
  }

  const activeTimelineBlock = document.querySelector(".timeline-block.active .timeline-block-copy span");
  if (activeTimelineBlock) activeTimelineBlock.textContent = formatTimer(elapsed);
  return true;
}

function renderFocusScreen() {
  const screen = document.querySelector("#focusScreen");
  if (!screen) return;
  const visible = focusScreenOpen && Boolean(activeStudyTimer);
  screen.classList.toggle("hidden", !visible);
  screen.setAttribute("aria-hidden", String(!visible));
  screen.classList.toggle("focus-screen-immersive", visible && focusStudyPanelCollapsed);
  document.body.classList.toggle("focus-open", visible);
  if (!visible) {
    focusPanelSignature = "";
    return;
  }

  const elapsed = elapsedStudyTimerMs();
  const subject = activeStudyTimer.subject;
  const todayValue = timerOperationalValue();
  document.querySelector("#focusStatus").textContent = activeStudyTimer.running ? "Foco" : "Pausado";
  document.querySelector("#focusTimer").textContent = formatTimer(elapsed);
  document.querySelector("#focusSubjectLabel").textContent = subject;
  document.querySelector("#focusSubjectTotal").textContent = formatTimer(studyMsBySubjectForDate(subject, todayValue) + elapsed);
  document.querySelector("#focusTodayTotal").textContent = formatTimer(studyMsToday() + elapsed);
  document.querySelector("#focusWeekTotal").textContent = formatTimer(studyMsForWeek() + elapsed);
  const countdownEl = document.querySelector("#focusEnemCountdown");
  if (countdownEl) {
    const daysLeft = enemDaysLeft();
    countdownEl.textContent = daysLeft > 0 ? `ENEM: ${daysLeft}d` : "ENEM hoje!";
  }
  const pauseBtn = document.querySelector("#focusPauseButton");
  if (pauseBtn) {
    pauseBtn.querySelector(".focus-pause-icon")?.classList.toggle("hidden", !activeStudyTimer.running);
    pauseBtn.querySelector(".focus-play-icon")?.classList.toggle("hidden", activeStudyTimer.running);
    pauseBtn.setAttribute("aria-label", activeStudyTimer.running ? "Pausar" : "Continuar");
  }
  const focusPanelToggle = document.querySelector("#focusPanelToggle");
  if (focusPanelToggle) {
    const label = focusStudyPanelCollapsed ? "Mostrar listas de apoio" : "Ocultar listas de apoio";
    focusPanelToggle.setAttribute("aria-label", label);
    focusPanelToggle.setAttribute("title", label);
    focusPanelToggle.classList.toggle("is-collapsed", focusStudyPanelCollapsed);
  }
  const panel = document.querySelector("#focusStudyPanel");
  if (panel) {
    panel.classList.toggle("is-collapsed", focusStudyPanelCollapsed);
    panel.setAttribute("aria-hidden", String(focusStudyPanelCollapsed));
    const signature = focusPanelDataSignature(subject);
    if (focusPanelSignature !== signature) {
      panel.innerHTML = renderFocusStudyPanel(subject);
      focusPanelSignature = signature;
    }
  }
}

function renderFocusStudyPanel(subject) {
  const todos = focusTodoItems(subject);
  const pendingTodos = todos.filter((item) => !item.done);
  const candidates = focusTodoCandidates(subject);
  const safeSubject = escapeHtml(subject);
  const pendingLessons = pendingLessonsPending(subject);
  const doneLessons = pendingLessonsRecentDone(subject);
  return `
    <section class="focus-panel-card focus-panel-card-pendingLessons">
      <div class="focus-panel-head">
        <div>
          <strong><span class="focus-panel-icon"><img src="assets/focus-temp-icon-v2.png" alt="" /></span>Aula pendente</strong>
          <span>Falta assistir em ${safeSubject}</span>
        </div>
        <small>${pendingLessons.length} pendente${pendingLessons.length === 1 ? "" : "s"}</small>
      </div>
      <form class="focus-lesson-picker" data-action="add-pending-lesson-form" data-subject="${escapeAttribute(subject)}">
        <input type="text" name="title" placeholder="Ex.: Revisar função exponencial" autocomplete="off" aria-label="Título da aula pendente" />
        <button class="ghost-button focus-lesson-picker-add" type="submit" aria-label="Adicionar aula" title="Adicionar aula">+</button>
      </form>
      <div class="focus-todo-list">
        ${[...pendingLessons, ...doneLessons].map(renderFocusPendingLessonItem).join("") || `
          <div class="focus-empty focus-empty-rich">
            <svg width="78" height="78" viewBox="0 0 78 78" fill="none" aria-hidden="true"><circle cx="39" cy="39" r="28" stroke="rgba(255,255,255,0.14)" stroke-width="2" stroke-dasharray="4 4"/><path d="M33 44l-4-4 4-4M45 44l4-4-4-4" stroke="rgba(255,255,255,0.22)" stroke-width="2" stroke-linecap="round"/></svg>
            <p>Nenhuma aula pendente. Aproveite o foco!</p>
          </div>
        `}
      </div>
    </section>
    <section class="focus-panel-card focus-panel-card-todo">
      <div class="focus-panel-head">
        <div>
          <strong><span class="focus-panel-icon"><img src="assets/focus-todo-icon-v2.png" alt="" /></span>To-do list</strong>
          <span>Escolha o que entra em ${safeSubject}</span>
        </div>
        <small>${pendingTodos.length} pendente${pendingTodos.length === 1 ? "" : "s"}</small>
      </div>
      <form class="focus-todo-picker" data-action="add-focus-todo-form">
        <select name="todoRef" ${candidates.length ? "" : "disabled"}>
          ${candidates.length
            ? candidates.map((item) => `<option value="${escapeAttribute(item.ref)}">${escapeHtml(item.origin)} · ${escapeHtml(item.title)}</option>`).join("")
            : '<option value="">Nenhum item disponível</option>'}
        </select>
        <button class="ghost-button" type="submit" ${candidates.length ? "" : "disabled"}>Adicionar</button>
      </form>
      <div class="focus-todo-list">
        ${pendingTodos.length ? pendingTodos.map(renderFocusTodoItem).join("") : `
          <div class="focus-empty focus-empty-rich">
            <svg width="78" height="78" viewBox="0 0 78 78" fill="none" aria-hidden="true"><rect x="18" y="14" width="42" height="52" rx="6" stroke="rgba(255,255,255,0.14)" stroke-width="2"/><path d="M30 40l6 6 12-12" stroke="rgba(255,255,255,0.22)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            <p>Nada pendente para esta sessão.</p>
          </div>
        `}
      </div>
    </section>
    <section class="focus-panel-card focus-panel-card-questions">
      <div class="focus-panel-head">
        <div>
          <strong><span class="focus-panel-icon"><img src="assets/focus-questions-icon-v2.png" alt="" /></span>Questões feitas</strong>
          <span>Salva direto em ${safeSubject}</span>
        </div>
      </div>
      <form class="focus-question-form" data-action="focus-question-form" data-smart-scope="focus-question-form">
        <input name="content" list="focusQuestionContentOptions" placeholder="Assunto/conteúdo" autocomplete="off" data-smart-field="content" data-smart-context="subjectContext" data-smart-autofill="contextual" />
        <input type="hidden" name="subjectContext" value="${escapeAttribute(subject)}" />
        <input name="questions" type="number" min="1" step="1" placeholder="Questões" required />
        <input name="correct" type="number" min="0" step="1" placeholder="Acertos" required />
        <button class="ghost-button" type="submit">Salvar</button>
        <datalist id="focusQuestionContentOptions">
          ${(state.subjectContents[subject] || []).map((content) => `<option value="${escapeAttribute(content)}"></option>`).join("")}
        </datalist>
      </form>
    </section>
  `;
}

function focusPanelDataSignature(subject) {
  return JSON.stringify({
    subject,
    contents: state.subjectContents[subject] || [],
    refs: focusTodoRefs(subject),
    todos: focusTodoItems(subject).map((item) => ({
      source: item.source,
      id: item.id,
      done: item.done,
      title: item.title,
      detail: item.detail,
    })),
    pendingLessons: [...pendingLessonsPending(subject), ...pendingLessonsRecentDone(subject)].map((item) => ({
      id: item.id,
      done: item.done,
      title: item.title,
    })),
  });
}

function focusTodoItems(subject) {
  const byRef = new Map(focusTodoCandidates(subject, true).map((item) => [item.ref, item]));
  return focusTodoRefs(subject)
    .map((ref) => {
      const item = byRef.get(ref.ref);
      if (item) ref.done = item.done;
      return item;
    })
    .filter(Boolean)
    .sort((a, b) => Number(a.done) - Number(b.done));
}

function focusTodoRefs(subject) {
  state.focusTodos ||= [];
  return activeRecords(state.focusTodos).filter((item) => item.subject === subject);
}

function upsertFocusTodo(item, subject) {
  if (!item?.ref || !subject) return false;
  state.focusTodos ||= [];
  const now = Date.now();
  const record = {
    id: focusTodoRecordId(item.ref) || uid(),
    ref: item.ref,
    origin: item.origin,
    source: item.source,
    originalId: item.id,
    subject,
    cycle: item.cycle || "",
    done: item.done,
    updatedAt: now,
    deletedAt: null,
  };
  const existingIndex = state.focusTodos.findIndex((entry) => entry?.ref === item.ref);
  if (existingIndex >= 0) {
    state.focusTodos = state.focusTodos.map((entry, index) =>
      index === existingIndex
        ? { ...entry, ...record, id: entry.id || record.id }
        : entry,
    );
  } else {
    state.focusTodos = [...state.focusTodos, record];
  }
  touchDomainRoot("focusTodos");
  return true;
}

function removeFocusTodoRef(ref = "") {
  const cleanRef = String(ref || "").trim();
  if (!cleanRef) return false;
  state.focusTodos ||= [];
  const now = Date.now();
  const existingIndex = state.focusTodos.findIndex((entry) => entry?.ref === cleanRef);
  const tombstone = {
    id: focusTodoRecordId(cleanRef) || uid(),
    ref: cleanRef,
    updatedAt: now,
    deletedAt: now,
  };
  if (existingIndex >= 0) {
    state.focusTodos = state.focusTodos.map((entry, index) =>
      index === existingIndex
        ? {
          ...entry,
          id: entry.id || tombstone.id,
          updatedAt: now,
          deletedAt: now,
        }
        : entry,
    );
  } else {
    state.focusTodos = [...state.focusTodos, tombstone];
  }
  touchDomainRoot("focusTodos");
  return true;
}

function focusTodoCandidates(subject, includeSelected = false) {
  const selectedRefs = new Set(focusTodoRefs(subject).map((item) => item.ref));

  const plannerItems = [...(state.planner || [])]
    .filter((item) => {
      const isPlannerGroup = item.isGroup === true;

      return (
        item.subject === subject &&
        !isPlannerGroup &&
        (includeSelected || !item.done)
      );
    })
    .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")))
    .map((item) => ({
      ref: `planner:${item.id}`,
      origin: "Planner",
      source: "planner",
      id: item.id,
      subject,
      title: item.title || "Tarefa do planner",
      detail: [item.type, item.date ? formatDate(item.date) : ""].filter(Boolean).join(" · "),
      done: Boolean(item.done),
    }));

  return plannerItems
    .filter((item) => includeSelected || !selectedRefs.has(item.ref))
    .sort((a, b) => Number(a.done) - Number(b.done));
}

function renderFocusTodoItem(item) {
  const encodedSubject = encodeURIComponent(item.subject);
  const encodedCycle = encodeURIComponent(item.cycle || "");
  const action = item.source === "planner" ? "toggle-focus-planner" : "toggle-focus-lesson";
  const title = escapeAttribute(item.title);
  return `
    <label class="focus-todo-item ${item.done ? "done" : ""}" title="${title}">
      <input type="checkbox" data-action="${action}" data-id="${item.id}" data-subject="${encodedSubject}" data-cycle="${encodedCycle}" ${item.done ? "checked" : ""} />
      <span>
        <strong title="${title}">${escapeHtml(item.title)}</strong>
        <small>${escapeHtml(item.detail || item.subject)}</small>
      </span>
      <em>${item.origin}</em>
      <button class="icon-button" data-action="remove-focus-todo" data-ref="${escapeAttribute(item.ref)}" type="button" title="Remover da lista">×</button>
    </label>
  `;
}

function renderFocusPendingLessonItem(item) {
  const title = escapeAttribute(item.title);
  return `
    <div class="focus-todo-item ${item.done ? "done" : ""}" title="${title}">
      <label>
        <input type="checkbox" data-action="toggle-pending-lesson" data-id="${escapeAttribute(item.id)}" ${item.done ? "checked" : ""} />
        <span>
          <strong title="${title}">${escapeHtml(item.title)}</strong>
          <small>${item.done ? "Concluída" : "Pendente"}</small>
        </span>
      </label>
      <em>${escapeHtml(item.subject)}</em>
      <div class="pending-lessons-item-actions">
        <button class="ghost-button symbol-button" data-action="edit-pending-lesson" data-id="${escapeAttribute(item.id)}" type="button" title="Editar aula" aria-label="Editar aula">✎</button>
        <button class="ghost-button danger-button symbol-button" data-action="delete-pending-lesson" data-id="${escapeAttribute(item.id)}" type="button" title="Excluir aula" aria-label="Excluir aula">×</button>
      </div>
    </div>
  `;
}

function subjectInitials(name) {
  if (!name) return "?";
  const words = name.trim().split(/\s+/);
  if (words.length >= 2 && words[1].length > 1) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function renderStudyTimeline(container) {
  const date = selectedStudyTimelineDate || timerOperationalValue();
  selectedStudyTimelineDate = date;
  const visibleSubjects = new Set(getTimerSubjects().map((subject) => normalizeHeader(subject)));
  const sessions = activeRecords(state.studySessions || [])
    .filter((item) => visibleSubjects.has(normalizeHeader(item.subject)))
    .filter((item) => item.date === date)
    .sort((a, b) => String(a.startTime || "").localeCompare(String(b.startTime || "")));
  const timedSessions = sessions.filter((item) => item.startTime);
  const untimedSessions = sessions.filter((item) => !item.startTime);
  const activeDate = activeStudyTimer ? timerOperationalValue() : "";
  const activeEntry = activeStudyTimer && activeDate === date && visibleSubjects.has(normalizeHeader(activeStudyTimer.subject))
    ? {
        id: "active",
        subject: activeStudyTimer.subject,
        startTime: activeStudyTimer.startTime,
        durationMs: elapsedStudyTimerMs(),
        active: true,
      }
    : null;
  const now = new Date();
  const isToday = date === timerOperationalValue(now);
  const groupBySubject = container.dataset.groupBySubject === "true";

  const allForDist = timedSessions.concat(activeEntry ? [activeEntry] : []);
  const perSubject = {};
  let totalDayMs = 0;
  for (const item of allForDist) {
    const ms = item.active ? item.durationMs : studySessionMs(item);
    const subj = item.subject;
    perSubject[subj] = (perSubject[subj] || 0) + ms;
    totalDayMs += ms;
  }

  const distSegments = Object.entries(perSubject)
    .map(([subject, ms]) => ({
      subject,
      ms,
      pct: totalDayMs > 0 ? (ms / totalDayMs) * 100 : 0,
      color: colors[subject] || "var(--blue)",
    }))
    .sort((a, b) => b.ms - a.ms);

  let sessionHtml = "";

  if (groupBySubject) {
    const groups = {};
    for (const item of timedSessions) {
      (groups[item.subject] = groups[item.subject] || []).push(item);
    }
    for (const [subject, items] of Object.entries(groups)) {
      const subTotal = items.reduce((sum, it) => sum + studySessionMs(it), 0);
      const subColor = colors[subject] || "var(--blue)";
      sessionHtml += `
      <details class="study-session-group" open>
        <summary class="study-session-group-head" style="--group-color:${subColor}">
          <span class="study-session-group-dot" style="background:${subColor}"></span>
          <span class="study-session-group-subject">${escapeHtml(subject)}</span>
          <span class="study-session-group-total">${formatTimer(subTotal)}</span>
        </summary>
        <div class="study-session-group-body">
          <div class="study-sessions-list">
            ${items.map((item) => renderStudySessionCard(item, isToday)).join("")}
          </div>
        </div>
      </details>`;
    }
    if (activeEntry) {
      sessionHtml = renderStudySessionCard(activeEntry, isToday) + (sessionHtml ? "<div class=\"study-sessions-list\">" + sessionHtml + "</div>" : "");
    }
  } else {
    sessionHtml = timedSessions.map((item) => renderStudySessionCard(item, isToday)).join("");
    if (activeEntry) sessionHtml += renderStudySessionCard(activeEntry, isToday);
  }

  const metrics = getDailyMetrics(date);
  const summaryItems = [];
  if (metrics.tasksCompleted) summaryItems.push(`<span class="study-session-summary-item"><strong>${metrics.tasksCompleted}</strong> tarefa${metrics.tasksCompleted !== 1 ? "s" : ""}</span>`);
  if (metrics.questionsDone) summaryItems.push(`<span class="study-session-summary-item"><strong>${metrics.questionsDone}</strong> questõe${metrics.questionsDone !== 1 ? "s" : ""}</span>`);
  if (metrics.errorsReviewed) summaryItems.push(`<span class="study-session-summary-item"><strong>${metrics.errorsReviewed}</strong> erro${metrics.errorsReviewed !== 1 ? "s" : ""} revisado${metrics.errorsReviewed !== 1 ? "s" : ""}</span>`);
  if (metrics.mocksTaken) summaryItems.push(`<span class="study-session-summary-item"><strong>${metrics.mocksTaken}</strong> simulado${metrics.mocksTaken !== 1 ? "s" : ""}</span>`);
  if (metrics.cardsReviewed) summaryItems.push(`<span class="study-session-summary-item"><strong>${metrics.cardsReviewed}</strong> flash-card${metrics.cardsReviewed !== 1 ? "s" : ""}</span>`);
  if (metrics.essaysWritten) summaryItems.push(`<span class="study-session-summary-item"><strong>${metrics.essaysWritten}</strong> redação${metrics.essaysWritten !== 1 ? "ões" : ""}</span>`);

  const summaryCard = summaryItems.length ? `
    <article class="study-session-card summary" style="--card-color:var(--muted)">
      <span class="study-session-avatar summary">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 14l2 2 4-4"/></svg>
      </span>
      <div class="study-session-info">
        <div class="study-session-subject">Resumo do dia</div>
        <div class="study-session-summary-line">${summaryItems.join("")}</div>
      </div>
    </article>
  ` : "";

  if (summaryCard) sessionHtml = summaryCard + (sessionHtml ? sessionHtml : "");

  const hasSessions = timedSessions.length || activeEntry || untimedSessions.length || summaryItems.length;

  container.innerHTML = `
    <div class="timeline-head">
      <div class="timeline-head-row">
        <div class="timeline-title">
          <strong>${timelineDateLabel(date)}</strong>
        </div>
        <div class="timeline-stats">
          <span class="timeline-total">${formatTimer(totalDayMs)}</span>
          <span class="timeline-total-label">no dia</span>
        </div>
      </div>
      ${distSegments.length > 1 ? `
      <div class="timeline-distribution">
        ${distSegments.map((seg) => `<div class="timeline-distribution-seg" style="flex:0 0 ${seg.pct}%;background:${seg.color}" title="${escapeHtml(seg.subject)}: ${formatTimer(seg.ms)}"></div>`).join("")}
      </div>` : ""}
      <div class="timeline-controls">
        <button class="ghost-button" data-action="timeline-prev-day" type="button">‹</button>
        <input id="studyTimelineDate" type="date" value="${date}" aria-label="Selecionar dia da linha do tempo" />
        <button class="ghost-button" data-action="timeline-next-day" type="button">›</button>
        <button class="ghost-button" data-action="timeline-today" type="button">Hoje</button>
        <button class="ghost-button ${groupBySubject ? "active" : ""}" data-action="toggle-timeline-group" type="button" title="${groupBySubject ? "Desagrupar" : "Agrupar por matéria"}" aria-label="${groupBySubject ? "Desagrupar sessões" : "Agrupar sessões por matéria"}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="8" y="2" width="8" height="4" rx="1"/><rect x="2" y="10" width="20" height="4" rx="1"/><rect x="8" y="18" width="8" height="4" rx="1"/></svg>
        </button>
      </div>
    </div>
    <div class="study-sessions-list">
      ${hasSessions ? sessionHtml : sharedRenderEmptyState("Nenhum estudo registrado neste dia.", { className: "timeline-empty", ctaHtml: '<button class="timeline-empty-cta" data-action="start-study-from-empty" type="button">Iniciar sessão</button>' })}
    </div>
    ${untimedSessions.length ? `
    <div class="study-sessions-untimed">
      <strong>Sessões sem horário</strong>
      <div class="study-sessions-list">
        ${untimedSessions.map((item) => renderStudySessionCard(item, isToday)).join("")}
      </div>
    </div>` : ""}
  `;
}

function renderStudySessionCard(item) {
  const color = colors[item.subject] || "var(--blue)";
  const sessionAttributes = item.active ? "" : ` data-study-session="${escapeAttribute(item.id)}"`;
  const isUntimed = !item.startTime;
  const startFormatted = item.startTime ? formatTime(item.startTime) : "";
  const endFormatted = item.endTime ? formatTime(item.endTime) : (item.active ? formatTime(new Date().toISOString()) : "");
  const durationMs = item.active ? item.durationMs : studySessionMs(item);
  const durationFormatted = item.active ? formatTimer(item.durationMs) : formatTimer(studySessionMs(item));
  const initials = subjectInitials(item.subject);
  const isShort = !item.active && durationMs < 60000;

  const baseClass = `study-session-card${item.active ? " active" : ""}${isUntimed ? " untimed" : ""}${isShort ? " short" : ""}`;

  const avatarHtml = `<span class="study-session-avatar" style="background:${color}">${escapeHtml(initials)}</span>`;

  const actionButtons = item.active ? "" : `
    <div class="study-session-actions">
      <button class="study-session-action-btn" data-action="edit-study-session" data-id="${escapeAttribute(item.id)}" type="button" aria-label="Editar" title="Editar">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
      </button>
      <button class="study-session-action-btn delete" data-action="delete-study-session" data-id="${escapeAttribute(item.id)}" type="button" aria-label="Excluir" title="Excluir">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
      </button>
    </div>`;

  const badgeHtml = item.active ? `<span class="study-session-badge">Em andamento</span>` : (isShort ? `<span class="study-session-badge short">Rápida</span>` : "");

  return `
    <article class="${baseClass}"${sessionAttributes} style="--card-color:${color}">
      ${avatarHtml}
      <div class="study-session-info">
        <div class="study-session-subject">${escapeHtml(item.subject)}</div>
        <span class="study-session-time">${isUntimed ? "sem horário" : `${startFormatted} → ${endFormatted}`}</span>
      </div>
      <div class="study-session-duration">${durationFormatted}</div>
      ${badgeHtml}
      ${actionButtons}
    </article>
  `;
}

function closeTimerSubjectMenu() {
  document.querySelector(".timer-subject-menu")?.remove();
}

function openCardMenu({ buttons, className = "", anchor }) {
  const menu = document.createElement("div");
  menu.className = `planner-card-menu ${className}`.trim();
  menu.dataset.floatingPanel = "true";
  menu.innerHTML = Array.isArray(buttons) ? buttons.join("") : buttons;
  document.body.appendChild(menu);
  registerFloatingPanel(menu, anchor, { gap: 8, padding: 8, mode: anchor instanceof Event ? "point" : "anchor" });
  return menu;
}

function openTimerSubjectMenu(subject, anchor) {
  closeAllOverlays();
  closeTimerSubjectMenu();
  openCardMenu({
    className: "timer-subject-menu",
    anchor,
    buttons: [
      `<button type="button" data-action="edit-timer-subject" data-subject="${escapeAttribute(subject)}">Editar</button>`,
      `<button type="button" data-action="delete-timer-subject" data-subject="${escapeAttribute(subject)}">Excluir</button>`,
    ],
  });
}

function closeTimerSubjectModal() {
  document.querySelector(".timer-subject-modal")?.remove();
  syncBodyScrollLock();
}

function closeSubjectModal() {
  document.querySelector(".subject-modal")?.remove();
  syncBodyScrollLock();
}

function openSubjectModal() {
  closeAllOverlays();
  closeSubjectModal();
  const modal = document.createElement("div");
  modal.className = "image-editor-modal subject-modal";
  modal.innerHTML = `
    <div class="modal timer-subject-dialog">
      <div class="modal-content timer-subject-content">
        <div class="planner-builder-head">
          <div>
            <strong>Nova matéria</strong>
            <small>Adicione uma matéria à seção Matérias</small>
          </div>
          <button class="icon-button" type="button" data-action="close-subject-modal" aria-label="Fechar">×</button>
        </div>
        <form class="timer-subject-form" data-action="submit-create-subject">
          <input name="name" placeholder="Nome da matéria" autocomplete="off" required />
          <div class="timer-subject-actions">
            <button class="ghost-button" type="button" data-action="close-subject-modal">Cancelar</button>
            <button type="submit">Adicionar</button>
          </div>
        </form>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  syncBodyScrollLock();
  setTimeout(() => modal.querySelector("input")?.focus(), 0);
}

function openTimerSubjectModal(mode = "create", subject = "") {
  closeAllOverlays();
  closeTimerSubjectMenu();
  closeTimerSubjectModal();
  const modal = document.createElement("div");
  const editing = mode === "edit";
  modal.className = "image-editor-modal timer-subject-modal";
  modal.innerHTML = `
    <div class="modal timer-subject-dialog">
      <div class="modal-content timer-subject-content">
        <div class="planner-builder-head">
          <div>
            <strong>${editing ? "Editar matéria" : "Nova matéria"}</strong>
            <small>${editing ? "Renomeie a matéria desta seção" : "Adicionar ao Cronômetro"}</small>
          </div>
          <button class="icon-button" type="button" data-action="close-timer-subject-modal" aria-label="Fechar">×</button>
        </div>
        <form class="timer-subject-form" data-action="${editing ? "submit-edit-timer-subject" : "submit-create-timer-subject"}" data-subject="${escapeAttribute(subject)}">
          <input name="name" placeholder="Nome da matéria" value="${escapeAttribute(subject)}" autocomplete="off" required />
          <div class="timer-subject-actions">
            <button class="ghost-button" type="button" data-action="close-timer-subject-modal">Cancelar</button>
            <button type="submit">${editing ? "Salvar" : "Adicionar"}</button>
          </div>
        </form>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  syncBodyScrollLock();
  modal.querySelector('input[name="name"]')?.focus();
  modal.querySelector('input[name="name"]')?.select();
}

function openSessionStartPicker() {
  const subjects = getTimerSubjects();
  if (!subjects.length) {
    notify("Adicione matérias ao cronômetro primeiro.", "error");
    return;
  }
  closeAllOverlays();
  closeTimerSubjectModal();
  const modal = document.createElement("div");
  modal.className = "image-editor-modal timer-subject-modal";
  modal.innerHTML = `
    <div class="modal timer-subject-dialog">
      <div class="modal-content timer-subject-content">
        <div class="planner-builder-head">
          <div>
            <strong>Iniciar sessão</strong>
            <small>Escolha a matéria para começar a estudar</small>
          </div>
          <button class="icon-button" type="button" data-action="close-timer-subject-modal" aria-label="Fechar">×</button>
        </div>
        <div class="timer-subject-actions" style="display:grid;gap:8px">
          ${subjects.map((s) => `
            <button class="ghost-button" data-action="start-study-session-pick" data-subject="${escapeAttribute(s)}" type="button" style="display:flex;align-items:center;gap:10px;width:100%;text-align:left">
              <span class="subject-dot" style="flex:0 0 auto;background:${colors[s] || "var(--blue)"}"></span>
              <span>${escapeHtml(s)}</span>
            </button>
          `).join("")}
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  syncBodyScrollLock();
}

function closeStudySessionMenu() {
  document.querySelector(".study-session-menu")?.remove();
}

function openStudySessionMenu(sessionId, anchor) {
  closeAllOverlays();
  closeStudySessionMenu();
  openCardMenu({
    className: "study-session-menu",
    anchor,
    buttons: [
      `<button type="button" data-action="edit-study-session" data-id="${escapeAttribute(sessionId)}">Editar estudo</button>`,
      `<button type="button" data-action="delete-study-session" data-id="${escapeAttribute(sessionId)}">Excluir</button>`,
    ],
  });
}

function closeStudySessionModal() {
  document.querySelector(".study-session-modal")?.remove();
  syncBodyScrollLock();
}

function openStudySessionModal(sessionId) {
  const item = (state.studySessions || []).find((entry) => entry.id === sessionId);
  if (!item) return;

  closeAllOverlays();
  closeStudySessionMenu();
  closeStudySessionModal();

  const durationMinutes = Math.max(1, Math.round(studySessionMs(item) / 60000));
  const dateValue = item.date || localDateValue(item.startTime ? new Date(item.startTime) : new Date());
  const startDateTime = item.startTime || localDateTimeValue(dateValue, "08:00");
  const endDateTime = item.endTime || localDateTimeValue(dateValue, minutesToTime(plannerTimeToMinutes(timeValueFromDate(startDateTime) || "08:00") + durationMinutes));
  const modal = document.createElement("div");
  modal.className = "image-editor-modal study-session-modal";
  modal.innerHTML = `
    <div class="modal timer-subject-dialog">
      <div class="modal-content timer-subject-content">
        <div class="planner-builder-head">
          <div>
            <strong>Editar estudo</strong>
            <small>${escapeHtml(item.subject)}</small>
          </div>
          <button class="icon-button" type="button" data-action="close-study-session-modal" aria-label="Fechar">×</button>
        </div>
        <form class="timer-subject-form study-session-form" data-action="submit-edit-study-session" data-id="${escapeAttribute(sessionId)}">
          <input name="subject" list="studySessionSubjectOptions" value="${escapeAttribute(item.subject)}" autocomplete="off" required />
          <datalist id="studySessionSubjectOptions">
            ${getTimerSubjects().map((subject) => `<option value="${escapeAttribute(subject)}"></option>`).join("")}
          </datalist>
          <input name="date" type="date" value="${escapeAttribute(dateValue)}" required />
          <div class="study-session-time-grid">
            <label>
              <span>Começo</span>
              <input name="startTime" type="time" value="${escapeAttribute(timeValueFromDate(startDateTime) || "08:00")}" required />
            </label>
            <label>
              <span>Final</span>
              <input name="endTime" type="time" value="${escapeAttribute(timeValueFromDate(endDateTime) || "08:30")}" required />
            </label>
          </div>
          <label>
            <span>Tempo de estudo</span>
            <input name="durationMinutes" type="number" min="1" step="1" value="${durationMinutes}" required />
          </label>
          <div class="timer-subject-actions">
            <button class="ghost-button" type="button" data-action="close-study-session-modal">Cancelar</button>
            <button type="submit">Salvar</button>
          </div>
        </form>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  syncBodyScrollLock();
  modal.querySelector('input[name="date"]')?.focus();
}

function startStudyTimer(subject) {
  if (activeStudyTimer && activeStudyTimer.subject !== subject) {
    notify(`Finalize ou pause a sessão de ${activeStudyTimer.subject} antes de iniciar outra.`, "error");
    return;
  }

  focusScreenOpen = true;

  if (!activeStudyTimer) {
    activeStudyTimer = {
      id: uid(),
      subject,
      startTime: new Date().toISOString(),
      elapsedMs: 0,
      runningSince: Date.now(),
      running: true,
    };
    startStudyTimerTick();
  } else if (!activeStudyTimer.running) {
    activeStudyTimer.running = true;
    activeStudyTimer.runningSince = Date.now();
    startStudyTimerTick();
  }

  renderFocusScreen();
}

function pauseStudyTimer() {
  if (!activeStudyTimer?.running) return;
  activeStudyTimer.elapsedMs = elapsedStudyTimerMs();
  activeStudyTimer.running = false;
  activeStudyTimer.runningSince = 0;
  stopStudyTimerTick();
}

function toggleStudyTimer() {
  if (!activeStudyTimer) return;
  if (activeStudyTimer.running) {
    pauseStudyTimer();
    return;
  }
  activeStudyTimer.running = true;
  activeStudyTimer.runningSince = Date.now();
  startStudyTimerTick();
}

function finishStudyTimer() {
  if (!activeStudyTimer) return;
  const endTime = new Date().toISOString();
  const durationMs = Math.max(1000, Math.round(elapsedStudyTimerMs()));
  const duration = Math.max(1, Math.round(durationMs / 60000));
  const timerSnapshot = { ...activeStudyTimer, elapsedMs: activeStudyTimer.elapsedMs };
  const sessionCountBefore = (state.studySessions || []).length;
  state.studySessions ||= [];
  const sessionRecord = {
    id: activeStudyTimer.id || uid(),
    date: timerOperationalValue(new Date(endTime)),
    subject: activeStudyTimer.subject,
    duration,
    durationMs,
    startTime: activeStudyTimer.startTime,
    endTime,
  };
  state.studySessions = [...state.studySessions, sessionRecord];
  touchRecord("studySessions", sessionRecord.id);
  activeStudyTimer = null;
  syncActiveStudyTimerState();
  focusScreenOpen = false;
  stopStudyTimerTick();
  renderFocusController();

  const toast = showToast(`Sessão finalizada em ${timerSnapshot.subject}`, { type: "success", duration: 9000 });
  if (toast) {
    const actions = document.createElement("div");
    actions.className = "app-toast-actions";
    const undoButton = document.createElement("button");
    undoButton.className = "app-toast-action";
    undoButton.type = "button";
    undoButton.textContent = "Desfazer";
    undoButton.addEventListener("click", () => {
      state.studySessions = (state.studySessions || []).slice(0, sessionCountBefore);
      touchDomainRoot("studySessions");
      activeStudyTimer = timerSnapshot;
      syncActiveStudyTimerState();
      if (timerSnapshot.running) startStudyTimerTick();
      focusScreenOpen = true;
      renderFocusController();
      render();
      toast.classList.add("is-leaving");
      setTimeout(() => toast.remove(), 180);
    });
    actions.appendChild(undoButton);
    toast.appendChild(actions);
  }
}

function removeStudySession(sessionId, options = {}) {
  const item = activeRecords(state.studySessions || []).find((entry) => entry.id === sessionId);
  if (!item) return false;
  const shouldConfirm = options.confirm !== false;
  if (shouldConfirm) {
    openDeleteConfirmModal({
      title: "Excluir sessão de estudo",
      subtitle: "Essa sessão será removida do histórico",
      message: `Excluir o estudo de ${item.subject} em ${formatDate(item.date || timerOperationalValue())}?`,
      onConfirm: () => {
        if (!removeStudySession(sessionId, { confirm: false })) return;
        renderSectionNow("tempo", { save: true, commit: true });
      },
    });
    return false;
  }
  removeRecord("studySessions", sessionId);
  closeStudySessionMenu();
  closeStudySessionModal();
  return true;
}

function startStudyTimerTick() {
  if (studyTimerTick) return;
  studyTimerTickLastUpdate = 0;

  function tick() {
    const now = performance.now();
    if (now - studyTimerTickLastUpdate >= STUDY_TIMER_TICK_MS) {
      studyTimerTickLastUpdate = now;
      updateStudyTimerDisplay();
    }
    studyTimerTick = requestAnimationFrame(tick);
  }

  studyTimerTick = requestAnimationFrame(tick);
}

function stopStudyTimerTick() {
  if (studyTimerTick) {
    cancelAnimationFrame(studyTimerTick);
    studyTimerTick = null;
  }
}

function smartMemoryScopeKey(scope, field) {
  const safeScope = String(scope || "global").trim() || "global";
  const safeField = String(field || "").trim();
  return safeField ? `${safeScope}::${safeField}` : "";
}

function smartMemoryContextKey(context = {}) {
  const entries = Object.entries(context)
    .map(([key, value]) => [String(key || "").trim(), String(value || "").trim()])
    .filter(([key, value]) => key && value)
    .sort(([left], [right]) => left.localeCompare(right, "pt-BR", { sensitivity: "base" }));
  return entries.map(([key, value]) => `${key}=${value}`).join("|");
}

function smartMemoryFromField(field) {
  if (!field?.dataset?.smartField) return null;
  const form = field.form || field.closest("form");
  const scope = field.dataset.smartScope || form?.dataset.smartScope || form?.id || form?.dataset.action || "global";
  const contextNames = (field.dataset.smartContext || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  const context = {};
  contextNames.forEach((name) => {
    const source = form?.elements?.[name];
    if (!source) return;
    const value = source instanceof RadioNodeList ? source.value : source.value;
    const clean = String(value || "").trim();
    if (clean) context[name] = clean;
  });
  return {
    form,
    field,
    fieldName: field.dataset.smartField,
    scope,
    context,
    key: smartMemoryScopeKey(scope, field.dataset.smartField),
    contextKey: smartMemoryContextKey(context),
  };
}

function smartMemoryEntries(scope, fieldName) {
  state.smartFormMemory ||= {};
  return state.smartFormMemory[smartMemoryScopeKey(scope, fieldName)] || [];
}

function rememberSmartFieldValue(scope, fieldName, value, context = {}) {
  const cleanValue = String(value || "").trim();
  if (!fieldName || !cleanValue) return;
  state.smartFormMemory ||= {};
  const key = smartMemoryScopeKey(scope, fieldName);
  const contextKey = smartMemoryContextKey(context);
  const entries = state.smartFormMemory[key] || [];
  const now = Date.now();
  const existing = entries.find((entry) =>
    normalizeHeader(entry.value) === normalizeHeader(cleanValue) &&
    (entry.contextKey || "") === contextKey,
  );
  let nextEntries;
  if (existing) {
    nextEntries = entries.map((entry) => entry === existing
      ? {
          ...entry,
          value: cleanValue,
          count: (entry.count || 0) + 1,
          updatedAt: now,
        }
      : entry);
  } else {
    nextEntries = [...entries, {
      value: cleanValue,
      contextKey,
      count: 1,
      updatedAt: now,
    }];
  }
  nextEntries.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  touchMapKey("smartFormMemory", key, nextEntries.slice(0, 40));
}

function smartFieldSuggestions(field) {
  const memory = smartMemoryFromField(field);
  if (!memory?.key) return [];
  const entries = [...smartMemoryEntries(memory.scope, memory.fieldName)];
  const exact = [];
  const fallback = [];
  entries.forEach((entry) => {
    if (!entry?.value) return;
    if (memory.contextKey && entry.contextKey === memory.contextKey) {
      exact.push(entry);
      return;
    }
    fallback.push(entry);
  });
  return [...exact, ...fallback]
    .filter((entry, index, array) =>
      array.findIndex((candidate) => normalizeHeader(candidate.value) === normalizeHeader(entry.value)) === index,
    )
    .slice(0, 12)
    .map((entry) => entry.value);
}

function smartFieldAutofillValue(field) {
  const mode = field?.dataset?.smartAutofill || "";
  if (!mode) return "";
  const suggestions = smartFieldSuggestions(field);
  if (!suggestions.length) return "";
  if (mode === "contextual") {
    const memory = smartMemoryFromField(field);
    const exact = smartMemoryEntries(memory.scope, memory.fieldName).find((entry) => entry.contextKey === memory.contextKey);
    return exact?.value || "";
  }
  return suggestions[0] || "";
}

function ensureSmartFieldDatalist(field) {
  if (!field?.dataset?.smartField || field.tagName !== "INPUT") return;
  const type = (field.type || "text").toLowerCase();
  if (!["text", "search"].includes(type)) return;
  const form = field.form || field.closest("form");
  if (!form) return;
  const existingListId = field.getAttribute("list");
  if (existingListId && !existingListId.startsWith("smart-")) {
    const list = document.querySelector(`#${CSS.escape(existingListId)}`);
    if (!list) return;
    const existingValues = [...list.querySelectorAll("option")]
      .map((option) => option.value)
      .filter(Boolean);
    const suggestions = [...new Set([...existingValues, ...smartFieldSuggestions(field)])];
    list.innerHTML = suggestions.map((value) => `<option value="${escapeAttribute(value)}"></option>`).join("");
    return;
  }
  const scope = field.dataset.smartScope || form.dataset.smartScope || form.id || form.dataset.action || "global";
  const id = `smart-${scope.replace(/[^a-z0-9_-]+/gi, "-")}-${field.dataset.smartField.replace(/[^a-z0-9_-]+/gi, "-")}-${field.name || "field"}-${field.dataset.smartContext || "all"}`;
  let list = document.querySelector(`#${CSS.escape(id)}`);
  if (!list) {
    list = document.createElement("datalist");
    list.id = id;
    document.body.appendChild(list);
  }
  const suggestions = smartFieldSuggestions(field);
  list.innerHTML = suggestions.map((value) => `<option value="${escapeAttribute(value)}"></option>`).join("");
  if (suggestions.length) {
    field.setAttribute("list", id);
  } else if (field.getAttribute("list") === id) {
    field.removeAttribute("list");
  }
}

function shouldAutofillSmartField(field) {
  if (!field?.dataset?.smartAutofill) return false;
  if (field.dataset.smartInitialized === "true") return false;
  const tag = field.tagName;
  if (tag === "SELECT") return true;
  return !String(field.value || "").trim();
}

function resetSmartFormState(form) {
  form?.querySelectorAll("[data-smart-field]").forEach((field) => {
    delete field.dataset.smartInitialized;
  });
}

function clearSmartFieldValue(field) {
  if (!(field instanceof HTMLElement)) return;
  if (field.tagName === "SELECT") {
    field.selectedIndex = field.options.length ? 0 : -1;
    return;
  }
  if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
    if (field.type === "hidden") return;
    field.value = "";
  }
}

function resetSmartContextDependents(form, contextName, options = {}) {
  form?.querySelectorAll(`[data-smart-context*="${contextName}"]`).forEach((field) => {
    delete field.dataset.smartInitialized;
    if (options.clearValues) clearSmartFieldValue(field);
  });
}

function applySmartFieldMemory(root = document) {
  root.querySelectorAll("[data-smart-field]").forEach((field) => {
    if (!(field instanceof HTMLElement)) return;
    ensureSmartFieldDatalist(field);
    if (shouldAutofillSmartField(field)) {
      const suggestion = smartFieldAutofillValue(field);
      if (suggestion && field.value !== suggestion) {
        field.value = suggestion;
        field.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }
    field.dataset.smartInitialized = "true";
  });
}

function rememberSmartForm(form, data = null) {
  if (!form) return;
  const values = data || Object.fromEntries(new FormData(form).entries());
  form.querySelectorAll("[data-smart-field]").forEach((field) => {
    const memory = smartMemoryFromField(field);
    if (!memory?.fieldName) return;
    const value = values[field.name];
    if (Array.isArray(value)) {
      value.forEach((entry) => rememberSmartFieldValue(memory.scope, memory.fieldName, entry, memory.context));
      return;
    }
    rememberSmartFieldValue(memory.scope, memory.fieldName, value, memory.context);
  });
}

function renderContentOptions() {
  renderContentDatalist("#questionForm", "#questionContentOptions");
  renderContentDatalist("#errorForm", "#errorContentOptions");
  renderContentDatalist('.planner-builder-form[data-type="Questões"]', "#plannerQuestionContentOptions");
}

function renderContentDatalist(formSelector, listSelector) {
  const form = document.querySelector(formSelector);
  const list = document.querySelector(listSelector);
  if (!form || !list) return;
  const subject = form.querySelector('select[name="subject"]')?.value;
  const options = subject ? state.subjectContents[subject] || [] : [];
  list.innerHTML = options.map((content) => `<option value="${content}"></option>`).join("");
}

function rememberContent(subject, content) {
  if (!subject || !content?.trim()) return;
  ensureSubjectState(subject);
  state.subjectContents[subject] ||= [];
  const clean = content.trim();
  const exists = state.subjectContents[subject].some(
    (item) => item.toLowerCase() === clean.toLowerCase(),
  );
  if (!exists) touchMapKey("subjectContents", subject, [...state.subjectContents[subject], clean]);
}

/* =====================================================
   MATERIAS E CICLOS
   Galeria de materias, ciclos, conteudos, aulas e importacoes.
===================================================== */
function scheduleSubjectNotesSave() {
  clearTimeout(subjectNoteSaveTimer);
  subjectNoteSaveTimer = setTimeout(() => {
    saveState();
  }, 220);
}

function studySubjectIconAsset(subject = "") {
  const icons = {
    "História": "assets/historia.png",
    "Geografia": "assets/Geografia.png",
    "Filosofia": "assets/filosofia.png",
    "Sociologia": "assets/sociologia.png",
    "Matemática": "assets/matematica.png",
    "Física": "assets/fisica.png",
    "Química": "assets/quimica.png",
    "Biologia": "assets/biologia.png",
    "Redação": "assets/redacao-materia.png",
    "Linguagens": "assets/linguagens.png",
    "Português": "assets/linguagens.png",
  };
  return icons[subject] || "assets/fisica.png";
}

function lessonGroupKey(group) {
  return `${selectedStudySubject}::${selectedStudyCycle}::${group}`;
}

function isLessonGroupCollapsed(group) {
  return Boolean(state.collapsedLessonGroups?.[lessonGroupKey(group)]);
}

function toggleLessonGroup(group) {
  state.collapsedLessonGroups ||= {};
  const key = lessonGroupKey(group);
  touchScalar("collapsedLessonGroups", {
    ...state.collapsedLessonGroups,
    [key]: !state.collapsedLessonGroups[key],
  });
}

function getOrderedLessonGroups(subject, cycle, lessons = getCycleLessons(subject, cycle)) {
  const grouped = groupBy(activeRecords(lessons), (item) => item.group || "Sem grupo");
  const groups = Object.keys(grouped);
  const order = getCycleContentOrder(subject, cycle, groups);
  return order.map((group) => ({ group, items: grouped[group] })).filter((entry) => entry.items?.length);
}

function getCycleContentOrder(subject, cycle, groups = []) {
  state.cycleContentOrder ||= {};
  const key = cycleKey(subject, cycle);
  const current = Array.isArray(state.cycleContentOrder[key]) ? state.cycleContentOrder[key] : [];
  const groupSet = new Set(groups);
  const ordered = current.filter((group) => groupSet.has(group));
  groups.forEach((group) => {
    if (!ordered.includes(group)) ordered.push(group);
  });
  state.cycleContentOrder[key] = ordered;
  return ordered;
}

function ensureCycleContentGroup(subject, cycle, group) {
  if (!group) return;
  const key = cycleKey(subject, cycle);
  state.cycleContentOrder ||= {};
  const current = Array.isArray(state.cycleContentOrder[key]) ? state.cycleContentOrder[key] : [];
  if (!current.includes(group)) {
    touchScalar("cycleContentOrder", {
      ...state.cycleContentOrder,
      [key]: [...current, group],
    });
  }
}

function moveCycleContentGroupToPosition(fromGroup, beforeGroup = "") {
  if (!selectedStudySubject || !selectedStudyCycle || !fromGroup) return false;
  const lessons = activeRecords(getCycleLessons(selectedStudySubject, selectedStudyCycle));
  const groups = Object.keys(groupBy(lessons, (item) => item.group || "Sem grupo"));
  const order = [...getCycleContentOrder(selectedStudySubject, selectedStudyCycle, groups)];
  const fromIndex = order.indexOf(fromGroup);
  if (fromIndex < 0) return false;
  const [moved] = order.splice(fromIndex, 1);
  const beforeIndex = beforeGroup ? order.indexOf(beforeGroup) : -1;
  if (beforeIndex >= 0) order.splice(beforeIndex, 0, moved);
  else order.push(moved);
  touchScalar("cycleContentOrder", {
    ...(state.cycleContentOrder || {}),
    [cycleKey(selectedStudySubject, selectedStudyCycle)]: order,
  });
  return true;
}

function updateLessonGroupDropTarget(event, draggedGroup = "") {
  const container = event.target.closest(".lesson-group")?.parentElement;
  if (!container) return null;
  const beforeValue = insertionBeforeDataValue(container, "[data-cycle-content-group]", "cycleContentGroup", event.clientY, encodeURIComponent(draggedGroup));
  const beforeElement = beforeValue ? container.querySelector(`[data-cycle-content-group="${CSS.escape(beforeValue)}"]`) : null;
  clearGenericDndState("lesson-group");
  container.classList.add("drag-over");
  renderBlockPlaceholder(container, beforeElement, "lesson-group", document.querySelector("[data-cycle-content-group].dragging"));
  return { beforeGroup: beforeValue ? decodeURIComponent(beforeValue) : "" };
}

function addLessonRow(group, values = {}) {
  ensureCycleContentGroup(selectedStudySubject, selectedStudyCycle, group);
  const key = cycleKey(selectedStudySubject, selectedStudyCycle);
  const lessons = getCycleLessons(selectedStudySubject, selectedStudyCycle);
  touchMapKey("cycleLessons", key, [...lessons, {
    id: uid(),
    group,
    topic: values.topic || "",
    theory: Boolean(values.theory),
    practice: Boolean(values.practice),
    review: Boolean(values.review),
  }]);
}

function addStudyCyclesFromText(text, subject = selectedStudySubject) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  let count = 0;
  lines.forEach((line) => {
    const before = getSubjectCycles(subject).length;
    addStudyCycle(line);
    if (getSubjectCycles(subject).length > before) count += 1;
  });
  return count;
}

function parseLessonBatchEntries(groupValue = "", text = "") {
  const fallbackGroup = String(groupValue || "").trim();
  return String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      if (line.includes("|")) {
        const [group, ...topicParts] = line.split("|");
        return {
          group: String(group || "").trim(),
          topic: topicParts.join("|").trim(),
        };
      }
      return {
        group: fallbackGroup,
        topic: line,
      };
    })
    .filter((entry) => entry.group && entry.topic);
}

function addLessonRowsFromBatch(groupValue, text) {
  const entries = parseLessonBatchEntries(groupValue, text);
  entries.forEach((entry) => addLessonRow(entry.group, { topic: entry.topic }));
  return entries.length;
}

function lessonDependencyCount(lessonId) {
  return activeRecords(state.planner || []).filter((item) => {
    const lessonIds = item.materialRef?.lessonIds || [item.materialRef?.lessonId].filter(Boolean);
    return lessonIds.includes(lessonId);
  }).length;
}

function importPastedLessonRows(group, text) {
  const rows = text
    .trim()
    .split(/\r?\n/)
    .map((row) => row.split("\t").map((cell) => cell.trim()))
    .filter((cells) => cells.some(Boolean));

  let count = 0;
  rows.forEach((cells, index) => {
    const first = normalizeHeader(cells[0]);
    if (index === 0 && ["assunto", "aula", "topico", "topic", "conteudo"].includes(first)) return;

    const hasContentColumn = ["conteudo", "grupo", "group"].includes(first) || cells.length >= 5;
    const topic = hasContentColumn ? cells[1] : cells[0];
    if (!topic) return;

    addLessonRow(group, {
      topic,
      theory: parseCsvBoolean(hasContentColumn ? cells[2] : cells[1]),
      practice: parseCsvBoolean(hasContentColumn ? cells[3] : cells[2]),
      review: parseCsvBoolean(hasContentColumn ? cells[4] : cells[3]),
    });
    count += 1;
  });

  return count;
}

function getSubjectCycles(subject) {
  state.subjectCycles ||= {};
  state.subjectCycles[subject] ||= [];
  return state.subjectCycles[subject];
}

function cycleKey(subject, cycle) {
  return `${subject}::${cycle}`;
}

function getCycleLessons(subject, cycle) {
  state.cycleLessons ||= {};
  const key = cycleKey(subject, cycle);
  state.cycleLessons[key] ||= [];
  return state.cycleLessons[key];
}

function contentNameFromFile(fileName) {
  return String(fileName || "Conteúdo")
    .replace(/\.[^.]+$/, "")
    .replace(/"[^"]*"/g, "")
    .replace(/\s+[a-f0-9]{16,}$/i, "")
    .replace(/\s+/g, " ")
    .trim() || "Sem grupo";
}

function dataUrlToImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

async function cropImageDataUrl(src, top, bottom) {
  const image = await dataUrlToImage(src);
  const safeTop = Math.max(0, Math.min(image.height - 24, Math.floor(top)));
  const safeBottom = Math.max(safeTop + 24, Math.min(image.height, Math.ceil(bottom)));
  const height = safeBottom - safeTop;
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  canvas.width = image.width;
  canvas.height = height;
  context.drawImage(image, 0, safeTop, image.width, height, 0, 0, image.width, height);
  const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
  canvas.width = 0;
  canvas.height = 0;
  return dataUrl;
}

function answerLetterFromText(text) {
  const match = String(text || "").match(/(?:^|[^A-ZÀ-Ú])([A-E])(?:\)|\.|\b)/i);
  return match ? match[1].toUpperCase() : "";
}

function hasCorrectAnswerLabel(text) {
  return /alternativa\s+correta|resposta\s+(?:correta|certa)|gabarito|correta\s*:/i.test(text);
}

function hasMarkedAnswerLabel(text) {
  return /alternativa\s+que\s+marquei|alternativa\s+marcada|resposta\s+marcada|minha\s+resposta|marquei|marcada\s*:/i.test(text);
}

function extractCorrectAnswer(text) {
  const value = String(text || "");
  const patterns = [
    /alternativa\s+correta\s*(?:[:=\-–]|é|foi)?\s*(?:alternativa\s*)?([A-E])\b/i,
    /resposta\s+(?:correta|certa)\s*(?:[:=\-–]|é|foi)?\s*(?:alternativa\s*)?([A-E])\b/i,
    /gabarito\s*(?:[:=\-–]|é|foi)?\s*(?:alternativa\s*)?([A-E])\b/i,
    /correta\s*(?:[:=\-–]|é|foi)?\s*(?:alternativa\s*)?([A-E])\b/i,
  ];
  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match) return match[1].toUpperCase();
  }
  return "";
}

function extractMarkedAnswer(text) {
  const value = String(text || "");
  const patterns = [
    /alternativa\s+que\s+marquei\s*(?:[:=\-–]|é|foi)?\s*(?:alternativa\s*)?([A-E])\b/i,
    /alternativa\s+marcada\s*(?:[:=\-–]|é|foi)?\s*(?:alternativa\s*)?([A-E])\b/i,
    /resposta\s+marcada\s*(?:[:=\-–]|é|foi)?\s*(?:alternativa\s*)?([A-E])\b/i,
    /minha\s+resposta\s*(?:[:=\-–]|é|foi)?\s*(?:alternativa\s*)?([A-E])\b/i,
    /marquei\s*(?:[:=\-–]|a|foi)?\s*(?:alternativa\s*)?([A-E])\b/i,
    /marcada\s*(?:[:=\-–]|é|foi)?\s*(?:alternativa\s*)?([A-E])\b/i,
  ];
  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match) return match[1].toUpperCase();
  }
  return "";
}

function isImportedMetaLine(text) {
  return /^alternativas?\b/i.test(text)
    || hasCorrectAnswerLabel(text)
    || hasMarkedAnswerLabel(text)
    || /^erro\b/i.test(text)
    || /^corre[cç][aã]o\b|^como corrigir/i.test(text);
}

function lineCanvasY(entry) {
  if (!entry?.page) return 0;
  return entry.page.height - (entry.y * entry.page.scale);
}

async function cropImportedQuestionImages(block) {
  const firstPage = block.find((entry) => entry.page)?.page;
  if (!firstPage?.image) return { headerImage: "", correctionImage: "" };

  const pageEntries = block.filter((entry) => entry.page === firstPage);
  const titleY = lineCanvasY(pageEntries[0]);
  const correctionEntry = pageEntries.find((entry) => /^corre[cç][aã]o\b|^como corrigir/i.test(entry.text));
  const firstMetaEntry = pageEntries.find((entry, index) => index > 0 && isImportedMetaLine(entry.text));
  const nextTitle = null;

  const top = Math.max(0, titleY - 28);
  const headerBottom = firstMetaEntry ? lineCanvasY(firstMetaEntry) - 8 : firstPage.height * 0.58;
  const headerImage = headerBottom > top + 80
    ? await cropImageDataUrl(firstPage.image, top, headerBottom)
    : "";

  let correctionImage = "";
  if (correctionEntry) {
    const correctionTop = Math.max(0, lineCanvasY(correctionEntry) - 12);
    const correctionBottom = nextTitle ? lineCanvasY(nextTitle) - 12 : firstPage.height;
    if (correctionBottom > correctionTop + 80) {
      correctionImage = await cropImageDataUrl(firstPage.image, correctionTop, correctionBottom);
    }
  }

  return { headerImage, correctionImage };
}

async function parseImportedErrorBlock(block) {
  const lines = block.map((entry) => entry.text);
  const page = block.find((entry) => entry.page)?.page || null;
  const pageImage = page?.image || "";
  const pageNumber = page?.number || "";
  const { headerImage, correctionImage } = await cropImportedQuestionImages(block);
  const title = lines[0].replace(/^[>›»-]\s*/, "").trim();
  const alternatives = [];
  const header = [];
  const fix = [];
  let reason = "";
  let correctAnswer = "";
  let markedAnswer = "";
  let section = "header";

  lines.slice(1).forEach((line, index, bodyLines) => {
    const correctFromLine = extractCorrectAnswer(line);
    const markedFromLine = extractMarkedAnswer(line);
    if (correctFromLine) correctAnswer = correctFromLine;
    if (markedFromLine) markedAnswer = markedFromLine;

    if (/^alternativas?\b/i.test(line)) {
      section = "alternatives";
      return;
    }
    if (hasCorrectAnswerLabel(line)) {
      const nextLine = bodyLines[index + 1] || "";
      if (!correctAnswer && !hasCorrectAnswerLabel(nextLine) && !hasMarkedAnswerLabel(nextLine)) {
        correctAnswer = answerLetterFromText(nextLine);
      }
      section = "meta";
      return;
    }
    if (hasMarkedAnswerLabel(line)) {
      const nextLine = bodyLines[index + 1] || "";
      if (!markedAnswer && !hasCorrectAnswerLabel(nextLine) && !hasMarkedAnswerLabel(nextLine)) {
        markedAnswer = answerLetterFromText(nextLine);
      }
      section = "meta";
      return;
    }
    if (/^erro\b/i.test(line)) {
      reason = line.split(":").slice(1).join(":").trim();
      section = "meta";
      return;
    }
    if (/^corre[cç][aã]o\b|^como corrigir/i.test(line)) {
      const firstFixLine = line.split(":").slice(1).join(":").trim();
      if (firstFixLine) fix.push(firstFixLine);
      section = "fix";
      return;
    }

    if (/^[A-E][).]\s*/i.test(line)) {
      alternatives.push(line.replace(/^[A-E][).]\s*/i, "").trim());
      section = "alternatives";
      return;
    }

    if (section === "fix") {
      fix.push(line);
    } else if (section === "alternatives") {
      alternatives.push(line);
    } else if (section === "header") {
      header.push(line);
    }
  });

  const joinedLines = lines.join(" ");
  correctAnswer ||= extractCorrectAnswer(joinedLines);
  markedAnswer ||= extractMarkedAnswer(joinedLines);

  return {
    title,
    questionHeader: header.join("\n").trim(),
    questionHeaderHtml: [
      escapeHtml(header.join("\n").trim()).replace(/\n/g, "<br>"),
      headerImage ? `<br><img src="${escapeAttribute(headerImage)}" alt="Cabeçalho da questão importado do PDF">` : "",
    ].filter(Boolean).join(""),
    alternatives: alternatives.join("\n").trim(),
    correctAnswer,
    markedAnswer,
    reason: reason || "Importado do PDF",
    fix: fix.join("\n").trim(),
    fixHtml: escapeHtml(fix.join("\n").trim()).replace(/\n/g, "<br>"),
    pdfPageImage: pageImage,
    pdfPageNumber: pageNumber,
    pdfCorrectionImage: correctionImage,
  };
}

function askErrorPdfDestination(file) {
  const form = document.querySelector("#errorForm");
  const suggestedSubject = selectedErrorSubject || (errorsSubjectFilter() !== "all" ? errorsSubjectFilter() : "") || getSubjects()[0] || "Geral";
  const subject = canonicalSubject(form?.elements.subject?.value || suggestedSubject);
  const content = String(
    form?.elements.content?.value
    || selectedErrorContent
    || contentNameFromFile(file?.name)
    || "Importação PDF",
  ).trim();
  if (form?.elements.subject && !form.elements.subject.value) form.elements.subject.value = subject;
  if (form?.elements.content && !form.elements.content.value) form.elements.content.value = content;
  syncErrorFormDateDefault();
  addSubject(subject);
  rememberContent(subject, content);
  return { subject, content };
}

function parseCsvBoolean(value) {
  const normalized = normalizeHeader(value);
  return ["sim", "s", "yes", "true", "1", "feito", "concluido", "checked"].includes(normalized);
}

function findEditableRecord(collection, id) {
  if (collection === "cycleLesson") {
    for (const lessons of Object.values(state.cycleLessons)) {
      const item = activeRecords(lessons).find((entry) => entry.id === id);
      if (item) return item;
    }
    return null;
  }

  return state[collection]?.find?.((entry) => entry.id === id) || null;
}

function editableDisplayValue(record, field, type) {
  const value = record[field];
  if (type === "date") return String(value || "").trim() ? formatDate(value) : "";
  if (type === "duration") return formatDuration(value);
  if (type === "optional-number" && (value === null || value === undefined)) return "";
  return value ?? "";
}

function parseEditableValue(rawValue, type) {
  const value = rawValue.trim();
  if (type === "date") {
    if (!value || normalizeHeader(value) === "sem data") return "";
    const parsed = parseBrazilianDate(value);
    if (!parsed) throw new Error("Data inválida. Use DD/MM/AAAA.");
    return parsed;
  }

  if (type === "number") {
    if (value === "") throw new Error("Digite um número.");
    const parsed = Number(value.replace(",", "."));
    if (!Number.isFinite(parsed)) throw new Error("Número inválido.");
    return parsed;
  }

  if (type === "duration") {
    if (value === "") throw new Error("Digite a duração em minutos.");
    const hoursMatch = /(\d+(?:[,.]\d+)?)\s*h/.exec(value);
    const minutesMatch = /(\d+(?:[,.]\d+)?)\s*(?:m|min)?$/.exec(value);
    if (hoursMatch) {
      const hours = Number(hoursMatch[1].replace(",", "."));
      const minutes = minutesMatch ? Number(minutesMatch[1].replace(",", ".")) : 0;
      return Math.max(1, Math.round(hours * 60 + minutes));
    }
    const parsed = Number(value.replace(",", "."));
    if (!Number.isFinite(parsed)) throw new Error("Duração inválida.");
    return Math.max(1, Math.round(parsed));
  }

  if (type === "optional-number") {
    if (value === "") return null;
    const parsed = Number(value.replace(",", "."));
    if (!Number.isFinite(parsed)) throw new Error("Número inválido.");
    return parsed;
  }

  return value;
}

function saveInlineEdit(element) {
  const { editRecord, id, field, type = "text" } = element.dataset;
  const record = findEditableRecord(editRecord, id);
  if (!record || !field) return;

  if (type !== "html") {
    const rawValue = element.textContent.trim();
    const currentDisplay = editableDisplayValue(record, field, type);
    if (rawValue === currentDisplay) {
      element.dataset.editing = "false";
      element.contentEditable = "false";
      element.classList.remove("inline-editing");
      removeInlineEditToolbar(element);
      return;
    }
  }

  try {
    const parsed = type === "html"
      ? sanitizeQuestionHeaderHtml(element.innerHTML)
      : parseEditableValue(element.textContent, type);
    record[field] = parsed;
    if (editRecord === "errors" && field === "questionHeaderHtml") {
      record.questionHeader = element.innerText.trim();
    }
    if (editRecord === "errors" && field === "fixHtml") {
      record.fix = element.innerText.trim();
    }

    if ((editRecord === "questions" || editRecord === "errors") && field === "content") {
      rememberContent(record.subject, parsed);
    }

    if (editRecord === "errors") {
      Object.assign(record, normalizeErrorRecord(record));
    }

    if (editRecord === "questions") {
      if (isQuestionGroup(record)) {
        record.name = String(record.name || "").trim() || "Grupo de listas";
        syncQuestionGroupState(record.id, { removeEmpty: false });
      } else {
        if (field === "questions") record.questions = Math.max(1, Number(record.questions));
        if (field === "correct") record.correct = Math.max(0, Number(record.correct));
        if (field === "date" && !String(record.date || "").trim()) record.date = questionTodayValue();
        record.subject = canonicalSubject(record.subject);
        if (record.subject && record.subject !== "Geral") ensureSubjectExists(record.subject);
        record.correct = Math.min(record.questions, record.correct);
        rememberContent(record.subject, record.content);
        syncQuestionPerformance(record);
        if (record.questionGroupId) syncQuestionGroupState(record.questionGroupId, { removeEmpty: false });
        touchRecord("questions", record.id);
        syncPlannerLinks();
      }
    }

    if (editRecord === "planner") {
      markPlannerUserEdited(record);
      if (record.flashcardData && field === "title") {
        const deckName = plannerFlashcardDeckNameFromTitle(record.title || "");
        record.flashcardData.deckName = deckName;
        record.title = plannerFlashcardTitle(deckName);
      }
      syncPlannerItemToSource(record);
    }

    if (editRecord === "essays") {
      ["c1", "c2", "c3", "c4", "c5"].forEach((competency) => {
        record[competency] = Math.min(200, Math.max(0, number(record[competency])));
      });
      record.score = essayScoreTotal(record);
    }

    if (editRecord === "mocks") {
      Object.assign(record, normalizeMockRecord(record));
    }

    saveState();
    element.dataset.editing = "false";
    removeInlineEditToolbar(element);
    requestAnimationFrame(() => {
      renderSectionNow(viewForEditableRecord(editRecord), { commit: true });
    });
  } catch (error) {
    notify(error.message, "error");
    element.textContent = editableDisplayValue(record, field, type);
    element.focus();
  }
}

function startInlineEdit(element) {
  if (element.dataset.editing === "true") return;
  const { editRecord, id, field, type = "text" } = element.dataset;
  const record = findEditableRecord(editRecord, id);
  if (!record || !field) return;

  element.dataset.editing = "true";
  if (type === "html") {
    element.innerHTML = field === "questionHeaderHtml"
      ? renderQuestionHeaderContent(record)
      : renderRichTextContent(record[field], record[field.replace(/Html$/, "")]);
  } else {
    element.textContent = editableDisplayValue(record, field, type);
  }
  element.contentEditable = "true";
  element.classList.add("inline-editing");
  element.focus();
  showInlineEditToolbar(element);
  selectEditableText(element);
}

function cancelInlineEdit(element) {
  const { editRecord, id, field, type = "text" } = element.dataset;
  const record = findEditableRecord(editRecord, id);
  if (record && field) {
    if (type === "html") {
      element.innerHTML = field === "questionHeaderHtml"
        ? renderQuestionHeaderContent(record)
        : renderRichTextContent(record[field], record[field.replace(/Html$/, "")]);
    } else {
      element.textContent = editableDisplayValue(record, field, type);
    }
  }
  element.dataset.editing = "false";
  element.contentEditable = "false";
  element.classList.remove("inline-editing");
  removeInlineEditToolbar(element);
}

function closePlannerCardMenu() {
  document.querySelector(".planner-card-menu")?.remove();
  document.querySelector(".planner-edit-popover")?.remove();
}

function removePlannerFocusReferences(plannerId = "") {
  if (!plannerId) return;
  removeFocusTodoRef(`planner:${plannerId}`);
}

function removePlannerItem(id, options = {}) {
  const plannerId = String(id || "");
  const item = activeRecords(state.planner || []).find((entry) => entry.id === plannerId);
  if (!item) return false;
  if (options.confirm) {
    openDeleteConfirmModal({
      title: isPlannerGroup(item) ? "Excluir grupo do Planner" : "Excluir tarefa do Planner",
      subtitle: "Vínculos relacionados podem ser atualizados",
      message: isPlannerGroup(item)
        ? "Excluir este grupo e todas as subtarefas dentro dele?"
        : "Excluir esta tarefa do Planner?",
      onConfirm: () => {
        if (!removePlannerItem(plannerId, { confirm: false, closeMenu: options.closeMenu })) return;
        renderSectionNow("planner", { save: true, commit: true });
        showUndoToast(isPlannerGroup(item) ? "Grupo do Planner excluído." : "Tarefa do Planner excluída.");
      },
    });
    return false;
  }

  if (isPlannerGroup(item)) {
    plannerGroupChildren(item.id).forEach((child) => {
      removePlannerItem(child.id, { confirm: false, closeMenu: false });
    });
  }

  if (item.questionData?.questionId) {
    const question = activeRecords(state.questions || []).find((entry) => entry.id === item.questionData.questionId);
    if (question && question.plannerId === plannerId) {
      delete question.plannerId;
      question.plannerDone = false;
      touchRecord("questions", question.id);
    }
  }

  removePlannerFocusReferences(plannerId);
  plannerSelectedTaskIds = plannerSelectedTaskIds.filter((entryId) => entryId !== plannerId);
  removeRecord("planner", plannerId);
  if (item.plannerGroupId) syncPlannerGroupState(item.plannerGroupId);

  if (activeStudyTimer?.focusTodoRef === `planner:${plannerId}`) {
    delete activeStudyTimer.focusTodoRef;
  }

  if (options.closeMenu !== false) closePlannerCardMenu();
  return true;
}

function editableTitleForPlannerCard(id) {
  return plannerCardInActiveView(id)?.querySelector('[data-edit-record="planner"][data-field="title"]');
}

function plannerActiveViewRoot() {
  return document.querySelector("#plannerActiveView[data-active]");
}

function plannerCardInActiveView(id) {
  const plannerId = String(id || "");
  if (!plannerId) return null;
  return plannerActiveViewRoot()?.querySelector(`[data-task-id="${CSS.escape(plannerId)}"]`) || null;
}

function isPlannerCardInActiveView(card) {
  return Boolean(card && plannerActiveViewRoot()?.contains(card));
}

function plannerActiveViewTaskIds() {
  const root = plannerActiveViewRoot();
  if (!root) return new Set();
  return new Set([...root.querySelectorAll("[data-task-id]")].map((card) => card.dataset.taskId).filter(Boolean));
}

function plannerSelectedTaskIdsInActiveView() {
  const activeTaskIds = plannerActiveViewTaskIds();
  return plannerSelectedTaskIds.filter((id) => activeTaskIds.has(id));
}

function plannerMaterialContentKey(materialRef = {}) {
  const subject = String(materialRef.subject || "").trim();
  const cycle = String(materialRef.cycle || "").trim();
  const group = String(materialRef.group || "").trim();
  return group ? `${subject}::${cycle}::${group}` : "";
}

function plannerMaterialGroupPartCount(contentKey = "") {
  if (!contentKey) return 0;
  const groupIds = new Set();
  activeRecords(state.planner || []).forEach((item) => {
    if (!item.plannerGroupId) return;
    if (plannerMaterialContentKey(item.materialRef) !== contentKey) return;
    groupIds.add(item.plannerGroupId);
  });
  return groupIds.size;
}

function plannerGroupTitleForCreatedItems(items = [], type = "") {
  const refs = items
    .map((item) => item.materialRef)
    .filter((ref) => ref && String(ref.group || "").trim());
  if (!refs.length) return "";
  const keys = [...new Set(refs.map((ref) => plannerMaterialContentKey(ref)))].filter(Boolean);
  if (keys.length !== 1) return "";
  const contentKey = keys[0];
  const name = String(refs[0].group || "").trim();
  if (!name) return "";

  const selectedIds = new Set(
    refs.flatMap((ref) => ref.lessonIds || [ref.lessonId].filter(Boolean)),
  );

  let complete;
  if (type === "Revisão") {
    complete = true;
  } else {
    const availableIds = materialReferenceOptions(type)
      .filter((option) => plannerMaterialContentKey(option) === contentKey)
      .map((option) => option.id)
      .filter(Boolean);
    complete = availableIds.length > 0 && availableIds.every((id) => selectedIds.has(id));
  }

  if (complete) return name;
  return `${name} · pt. ${plannerMaterialGroupPartCount(contentKey) + 1}`;
}

function appendPlannerGroupFromItems(items = [], options = {}) {
  const validItems = items.filter(Boolean);
  if (!validItems.length) return null;
  const group = {
    id: uid(),
    title: String(options.title || `Grupo com ${validItems.length} tarefas`).trim() || `Grupo com ${validItems.length} tarefas`,
    type: "Grupo",
    isGroup: true,
    collapsed: false,
  };
  validItems.forEach((item) => {
    item.plannerGroupId = group.id;
  });
  state.planner = [...(state.planner || []), group, ...validItems];
  touchRecord("planner", group.id);
  validItems.forEach((item) => touchRecord("planner", item.id));
  syncPlannerGroupState(group.id, { removeEmpty: false });
  plannerSelectedTaskIds = [group.id];
  return group;
}

function focusPlannerGroupTitleEdit(id) {
  if (!id) return;
  requestAnimationFrame(() => {
    const titleElement = editableTitleForPlannerCard(id);
    if (!titleElement) return;
    startInlineEdit(titleElement);
  });
}

function renderPlannerEditForm(item) {
  const flashcardName =
    item.flashcardData?.deckName ||
    plannerFlashcardDeckNameFromTitle(item.title || "");

  const heading = isPlannerGroup(item) ? "Editar grupo" : "Editar tarefa";
  const timeContext = plannerActiveView === "calendar";

  return `
    <section class="planner-builder planner-edit-popover">
      <div class="planner-builder-head">
        <div>
          <strong>${heading}</strong>
          <small>${formatDate(plannerDateValue(item) || localDateValue())}</small>
        </div>

        <button class="icon-button" data-action="close-planner-card-menu" type="button" aria-label="Fechar">×</button>
      </div>

      <form class="planner-builder-form" data-action="update-planner-card" data-id="${escapeAttribute(item.id)}" data-smart-scope="planner-edit-form">
        ${isPlannerGroup(item) ? "" : `<input name="date" type="date" value="${escapeAttribute(item.date || localDateValue())}" required />`}

        ${
          !isPlannerGroup(item) && timeContext && (state.plannerTimeEnabled || item.time)
            ? renderPlannerTimeFields(
                item.time || "05:00",
                item.endTime || defaultPlannerEndTime(item.time || "05:00")
              )
            : ""
        }

        ${
          item.type === "Flash-card"
            ? `
              <input name="deckName" value="${escapeAttribute(flashcardName)}" placeholder="Nome do deck (opcional)" data-smart-field="deckName" />
              ${renderPlannerFlashcardStatFields(item.flashcardData || {})}
            `
            : `<input name="title" value="${escapeAttribute(item.title || "")}" placeholder="Título da tarefa" required data-smart-field="title" />`
        }

        <button type="submit">Salvar</button>
        <button class="danger-button" data-action="delete-planner-from-edit" data-id="${escapeAttribute(item.id)}" type="button">Excluir</button>
      </form>
    </section>
  `;
}

function openPlannerEditPopover(id, anchor = null) {
  const card = plannerCardInActiveView(id);
  if (!card) return;
  const item = state.planner.find((entry) => entry.id === id);
  if (!item) return;

  closeAllOverlays();
  closePlannerCardMenu();

  const wrapper = document.createElement("div");
  wrapper.innerHTML = renderPlannerEditForm(item);

  const popover = wrapper.firstElementChild;
  popover.dataset.floatingPanel = "true";
  document.body.appendChild(popover);
  registerFloatingPanel(popover, anchor instanceof Event ? anchor : card, { gap: 8, padding: 8, mode: anchor instanceof Event ? "point" : "anchor" });

  popover.querySelector('input[name="title"], input[name="deckName"]')?.focus();
}

function openPlannerCardMenu(card, anchor = null) {
  if (!isPlannerCardInActiveView(card)) return;
  const taskId = card.dataset.taskId || "";

  if (!plannerSelectedTaskIdsInActiveView().includes(taskId)) {
    plannerSelectedTaskIds = [taskId];
    plannerSelectionAnchorId = "";
  }

  const selected = plannerSelectedTaskIdsInActiveView()
    .map((id) => (state.planner || []).find((item) => item.id === id))
    .filter(Boolean);

  const single = selected.length === 1 ? selected[0] : null;

  closeAllOverlays();
  closePlannerCardMenu();

  const buttons = [
    selected.length ? `<button type="button" data-action="open-planner-later-modal" data-id="${escapeAttribute(selected[0].id)}">Mudar dia</button>` : "",
    single ? `<button type="button" data-action="open-planner-edit" data-id="${escapeAttribute(single.id)}">Editar</button>` : "",
    canGroupPlannerSelection() ? '<button type="button" data-action="group-planner-selection">Agrupar</button>' : "",
    canUngroupPlannerSelection() ? `<button type="button" data-action="ungroup-planner-selection" data-id="${escapeAttribute(single.id)}">Desagrupar</button>` : "",
    single ? `<button type="button" data-action="delete-planner" data-id="${escapeAttribute(single.id)}">Excluir</button>` : "",
  ].filter(Boolean);

  if (!buttons.length) {
    openPlannerEditPopover(taskId, anchor || card);
    return;
  }

  openCardMenu({ buttons, anchor: anchor || card });
}
function studySubjectAreaLabel(subject = "") {
  const areas = {
    "Biologia": "Natureza",
    "Química": "Natureza",
    "Física": "Natureza",
    "Matemática": "Exatas",
    "Geografia": "Humanas",
    "História": "Humanas",
    "Filosofia": "Humanas",
    "Sociologia": "Humanas",
    "Linguagens": "Linguagens",
    "Português": "Linguagens",
    "Redação": "Linguagens",
  };
  return areas[subject] || "Geral";
}

function studySubjectProgress(subject = "") {
  const lessons = getSubjectCycles(subject).flatMap((cycle) => activeRecords(getCycleLessons(subject, cycle)));
  if (lessons.length) {
    const totalSteps = lessons.length * 3;
    const completedSteps = lessons.reduce((sum, lesson) => {
      return sum + Number(Boolean(lesson.theory)) + Number(Boolean(lesson.practice)) + Number(Boolean(lesson.review));
    }, 0);
    return Math.round((completedSteps / Math.max(1, totalSteps)) * 100);
  }
  const questions = questionLeafRecords(state.questions || []).filter((item) => item.subject === subject);
  if (questions.length) {
    const done = questions.filter((item) => questionRecordDone(item)).length;
    return Math.round((done / Math.max(1, questions.length)) * 100);
  }
  return studyMinutesBySubject(subject) > 0 ? 12 : 0;
}

function studySubjectCardStats(subject = "") {
  const minutes = studyMinutesBySubject(subject);
  const questionTotal = questionLeafRecords(state.questions || [])
    .filter((item) => item.subject === subject)
    .reduce((sum, item) => sum + Math.max(0, number(item.questions)), 0);
  const essayTotal = (state.essays || []).filter((item) => canonicalSubject(item.subject || "Redação") === subject).length;
  return {
    minutes,
    progress: studySubjectProgress(subject),
    secondaryValue: subject === "Redação" ? essayTotal : questionTotal,
    secondaryLabel: subject === "Redação" ? "Produções" : "Questões",
  };
}

function studySubjectsHomeSummary() {
  const subjectsList = getSubjects();
  const progresses = subjectsList.map((subject) => studySubjectProgress(subject));
  const activeSubjects = subjectsList.filter((subject) => {
    return studyMinutesBySubject(subject) > 0
      || getSubjectCycles(subject).length > 0
      || questionLeafRecords(state.questions || []).some((item) => item.subject === subject);
  }).length;
  return {
    totalSubjects: subjectsList.length,
    activeSubjects,
    totalMinutes: (state.studySessions || []).reduce((sum, item) => sum + studySessionMinutes(item), 0),
    averageProgress: progresses.length ? Math.round(progresses.reduce((sum, value) => sum + value, 0) / progresses.length) : 0,
  };
}

function pendingLessonsFor(subject = "") {
  const needle = normalizeHeader(subject);
  return (state.pendingLessons || []).filter(
    (r) => r && typeof r === "object" && !r.deletedAt && normalizeHeader(r.subject) === needle,
  );
}

function pendingLessonsPending(subject = "") {
  return pendingLessonsFor(subject).filter((r) => !r.done);
}

function pendingLessonsRecentDone(subject = "") {
  return pendingLessonsFor(subject).filter((r) => Boolean(r.done));
}

function pendingLessonsCount(subject = "") {
  return pendingLessonsPending(subject).length;
}

function addPendingLesson(subject = "", title = "") {
  const cleanSubject = String(subject || "").trim();
  const cleanTitle = String(title || "").trim();
  if (!cleanSubject || !cleanTitle) return null;
  state.pendingLessons ||= [];
  const record = {
    id: uid(),
    subject: cleanSubject,
    title: cleanTitle,
    createdAt: Date.now(),
    done: false,
    doneAt: null,
  };
  state.pendingLessons.push(record);
  touchRecord("pendingLessons", record.id);
  saveState();
  return record;
}

function togglePendingLesson(id = "") {
  const record = activeRecords(state.pendingLessons || []).find((r) => r.id === id);
  if (!record) return false;
  const becomingDone = !record.done;
  updateRecord("pendingLessons", id, {
    done: becomingDone,
    doneAt: becomingDone ? Date.now() : null,
  });
  saveState();
  return becomingDone;
}

// Aulas marcadas como assistidas ha mais de PENDING_LESSON_DONE_RETENTION_MS
// viram tombstone (somem da UI e propagam a exclusao no merge) — nao encarece o cache.
function expirePendingLessons(target = state, now = Date.now()) {
  const cutoff = now - PENDING_LESSON_DONE_RETENTION_MS;
  const list = target.pendingLessons;
  if (!Array.isArray(list) || !list.length) return false;
  let changed = false;
  list.forEach((record) => {
    if (
      record
      && typeof record === "object"
      && !record.deletedAt
      && record.done
      && isFiniteTimestamp(record.doneAt)
      && record.doneAt < cutoff
    ) {
      record.updatedAt = now;
      record.deletedAt = now;
      changed = true;
    }
  });
  if (changed) {
    target.mergeStamps ||= {};
    target.mergeStamps.pendingLessons = Math.max(target.mergeStamps.pendingLessons || 0, now);
  }
  return changed;
}

function openQuestionCardMenu(row, anchor = null) {
  const questionId = row.dataset.questionId || "";
  if (!questionSelectedRowIds.includes(questionId)) questionSelectedRowIds = [questionId];
  const selected = questionSelectedRowIds
    .map((id) => (state.questions || []).find((item) => item.id === id))
    .filter(Boolean);
  const single = selected.length === 1 ? selected[0] : null;
  closePlannerCardMenu();

  const buttons = [
    single ? `<button type="button" data-action="${isQuestionGroup(single) ? "edit-question-group" : "open-question-edit"}" data-id="${escapeAttribute(single.id)}">Editar</button>` : "",
    canGroupQuestionSelection() ? '<button type="button" data-action="group-question-selection">Agrupar em lista</button>' : "",
    canAddQuestionSelectionToGroup() ? '<button type="button" data-action="add-question-to-group">Adicionar a um grupo</button>' : "",
    canUngroupQuestionSelection() ? `<button type="button" data-action="ungroup-question-selection" data-id="${escapeAttribute(single.id)}">Desagrupar</button>` : "",
    canRemoveQuestionSelectionFromGroup() ? '<button type="button" data-action="remove-question-from-group">Remover do grupo</button>' : "",
    single ? `<button type="button" data-action="delete-question" data-id="${escapeAttribute(single.id)}">Excluir</button>` : "",
  ].filter(Boolean);
  if (!buttons.length) return;
  openCardMenu({ buttons, anchor: anchor || row });
}

function handleInlineEditKey(event) {
  const element = event.target.closest("[data-edit-record][contenteditable='true']");
  if (element) {
    if (event.key === "Escape") {
      event.preventDefault();
      cancelInlineEdit(element);
      return;
    }

    if (event.key === "Tab") {
      event.preventDefault();
      saveInlineEdit(element);
      focusNextEditable(element, "[data-edit-record]");
      return;
    }

    if (event.key === "Enter" && !event.shiftKey && element.dataset.type !== "html" && element.dataset.type !== "textarea") {
      event.preventDefault();
      saveInlineEdit(element);
      return;
    }

    if ((event.key === "Enter" && event.ctrlKey) || (event.key === "Enter" && event.metaKey)) {
      event.preventDefault();
      saveInlineEdit(element);
    }
    return;
  }

  const customElement = event.target.closest("[data-edit-key][contenteditable='true']");
  if (!customElement || !isInlineEditableKey(customElement.dataset.editKey)) return;

  if (event.key === "Escape") {
    event.preventDefault();
    cancelCustomInlineEdit(customElement);
    return;
  }

  if (event.key === "Tab") {
    event.preventDefault();
    saveCustomInlineEdit(customElement);
    focusNextEditable(customElement, "[data-edit-key]");
    return;
  }

  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    saveCustomInlineEdit(customElement);
  }
}

function focusNextEditable(currentElement, selector) {
  const editables = [...document.querySelectorAll(selector)]
    .filter((el) => el.offsetParent !== null);
  const idx = editables.indexOf(currentElement);
  for (let i = idx + 1; i < editables.length; i++) {
    if (!editables[i].isEqualNode(currentElement)) {
      editables[i].scrollIntoView({ behavior: "smooth", block: "nearest" });
      if (editables[i].matches("[data-edit-record]")) {
        startInlineEdit(editables[i]);
      } else {
        startCustomInlineEdit(editables[i]);
      }
      return;
    }
  }
}

function renderBreakdown() {
  const container = document.querySelector("#subjectBreakdown");
  if (!container) return;
  const filter = dashboardSubjectFilter();
  const visibleQuestions = dashboardQuestions();
  const breakdownSubjects = filter === "all" ? dashboardSubjects() : [filter];
  const totals = breakdownSubjects
    .map((subject) => {
      const items = visibleQuestions.filter((item) => item.subject === subject);
      const questions = items.reduce((sum, item) => sum + item.questions, 0);
      const correct = items.reduce((sum, item) => sum + item.correct, 0);
      return { subject, questions, accuracy: pct(correct, questions) };
    })
    .filter((item) => item.questions > 0);

  if (!totals.length) {
    container.innerHTML = '<div class="empty">Sem questões registradas ainda.</div>';
    return;
  }

  const max = Math.max(...totals.map((item) => item.questions));
  container.innerHTML = totals
    .map(
      (item) => `
        <div class="subject-row">
          <strong>${escapeHtml(item.subject)}</strong>
          <div class="subject-bar">
            <span style="width:${Math.round((item.questions / max) * 100)}%;background:${colors[item.subject] || "var(--yellow)"}"></span>
          </div>
          <span>${item.questions} | ${item.accuracy}%</span>
        </div>
      `,
    )
    .join("");
}

function renderUpcomingTasks() {
  const container = document.querySelector("#upcomingTasks");
  if (!container) return;
  const todayValue = dashboardTodayValue();
  const tasks = [...state.planner]
    .filter((item) => plannerItemVisible(item))
    .filter((item) => matchesSubject(item, dashboardSubjectFilter(), "Geral"))
    .filter((item) => taskStatus(item) !== "done")
    .filter((item) => plannerDateValue(item) >= todayValue)
    .sort((a, b) => {
      const dateCompare = plannerDateValue(a).localeCompare(plannerDateValue(b));
      if (dateCompare !== 0) return dateCompare;
      return String(a.time || "").localeCompare(String(b.time || ""));
    })
    .slice(0, 5);

  if (!tasks.length) {
    container.innerHTML = '<div class="empty">Nenhuma tarefa aberta nas próximas datas.</div>';
    return;
  }

  container.innerHTML = tasks
    .map(
      (item) => `
        <article class="item">
          <div class="item-head">
            <strong data-edit-record="planner" data-id="${escapeAttribute(item.id)}" data-field="title" data-type="text">${escapeHtml(plannerCardHeading(item))}</strong>
            <span class="pill" data-edit-record="planner" data-id="${escapeAttribute(item.id)}" data-field="type" data-type="text">${escapeHtml(item.type)}</span>
          </div>
          <small>
            <span data-edit-record="planner" data-id="${escapeAttribute(item.id)}" data-field="date" data-type="date">${formatDate(plannerDateValue(item))}</span>
           ·
            <span>${escapeHtml(plannerSubjectsLabel(item))}</span>
          </small>
        </article>
      `,
    )
    .join("");
}

function dashboardBreakdownSnapshot() {
  const filter = dashboardSubjectFilter();
  const visibleQuestions = dashboardQuestions();
  const breakdownSubjects = filter === "all" ? dashboardSubjects() : [filter];
  return breakdownSubjects
    .map((subject) => {
      const items = visibleQuestions.filter((item) => item.subject === subject);
      const questions = items.reduce((sum, item) => sum + item.questions, 0);
      const correct = items.reduce((sum, item) => sum + item.correct, 0);
      return {
        subject,
        questions,
        accuracy: pct(correct, questions),
        color: colors[subject] || "var(--yellow)",
      };
    })
    .filter((item) => item.questions > 0);
}

/* =====================================================
   PLANNER
   Criacao, agrupamento, drag/drop, kanban, calendario e tabela.
===================================================== */
function renderPlannerBuilderPanel(extraClass = "") {
  if (!plannerBuilderOpen) return "";
  return `
    <section class="planner-builder ${extraClass}">
      <div class="planner-builder-head">
        <div>
          <strong>${plannerBuilderType ? plannerBuilderType : "Selecione o tipo"}</strong>
          <small>${formatDate(plannerBuilderDate || localDateValue())}</small>
        </div>
        <button class="icon-button" data-action="close-planner-builder" type="button" aria-label="Fechar">×</button>
      </div>
      ${
        plannerBuilderType
          ? renderPlannerTypeForm(plannerBuilderType)
          : `<div class="planner-type-grid">${plannerBlockTypes.map(renderPlannerTypeButton).join("")}</div>`
      }
    </section>
  `;
}

function renderPlannerTypeButton(type) {
  return `
    <button class="planner-type-card" data-action="select-planner-block-type" data-type="${escapeAttribute(type.id)}" type="button">
      ${type.icon ? `<img src="${escapeAttribute(type.icon)}" alt="" />` : '<span class="planner-type-initial">+</span>'}
      <strong>${escapeHtml(type.id)}</strong>
    </button>
  `;
}

function createPlannerBlock(form, data) {
  const type = form.dataset.type;
  state.planner ||= [];
  const base = {
    id: uid(),
    date: data.date || localDateValue(),
    type,
    done: false,
    status: "todo",
  };
  if (state.plannerTimeEnabled && data.time) {
    const endTime = data.endTime || defaultPlannerEndTime(data.time);
    if (!isPlannerEndAfterStart(data.time, endTime)) {
      alert("O horário final deve ser depois do horário inicial.");
      return false;
    }
    base.time = data.time;
    base.endTime = endTime;
  }

  const createdItems = [];

  if (type === "Outro") {
    const title = String(data.title || "").trim();
    if (!title) {
      alert("Digite um título para o compromisso.");
      return false;
    }
    createdItems.push({
      ...base,
      subject: "Geral",
      title,
    });
  }

  if (["Teoria", "Revisão"].includes(type)) {
    const selectedRefs = plannerSelectedMaterialValues(form);
    const creatingNew = data.materialRef === "__new__" || plannerNewMaterialFieldsUsed(form, data);
    let materials;
    if (creatingNew) {
      const material = createMaterialReference(data);
      if (!material) {
        alert(plannerNewMaterialMissingFieldMessage(data));
        return false;
      }
      materials = [material];
    } else {
      materials = selectedRefs.map((ref) => findMaterialReference(ref, type)).filter(Boolean);
    }
    if (!materials.length) {
      alert("Selecione ou crie um conteúdo de Matérias.");
      return false;
    }
    materials.forEach((material, index) => {
      createdItems.push({
        ...base,
        id: index === 0 ? base.id : uid(),
        subject: material.subject,
        title: type === "Teoria"
          ? plannerTheoryLabel(material)
          : plannerMaterialLabel(material),
        materialRef: {
          lessonId: material.id,
          lessonIds: material.lessonIds || [material.id],
          subject: material.subject,
          cycle: material.cycle,
          group: material.group,
          topic: material.topic,
          step: plannerLessonStep(type),
        },
      });
    });
  }

  if (type === "Flash-card") {
    const deckName = String(data.deckName || "").trim();
    const reviewCards = Math.max(0, number(data.reviewCards));
    const newCards = Math.max(0, number(data.newCards));
    const totalCards = reviewCards + newCards;
    createdItems.push({
      ...base,
      subject: "Geral",
      title: plannerFlashcardTitle(deckName),
      flashcardData: {
        deckName,
        totalCards,
        reviewCards,
        newCards,
      },
    });
  }

  if (type === "Simulado") {
    const subjects = new FormData(form).getAll("subjects");
    const newSubject = String(data.newSubject || "").trim();
    if (newSubject) {
      ensureSubjectExists(newSubject);
      subjects.push(newSubject);
    }
    if (!subjects.length) {
      alert("Selecione ou crie pelo menos uma matéria.");
      return false;
    }
    const examDayValue = String(data.examDay || "").trim();
    createdItems.push({
      ...base,
      subject: subjects.length === 1 ? subjects[0] : "Geral",
      subjects,
      title: `Simulado ${examDayValue}`,
      mockData: {
        examDay: examDayValue,
        subjects,
        mode: "planned-only",
      },
    });
  }

  if (type === "Redação") {
    const essayRef = String(data.essayRef || "__new__").trim();
    const selectedEssay = essayRef === "__new__"
      ? null
      : activeRecords(state.essays || []).find((essay) => essay.id === essayRef && essayIsUngraded(essay));
    const theme = String(selectedEssay?.theme || data.theme || "").trim();
    if (!theme) {
      alert("Digite o tema da redação ou selecione uma redação sem nota.");
      return false;
    }

    const essay = selectedEssay || {
      id: uid(),
      date: base.date,
      theme,
      c1: 0,
      c2: 0,
      c3: 0,
      c4: 0,
      c5: 0,
      score: 0,
    };
    essay.date = base.date;
    essay.theme = theme;
    if (!selectedEssay) state.essays = [...(state.essays || []), essay];
    touchRecord("essays", essay.id);

    createdItems.push({
      ...base,
      subject: "Redação",
      title: `Redação: ${theme}`,
      essayData: { essayId: essay.id, theme },
    });
  }

  if (type === "Questões") {
    const selectedRefs = plannerSelectedQuestionValues(form).filter((value) => value !== "__new__");
    if (data.questionRef !== "__new__" && selectedRefs.length) {
      const questions = selectedRefs
        .map((id) => questionLeafRecords(state.questions || []).find((item) => item.id === id))
        .filter(Boolean);
      if (!questions.length) {
        alert("Selecione pelo menos uma lista existente.");
        return false;
      }
      questions.forEach((question, index) => {
        const plannerId = index === 0 ? base.id : uid();
        question.date = data.date || base.date || question.date || questionTodayValue();
        question.plannerId = plannerId;
        createdItems.push({
          ...base,
          id: plannerId,
          subject: question.subject || "Geral",
          title: `Questões: ${plannerQuestionDisplayLabel(question)}`,
          questionData: {
            questionId: question.id,
            content: question.content || "",
            subcontent: question.subcontent || "",
            totalQuestions: question.questions,
          },
        });
      });
    } else {
      const totalQuestions = Math.max(1, number(data.totalQuestions));
      const subject = String(data.newSubject || data.subject || "").trim();
      const content = String(data.content || "").trim();
      const subcontent = String(data.subcontent || "").trim();
      if (!subject || !content) {
        alert("Preencha a matéria e o conteúdo para criar a lista.");
        return false;
      }
      const question = addQuestionRecord({
        date: data.date || questionTodayValue(),
        subject,
        content,
        subcontent,
        questions: totalQuestions,
        correct: 0,
      }, {
        extra: { plannerId: base.id },
      });
      createdItems.push({
        ...base,
        subject,
        title: `Questões: ${plannerQuestionDisplayLabel(question)}`,
        questionData: {
          questionId: question.id,
          content,
          subcontent,
          totalQuestions,
        },
      });
    }
  }

  if (!createdItems.length) return false;

  if (createdItems.length <= 1) {
    state.planner = [...(state.planner || []), ...createdItems];
    createdItems.forEach((item) => touchRecord("planner", item.id));
    plannerSelectedTaskIds = createdItems.map((item) => item.id);
    plannerSelectionAnchorId = "";
  } else {
    openPlannerCreationModal({
      itemCount: createdItems.length,
      onSimple: () => {
        state.planner = [...(state.planner || []), ...createdItems];
        createdItems.forEach((item) => touchRecord("planner", item.id));
        plannerSelectedTaskIds = createdItems.map((item) => item.id);
        plannerSelectionAnchorId = "";
        plannerBuilderOpen = false;
        plannerBuilderType = "";
        plannerBuilderTime = "";
        plannerSelectedMaterialRefs = [];
        plannerSelectedQuestionRefs = [];
        plannerSelectedMaterialAnchor = "";
        plannerSelectedQuestionAnchor = "";
        rememberSmartForm(form, data);
        renderSectionNow("planner", { save: true, commit: true });
      },
      onGroup: () => {
        appendPlannerGroupFromItems(createdItems, {
          date: base.date,
          title: plannerGroupTitleForCreatedItems(createdItems, type) || `Grupo com ${createdItems.length} tarefas`,
        });
        plannerBuilderOpen = false;
        plannerBuilderType = "";
        plannerBuilderTime = "";
        plannerSelectedMaterialRefs = [];
        plannerSelectedQuestionRefs = [];
        plannerSelectedMaterialAnchor = "";
        plannerSelectedQuestionAnchor = "";
        rememberSmartForm(form, data);
        renderSectionNow("planner", { save: true, commit: true });
      },
    });
    return false;
  }

  plannerBuilderOpen = false;
  plannerBuilderType = "";
  plannerBuilderTime = "";
  plannerSelectedMaterialRefs = [];
  plannerSelectedQuestionRefs = [];
  plannerSelectedMaterialAnchor = "";
  plannerSelectedQuestionAnchor = "";
  rememberSmartForm(form, data);
  return true;
}

function questionDateFromValue(value) {
  const clean = String(value || "").trim();
  return clean ? parseFlexibleDate(clean) : questionTodayValue();
}

function addQuestionRecord(data = {}, options = {}) {
  const subject = canonicalSubject(String(options.subject ?? data.subject ?? "").trim() || "Geral");
  const content = String(options.content ?? data.content ?? "").trim();
  const subcontent = String(options.subcontent ?? data.subcontent ?? "").trim();
  const questions = Math.max(1, number(options.questions ?? data.questions ?? data.totalQuestions));
  const correct = Math.min(questions, Math.max(0, number(options.correct ?? data.correct)));
  const record = syncQuestionPerformance({
    id: options.id || data.id || uid(),
    date: String(options.date ?? data.date ?? "").trim() || questionTodayValue(),
    subject,
    content,
    subcontent,
    questions,
    correct,
    done: Boolean(options.done ?? data.done ?? false),
    completedAt: String(options.completedAt ?? data.completedAt ?? "").trim() || "",
    ...options.extra,
  });
  if (subject && subject !== "Geral") ensureSubjectExists(subject);
  rememberContent(subject, content);
  state.questions ||= [];
  state.questions = [...state.questions, record];
  touchRecord("questions", record.id);
  return record;
}

function removeQuestionRecord(id, options = {}) {
  const questionId = String(id || "");
  const item = activeRecords(state.questions || []).find((entry) => entry.id === questionId);
  if (!item) return false;

  if (isQuestionGroup(item)) {
    questionGroupChildren(item.id, { subjectFilter: "all" }).forEach((child) => {
      removeQuestionRecord(child.id, { confirm: false });
    });
    removeRecord("questions", questionId);
    questionSelectedRowIds = questionSelectedRowIds.filter((entryId) => entryId !== questionId);
    return true;
  }

  const linkedPlannerId = item.plannerId || activeRecords(state.planner || []).find((entry) => entry.questionData?.questionId === questionId)?.id || "";
  const groupId = item.questionGroupId || "";
  if (options.confirm !== false) return false;
  if (linkedPlannerId) removePlannerItem(linkedPlannerId, { confirm: false, closeMenu: false });
  removeRecord("questions", questionId);
  questionSelectedRowIds = questionSelectedRowIds.filter((entryId) => entryId !== questionId);
  if (groupId) syncQuestionGroupState(groupId);
  return true;
}

function plannerLessonStep(type) {
  if (type === "Teoria") return "theory";
  if (type === "Flash-card") return "practice";
  return "review";
}

function triggerTaskCompletionFlash() {
  document.body.classList.remove("task-complete-flash");
  void document.body.offsetWidth;
  document.body.classList.add("task-complete-flash");
  clearTimeout(taskCompletionFlashTimer);
  taskCompletionFlashTimer = setTimeout(() => {
    document.body.classList.remove("task-complete-flash");
  }, 1200);
}

function flashIfTaskCompleted(wasDone, isDone) {
  if (!wasDone && isDone) triggerTaskCompletionFlash();
}

function findCycleLessonById(lessonId) {
  for (const [key, lessons] of Object.entries(state.cycleLessons || {})) {
    const lesson = lessons.find((item) => item.id === lessonId);
    if (!lesson) continue;
    const [subject, cycle] = key.split("::");
    return { lesson, subject, cycle };
  }
  return null;
}

function setPlannerDone(item, done, options = {}) {
  if (isPlannerGroup(item)) {
    syncPlannerGroupState(item.id, { removeEmpty: false });
    return;
  }
  const wasDone = Boolean(item.done);
  const result = domainTogglePlannerItemDone(state.planner || [], item.id, done, {
    completedAt: options.completedAt || reviewDayValue(),
    todayValue: reviewDayValue,
  });
  state.planner = result.planner;
  const updated = state.planner.find((entry) => entry.id === item.id);
  if (!updated) return;
  syncPlannerItemToSource(updated);
  touchRecord("planner", updated.id);
  if (!options.skipGroupSync && updated.plannerGroupId) syncPlannerGroupState(updated.plannerGroupId, { removeEmpty: false });
  if (options.flash) flashIfTaskCompleted(wasDone, updated.done);
}

function syncPlannerItemToSource(item) {
  if (item.type === "Flash-card" && item.flashcardData) {
    const reviewCards = Math.max(0, number(item.flashcardData.reviewCards || 0));
    const newCards = Math.max(0, number(item.flashcardData.newCards || 0));
    item.flashcardData.reviewCards = reviewCards;
    item.flashcardData.newCards = newCards;
    item.flashcardData.totalCards = reviewCards + newCards;
    item.title = plannerFlashcardTitle(item.flashcardData.deckName);
  }
  if (item.materialRef?.lessonId || item.materialRef?.lessonIds?.length) {
    const step = item.materialRef.step || plannerLessonStep(item.type);
    const lessonIds = item.materialRef.lessonIds || [item.materialRef.lessonId];
    if (item.type === "Teoria" && !item.userEditedAt) {
      item.title = plannerTheoryLabel(item.materialRef);
    }
    if (item.type === "Revisão" && !item.userEditedAt) {
      item.title = plannerMaterialLabel(item.materialRef);
    }
    lessonIds.forEach((lessonId) => {
      const found = findCycleLessonById(lessonId);
      if (found?.lesson && step) {
        found.lesson[step] = Boolean(item.done);
        if (step === "theory" && item.done) found.lesson.theoryCompletedAt = item.completedAt || reviewDayValue();
        touchMapKey("cycleLessons", cycleKey(found.subject, found.cycle), getCycleLessons(found.subject, found.cycle));
      }
    });
  }

  if (item.questionData?.questionId) {
    const question = state.questions.find((entry) => entry.id === item.questionData.questionId);
    if (question) {
      question.date = item.date;
      question.subject = item.subject;
      if ("content" in item.questionData) question.content = item.questionData.content || question.content || "";
      if ("subcontent" in item.questionData) question.subcontent = item.questionData.subcontent || question.subcontent || "";
      question.questions = Math.max(1, number(item.questionData.totalQuestions || question.questions));
      question.done = Boolean(item.done);
      question.completedAt = item.done ? (item.completedAt || reviewDayValue()) : "";
      question.plannerDone = Boolean(item.done);
      touchRecord("questions", question.id);
    }
  }
  touchRecord("planner", item.id);
}

function markPlannerUserEdited(item) {
  if (!item || isPlannerGroup(item)) return;
  item.userEditedAt = Date.now();
}

function applyPlannerOriginPatch(item, patch = {}, originStamp = 0) {
  Object.assign(item, applyProtectedPlannerFields(item, patch, originStamp));
}

function setQuestionDone(item, done, options = {}) {
  if (!item || isQuestionGroup(item)) return;
  const nextDone = Boolean(done);
  item.done = nextDone;
  item.completedAt = nextDone ? (options.completedAt || reviewDayValue()) : "";
  const linkedPlanner = options.skipPlannerSync ? null : linkedPlannerQuestionItem(item);
  if (linkedPlanner) {
    setPlannerDone(linkedPlanner, nextDone, {
      ...options,
      completedAt: item.completedAt || options.completedAt || reviewDayValue(),
      skipGroupSync: false,
    });
    const updatedPlanner = state.planner.find((entry) => entry.id === linkedPlanner.id);
    item.done = Boolean(updatedPlanner?.done);
    item.completedAt = updatedPlanner?.done ? (updatedPlanner.completedAt || item.completedAt) : "";
  }
  item.plannerDone = Boolean(item.done);
  touchRecord("questions", item.id);
  syncPlannerLinks();
  if (item.questionGroupId) syncQuestionGroupState(item.questionGroupId, { removeEmpty: false });
}

function syncPlannerLinksFromOrigins() {
  normalizePlannerGroups();
  state.planner = (state.planner || []).filter((item) => {
    if (!item.questionData?.questionId) return true;
    return state.questions.some((entry) => entry.id === item.questionData.questionId);
  });

  (state.planner || []).forEach((item) => {
    if (isPlannerGroup(item)) {
      syncPlannerGroupState(item.id, { removeEmpty: false });
      return;
    }
    if (item.materialRef?.lessonId || item.materialRef?.lessonIds?.length) {
      const step = item.materialRef.step || plannerLessonStep(item.type);
      const lessonIds = item.materialRef.lessonIds || [item.materialRef.lessonId];
      const lessons = lessonIds.map((lessonId) => findCycleLessonById(lessonId)?.lesson).filter(Boolean);
      item.materialRef.lessonIds = lessons.map((lesson) => lesson.id);
      if (item.materialRef.lessonIds.length) item.materialRef.lessonId = item.materialRef.lessonIds[0];
      const originStamp = originUpdatedAt(...lessons);
      const patch = {};
      if (item.type === "Teoria") patch.title = plannerTheoryLabel(item.materialRef);
      if (item.type === "Revisão") patch.title = plannerMaterialLabel(item.materialRef);
      if (lessons.length && step) {
        patch.done = lessons.every((lesson) => Boolean(lesson[step]));
        patch.status = patch.done ? "done" : item.status === "done" ? "todo" : item.status || "todo";
      }
      applyPlannerOriginPatch(item, patch, originStamp);
    }

    if (item.questionData?.questionId) {
      const question = state.questions.find((entry) => entry.id === item.questionData.questionId);
      if (question) {
        const originStamp = originUpdatedAt(question);
        applyPlannerOriginPatch(item, {
          date: question.date || item.date,
          subject: question.subject,
          title: `Questões: ${plannerQuestionDisplayLabel(question)}`,
          done: Boolean(question.done),
          completedAt: question.done ? (question.completedAt || item.completedAt || reviewDayValue()) : "",
          status: question.done ? "done" : item.status === "done" ? "todo" : item.status || "todo",
        }, originStamp);
        item.questionData.content = question.content || "";
        item.questionData.subcontent = question.subcontent || "";
        item.questionData.totalQuestions = question.questions;
      }
    }

    if (item.essayData?.essayId) {
      const essay = activeRecords(state.essays || []).find((entry) => entry.id === item.essayData.essayId);
      if (essay) {
        const theme = essay.theme || item.essayData.theme || "Redação";
        item.date = essay.date || item.date;
        item.title = `Redação: ${theme}`;
        item.subject = "Redação";
        item.essayData.theme = theme;
      }
    }
  });
}

function syncPlannerLinks() {
  syncPlannerLinksFromOrigins();
}

function renderPlannerTypeForm(type) {
  if (["Teoria", "Revisão"].includes(type)) return renderPlannerMaterialForm(type);
  if (type === "Flash-card") return renderPlannerFlashcardForm();
  if (type === "Simulado") return renderPlannerMockForm();
  if (type === "Questões") return renderPlannerQuestionsForm();
  if (type === "Redação") return renderPlannerEssayForm();
  if (type === "Outro") return renderPlannerOtherForm();
  return "";
}

function renderPlannerBaseFields(extra = "") {
  return `
    <input name="date" type="date" value="${plannerBuilderDate || localDateValue()}" required />
    ${state.plannerTimeEnabled ? renderPlannerTimeFields() : ""}
    ${extra}
    <button type="submit">Salvar bloco</button>
  `;
}

function renderPlannerTimeFields(selectedStart = plannerBuilderTime || "05:00", selectedFinish = "") {
  const selected = selectedStart || "05:00";
  const selectedEnd = selectedFinish || defaultPlannerEndTime(selected);
  return `
    <div class="planner-time-range">
      <select name="time" required aria-label="Horário inicial">
        ${plannerTimeSlots().map((slot) => `<option value="${slot.start}" ${slot.start === selected ? "selected" : ""}>${slot.start}</option>`).join("")}
      </select>
      <span>até</span>
      <select name="endTime" required aria-label="Horário final">
        ${plannerEndTimeOptions().map((time) => `<option value="${time}" ${time === selectedEnd ? "selected" : ""}>${time}</option>`).join("")}
      </select>
    </div>
  `;
}

function plannerTimeSlots() {
  const slots = [];
  for (let minutes = 5 * 60; minutes < 24 * 60; minutes += 30) {
    const start = minutesToTime(minutes);
    const end = minutesToTime(minutes + 30);
    slots.push({ start, end, label: `${start}-${end}` });
  }
  return slots;
}

function minutesToTime(totalMinutes) {
  const minutesInDay = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const hours = Math.floor(minutesInDay / 60);
  const minutes = minutesInDay % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function plannerEndTimeOptions() {
  const options = [];
  for (let minutes = (5 * 60) + 30; minutes <= 24 * 60; minutes += 30) {
    options.push(minutesToTime(minutes));
  }
  return options;
}

function defaultPlannerEndTime(start) {
  return minutesToTime(plannerTimeToMinutes(start) + 30);
}

function plannerTimeToMinutes(time, isEnd = false) {
  const [hours, minutes] = String(time || "").split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;
  if (isEnd && hours === 0 && minutes === 0) return 24 * 60;
  return hours * 60 + minutes;
}

function isPlannerEndAfterStart(start, end) {
  return plannerTimeToMinutes(end, true) > plannerTimeToMinutes(start);
}

function syncStudySessionForm(form, source = "") {
  if (!form?.classList?.contains("study-session-form")) return;
  const startInput = form.elements.startTime;
  const endInput = form.elements.endTime;
  const durationInput = form.elements.durationMinutes;
  if (!startInput || !endInput || !durationInput) return;

  const start = plannerTimeToMinutes(startInput.value);
  let duration = Math.max(1, Math.round(number(durationInput.value || 0)));

  if (source === "endTime") {
    const end = plannerTimeToMinutes(endInput.value, true);
    if (end > start) durationInput.value = String(Math.max(1, end - start));
    return;
  }

  durationInput.value = String(duration);
  const nextEnd = start + duration;
  if (nextEnd >= 24 * 60) return;
  endInput.value = minutesToTime(nextEnd);
}

function renderPlannerOtherForm() {
  return `
    <form class="planner-builder-form" data-action="create-planner-block" data-type="Outro" data-smart-scope="planner-builder-other">
      ${renderPlannerBaseFields('<input name="title" placeholder="Título do compromisso" required data-smart-field="title" />')}
    </form>
  `;
}

function renderPlannerMaterialForm(type) {
  const options = materialReferenceOptions(type);
  const selectedValue = options[0]?.id || "__new__";
  const creatingNew = selectedValue === "__new__";
  return `
    <form class="planner-builder-form" data-action="create-planner-block" data-type="${escapeAttribute(type)}" data-smart-scope="planner-builder-material">
      ${renderPlannerBaseFields(`
        <input type="hidden" name="materialRef" value="${escapeAttribute(selectedValue)}" />
        ${renderPlannerMaterialPicker(options, selectedValue)}
        <details class="new-material-fields ${creatingNew ? "is-open" : ""}" ${creatingNew ? "open" : ""}>
          <summary>Criar novo conteúdo</summary>
          <select name="subject" data-smart-field="subject" data-smart-autofill="last">
            ${getSubjects().map((subject) => `<option value="${escapeAttribute(subject)}">${escapeHtml(subject)}</option>`).join("")}
            <option value="__new__">+ Criar nova matéria</option>
          </select>
          <input class="hidden" name="newSubject" placeholder="Nome da matéria nova" data-smart-field="newSubject" data-smart-context="subject" data-smart-autofill="contextual" />
          ${renderMaterialCycleSelect(getSubjects()[0] || "")}
          <input class="hidden" name="cycle" placeholder="Novo ciclo" data-smart-field="cycle" data-smart-context="subject" data-smart-autofill="contextual" />
          ${renderMaterialGroupSelect(getSubjects()[0] || "", getSubjectCycles(getSubjects()[0] || "")[0] || "")}
          <input class="hidden" name="group" placeholder="Novo conteúdo" data-smart-field="group" data-smart-context="subject,cycleSelect" data-smart-autofill="contextual" />
          <input name="topic" placeholder="Aula/tópico" data-smart-field="topic" data-smart-context="subject,cycleSelect,groupSelect,group" />
        </details>
      `)}
    </form>
  `;
}

function renderPlannerFlashcardForm() {
  return `
    <form class="planner-builder-form" data-action="create-planner-block" data-type="Flash-card" data-smart-scope="planner-builder-flashcard">
      ${renderPlannerBaseFields(`
        <input name="deckName" placeholder="Nome do deck (opcional)" data-smart-field="deckName" />
        ${renderPlannerFlashcardStatFields()}
      `)}
    </form>
  `;
}

function renderPlannerFlashcardStatFields(values = {}) {
  const reviewCards = Math.max(0, number(values.reviewCards || 0));
  const newCards = Math.max(0, number(values.newCards || 0));
  const totalCards = reviewCards + newCards;
  return `
    <div class="planner-flashcard-stats">
      <label class="planner-flashcard-field">
        <span>Total de cards</span>
        <input name="totalCards" type="number" min="0" step="1" value="${escapeAttribute(String(totalCards))}" readonly tabindex="-1" aria-readonly="true" />
      </label>
      <label class="planner-flashcard-field">
        <span>Revisão</span>
        <input name="reviewCards" type="number" min="0" step="1" value="${escapeAttribute(String(reviewCards))}" required />
      </label>
      <label class="planner-flashcard-field">
        <span>Novo</span>
        <input name="newCards" type="number" min="0" step="1" value="${escapeAttribute(String(newCards))}" required />
      </label>
    </div>
  `;
}

function plannerFlashcardDeckNameFromTitle(title = "") {
  return String(title).replace(/^Flash-card:\s*/i, "").replace(/^Flash-card$/i, "").trim();
}

function plannerFlashcardCounts(item = {}) {
  const reviewCards = Math.max(0, number(item.flashcardData?.reviewCards || 0));
  const newCards = Math.max(0, number(item.flashcardData?.newCards || 0));
  return {
    reviewCards,
    newCards,
    totalCards: reviewCards + newCards,
  };
}

function plannerFlashcardTitle(deckName = "") {
  const trimmed = String(deckName || "").trim();
  return trimmed ? `Flash-card: ${trimmed}` : "Flash-card";
}

function plannerMaterialLabel(materialRef = {}) {
  return materialRef.group || materialRef.topic || "Conteúdo";
}

function plannerTheoryLabel(materialRef = {}) {
  return materialRef.topic || materialRef.group || "Tópico";
}

function plannerCardHeading(item = {}) {
  if (item.materialRef && item.type === "Teoria") {
    return plannerTheoryLabel(item.materialRef);
  }
  if (item.materialRef && item.type === "Revisão") {
    return plannerMaterialLabel(item.materialRef);
  }
  if (item.type === "Questões") {
    return `Questões: ${plannerQuestionDisplayLabel(item)}`;
  }
  if (item.type === "Redação") {
    return item.essayData?.theme || String(item.title || "Redação").replace(/^Redação:\s*/i, "") || "Redação";
  }
  if (item.type === "Flash-card") {
    if (item.flashcardData) return plannerFlashcardTitle(item.flashcardData.deckName);
    return "Flash-card";
  }
  return item.title || "Tarefa do planner";
}

function plannerTypeClass(item = {}) {
  const type = normalizeHeader(item.type || "");
  if (type.includes("flash-card") || type.includes("flash card")) return "planner-type-flashcard";
  if (type.includes("revisao")) return "planner-type-review";
  return "";
}

function updatePlannerFlashcardTotal(form) {
  if (!form) return;
  const totalInput = form.querySelector('input[name="totalCards"]');
  const reviewInput = form.querySelector('input[name="reviewCards"]');
  const newInput = form.querySelector('input[name="newCards"]');
  if (!totalInput || !reviewInput || !newInput) return;
  totalInput.value = String(Math.max(0, number(reviewInput.value)) + Math.max(0, number(newInput.value)));
}

function plannerSelectedMaterialValues(form) {
  const selected = plannerSelectedMaterialRefs.filter(Boolean);
  if (selected.length) return selected;
  const input = form?.querySelector('input[name="materialRef"]');
  return input?.value ? [input.value] : [];
}

function plannerNewMaterialFieldsUsed(form, data = {}) {
  const fields = form?.querySelector(".new-material-fields");
  if (!fields) return false;
  const isOpen = fields.hasAttribute("open") || fields.classList.contains("is-open");
  if (!isOpen) return false;
  const valueOf = (name) => String(data[name] ?? fields.querySelector(`[name="${name}"]`)?.value ?? "").trim();
  return Boolean(
    valueOf("newSubject") ||
    valueOf("cycle") ||
    valueOf("group") ||
    valueOf("topic") ||
    fields.querySelector('select[name="subject"]')?.value === "__new__",
  );
}

function plannerSelectedQuestionValues(form) {
  const selected = plannerSelectedQuestionRefs.filter(Boolean);
  if (selected.length) return selected;
  const input = form?.querySelector('input[name="questionRef"]');
  return input?.value ? [input.value] : [];
}

function plannerPickerOptionOrder(form, selector) {
  return [...(form?.querySelectorAll(selector) || [])]
    .map((option) => option.dataset.value || "")
    .filter((value) => value && value !== "__new__" && value !== "__new_subject__");
}

function plannerPickerRangeSelection(ordered, anchorId, targetId) {
  const anchorIndex = anchorId ? ordered.indexOf(anchorId) : -1;
  const targetIndex = ordered.indexOf(targetId);
  if (anchorIndex === -1 || targetIndex === -1) return [];
  const [from, to] = anchorIndex <= targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex];
  return ordered.slice(from, to + 1);
}

function isPlannerGroup(item = {}) {
  return sharedIsPlannerGroup(item);
}

function plannerGroupChildren(groupId = "") {
  return sharedPlannerGroupChildren(activeRecords(state.planner || []), groupId, comparePlannerVisualOrder);
}

function plannerTopLevelItems() {
  return sharedPlannerTopLevelItems(activeRecords(state.planner || []));
}

function plannerCountableItems(items = []) {
  return sharedPlannerCountableItems(activeRecords(items));
}

function plannerGroupSubjectList(item = {}) {
  if (Array.isArray(item.subjects) && item.subjects.length) return [...item.subjects];
  if (item.subject) return [item.subject];
  const subjectsList = plannerGroupChildren(item.id)
    .map((entry) => entry.subject || "Geral")
    .filter(Boolean);
  return [...new Set(subjectsList)];
}

function plannerGroupProgress(item = {}) {
  const children = plannerGroupChildren(item.id);
  const total = children.length;
  const completed = children.filter((entry) => entry.done).length;
  return { total, completed, children };
}

function plannerGroupStatus(children = []) {
  return sharedPlannerGroupStatus(children, taskStatus);
}

function syncPlannerGroupMetadata(group) {
  if (!isPlannerGroup(group)) return;
  const children = plannerGroupChildren(group.id);
  group.subjects = [...new Set(children.map((item) => item.subject || "Geral").filter(Boolean))];
  group.subject = group.subjects.length === 1 ? group.subjects[0] : "Geral";
  if (!group.title) group.title = "Novo grupo";
  if (!children.length) return;
  const timedChildren = children.filter((item) => item.time);
  if (timedChildren.length) {
    const orderedTimes = [...timedChildren].sort((a, b) => plannerTimeToMinutes(a.time || "00:00") - plannerTimeToMinutes(b.time || "00:00"));
    group.time = orderedTimes[0].time || "";
    const latest = [...timedChildren].sort(
      (a, b) => plannerTimeToMinutes(b.endTime || defaultPlannerEndTime(b.time || "05:00"), true)
        - plannerTimeToMinutes(a.endTime || defaultPlannerEndTime(a.time || "05:00"), true),
    )[0];
    group.endTime = latest?.endTime || (latest?.time ? defaultPlannerEndTime(latest.time) : "");
  } else {
    delete group.time;
    delete group.endTime;
  }
}

function syncPlannerGroupState(groupId = "", options = {}) {
  const group = (state.planner || []).find((item) => item.id === groupId && isPlannerGroup(item));
  if (!group) return;
  const { children } = plannerGroupProgress(group);
  if (!children.length) {
    if (options.removeEmpty !== false) {
      state.planner = (state.planner || []).filter((item) => item.id !== group.id);
      plannerSelectedTaskIds = plannerSelectedTaskIds.filter((id) => id !== group.id);
    }
    return;
  }
  syncPlannerGroupMetadata(group);
  delete group.done;
  delete group.completedAt;
  delete group.status;
  delete group.date;
  touchRecord("planner", group.id);
}

function normalizePlannerGroups() {
  state.planner ||= [];
  const ids = new Set(state.planner.map((item) => item.id));
  state.planner.forEach((item) => {
    if (!item || typeof item !== "object") return;
    if (String(item.plannerGroupId || "").trim() && !ids.has(item.plannerGroupId)) {
      delete item.plannerGroupId;
    }
    if (isPlannerGroup(item)) {
      item.type = "Grupo";
      item.collapsed = Boolean(item.collapsed);
      item.done = false;
      delete item.completedAt;
      item.title = String(item.title || "Novo grupo").trim() || "Novo grupo";
    }
  });
  plannerTopLevelItems()
    .filter((item) => isPlannerGroup(item))
    .forEach((item) => syncPlannerGroupState(item.id));
}

function canGroupPlannerSelection() {
  const selected = plannerSelectedTaskIdsInActiveView()
    .map((id) => (state.planner || []).find((item) => item.id === id))
    .filter(Boolean);
  if (selected.length < 2) return false;
  if (selected.some((item) => isPlannerGroup(item))) return false;
  if (selected.some((item) => String(item.plannerGroupId || "").trim())) return false;
  const dates = [...new Set(selected.map((item) => item.date || ""))];
  return dates.length === 1;
}

function canUngroupPlannerSelection() {
  const selectedIds = plannerSelectedTaskIdsInActiveView();
  if (selectedIds.length !== 1) return false;
  const selected = (state.planner || []).find((item) => item.id === selectedIds[0]);
  return Boolean(selected && isPlannerGroup(selected));
}

function createPlannerGroupFromSelection() {
  if (!canGroupPlannerSelection()) {
    alert("Selecione pelo menos 2 tarefas soltas do mesmo dia para agrupar.");
    return false;
  }
  const selected = plannerSelectedTaskIdsInActiveView()
    .map((id) => (state.planner || []).find((item) => item.id === id))
    .filter(Boolean)
    .sort(comparePlannerVisualOrder);
  const group = {
    id: uid(),
    title: `Grupo com ${selected.length} tarefas`,
      type: "Grupo",
      isGroup: true,
      collapsed: false,
  };
  state.planner = [...(state.planner || []), group];
  touchRecord("planner", group.id);
  selected.forEach((item) => {
    item.plannerGroupId = group.id;
    touchRecord("planner", item.id);
  });
  syncPlannerGroupState(group.id, { removeEmpty: false });
  plannerSelectedTaskIds = [group.id];
  plannerSelectionAnchorId = "";
  closePlannerCardMenu();
  return true;
}

function ungroupPlannerGroup(groupId = "") {
  const group = (state.planner || []).find((item) => item.id === groupId && isPlannerGroup(item));
  if (!group) return false;
  plannerGroupChildren(group.id).forEach((item) => {
    delete item.plannerGroupId;
  });
  state.planner = (state.planner || []).filter((item) => item.id !== group.id);
  plannerSelectedTaskIds = [];
  plannerSelectionAnchorId = "";
  closePlannerCardMenu();
  return true;
}

function syncPlannerMaterialSelection(form, selectedValues = [], anchorValue = "") {
  if (!form) return;
  const uniqueValues = [...new Set(selectedValues.filter(Boolean))];
  plannerSelectedMaterialRefs = uniqueValues;
  const input = form.querySelector('input[name="materialRef"]');
  if (input) input.value = anchorValue || uniqueValues[0] || "";
  form.querySelectorAll('[data-action="select-planner-material"]').forEach((option) => {
    const isSelected = uniqueValues.includes(option.dataset.value || "");
    option.classList.toggle("active", isSelected);
    option.setAttribute("aria-pressed", String(isSelected));
  });
  const newFields = form.querySelector(".new-material-fields");
  const creatingNew = uniqueValues.includes("__new__") || (!uniqueValues.length && (input?.value || "") === "__new__");
  if (newFields) {
    if (creatingNew) newFields.setAttribute("open", "open");
    else newFields.removeAttribute("open");
    newFields.classList.toggle("is-open", creatingNew);
  }
}

function plannerSelectionOrder() {
  const activeTaskIds = plannerActiveViewTaskIds();
  return [...(state.planner || [])]
    .filter((item) => activeTaskIds.has(item.id))
    .sort(comparePlannerVisualOrder);
}

function togglePlannerTaskSelection(id, options = {}) {
  if (!plannerCardInActiveView(id)) return;
  const append = typeof options === "boolean" ? options : Boolean(options.append);
  const range = typeof options === "boolean" ? false : Boolean(options.range);
  if (range) {
    // Shift+clique: seleciona o intervalo contíguo (na ordem visual do planner)
    // entre a âncora (último item marcado) e o item atual.
    const anchorId = plannerSelectionAnchorId || id;
    const ordered = plannerSelectionOrder();
    const anchorIndex = ordered.findIndex((entry) => entry.id === anchorId);
    const targetIndex = ordered.findIndex((entry) => entry.id === id);
    if (anchorIndex !== -1 && targetIndex !== -1) {
      const [from, to] = anchorIndex <= targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex];
      plannerSelectedTaskIds = ordered.slice(from, to + 1).map((entry) => entry.id);
      return;
    }
    // Sem âncora válida: comporta como toggle individual.
    const fallback = new Set(plannerSelectedTaskIdsInActiveView());
    if (fallback.has(id)) fallback.delete(id);
    else fallback.add(id);
    plannerSelectedTaskIds = [...fallback];
    plannerSelectionAnchorId = id;
    return;
  }
  const current = new Set(plannerSelectedTaskIdsInActiveView());
  if (!append) {
    plannerSelectedTaskIds = current.has(id) && current.size === 1 ? [] : [id];
    plannerSelectionAnchorId = plannerSelectedTaskIds[0] || "";
    return;
  }
  if (current.has(id)) current.delete(id);
  else current.add(id);
  plannerSelectedTaskIds = [...current];
  plannerSelectionAnchorId = id;
}

function plannerDragIds(id) {
  const selectedIds = plannerSelectedTaskIdsInActiveView();
  if (selectedIds.includes(id)) return selectedIds;
  return id ? [id] : [];
}

function plannerDragDataIds(event, fallbackId = "") {
  const transfer = event?.dataTransfer;
  if (!transfer) return [fallbackId].filter(Boolean);
  try {
    const raw = transfer.getData("text/planner-ids");
    const parsed = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed) && parsed.length) return parsed.filter(Boolean);
  } catch (err) {
    console.error("[plannerDragDataIds]", err);
  }
  const plainId = transfer.getData("text/plain") || fallbackId;
  return [plainId].filter(Boolean);
}

function hasPlannerDragData(event) {
  const types = [...(event?.dataTransfer?.types || [])];
  return types.includes("text/planner-ids");
}

function clearPlannerDragVisualState({ removePlaceholder = true } = {}) {
  document.body.classList.remove("planner-dragging");
  if (removePlaceholder) document.querySelector("[data-planner-drop-placeholder]")?.remove();
  document.querySelectorAll(
    "[data-task-dropzone].drag-over, [data-calendar-date].drag-over, [data-planner-group-card].drag-over, [data-planner-group-children].drag-over, [data-task-id].drop-before, [data-task-id].drop-after",
  ).forEach((element) => {
    element.classList.remove("drag-over", "drop-before", "drop-after");
    element.removeAttribute("data-placeholder");
  });
}

function plannerDropPlaceholder() {
  let placeholder = document.querySelector("[data-planner-drop-placeholder]");
  if (!placeholder) {
    placeholder = document.createElement("div");
    placeholder.className = "planner-drop-placeholder";
    placeholder.dataset.plannerDropPlaceholder = "true";
    placeholder.setAttribute("aria-hidden", "true");
  }
  return placeholder;
}

function plannerDraggedItems(ids = []) {
  return [...new Set(ids.filter(Boolean))]
    .map((id) => (state.planner || []).find((item) => item.id === id))
    .filter(Boolean);
}

function canMovePlannerItemsToDestination(ids = [], destination = {}) {
  const items = plannerDraggedItems(ids);
  if (!items.length || !destination?.kind) return false;
  if (destination.status === "overdue") return false;
  if (destination.kind === "date" && !destination.date) return false;
  if (destination.kind === "group") {
    const group = (state.planner || []).find((item) => item.id === destination.groupId && isPlannerGroup(item));
    if (!group) return false;
    if (items.some((item) => isPlannerGroup(item))) return false;
    if (ids.includes(group.id)) return false;
    const groupDate = plannerDateValue(group);
    if (groupDate && items.some((item) => plannerDateValue(item) && plannerDateValue(item) !== groupDate)) return false;
  }
  if (destination.beforeId && ids.includes(destination.beforeId)) return false;
  return true;
}

function plannerContainerCards(container, draggedIds = []) {
  return [...(container?.querySelectorAll("[data-task-id]") || [])]
    .filter((card) => !draggedIds.includes(card.dataset.taskId))
    .filter((card) => !card.matches("[data-planner-drop-placeholder]"));
}

function plannerInsertionBeforeId(container, clientY, draggedIds = []) {
  const cards = plannerContainerCards(container, draggedIds);
  let candidate = null;
  let closestOffset = Number.NEGATIVE_INFINITY;
  cards.forEach((card) => {
    const rect = card.getBoundingClientRect();
    const offset = clientY - rect.top - rect.height / 2;
    if (offset < 0 && offset > closestOffset) {
      closestOffset = offset;
      candidate = card;
    }
  });
  return candidate?.dataset.taskId || "";
}

function renderPlannerDropPlaceholder(container, beforeId = "", draggedIds = []) {
  if (!container) return;
  const placeholder = plannerDropPlaceholder();
  const beforeCard = beforeId
    ? [...container.querySelectorAll("[data-task-id]")].find((card) => card.dataset.taskId === beforeId)
    : null;
  placeholder.textContent = "";
  placeholder.style.minHeight = `${Math.max(56, Math.min(120, document.querySelector("[data-task-id].dragging")?.getBoundingClientRect().height || 72))}px`;
  if (placeholder.parentElement !== container) container.appendChild(placeholder);
  if (beforeCard && beforeCard.parentElement === container) container.insertBefore(placeholder, beforeCard);
  else container.appendChild(placeholder);

  container.querySelectorAll("[data-task-id]").forEach((card) => card.classList.remove("drop-before", "drop-after"));
  if (beforeCard && !draggedIds.includes(beforeId)) beforeCard.classList.add("drop-before");
  else {
    const cards = plannerContainerCards(container, draggedIds);
    cards.at(-1)?.classList.add("drop-after");
  }
}

function plannerGroupDropContainer(groupCard) {
  const children = groupCard?.querySelector("[data-planner-group-children]");
  if (!children || children.classList.contains("collapsed")) return groupCard;
  return children;
}

function plannerDropDestinationFromEvent(event, ids = []) {
  const draggedIds = [...new Set(ids.filter(Boolean))];
  const draggingGroup = plannerDraggedItems(draggedIds).some((item) => isPlannerGroup(item));
  const dropzone = event.target.closest("[data-task-dropzone]");
  if (dropzone) {
    const status = dropzone.dataset.taskDropzone || "";
    const beforeId = plannerInsertionBeforeId(dropzone, event.clientY, draggedIds);
    return { kind: "column", status, beforeId, container: dropzone, scopeDate: plannerTodayValue() };
  }

  const groupChildren = event.target.closest("[data-planner-group-children]");
  if (groupChildren && !draggingGroup) {
    const groupId = groupChildren.dataset.plannerGroupChildren || "";
    const beforeId = plannerInsertionBeforeId(groupChildren, event.clientY, draggedIds);
    return { kind: "group", groupId, beforeId, container: groupChildren };
  }

  const groupCard = event.target.closest("[data-planner-group-card]");
  if (groupCard && !draggedIds.includes(groupCard.dataset.plannerGroupCard || "") && !draggingGroup) {
    const children = plannerGroupDropContainer(groupCard);
    const beforeId = children ? plannerInsertionBeforeId(children, event.clientY, draggedIds) : "";
    return { kind: "group", groupId: groupCard.dataset.plannerGroupCard || "", beforeId, container: children || groupCard };
  }

  const calendarDay = event.target.closest("[data-calendar-date]");
  if (calendarDay) {
    const beforeId = plannerInsertionBeforeId(calendarDay, event.clientY, draggedIds);
    return {
      kind: "date",
      date: calendarDay.dataset.calendarDate || "",
      time: calendarDay.dataset.calendarTime || "",
      beforeId,
      container: calendarDay,
    };
  }

  return null;
}

function updatePlannerDropTarget(event, ids = []) {
  const destination = plannerDropDestinationFromEvent(event, ids);
  clearPlannerDragVisualState({ removePlaceholder: false });
  document.body.classList.add("planner-dragging");
  if (!canMovePlannerItemsToDestination(ids, destination)) {
    document.querySelector("[data-planner-drop-placeholder]")?.remove();
    return null;
  }
  destination.container?.classList.add("drag-over");
  if (destination.kind === "group") {
    document.querySelector(`[data-planner-group-card="${CSS.escape(destination.groupId)}"]`)?.classList.add("drag-over");
  }
  renderPlannerDropPlaceholder(destination.container, destination.beforeId, ids);
  return destination;
}

function plannerAutoScroll(event) {
  const now = performance.now();
  if (now - lastPlannerAutoScrollAt < 24) return;
  lastPlannerAutoScrollAt = now;
  const scrollParent = event.target.closest(".task-dropzone, .planner-group-children, .calendar-grid, .calendar-time-grid");
  const edge = 56;
  const speed = 18;
  if (scrollParent) {
    const rect = scrollParent.getBoundingClientRect();
    if (event.clientY < rect.top + edge) scrollParent.scrollTop -= speed;
    if (event.clientY > rect.bottom - edge) scrollParent.scrollTop += speed;
    return;
  }
  if (event.clientY < edge) window.scrollBy(0, -speed);
  if (event.clientY > window.innerHeight - edge) window.scrollBy(0, speed);
}

function plannerIdsWithGroupChildren(ids = []) {
  const expanded = new Set();
  ids.filter(Boolean).forEach((id) => {
    expanded.add(id);
    const item = (state.planner || []).find((entry) => entry.id === id);
    if (item && isPlannerGroup(item)) {
      plannerGroupChildren(item.id).forEach((child) => expanded.add(child.id));
    }
  });
  return [...expanded];
}

function syncPlannerGroupsForDraggedIds(ids = []) {
  const groupIds = new Set();
  plannerIdsWithGroupChildren(ids).forEach((id) => {
    const item = (state.planner || []).find((entry) => entry.id === id);
    if (!item) return;
    if (isPlannerGroup(item)) groupIds.add(item.id);
    if (String(item.plannerGroupId || "").trim()) groupIds.add(item.plannerGroupId);
  });
  groupIds.forEach((groupId) => syncPlannerGroupState(groupId, { removeEmpty: false }));
}

function plannerSortValue(item = {}) {
  return Number.isFinite(Number(item.order)) ? Number(item.order) : 0;
}

function plannerScopeItemIds(destination = {}) {
  if (destination.kind === "group") {
    return plannerGroupChildren(destination.groupId).map((item) => item.id);
  }
  if (destination.kind === "column") {
    return plannerTopLevelItems()
      .filter((item) => plannerBoardStatus(item) === destination.status)
      .filter((item) => !destination.scopeDate || plannerDateValue(item) === destination.scopeDate)
      .sort(comparePlannerVisualOrder)
      .map((item) => item.id);
  }
  if (destination.kind === "date") {
    return plannerTopLevelItems()
      .filter((item) => plannerDateValue(item) === destination.date)
      .sort(comparePlannerVisualOrder)
      .map((item) => item.id);
  }
  return [];
}

function rewritePlannerOrderForDestination(destination = {}, movedIds = []) {
  const ids = [...new Set(movedIds.filter(Boolean))];
  const remaining = plannerScopeItemIds(destination).filter((id) => !ids.includes(id));
  const beforeIndex = destination.beforeId ? remaining.indexOf(destination.beforeId) : -1;
  const nextOrder = beforeIndex >= 0
    ? [...remaining.slice(0, beforeIndex), ...ids, ...remaining.slice(beforeIndex)]
    : [...remaining, ...ids];
  nextOrder.forEach((id, index) => {
    const item = (state.planner || []).find((entry) => entry.id === id);
    if (item) item.order = (index + 1) * 1000;
  });
}

function applyPlannerDropDestination(ids = [], destination = {}) {
  const draggedIds = [...new Set(ids.filter(Boolean))];
  if (!canMovePlannerItemsToDestination(draggedIds, destination)) return false;
  const draggedItems = plannerDraggedItems(draggedIds);
  const previousGroupIds = new Set();

  draggedItems.forEach((item) => {
    if (String(item.plannerGroupId || "").trim()) previousGroupIds.add(item.plannerGroupId);
  });

  if (destination.kind === "group") {
    const group = (state.planner || []).find((item) => item.id === destination.groupId && isPlannerGroup(item));
    if (!group) return false;
    draggedItems.forEach((item) => {
      item.plannerGroupId = group.id;
      item.status = item.done ? "done" : taskStatus(item);
      syncPlannerItemToSource(item);
    });
    rewritePlannerOrderForDestination(destination, draggedIds);
    previousGroupIds.forEach((groupId) => syncPlannerGroupState(groupId));
    syncPlannerGroupState(group.id, { removeEmpty: false });
    plannerSelectedTaskIds = draggedIds;
    plannerSelectionAnchorId = "";
    closePlannerCardMenu();
    return true;
  }

  if (destination.kind === "column") {
    draggedItems.forEach((item) => {
      if (!isPlannerGroup(item)) delete item.plannerGroupId;
      const movedIds = isPlannerGroup(item) ? plannerIdsWithGroupChildren([item.id]) : [item.id];
      movedIds.forEach((entryId) => {
        const entry = (state.planner || []).find((candidate) => candidate.id === entryId);
        if (!entry || isPlannerGroup(entry)) return;
        const result = domainMovePlannerItemToColumn(state.planner || [], entry.id, destination.status, {
          completedAt: reviewDayValue(),
          todayValue: reviewDayValue,
        });
        state.planner = result.planner;
        const updated = state.planner.find((candidate) => candidate.id === entry.id);
        if (updated) syncPlannerItemToSource(updated);
      });
      if (isPlannerGroup(item)) {
        syncPlannerGroupState(item.id, { removeEmpty: false });
      }
    });
    rewritePlannerOrderForDestination(destination, draggedIds);
    previousGroupIds.forEach((groupId) => syncPlannerGroupState(groupId));
    syncPlannerGroupsForDraggedIds(draggedIds);
    plannerSelectedTaskIds = draggedIds;
    plannerSelectionAnchorId = "";
    closePlannerCardMenu();
    return true;
  }

  if (destination.kind === "date") {
    const expandedIds = plannerIdsWithGroupChildren(draggedIds);
    const touchedIds = [];
    expandedIds.forEach((entryId) => {
      const entry = (state.planner || []).find((candidate) => candidate.id === entryId);
      if (!entry || isPlannerGroup(entry)) return;
      const previousDuration = entry.time
        ? Math.max(30, plannerTimeToMinutes(entry.endTime || defaultPlannerEndTime(entry.time), true) - plannerTimeToMinutes(entry.time))
        : 30;
      const schedule = { date: destination.date };
      if (state.plannerTimeEnabled) {
        schedule.time = destination.time || "";
        schedule.endTime = schedule.time ? minutesToTime(plannerTimeToMinutes(schedule.time) + previousDuration) : "";
      }
      const result = domainReschedulePlannerItem(state.planner || [], entry.id, schedule);
      state.planner = result.planner;
      const updated = state.planner.find((candidate) => candidate.id === entry.id);
      if (!updated) return;
      markPlannerUserEdited(updated);
      syncPlannerItemToSource(updated);
      touchedIds.push(updated.id);
    });
    if (!touchedIds.length) return false;
    rewritePlannerOrderForDestination(destination, draggedIds);
    syncPlannerGroupsForDraggedIds(draggedIds);
    plannerSelectedTaskIds = draggedIds;
    plannerSelectionAnchorId = "";
    closePlannerCardMenu();
    return true;
  }

  return false;
}

function plannerSelectedCardClass(item) {
  return plannerSelectedTaskIds.includes(item.id) ? "planner-card-selected" : "";
}

function plannerDoneClass(item = {}) {
  return item.done ? "planner-item-done" : "";
}

function plannerStatusClass(item = {}) {
  return `planner-status-${taskStatus(item)}`;
}

function plannerGroupCompleteClass(item = {}) {
  const { total, completed } = plannerGroupProgress(item);
  return total > 0 && completed === total ? "planner-group-complete" : "";
}

function renderPlannerMaterialPicker(options, selectedValue) {
  const grouped = materialReferenceGroups(options);
  const groupsHtml = grouped.length
    ? grouped.map(({ subject, cycles, totalItems }) => `
        <details class="planner-material-group">
          <summary>${escapeHtml(subject)} <span>${totalItems}</span></summary>
          <div class="planner-material-cycle-list">
            ${cycles.map((cycleGroup) => renderPlannerMaterialCycleGroup(cycleGroup, selectedValue)).join("")}
          </div>
        </details>
      `).join("")
    : '<p class="planner-material-empty">Nenhum conteúdo pendente encontrado.</p>';

  return `
    <div class="planner-material-picker">
      ${groupsHtml}
    </div>
  `;
}

function materialReferenceGroups(options) {
  const grouped = new Map();
  options.forEach((option) => {
    const subject = option.subject || "Sem matéria";
    const cycle = option.cycle || "Sem ciclo";
    const content = option.group || "Sem conteúdo";
    if (!grouped.has(subject)) grouped.set(subject, new Map());
    const cycles = grouped.get(subject);
    if (!cycles.has(cycle)) cycles.set(cycle, new Map());
    const contents = cycles.get(cycle);
    if (!contents.has(content)) contents.set(content, []);
    contents.get(content).push(option);
  });
  return [...grouped.entries()].map(([subject, cycles]) => {
    const orderedCycles = [...cycles.entries()]
      .map(([cycle, contents]) => ({
        cycle,
        contents: [...contents.entries()]
          .map(([content, items]) => ({
            content,
            items,
          }))
      }))
    return {
      subject,
      cycles: orderedCycles,
      totalItems: orderedCycles.reduce((sum, cycleGroup) =>
        sum + cycleGroup.contents.reduce((contentSum, contentGroup) => contentSum + contentGroup.items.length, 0), 0),
    };
  });
}

function renderPlannerMaterialCycleGroup(cycleGroup, selectedValue) {
  return `
    <details class="planner-material-cycle-group">
      <summary>${escapeHtml(cycleGroup.cycle)} <span>${cycleGroup.contents.reduce((sum, contentGroup) => sum + contentGroup.items.length, 0)}</span></summary>
      <div class="planner-material-content-list">
        ${cycleGroup.contents.map((contentGroup) => renderPlannerMaterialContentGroup(contentGroup, selectedValue)).join("")}
      </div>
    </details>
  `;
}

function renderPlannerMaterialContentGroup(contentGroup, selectedValue) {
  const lessonIds = contentGroup.items.map((item) => item.id).filter(Boolean).join(",");
  return `
    <details class="planner-material-content-group">
      <summary data-action="select-planner-material-group" data-values="${escapeAttribute(lessonIds)}" title="Shift + clique para selecionar todas as aulas deste conteúdo">${escapeHtml(contentGroup.content)} <span>${contentGroup.items.length}</span></summary>
      <div class="planner-material-list">
        ${contentGroup.items.map((item) => renderPlannerMaterialOption(item, item.id === selectedValue)).join("")}
      </div>
    </details>
  `;
}

function renderPlannerMaterialOption(item, selected = false) {
  const detail = [item.group].filter(Boolean).join(" · ");
  return `
    <button class="planner-material-option ${selected ? "active" : ""}" data-action="select-planner-material" data-value="${escapeAttribute(item.id)}" type="button" aria-pressed="${selected}">
      <strong>${escapeHtml(item.topic || item.label || "Conteúdo")}</strong>
      ${detail ? `<small>${escapeHtml(detail)}</small>` : ""}
    </button>
  `;
}

function renderMaterialCycleSelect(subject, selected = "") {
  const cycles = getSubjectCycles(subject);
  const value = selected || cycles[0] || "";
  return `
    <select name="cycleSelect" aria-label="Ciclo existente" data-smart-field="cycleSelect" data-smart-context="subject" data-smart-autofill="contextual">
      ${cycles.length
        ? cycles.map((cycle) => `<option value="${escapeAttribute(cycle)}" ${cycle === value ? "selected" : ""}>${escapeHtml(cycle)}</option>`).join("")
        : '<option value="">Nenhum ciclo existente</option>'}
      <option value="__new__">+ Criar novo ciclo</option>
    </select>
  `;
}

function renderMaterialGroupSelect(subject, cycle, selected = "") {
  const groups = getOrderedLessonGroups(subject, cycle).map((entry) => entry.group);
  const value = selected || groups[0] || "";
  return `
    <select name="groupSelect" aria-label="Conteúdo existente" data-smart-field="groupSelect" data-smart-context="subject,cycleSelect" data-smart-autofill="contextual">
      ${groups.length
        ? groups.map((group) => `<option value="${escapeAttribute(group)}" ${group === value ? "selected" : ""}>${escapeHtml(group)}</option>`).join("")
        : '<option value="">Nenhum conteúdo existente</option>'}
      <option value="__new__">+ Criar novo conteúdo</option>
    </select>
  `;
}

function plannerTypePriority(item) {
  if (item?.type === "Flash-card") return 0;
  return 1;
}

function comparePlannerVisualOrder(a, b) {
  const orderDiff = plannerSortValue(a) - plannerSortValue(b);
  if (orderDiff !== 0) return orderDiff;
  const priorityDiff = plannerTypePriority(a) - plannerTypePriority(b);
  if (priorityDiff !== 0) return priorityDiff;
  return `${plannerDateValue(a)} ${a.time || ""}`.localeCompare(`${plannerDateValue(b)} ${b.time || ""}`);
}

function renderPlannerMockForm() {
  return `
    <form class="planner-builder-form mock-block-form" data-action="create-planner-block" data-type="Simulado" data-smart-scope="planner-builder-mock">
      ${renderPlannerBaseFields(`
        <select name="examDay" required data-smart-field="examDay" data-smart-autofill="last">
          <option value="Dia 1">Dia 1</option>
          <option value="Dia 2">Dia 2</option>
        </select>
        <select name="subjects" multiple hidden aria-hidden="true" class="ds-multi-native">
          ${getSubjects().map((subject) => `<option value="${escapeAttribute(subject)}">${escapeHtml(subject)}</option>`).join("")}
        </select>
        <div class="ds-multi-picker" data-ds-multi>
          <div class="ds-multi-label-row">
            <span class="ds-multi-label">Matérias</span>
            <span class="ds-multi-count" data-ds-count>0 selecionadas</span>
          </div>
          <div class="ds-multi-chips" data-ds-chips></div>
          <div class="ds-multi-grid">
            ${getSubjects().map((subject) => `
              <button type="button" class="ds-multi-opt" data-ds-value="${escapeAttribute(subject)}" aria-pressed="false">${escapeHtml(subject)}</button>
            `).join("")}
          </div>
        </div>
        <input name="newSubject" placeholder="Matéria nova (opcional)" data-smart-field="newSubject" />
      `)}
    </form>
  `;
}

function renderPlannerQuestionsForm() {
  const options = plannerPendingQuestionOptions();
  const selectedValue = options[0]?.id || "__new__";
  return `
    <form class="planner-builder-form" data-action="create-planner-block" data-type="Questões" data-smart-scope="planner-builder-questions">
      ${renderPlannerBaseFields(`
        <input type="hidden" name="questionRef" value="${escapeAttribute(selectedValue)}" />
        ${renderPlannerQuestionPicker(options, selectedValue)}
        <details class="new-material-fields ${selectedValue === "__new__" ? "is-open" : ""}" ${selectedValue === "__new__" ? "open" : ""}>
          <summary>Criar nova lista em Questões</summary>
          <select name="subject" data-smart-field="subject" data-smart-autofill="last">
            ${getSubjects().map((subject) => `<option value="${escapeAttribute(subject)}">${escapeHtml(subject)}</option>`).join("")}
          </select>
          <input name="newSubject" placeholder="Matéria nova (opcional)" data-smart-field="newSubject" />
          <input name="content" list="plannerQuestionContentOptions" placeholder="Conteúdo" data-smart-field="content" data-smart-context="subject" data-smart-autofill="contextual" />
          <datalist id="plannerQuestionContentOptions"></datalist>
          <input name="subcontent" placeholder="Subconteúdo" data-smart-field="subcontent" data-smart-context="subject,content" data-smart-autofill="contextual" />
          <input name="totalQuestions" type="number" min="1" step="1" placeholder="Nº de questões" />
        </details>
      `)}
    </form>
  `;
}

function renderPlannerEssayForm() {
  const essays = plannerPendingEssayOptions();
  return `
    <form class="planner-builder-form essay-block-form" data-action="create-planner-block" data-type="Redação" data-smart-scope="planner-builder-essay">
      ${renderPlannerBaseFields(`
        <select name="essayRef" aria-label="Redação existente">
          <option value="__new__">Nova redação</option>
          ${essays.map((essay) => `<option value="${escapeAttribute(essay.id)}">${escapeHtml(essay.theme || "Sem tema")} · ${escapeHtml(formatDate(essay.date))}</option>`).join("")}
        </select>
        <input name="theme" placeholder="Tema da nova redação" />
      `)}
    </form>
  `;
}

function plannerPendingQuestionOptions() {
  return questionLeafRecords(state.questions || [])
    .filter((item) => !questionRecordDone(item))
    .filter((item) => !linkedPlannerQuestionItem(item))
    .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")) || plannerQuestionDisplayLabel(a).localeCompare(plannerQuestionDisplayLabel(b), "pt-BR"))
    .map((item) => ({
      id: item.id,
      subject: item.subject || "Geral",
      content: item.content || "",
      subcontent: item.subcontent || "",
      questions: item.questions,
      correct: item.correct,
      label: plannerQuestionDisplayLabel(item),
      detail: [item.subject, item.questions ? `${item.questions} questões` : ""].filter(Boolean).join(" · "),
    }));
}

function renderPlannerQuestionPicker(options = [], selectedValue = "__new__") {
  const grouped = groupBy(options, (item) => item.subject || "Geral");
  const groupsHtml = options.length
    ? Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b, "pt-BR"))
      .map(([subject, items]) => `
        <details class="planner-material-group">
          <summary>${escapeHtml(subject)} <span>${items.length}</span></summary>
          <div class="planner-material-list">
            ${items.map((item) => renderPlannerQuestionOption(item, item.id === selectedValue)).join("")}
          </div>
        </details>
      `).join("")
    : '<p class="planner-material-empty">Nenhuma lista pendente encontrada fora do Planner.</p>';

  return `
    <div class="planner-material-picker">
      ${groupsHtml}
      <button class="planner-material-option planner-material-create ${selectedValue === "__new__" ? "active" : ""}" data-action="select-planner-question" data-value="__new__" type="button" aria-pressed="${selectedValue === "__new__"}">
        <strong>+ Criar nova lista em Questões</strong>
        <small>Use os campos abaixo</small>
      </button>
    </div>
  `;
}

function renderPlannerQuestionOption(item, selected = false) {
  return `
    <button class="planner-material-option ${selected ? "active" : ""}" data-action="select-planner-question" data-value="${escapeAttribute(item.id)}" type="button" aria-pressed="${selected}">
      <strong>${escapeHtml(item.label || "Lista de questões")}</strong>
      ${item.detail ? `<small>${escapeHtml(item.detail)}</small>` : ""}
    </button>
  `;
}

function materialReferenceOptions(type = "") {
  const step = plannerLessonStep(type);
  const plannedLessonIds = new Set(
    activeRecords(state.planner || [])
      .filter((item) => item.type === type)
      .flatMap((item) => item.materialRef?.lessonIds || [item.materialRef?.lessonId].filter(Boolean)),
  );
  const plannedReviewGroups = new Set(
    activeRecords(state.planner || [])
      .filter((item) => item.type === "Revisão")
      .map((item) => `${item.materialRef?.subject || ""}::${item.materialRef?.cycle || ""}::${item.materialRef?.group || ""}`),
  );
  return getSubjects().flatMap((subject) =>
    getSubjectCycles(subject).flatMap((cycle) => {
      const lessons = activeRecords(getCycleLessons(subject, cycle));
      if (type === "Revisão") {
        return getOrderedLessonGroups(subject, cycle, lessons)
          .filter(({ group, items }) => {
            const groupKey = `${subject}::${cycle}::${group}`;
            const hasTheory = items.some((lesson) => Boolean(lesson?.theory));
            const fullyReviewed = items.length > 0 && items.every((lesson) => Boolean(lesson?.review));
            return hasTheory && !fullyReviewed && !plannedReviewGroups.has(groupKey);
          })
          .map(({ group, items }) => ({
            id: `group:${subject}:${cycle}:${group}`,
            subject,
            cycle,
            group,
            topic: group,
            label: group,
            lessonIds: items.map((lesson) => lesson.id),
          }));
      }

      return lessons
        .filter((lesson) => (!step || !lesson[step]) && !plannedLessonIds.has(lesson.id))
        .map((lesson) => ({
          id: lesson.id,
          subject,
          cycle,
          group: lesson.group || "Sem grupo",
          topic: lesson.topic || lesson.group || "Aula sem nome",
          label: lesson.topic || lesson.group || "Aula sem nome",
        }));
    }),
  );
}

function findMaterialReference(lessonId, type = "") {
  return materialReferenceOptions(type).find((option) => option.id === lessonId) || null;
}

function plannerNewMaterialMissingFieldMessage(data = {}) {
  const valueOf = (name) => String(data[name] ?? "").trim();
  const subjectValue = valueOf("subject");
  const newSubjectValue = valueOf("newSubject");
  if (subjectValue === "__new__" || (!subjectValue && !newSubjectValue)) {
    return "Digite o nome da matéria nova (campo \"Nome da matéria nova\").";
  }
  if (valueOf("cycleSelect") === "__new__" || !valueOf("cycleSelect")) {
    return "Digite o nome do novo ciclo (campo \"Novo ciclo\").";
  }
  if (valueOf("groupSelect") === "__new__" || !valueOf("groupSelect")) {
    return "Digite o nome do novo conteúdo (campo \"Novo conteúdo\").";
  }
  if (!valueOf("topic")) {
    return "Digite a aula/tópico (campo \"Aula/tópico\").";
  }
  return "Preencha os campos para criar o conteúdo de Matérias.";
}

function createMaterialReference(data) {
  const subjectRaw = String(data.newSubject || data.subject || "").trim();
  const subject = subjectRaw === "__new__" ? "" : subjectRaw;
  const cycle = String(data.cycleSelect === "__new__" ? data.cycle : data.cycleSelect || data.cycle || "").trim();
  const group = String(data.groupSelect === "__new__" ? data.group : data.groupSelect || data.group || "").trim();
  const topic = String(data.topic || group || "").trim();
  if (!subject || !cycle || !group || !topic) return null;
  ensureSubjectExists(subject);
  ensureSubjectState(subject);
  const cycles = getSubjectCycles(subject);
  if (!cycles.some((item) => item.toLowerCase() === cycle.toLowerCase())) {
    touchMapKey("subjectCycles", subject, [...cycles, cycle]);
  }
  ensureCycleContentGroup(subject, cycle, group);
  const lesson = {
    id: uid(),
    group,
    topic,
    theory: false,
    practice: false,
    review: false,
  };
  const key = cycleKey(subject, cycle);
  touchMapKey("cycleLessons", key, [...getCycleLessons(subject, cycle), lesson]);
  return {
    id: lesson.id,
    subject,
    cycle,
    group,
    topic,
    label: `${subject} > ${cycle} > ${group} > ${topic}`,
  };
}

function ensureSubjectExists(subject) {
  const clean = String(subject || "").trim();
  if (!clean) return;
  const exists = [...subjects, ...(state.customSubjects || [])].some((item) => normalizeHeader(item) === normalizeHeader(clean));
  if (!exists || isSubjectHidden(clean)) addSubject(clean);
  else ensureSubjectState(clean);
  fillSubjectSelects();
}

function syncPlannerMaterialFormSelectors() {
  if (!plannerBuilderOpen) return;
  document
    .querySelectorAll('form.planner-builder-form[data-action="create-planner-block"][data-type="Teoria"], form.planner-builder-form[data-action="create-planner-block"][data-type="Revisão"]')
    .forEach((form) => updatePlannerMaterialSelectors(form));
}

function updatePlannerMaterialSelectors(form) {
  const fields = form.querySelector(".new-material-fields");
  if (!fields) return;
  const subjectSelect = fields.querySelector('select[name="subject"]');
  const cycleSelect = fields.querySelector('select[name="cycleSelect"]');
  const groupSelect = fields.querySelector('select[name="groupSelect"]');
  if (!subjectSelect || !cycleSelect || !groupSelect) return;
  const cycleInput = fields.querySelector('input[name="cycle"]');
  const groupInput = fields.querySelector('input[name="group"]');
  const activeElement = document.activeElement;
  const contextName =
    activeElement === subjectSelect
      ? "subject"
      : activeElement === cycleSelect
      ? "cycleSelect"
      : activeElement === groupSelect
      ? "groupSelect"
      : "";
  if (contextName) resetSmartContextDependents(fields, contextName, { clearValues: true });

  const subject = subjectSelect.value;
  const previousCycle = cycleSelect.value;
  const cycles = getSubjectCycles(subject);
  const cycle = previousCycle === "__new__"
    ? "__new__"
    : cycles.includes(previousCycle)
    ? previousCycle
    : cycles[0] || "";
  cycleSelect.innerHTML = [
    ...(cycles.length
      ? cycles.map((item) => `<option value="${escapeAttribute(item)}" ${item === cycle ? "selected" : ""}>${escapeHtml(item)}</option>`)
      : ['<option value="">Nenhum ciclo existente</option>']),
    `<option value="__new__" ${cycle === "__new__" ? "selected" : ""}>+ Criar novo ciclo</option>`,
  ].join("");

  const previousGroup = groupSelect.value;
  const groups = getOrderedLessonGroups(subject, cycle === "__new__" ? "" : cycle).map((entry) => entry.group);
  const group = previousGroup === "__new__"
    ? "__new__"
    : groups.includes(previousGroup)
    ? previousGroup
    : groups[0] || "";
  groupSelect.innerHTML = [
    ...(groups.length
      ? groups.map((item) => `<option value="${escapeAttribute(item)}" ${item === group ? "selected" : ""}>${escapeHtml(item)}</option>`)
      : ['<option value="">Nenhum conteúdo existente</option>']),
    `<option value="__new__" ${group === "__new__" ? "selected" : ""}>+ Criar novo conteúdo</option>`,
  ].join("");
  cycleInput?.classList.toggle("hidden", cycleSelect.value !== "__new__");
  groupInput?.classList.toggle("hidden", groupSelect.value !== "__new__");
  const newSubjectInput = fields.querySelector('input[name="newSubject"]');
  newSubjectInput?.classList.toggle("hidden", subjectSelect.value !== "__new__");
  applySmartFieldMemory(fields);
}

function plannerGroupSummaryText(item = {}) {
  const { total } = plannerGroupProgress(item);
  return `${total} tarefa${total === 1 ? "" : "s"}`;
}

function plannerItemIsOverdue(item = {}) {
  const date = plannerDateValue(item);
  if (isPlannerGroup(item)) {
    const { total, completed } = plannerGroupProgress(item);
    if (total > 0 && completed === total) return false;
  }
  return sharedPlannerOverdue({ ...item, date }, plannerTodayValue());
}

function plannerStatusLabel(item = {}) {
  if (item.done) return "Concluído";
  if (!plannerDateValue(item)) return "Sem data";
  if (plannerItemIsOverdue(item)) return "Atrasada";
  if (taskStatus(item) === "doing") return "Fazendo";
  if (taskStatus(item) === "later") return "Outro dia";
  return "A fazer";
}

function plannerDateLabel(item = {}) {
  const date = plannerDateValue(item);
  return date ? formatDate(date) : "Sem data";
}

function renderPlannerLight() {
  renderSectionNow(activeViewId(), { save: true, commit: true });
}

function taskStatus(item) {
  if (isPlannerGroup(item)) return plannerGroupStatus(plannerGroupChildren(item.id));
  if (item.done) return "done";
  return item.status || "todo";
}

function plannerBoardStatus(item = {}) {
  const status = taskStatus(item);
  return status === "later" ? "todo" : status;
}

function plannerDateValue(item = {}) {
  if (isPlannerGroup(item)) return sharedPlannerGroupDate(plannerGroupChildren(item.id), item.date || "");
  return String(item.date || "").trim();
}

function plannerSubjectsLabel(item) {
  if (isPlannerGroup(item)) {
    const subjectsList = plannerGroupSubjectList(item);
    if (!subjectsList.length) return "Grupo";
    return subjectsList.length === 1 ? subjectsList[0] : `${subjectsList.length} matérias`;
  }
  if (item.type === "Flash-card" && item.flashcardData) {
    const { totalCards, reviewCards, newCards } = plannerFlashcardCounts(item);
    return `${totalCards} cards | R: ${reviewCards} | N: ${newCards}`;
  }
  if (Array.isArray(item.subjects) && item.subjects.length) return item.subjects.join(", ");
  return item.subject || "Geral";
}

/* =====================================================
   QUESTOES E CADERNO DE ERROS
   Listas, grupos, filtros, importacao de PDF e acordions.
===================================================== */
function questionGroupChildren(groupId = "", options = {}) {
  const items = sharedQuestionGroupChildren(activeRecords(state.questions || []), groupId, { subjectFilter: "all" });
  const filter = options.subjectFilter || "all";
  return items
    .filter((item) => matchesSubject(item, filter))
    .sort(compareQuestionVisualOrder);
}

function questionTopLevelItems() {
  return sharedQuestionTopLevelItems(activeRecords(state.questions || []));
}

function compareQuestionVisualOrder(a, b) {
  const orderDiff = questionSortValue(a) - questionSortValue(b);
  if (orderDiff !== 0) return orderDiff;
  const dateDiff = String(b?.date || "").localeCompare(String(a?.date || ""));
  if (dateDiff !== 0) return dateDiff;
  const aLabel = isQuestionGroup(a) ? String(a?.name || "") : plannerQuestionDisplayLabel(a);
  const bLabel = isQuestionGroup(b) ? String(b?.name || "") : plannerQuestionDisplayLabel(b);
  return aLabel.localeCompare(bLabel, "pt-BR");
}

function compareQuestionDateOrder(a, b, mode = questionsDateFilter()) {
  const direction = sanitizeQuestionDateFilter(mode);
  if (!direction) return compareQuestionVisualOrder(a, b);
  const aDate = String(a?.date || "").trim();
  const bDate = String(b?.date || "").trim();
  const hasADate = /^\d{4}-\d{2}-\d{2}$/.test(aDate);
  const hasBDate = /^\d{4}-\d{2}-\d{2}$/.test(bDate);
  if (hasADate && hasBDate) {
    const dateDiff = direction === "old" ? aDate.localeCompare(bDate) : bDate.localeCompare(aDate);
    if (dateDiff !== 0) return dateDiff;
  }
  if (hasADate !== hasBDate) return hasADate ? -1 : 1;
  return compareQuestionVisualOrder(a, b);
}

function questionSortValue(item = {}) {
  return Number.isFinite(Number(item.order)) ? Number(item.order) : 0;
}

function questionScopeItemIds(destination = {}) {
  if (destination.kind === "group") {
    return questionGroupChildren(destination.groupId, { subjectFilter: "all" }).map((item) => item.id);
  }
  if (destination.kind === "table") {
    return questionTopLevelItems().sort(compareQuestionVisualOrder).map((item) => item.id);
  }
  return [];
}

function rewriteQuestionOrderForDestination(destination = {}, movedIds = []) {
  const ids = [...new Set(movedIds.filter(Boolean))];
  const remaining = questionScopeItemIds(destination).filter((id) => !ids.includes(id));
  const beforeIndex = destination.beforeId ? remaining.indexOf(destination.beforeId) : -1;
  const nextOrder = beforeIndex >= 0
    ? [...remaining.slice(0, beforeIndex), ...ids, ...remaining.slice(beforeIndex)]
    : [...remaining, ...ids];
  nextOrder.forEach((id, index) => {
    const item = (state.questions || []).find((entry) => entry.id === id);
    if (item) {
      item.order = (index + 1) * 1000;
      touchRecord("questions", item.id);
    }
  });
}

function questionGroupMetrics(children = []) {
  return sharedQuestionGroupMetrics(children, questionPerformanceLabel);
}

function questionGroupSubjectList(group = {}) {
  const subjectsList = questionGroupChildren(group.id, { subjectFilter: "all" })
    .map((entry) => entry.subject || "Geral")
    .filter(Boolean);
  return [...new Set(subjectsList)];
}

function questionGroupSubjectLabel(group = {}, children = questionGroupChildren(group.id, { subjectFilter: "all" })) {
  const subjectsList = [...new Set(children.map((item) => item.subject || "Geral").filter(Boolean))];
  if (!subjectsList.length) return "Grupo";
  return subjectsList.length === 1 ? subjectsList[0] : `${subjectsList.length} matérias`;
}

function groupSubjectsLabel(group = {}) {
  return questionGroupSubjectList(group).join(", ") || "Geral";
}

function questionGroupContentLabel(children = []) {
  const contents = [...new Set(children.map((item) => String(item.content || "").trim() || "Sem conteúdo"))];
  if (!contents.length) return "Sem conteúdo";
  return contents.length === 1 ? contents[0] : `${contents.length} conteúdos`;
}

function questionGroupSummaryText(group = {}, children = questionGroupChildren(group.id, { subjectFilter: "all" })) {
  const metrics = questionGroupMetrics(children);
  return `${metrics.totalQuestions} questões • ${metrics.totalCorrect} acertos • ${metrics.accuracy}%`;
}

function syncQuestionGroupMetadata(group) {
  if (!isQuestionGroup(group)) return;
  const children = questionGroupChildren(group.id, { subjectFilter: "all" });
  group.subjects = questionGroupSubjectList(group);
  group.subject = group.subjects.length === 1 ? group.subjects[0] : "Geral";
  group.content = questionGroupContentLabel(children);
  if (!group.name) group.name = "Nova lista";
  if (!children.length) return;
  const ordered = [...children].sort(compareQuestionVisualOrder);
  group.date = ordered[0]?.date || group.date || questionTodayValue();
}

function syncQuestionGroupState(groupId = "", options = {}) {
  const group = (state.questions || []).find((item) => item.id === groupId && isQuestionGroup(item));
  if (!group) return;
  const children = questionGroupChildren(group.id, { subjectFilter: "all" });
  if (!children.length) {
    if (options.removeEmpty !== false) {
      state.questions = (state.questions || []).filter((item) => item.id !== group.id);
      questionSelectedRowIds = questionSelectedRowIds.filter((id) => id !== group.id);
    }
    return;
  }
  syncQuestionGroupMetadata(group);
  const metrics = questionGroupMetrics(children);
  group.questions = metrics.totalQuestions;
  group.correct = metrics.totalCorrect;
  group.difficulty = metrics.performance;
  group.totalLists = metrics.totalLists;
  touchRecord("questions", group.id);
}

function normalizeQuestionGroups() {
  state.questions ||= [];
  const ids = new Set(state.questions.map((item) => item.id));
  questionSelectedRowIds = questionSelectedRowIds.filter((id) => ids.has(id));
  state.questions.forEach((item) => {
    if (!item || typeof item !== "object") return;
    if (String(item.questionGroupId || "").trim() && !ids.has(item.questionGroupId)) {
      delete item.questionGroupId;
    }
    if (isQuestionGroup(item)) {
      item.name = String(item.name || "Grupo de listas").trim() || "Grupo de listas";
      item.collapsed = Boolean(item.collapsed);
      item.subject = String(item.subject || "Geral").trim() || "Geral";
    } else {
      item.done = Boolean(item.done || item.plannerDone);
      item.completedAt = String(item.completedAt || "").trim();
    }
  });
  questionTopLevelItems()
    .filter((item) => isQuestionGroup(item))
    .forEach((item) => syncQuestionGroupState(item.id));
}

function canGroupQuestionSelection() {
  const selected = questionSelectedRowIds
    .map((id) => (state.questions || []).find((item) => item.id === id))
    .filter(Boolean);
  if (selected.length < 2) return false;
  if (selected.some((item) => isQuestionGroup(item))) return false;
  return !selected.some((item) => String(item.questionGroupId || "").trim());
}

function canUngroupQuestionSelection() {
  if (questionSelectedRowIds.length !== 1) return false;
  const selected = (state.questions || []).find((item) => item.id === questionSelectedRowIds[0]);
  return Boolean(selected && isQuestionGroup(selected));
}

function canRemoveQuestionSelectionFromGroup() {
  const selected = questionSelectedRowIds
    .map((id) => (state.questions || []).find((item) => item.id === id))
    .filter(Boolean);
  if (!selected.length) return false;
  if (selected.some((item) => isQuestionGroup(item))) return false;
  return selected.some((item) => String(item.questionGroupId || "").trim());
}

function addableQuestionSelection() {
  return questionSelectedRowIds
    .map((id) => (state.questions || []).find((item) => item.id === id))
    .filter(Boolean)
    .filter((item) => !isQuestionGroup(item) && !String(item.questionGroupId || "").trim());
}

function canAddQuestionSelectionToGroup() {
  const addable = addableQuestionSelection();
  if (!addable.length || addable.length !== questionSelectedRowIds.length) return false;
  return questionTopLevelItems().some((item) => isQuestionGroup(item));
}

function addQuestionsToGroup(itemIds = [], groupId = "") {
  const ids = [...new Set(itemIds.filter(Boolean))];
  const group = (state.questions || []).find((entry) => entry.id === groupId && isQuestionGroup(entry));
  if (!group || !ids.length) return false;
  const previousGroups = new Set();
  const items = ids
    .map((id) => (state.questions || []).find((entry) => entry.id === id))
    .filter(Boolean)
    .filter((entry) => !isQuestionGroup(entry) && String(entry.questionGroupId || "").trim() !== groupId);
  if (!items.length) return false;
  items.forEach((entry) => {
    if (String(entry.questionGroupId || "").trim()) previousGroups.add(entry.questionGroupId);
    entry.questionGroupId = groupId;
    touchRecord("questions", entry.id);
  });
  rewriteQuestionOrderForDestination({ kind: "group", groupId, beforeId: "" }, ids);
  previousGroups.forEach((previousGroupId) => syncQuestionGroupState(previousGroupId));
  syncQuestionGroupState(groupId, { removeEmpty: false });
  questionSelectedRowIds = [...ids];
  closePlannerCardMenu();
  return true;
}

function openAddQuestionToGroupPicker() {
  const addable = addableQuestionSelection();
  if (!addable.length) {
    notify("Selecione uma lista sem grupo para adicionar a um grupo.", "error");
    return false;
  }
  const selectedSubjects = [...new Set(addable.map((item) => canonicalSubject(item.subject || "Geral")))];
  const groups = questionTopLevelItems()
    .filter((item) => isQuestionGroup(item))
    .filter((group) => {
      const groupSubjects = questionGroupSubjectList(group);
      return groupSubjects.length === 1 && selectedSubjects.includes(groupSubjects[0]);
    });
  if (!groups.length) {
    notify("Não há grupos dessa matéria disponíveis para adicionar a lista.", "error");
    return false;
  }
  closeAllOverlays();
  closePlannerCardMenu();
  const count = addable.length;
  const subjectLabel = selectedSubjects.length === 1 ? selectedSubjects[0] : `${selectedSubjects.length} matérias`;
  const groupButtons = groups.map((group) => {
    const childCount = questionGroupChildren(group.id, { subjectFilter: "all" }).length;
    const metrics = questionGroupMetrics(questionGroupChildren(group.id, { subjectFilter: "all" }));
    const contentLabel = questionGroupContentLabel(questionGroupChildren(group.id, { subjectFilter: "all" }));
    return `
      <button class="question-add-group-option" data-action="pick-question-group" data-id="${escapeAttribute(group.id)}" type="button">
        <span class="question-add-group-option-main">
          <strong>${escapeHtml(group.name || "Grupo de listas")}</strong>
          <small>${escapeHtml(groupSubjectsLabel(group))} · ${escapeHtml(contentLabel)}</small>
        </span>
        <span class="question-add-group-option-meta">
          <b>${childCount} lista${childCount === 1 ? "" : "s"}</b>
          <small>${metrics.totalQuestions} questões · ${metrics.totalCorrect} acertos · ${metrics.accuracy}%</small>
        </span>
      </button>
    `;
  });
  const modal = document.createElement("div");
  modal.className = "image-editor-modal timer-subject-modal question-add-group-picker";
  modal.dataset.questionAddGroupPicker = "true";
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-labelledby", "questionAddGroupPickerTitle");
  modal.innerHTML = `
    <div class="modal timer-subject-dialog">
      <div class="modal-content timer-subject-content">
        <div class="planner-builder-head">
          <div>
            <strong id="questionAddGroupPickerTitle">Mover para um grupo</strong>
            <small>${count === 1 ? "Escolha um grupo de" : `Escolha um grupo para as ${count} listas de`} ${escapeHtml(subjectLabel)}</small>
          </div>
          <button class="icon-button" type="button" data-action="close-timer-subject-modal" aria-label="Fechar">×</button>
        </div>
        <div class="question-add-group-list">
          ${groupButtons.join("")}
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  syncBodyScrollLock();
  return true;
}

function toggleQuestionRowSelection(id, append = false) {
  if (!id) return;
  const current = new Set(questionSelectedRowIds);
  if (!append) {
    questionSelectedRowIds = current.has(id) && current.size === 1 ? [] : [id];
    return;
  }
  if (current.has(id)) current.delete(id);
  else current.add(id);
  questionSelectedRowIds = [...current];
}

function questionDragIds(id) {
  if (questionSelectedRowIds.includes(id)) return [...questionSelectedRowIds];
  return id ? [id] : [];
}

function resolveDraggedQuestionIds(event, fallbackId = "") {
  if (activeQuestionDragIds.length) return [...activeQuestionDragIds];
  try {
    const draggedIds = JSON.parse(event.dataTransfer?.getData("text/question-ids") || "[]");
    if (draggedIds.length) return draggedIds;
  } catch (err) {
    console.error("[resolveDraggedQuestionIds]", err);
  }
  const plainId = event.dataTransfer?.getData("text/plain") || fallbackId;
  return [plainId].filter(Boolean);
}

function questionSelectedRowClass(item) {
  return questionSelectedRowIds.includes(item.id) ? "planner-card-selected" : "";
}

function createQuestionGroupFromSelectionWithName(name = "", subject = "") {
  if (!canGroupQuestionSelection()) {
    alert("Selecione pelo menos 2 listas soltas para agrupar.");
    return false;
  }
  const selected = questionSelectedRowIds
    .map((id) => (state.questions || []).find((item) => item.id === id))
    .filter(Boolean)
    .sort(compareQuestionVisualOrder);
  const selectedSubject = subject && subject !== "__mixed__" ? canonicalSubject(subject) : "";
  if (selectedSubject) {
    selected.forEach((item) => {
      item.subject = selectedSubject;
      if (selectedSubject !== "Geral") ensureSubjectExists(selectedSubject);
      rememberContent(selectedSubject, item.content);
      syncQuestionPerformance(item);
      touchRecord("questions", item.id);
    });
  }
  const group = {
    id: uid(),
    name: String(name || `Grupo com ${selected.length} listas`).trim() || `Grupo com ${selected.length} listas`,
    isGroup: true,
    collapsed: false,
    subject: "Geral",
    content: "",
    questions: 0,
    correct: 0,
    difficulty: "-",
    totalLists: selected.length,
    date: selected[0]?.date || questionTodayValue(),
  };
  state.questions = [...(state.questions || []), group];
  touchRecord("questions", group.id);
  selected.forEach((item) => {
    item.questionGroupId = group.id;
    touchRecord("questions", item.id);
  });
  syncQuestionGroupState(group.id, { removeEmpty: false });
  questionSelectedRowIds = [group.id];
  closePlannerCardMenu();
  return group;
}

function createQuestionGroupFromSelection() {
  if (!canGroupQuestionSelection()) {
    alert("Selecione pelo menos 2 listas soltas para agrupar.");
    return false;
  }
  const count = questionSelectedRowIds.length;
  const selectedSubjects = [...new Set(questionSelectedRowIds
    .map((id) => (state.questions || []).find((item) => item.id === id))
    .filter(Boolean)
    .map((item) => canonicalSubject(item.subject || "Geral")))];
  return openQuestionGroupModal({
    itemCount: count,
    defaultName: `Grupo com ${count} listas`,
    defaultSubject: selectedSubjects.length === 1 ? selectedSubjects[0] : (selectedSubjects[0] || "Geral"),
    onConfirm: (name, subject) => {
      const group = createQuestionGroupFromSelectionWithName(name, subject);
      if (!group) return;
      renderSectionNow("questoes", { save: true, commit: true });
      focusQuestionGroupTitleEdit(group.id);
    },
  });
}

function syncPlannerQuestionSelection(form, selectedValues = [], anchorValue = "") {
  if (!form) return;
  const uniqueValues = [...new Set(selectedValues.filter(Boolean))];
  plannerSelectedQuestionRefs = uniqueValues;
  const input = form.querySelector('input[name="questionRef"]');
  if (input) input.value = anchorValue || uniqueValues[0] || "";
  form.querySelectorAll('[data-action="select-planner-question"]').forEach((option) => {
    const isSelected = uniqueValues.includes(option.dataset.value || "");
    option.classList.toggle("active", isSelected);
    option.setAttribute("aria-pressed", String(isSelected));
  });
  const newFields = form.querySelector(".new-material-fields");
  const creatingNew = uniqueValues.includes("__new__") || (!uniqueValues.length && (input?.value || "") === "__new__");
  if (newFields) {
    if (creatingNew) newFields.setAttribute("open", "open");
    else newFields.removeAttribute("open");
    newFields.classList.toggle("is-open", creatingNew);
  }
}

function ungroupQuestionGroup(groupId = "") {
  const group = (state.questions || []).find((item) => item.id === groupId && isQuestionGroup(item));
  if (!group) return false;
  questionGroupChildren(group.id, { subjectFilter: "all" }).forEach((item) => {
    delete item.questionGroupId;
  });
  state.questions = (state.questions || []).filter((item) => item.id !== group.id);
  questionSelectedRowIds = [];
  closePlannerCardMenu();
  return true;
}

function removeQuestionsFromGroup(itemIds = []) {
  const ids = [...new Set(itemIds.filter(Boolean))];
  if (!ids.length) return false;
  const previousGroups = new Set();
  const items = ids
    .map((id) => (state.questions || []).find((item) => item.id === id))
    .filter(Boolean)
    .filter((item) => !isQuestionGroup(item) && String(item.questionGroupId || "").trim());
  if (!items.length) return false;
  items.forEach((item) => {
    if (item.questionGroupId) previousGroups.add(item.questionGroupId);
    delete item.questionGroupId;
  });
  previousGroups.forEach((groupId) => syncQuestionGroupState(groupId));
  questionSelectedRowIds = items.map((item) => item.id);
  closePlannerCardMenu();
  return true;
}

function questionDropGroupIdFromElement(target) {
  const row = target?.closest?.("[data-question-group-row], [data-question-group-parent]");
  if (!row) return "";
  return String(row.dataset.questionGroupRow || row.dataset.questionGroupParent || "").trim();
}

function questionTableDropzone(target) {
  return target?.closest?.(
    ".questions-table tbody, #questionsTable, .questions-table, #questoes .table-wrap, "
    + ".question-group-view-grid, [data-question-group-view-grid]",
  ) || null;
}

function questionDropContainer(target) {
  return target?.closest?.("tbody, [data-question-group-view-grid], .question-group-view-grid, #questionsTable") || null;
}

function questionDropDestinationFromEvent(event, ids = []) {
  const draggedIds = [...new Set(ids.filter(Boolean))];
  const groupRow = event.target?.closest?.("[data-question-group-row]");
  if (groupRow) {
    return {
      kind: "group",
      groupId: String(groupRow.dataset.questionGroupRow || "").trim(),
      beforeId: "",
      container: groupRow,
      appendToGroup: true
    };
  }
  const groupChild = event.target?.closest?.("[data-question-group-parent]");
  const groupId = groupChild
    ? String(groupChild.dataset.questionGroupParent || "").trim()
    : questionDropGroupIdFromElement(event.target);
  const container = questionDropContainer(event.target) || questionTableDropzone(event.target);
  const beforeId = insertionBeforeDataValue(container, "[data-question-id]", "questionId", event.clientY, draggedIds[0] || "");
  if (groupId) {
    const movingIntoGroup = draggedIds
      .map((id) => (state.questions || []).find((item) => item.id === id))
      .filter(Boolean)
      .some((item) => String(item.questionGroupId || "").trim() !== groupId);
    return {
      kind: "group",
      groupId,
      beforeId: movingIntoGroup ? "" : beforeId,
      container,
      appendToGroup: movingIntoGroup,
    };
  }
  if (event.target?.closest?.("#questionGroupViewModal")) return null;
  if (questionTableDropzone(event.target)) return { kind: "table", beforeId, container };
  return null;
}

function canMoveQuestionsToDestination(ids = [], destination = {}) {
  const items = [...new Set(ids.filter(Boolean))]
    .map((id) => (state.questions || []).find((item) => item.id === id))
    .filter(Boolean);
  if (!items.length || !destination?.kind) return false;
  if (destination.kind === "group") {
    const group = (state.questions || []).find((item) => item.id === destination.groupId && isQuestionGroup(item));
    if (!group) return false;
    if (items.some((item) => isQuestionGroup(item))) return false;
    if (ids.includes(group.id)) return false;
  }
  if (destination.beforeId && ids.includes(destination.beforeId)) return false;
  return true;
}

function questionPlaceholderFor(container) {
  const isTable = container?.tagName === "TBODY" || container?.matches?.("table, #questionsTable");
  const selector = isTable ? 'tr[data-dnd-placeholder="question"]' : '[data-dnd-placeholder="question"]';
  let placeholder = document.querySelector(selector);
  if (!placeholder) {
    placeholder = isTable ? document.createElement("tr") : document.createElement("div");
    placeholder.className = "dnd-drop-placeholder question-drop-placeholder";
    placeholder.dataset.dndPlaceholder = "question";
    if (isTable) placeholder.innerHTML = '<td colspan="8"><div class="question-drop-placeholder-line"></div></td>';
    placeholder.setAttribute("aria-hidden", "true");
  }
  return placeholder;
}

function renderQuestionDropPlaceholder(destination = {}, ids = []) {
  const container = destination.container;
  if (!container) return;
  if (destination.appendToGroup || container.matches?.("[data-question-group-row]")) {
    container.classList.add("drag-over");
    return;
  }
  const placeholder = questionPlaceholderFor(container);
  const beforeElement = destination.beforeId
    ? container.querySelector(`[data-question-id="${CSS.escape(destination.beforeId)}"]`)
    : null;
  if (placeholder.parentElement !== container) container.appendChild(placeholder);
  if (beforeElement && beforeElement.parentElement === container) container.insertBefore(placeholder, beforeElement);
  else container.appendChild(placeholder);
  container.querySelectorAll("[data-question-id]").forEach((row) => row.classList.remove("drop-before", "drop-after"));
  if (beforeElement && !ids.includes(destination.beforeId)) beforeElement.classList.add("drop-before");
  else [...container.querySelectorAll("[data-question-id]")].filter((row) => !ids.includes(row.dataset.questionId)).at(-1)?.classList.add("drop-after");
}

function updateQuestionDropTarget(event, ids = []) {
  const destination = questionDropDestinationFromEvent(event, ids);
  clearGenericDndState("question");
  if (!canMoveQuestionsToDestination(ids, destination)) return null;
  destination.container?.classList.add("drag-over");
  if (destination.kind === "group") {
    document.querySelector(`[data-question-group-row="${CSS.escape(destination.groupId)}"]`)?.classList.add("drag-over");
  }
  renderQuestionDropPlaceholder(destination, ids);
  return destination;
}

function applyQuestionDropDestination(ids = [], destination = {}) {
  if (!canMoveQuestionsToDestination(ids, destination)) return false;
  const draggedIds = [...new Set(ids.filter(Boolean))];
  const previousGroups = new Set();
  const items = draggedIds
    .map((id) => (state.questions || []).find((item) => item.id === id))
    .filter(Boolean);
  items.forEach((item) => {
    if (String(item.questionGroupId || "").trim()) previousGroups.add(item.questionGroupId);
    if (destination.kind === "group") item.questionGroupId = destination.groupId;
    if (destination.kind === "table") delete item.questionGroupId;
  });
  rewriteQuestionOrderForDestination(destination, draggedIds);
  previousGroups.forEach((groupId) => syncQuestionGroupState(groupId));
  if (destination.kind === "group") syncQuestionGroupState(destination.groupId, { removeEmpty: false });
  questionSelectedRowIds = draggedIds;
  closePlannerCardMenu();
  return true;
}

function editableTitleForQuestionGroup(id) {
  if (!id) return null;
  const modalTitle = document.querySelector(`#questionGroupViewModal [data-edit-record="questions"][data-id="${CSS.escape(id)}"][data-field="name"]`);
  if (modalTitle) return modalTitle;
  return [...document.querySelectorAll("[data-question-id]")]
    .filter((row) => row.dataset.questionId === id)
    .map((row) => row.querySelector('[data-edit-record="questions"][data-field="name"]'))
    .find(Boolean);
}

function focusQuestionGroupTitleEdit(id) {
  if (!id) return;
  openQuestionGroupView(id);
  requestAnimationFrame(() => {
    const titleElement = editableTitleForQuestionGroup(id);
    if (!titleElement) return;
    startInlineEdit(titleElement);
  });
}

function questionGroupTone(accuracy) {
  return accuracy >= 80 ? "green" : accuracy >= 60 ? "yellow" : "red";
}

function activeQuestionGroupView() {
  if (!activeQuestionGroupViewId) return null;
  return (state.questions || []).find((item) => item.id === activeQuestionGroupViewId && isQuestionGroup(item)) || null;
}

function setQuestionGroupViewModalOpen(open) {
  if (open) {
    lockBodyScroll();
    return;
  }
  syncBodyScrollLock();
}

function openQuestionGroupView(groupId = "") {
  const group = (state.questions || []).find((item) => item.id === groupId && isQuestionGroup(item));
  if (!group) return;
  closeAllOverlays();
  activeQuestionGroupViewId = group.id;
  questionGroupViewSearch = "";
  setQuestionGroupViewModalOpen(true);
  renderQuestionGroupViewModal();
  pushSubStateToHistory();
}

function closeQuestionGroupView() {
  activeQuestionGroupViewId = "";
  questionGroupViewSearch = "";
  renderQuestionGroupViewModal();
  setQuestionGroupViewModalOpen(false);
  syncURLParams();
}

function questionGroupViewCardSearchText(item = {}) {
  return normalizeHeader([
    item.subject,
    item.content,
    item.subcontent,
    item.date,
    String(item.questions),
    String(item.correct),
    questionPerformanceLabel(item.correct, item.questions),
  ].filter(Boolean).join(" "));
}

function renderQuestionGroupViewCard(item, groupId = "") {
  const accuracy = pct(item.correct, item.questions);
  const performance = questionPerformanceLabel(item.correct, item.questions);
  const done = questionRecordDone(item);
  const searchText = questionGroupViewCardSearchText(item);
  const itemId = escapeAttribute(item.id);
  const itemContent = item.content || "Sem conteúdo";
  const itemSubcontent = item.subcontent || "Sem subconteúdo";
  return `
<article class="question-group-view-card ${done ? "done" : ""} ${questionSelectedRowClass(item)}" data-question-id="${itemId}" data-question-group-parent="${escapeAttribute(groupId)}" data-question-group-view-card data-search="${escapeAttribute(searchText)}" draggable="true">
<div class="question-group-view-card-head">
<label class="question-group-view-check" title="Marcar como feita">
<input type="checkbox" data-action="toggle-question-done" data-id="${itemId}" ${done ? "checked" : ""}>
</label>
<span class="pill question-group-view-subject-pill">${escapeHtml(item.subject || "Geral")}</span>
<button class="icon-button question-group-view-delete" data-action="delete-question" data-id="${itemId}" type="button" title="Apagar" aria-label="Apagar lista">×</button>
</div>
<div class="question-group-view-card-body">
<strong data-edit-record="questions" data-id="${itemId}" data-field="content" data-type="text" class="question-group-view-title" title="${escapeAttribute(itemContent)}">${escapeHtml(itemContent)}</strong>
<small data-edit-record="questions" data-id="${itemId}" data-field="subcontent" data-type="text" class="question-group-view-subtitle">${escapeHtml(itemSubcontent)}</small>
<div class="question-group-view-meta">
<span class="question-group-view-date">${formatDate(item.date)}</span>
<span class="question-group-view-metric"><b class="question-group-view-metric-value" data-edit-record="questions" data-id="${itemId}" data-field="questions" data-type="number">${item.questions}</b><small>QUESTÕES</small></span>
</div>
<div class="question-group-view-stats">
<span class="pill ${questionGroupTone(accuracy)} question-group-view-badge">${accuracy}%</span>
<span class="pill ${performanceTone(performance)} question-group-view-badge">${performance}</span>
<span class="question-group-view-metric"><b class="question-group-view-metric-value" data-edit-record="questions" data-id="${itemId}" data-field="correct" data-type="number">${item.correct}</b><small>ACERTOS</small></span>
<span class="question-group-view-ratio"><b class="question-group-view-metric-value">${item.correct}</b>/<b class="question-group-view-metric-value">${item.questions}</b></span>
</div>
</div>
</article>
`;
}

function applyQuestionGroupViewSearch() {
  const modal = document.querySelector("#questionGroupViewModal");
  if (!modal) return;
  const query = normalizeHeader(questionGroupViewSearch);
  let visible = 0;
  modal.querySelectorAll("[data-question-group-view-card]").forEach((card) => {
    const match = !query || String(card.dataset.search || "").includes(query);
    card.hidden = !match;
    if (match) visible += 1;
  });
  const empty = modal.querySelector("[data-question-group-view-empty]");
  if (empty) empty.hidden = Boolean(visible);
}

function refreshQuestionGroupViewModalState(changedItemId = "") {
  const modal = document.querySelector("#questionGroupViewModal");
  const group = activeQuestionGroupView();
  if (!modal || !group) return;
  const children = questionGroupChildren(group.id, { subjectFilter: "all" });
  const metrics = questionGroupMetrics(children);
  const changedItem = children.find((item) => item.id === changedItemId);
  if (changedItem) {
    const done = questionRecordDone(changedItem);
    const card = modal.querySelector(`[data-question-id="${CSS.escape(changedItem.id)}"][data-question-group-view-card]`);
    card?.classList.toggle("done", done);
    const checkbox = card?.querySelector('input[data-action="toggle-question-done"]');
    if (checkbox) checkbox.checked = done;
    const label = card?.querySelector(".question-group-view-check");
    if (label) label.title = done ? "Marcar como pendente" : "Marcar como feita";
  }
  const summary = modal.querySelector(".question-group-view-title p");
  if (summary) summary.textContent = `${metrics.totalLists} lista${metrics.totalLists === 1 ? "" : "s"} • ${metrics.totalQuestions} questões • ${metrics.accuracy}% de precisão`;
  const progressText = modal.querySelector(".question-group-view-progress span");
  if (progressText) progressText.textContent = `${metrics.accuracy}%`;
  const progressBar = modal.querySelector(".question-group-view-progress i");
  if (progressBar) progressBar.style.width = `${metrics.accuracy}%`;
  applyQuestionGroupViewSearch();
}

function renderQuestionGroupViewModal() {
  let modal = document.querySelector("#questionGroupViewModal");
  const group = activeQuestionGroupView();
  if (!group) {
    if (modal) modal.remove();
    if (activeQuestionGroupViewId) activeQuestionGroupViewId = "";
    setQuestionGroupViewModalOpen(false);
    return;
  }
  const children = questionGroupChildren(group.id, { subjectFilter: "all" });
  const metrics = questionGroupMetrics(children);
  if (!modal) {
    modal = document.createElement("div");
    modal.id = "questionGroupViewModal";
    document.body.appendChild(modal);
  }
  modal.className = "question-group-view-modal";
  modal.setAttribute("aria-hidden", "false");
  modal.innerHTML = `
    <section class="question-group-view-dialog" role="dialog" aria-modal="true" aria-labelledby="questionGroupViewTitle">
      <header class="question-group-view-head">
        <button class="ghost-button question-group-view-back" data-action="close-question-group-view" type="button">Voltar</button>
        <div class="question-group-view-title">
          <span class="eyebrow">Grupo de listas</span>
          <h3 id="questionGroupViewTitle" data-edit-record="questions" data-id="${escapeAttribute(group.id)}" data-field="name" data-type="text">${escapeHtml(group.name || "Grupo de listas")}</h3>
          <p>${metrics.totalLists} lista${metrics.totalLists === 1 ? "" : "s"} • ${metrics.totalQuestions} questões • ${metrics.accuracy}% de precisão</p>
        </div>
        <div class="question-group-view-head-actions">
          <button class="ghost-button questions-add-button" data-action="open-question-modal" data-group-id="${escapeAttribute(group.id)}" type="button">+ Adicionar lista</button>
          <button class="icon-button question-group-view-close" data-action="close-question-group-view" type="button" aria-label="Fechar">×</button>
        </div>
      </header>
      <div class="question-group-view-tools">
        <label class="question-group-view-search">
          <span>Buscar listas</span>
          <input type="search" data-question-group-view-search placeholder="Buscar listas..." value="${escapeAttribute(questionGroupViewSearch)}">
        </label>
        <div class="question-group-view-progress" aria-label="Progresso do grupo">
          <span>${metrics.accuracy}%</span>
          <div><i style="width:${metrics.accuracy}%"></i></div>
        </div>
      </div>
      <div class="question-group-view-body">
        <div class="question-group-view-grid">
          ${children.map((item) => renderQuestionGroupViewCard(item, group.id)).join("")}
        </div>
        <div class="empty question-group-view-empty" data-question-group-view-empty ${children.length ? "hidden" : ""}>Nenhuma lista dentro deste grupo.</div>
      </div>
    </section>
  `;
  setQuestionGroupViewModalOpen(true);
  applyQuestionGroupViewSearch();
  const scrollableBody = modal.querySelector(".question-group-view-body");
  if (scrollableBody) {
    scrollableBody.addEventListener("wheel", (e) => e.stopPropagation(), { passive: false });
    scrollableBody.addEventListener("touchmove", (e) => {
      const atTop = scrollableBody.scrollTop <= 0;
      const atBottom = scrollableBody.scrollTop >= scrollableBody.scrollHeight - scrollableBody.clientHeight;
      if ((atTop && e.cancelable) || (atBottom && e.cancelable)) return;
      e.preventDefault();
    }, { passive: false });
  }
}

function getErrorAlternatives(item) {
  if (typeof item.alternatives === "string" && item.alternatives.trim()) {
    return item.alternatives
      .split(/\r?\n/)
      .map((line, index) => ({ letter: String.fromCharCode(65 + index), text: line.trim() }))
      .filter((alternative) => alternative.text);
  }
  const letters = ["A", "B", "C", "D", "E"];
  return letters
    .map((letter) => ({ letter, text: item[`alternative${letter}`] || "" }))
    .filter((alternative) => alternative.text.trim());
}

function generateErrorContentPdf() {
  if (!selectedErrorSubject || !selectedErrorContent) return;
  const items = state.errors
    .filter((item) => item.subject === selectedErrorSubject)
    .filter((item) => (item.content || "Sem conteúdo") === selectedErrorContent)
    .filter((item) => !item.done)
    .sort((a, b) => a.date.localeCompare(b.date));

  if (!items.length) {
    alert("Nao ha questoes pendentes de revisao para gerar neste PDF.");
    return;
  }

  const printable = window.open("", "_blank");
  if (!printable) {
    alert("Permita pop-ups para gerar o PDF.");
    return;
  }

  printable.document.write(`
    <!doctype html>
    <html lang="pt-BR">
      <head>
        <meta charset="UTF-8">
        <title>${escapeHtml(selectedErrorContent)} - caderno de erros</title>
        <style>
          :root {
            --ink: #18201d;
            --muted: #5f6f67;
            --line: #d8e1db;
            --soft: #f5f8f6;
            --accent: #2f7a59;
            --accent-soft: #e6f1eb;
            --danger-soft: #fff1ef;
          }
          * { box-sizing: border-box; }
          body {
            font-family: Inter, system-ui, sans-serif;
            color: var(--ink);
            margin: 0;
            padding: 24px;
            line-height: 1.55;
            background: #eef4f0;
          }
          button {
            border: 0;
            border-radius: 999px;
            padding: 12px 18px;
            background: var(--accent);
            color: #fff;
            font-weight: 700;
            cursor: pointer;
            margin-bottom: 18px;
          }
          .sheet {
            max-width: 920px;
            margin: 0 auto;
          }
          .cover {
            padding: 28px;
            border: 1px solid var(--line);
            border-radius: 24px;
            background: linear-gradient(145deg, #ffffff, #f3f8f5);
            box-shadow: 0 14px 40px rgba(20, 40, 30, 0.08);
            margin-bottom: 18px;
          }
          .eyebrow {
            display: inline-block;
            padding: 6px 10px;
            border-radius: 999px;
            background: var(--accent-soft);
            color: var(--accent);
            font-size: 12px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.08em;
          }
          h1 {
            margin: 14px 0 8px;
            font-size: 30px;
            line-height: 1.15;
          }
          .meta {
            color: var(--muted);
            margin: 0;
            font-size: 15px;
          }
          .summary {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 12px;
            margin-top: 20px;
          }
          .summary-card {
            padding: 14px 16px;
            border: 1px solid var(--line);
            border-radius: 18px;
            background: #fff;
          }
          .summary-card span {
            display: block;
            color: var(--muted);
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            font-weight: 800;
          }
          .summary-card strong {
            display: block;
            margin-top: 6px;
            font-size: 24px;
          }
          .question {
            break-inside: avoid;
            margin-top: 18px;
            padding: 22px 24px;
            border: 1px solid var(--line);
            border-radius: 22px;
            background: #fff;
            box-shadow: 0 10px 28px rgba(20, 40, 30, 0.05);
          }
          .question-head {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            align-items: flex-start;
            margin-bottom: 12px;
          }
          .question-title {
            margin: 0;
            font-size: 20px;
            line-height: 1.25;
          }
          .question-badge {
            flex: none;
            padding: 6px 10px;
            border-radius: 999px;
            background: var(--danger-soft);
            color: #9c4339;
            font-size: 12px;
            font-weight: 800;
          }
          .question-meta {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 14px;
          }
          .question-meta span {
            padding: 5px 10px;
            border-radius: 999px;
            background: var(--soft);
            color: var(--muted);
            font-size: 12px;
            font-weight: 700;
          }
          .question-block {
            margin-top: 14px;
          }
          .question-block h3 {
            margin: 0 0 8px;
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            color: var(--muted);
          }
          .header {
            white-space: pre-wrap;
          }
          .header > *:first-child { margin-top: 0; }
          .header > *:last-child { margin-bottom: 0; }
          img {
            max-width: 100%;
            max-height: 460px;
            margin: 12px 0 0;
            border: 1px solid var(--line);
            border-radius: 16px;
            display: block;
          }
          ol {
            margin: 0;
            padding-left: 24px;
          }
          li {
            margin: 8px 0;
            white-space: pre-wrap;
            padding-left: 4px;
          }
          .answer-key {
            break-before: page;
            margin-top: 20px;
            padding: 24px;
            border: 1px solid var(--line);
            border-radius: 22px;
            background: #fff;
          }
          .answer-key h2 {
            margin: 0 0 12px;
            font-size: 22px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            overflow: hidden;
            border-radius: 16px;
            border: 1px solid var(--line);
          }
          thead {
            background: var(--soft);
          }
          th, td {
            border-bottom: 1px solid var(--line);
            padding: 10px 12px;
            text-align: left;
            vertical-align: top;
          }
          tbody tr:last-child td {
            border-bottom: 0;
          }
          @media print {
            body {
              margin: 0;
              padding: 0;
              background: #fff;
            }
            button { display: none; }
            .sheet {
              max-width: none;
              margin: 0;
              padding: 16mm;
            }
            .cover, .question, .answer-key {
              box-shadow: none;
            }
          }
        </style>
      </head>
      <body>
        <button onclick="window.print()">Salvar como PDF</button>
        <main class="sheet">
          <section class="cover">
            <span class="eyebrow">Caderno de erros</span>
            <h1>${escapeHtml(selectedErrorContent)}</h1>
            <p class="meta">${escapeHtml(selectedErrorSubject)} · somente questões pendentes de revisão</p>
            <div class="summary">
              <article class="summary-card">
                <span>Questões no PDF</span>
                <strong>${items.length}</strong>
              </article>
              <article class="summary-card">
                <span>Filtro</span>
                <strong>Pendentes</strong>
              </article>
              <article class="summary-card">
                <span>Gerado em</span>
                <strong>${formatDate(localDateValue())}</strong>
              </article>
            </div>
          </section>
          ${items.map(renderErrorPdfQuestion).join("")}
          <section class="answer-key">
            <h2>Gabarito rápido</h2>
            <table>
              <thead><tr><th>#</th><th>Questão</th><th>Correta</th><th>Marcada</th></tr></thead>
              <tbody>
                ${items.map((item, index) => `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${escapeHtml(item.source || "")}</td>
                    <td>${escapeHtml(item.correctAnswer || "-")}</td>
                    <td>${escapeHtml(item.markedAnswer || "-")}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          </section>
        </main>
        <script>
          window.addEventListener("load", () => setTimeout(() => window.print(), 300));
        </script>
      </body>
    </html>
  `);
  printable.document.close();
}

function renderErrorPdfQuestion(item, index) {
  const alternatives = getErrorAlternatives(item);
  return `
    <section class="question">
      <div class="question-head">
        <h2 class="question-title">${index + 1}. ${escapeHtml(item.source || "Questão")}</h2>
        <span class="question-badge">Revisão pendente</span>
      </div>
      <div class="question-meta">
        <span>${escapeHtml(item.subject || "Sem matéria")}</span>
        <span>${escapeHtml(item.content || "Sem conteúdo")}</span>
        <span>${formatDate(item.date || localDateValue())}</span>
      </div>
      <div class="question-block">
        <h3>Enunciado</h3>
        <div class="header">${renderQuestionHeaderContent(item)}</div>
      </div>
      ${
        alternatives.length
          ? `
            <div class="question-block">
              <h3>Alternativas</h3>
              <ol type="A">${alternatives.map((alternative) => `<li>${escapeHtml(alternative.text)}</li>`).join("")}</ol>
            </div>
          `
          : ""
      }
    </section>
  `;
}

function escapeHtml(value) {
  return sharedEscapeHtml(value);
}

function escapeAttribute(value) {
  return sharedEscapeAttribute(value);
}

function safeOption(value) {
  return sharedSafeOption(value);
}

function groupBy(items, callback) {
  return sharedGroupBy(items, callback);
}

function subjectClass(subject) {
  return subject
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/ç/g, "c")
    .replace(/\s+/g, "-");
}

function removeMockRecord(id = "", shouldConfirm = true) {
  const record = activeRecords(state.mocks || []).find((item) => item.id === id);
  if (!record) return false;
  const label = record.name || "este simulado";
  if (shouldConfirm) {
    openDeleteConfirmModal({
      title: "Excluir simulado",
      subtitle: "Esse registro sairá da seção Simulados",
      message: `Excluir "${label}" da seção Simulados?`,
      onConfirm: () => {
        if (!removeMockRecord(id, false)) return;
        renderSectionNow("simulados", { save: true, commit: true });
        showUndoToast("Simulado excluído.");
      },
    });
    return false;
  }
  removeRecord("mocks", id);
  return true;
}

function removeEssayRecord(id = "", shouldConfirm = true) {
  const record = activeRecords(state.essays || []).find((item) => item.id === id);
  if (!record) return false;
  const label = record.theme || "esta redação";
  if (shouldConfirm) {
    openDeleteConfirmModal({
      title: "Excluir redação",
      subtitle: "Esse registro sairá da seção Redações",
      message: `Excluir "${label}" da seção Redações?`,
      onConfirm: () => {
        if (!removeEssayRecord(id, false)) return;
        renderSectionNow("redacoes", { save: true, commit: true });
        showUndoToast("Redação excluída.");
      },
    });
    return false;
  }
  removeRecord("essays", id);
  return true;
}

function removeErrorRecord(id = "", shouldConfirm = true) {
  const record = activeRecords(state.errors || []).find((item) => item.id === id);
  if (!record) return false;
  const label = record.source || "este erro";
  if (shouldConfirm) {
    openDeleteConfirmModal({
      title: "Excluir item do Caderno de Erros",
      subtitle: "Esse registro será removido da lista",
      message: `Excluir "${label}" do Caderno de Erros?`,
      onConfirm: () => {
        if (!removeErrorRecord(id, false)) return;
        renderSectionNow("erros", { save: true, commit: true });
        showUndoToast("Questão do caderno excluída.");
      },
    });
    return false;
  }
  removeRecord("errors", id);
  if (selectedErrorContent && (!state.errors.some((item) => (item.content || "Sem conteúdo") === selectedErrorContent))) {
    selectedErrorContent = "";
  }
  if (selectedErrorSubject && (!state.errors.some((item) => subjectMatchesName(item.subject, selectedErrorSubject)))) {
    selectedErrorSubject = "";
  }
  return true;
}

function normalizeErrorRecord(item = {}) {
  const normalized = {
    ...item,
    date: String(item.date || "").trim() || errorTodayValue(),
    subject: canonicalSubject(item.subject || "Geral"),
    content: String(item.content || "").trim() || "Sem conteúdo",
    source: String(item.source || "").trim() || "Questão",
    reason: String(item.reason || "").trim(),
    correctAnswer: String(item.correctAnswer || "").trim().toUpperCase(),
    markedAnswer: String(item.markedAnswer || "").trim().toUpperCase(),
    done: Boolean(item.done),
  };
  if (normalized.subject && normalized.subject !== "Geral") ensureSubjectExists(normalized.subject);
  rememberContent(normalized.subject, normalized.content);
  return normalized;
}

function performanceLabel(correct, total) {
  if (correct === null || correct === undefined || correct === "") return "-";
  const parsedCorrect = Number(correct);
  const parsedTotal = Number(total);
  if (!Number.isFinite(parsedCorrect) || !Number.isFinite(parsedTotal) || parsedTotal <= 0) return "-";
  if (parsedCorrect >= parsedTotal * 0.8) return "ótimo";
  if (parsedCorrect >= parsedTotal * 0.6) return "regular";
  return "ruim";
}

function performanceTone(label) {
  const normalized = normalizeHeader(label);
  if (normalized === "otimo") return "green";
  if (["bom", "regular"].includes(normalized)) return "yellow";
  if (normalized === "ruim") return "red";
  return "";
}

async function namedImageFromForm(form, name, options = {}) {
  const pasted = form.dataset[`${name}Pasted`];
  if (pasted) return pasted;
  const input = form.querySelector(`input[type="file"][name="${name}"]`);
  const file = input?.files?.[0];
  if (!file) return "";

  return imageFromFile(file, options);
}

async function imageFromFile(file, options = {}) {
  const finalFile = options.edit ? await editImageFile(file) : file;
  if (window.studyCloud?.enabled) {
    try {
      return await window.studyCloud.uploadImage(finalFile);
    } catch (error) {
      alert(`Não consegui sincronizar a imagem agora: ${error.message}`);
    }
  }

  return imageToDataUrl(finalFile);
}

function imageToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result));
    reader.addEventListener("error", () => reject(reader.error));
    reader.readAsDataURL(file);
  });
}

function editImageFile(file) {
  return new Promise((resolve) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(url);
      openImageEditor(image, file, resolve);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    image.src = url;
  });
}

function openImageEditor(image, file, resolve) {
  const modal = document.createElement("div");
  modal.className = "image-editor-modal";
  modal.innerHTML = `
    <div class="image-editor-card">
      <div class="image-editor-head">
        <strong>Editar imagem</strong>
        <button class="icon-button" type="button" data-editor-action="cancel">×</button>
      </div>
      <canvas width="760" height="460"></canvas>
      <div class="image-editor-controls">
        <label>
          <span>Zoom</span>
          <input type="range" min="0.2" max="3" step="0.05" value="1" data-editor-zoom>
        </label>
        <button class="ghost-button" type="button" data-editor-action="rotate-left">Girar -90°</button>
        <button class="ghost-button" type="button" data-editor-action="rotate-right">Girar +90°</button>
        <button class="ghost-button" type="button" data-editor-action="keep">Usar original</button>
        <button type="button" data-editor-action="apply">Aplicar corte</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  syncBodyScrollLock();

  const canvas = modal.querySelector("canvas");
  const ctx = canvas.getContext("2d");
  const zoomInput = modal.querySelector("[data-editor-zoom]");
  const crop = { x: 70, y: 42, width: canvas.width - 140, height: canvas.height - 84 };
  const state = { zoom: 1, rotate: 0, offsetX: 0, offsetY: 0, dragging: false, lastX: 0, lastY: 0 };

  const draw = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#101211";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.beginPath();
    ctx.rect(crop.x, crop.y, crop.width, crop.height);
    ctx.clip();
    ctx.translate(canvas.width / 2 + state.offsetX, canvas.height / 2 + state.offsetY);
    ctx.rotate((state.rotate * Math.PI) / 180);
    const rotated = Math.abs(state.rotate % 180) === 90;
    const baseWidth = rotated ? image.height : image.width;
    const baseHeight = rotated ? image.width : image.height;
    const scale = Math.max(crop.width / baseWidth, crop.height / baseHeight) * state.zoom;
    ctx.drawImage(image, (-image.width * scale) / 2, (-image.height * scale) / 2, image.width * scale, image.height * scale);
    ctx.restore();
    ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
    ctx.fillRect(0, 0, canvas.width, crop.y);
    ctx.fillRect(0, crop.y + crop.height, canvas.width, canvas.height - crop.y - crop.height);
    ctx.fillRect(0, crop.y, crop.x, crop.height);
    ctx.fillRect(crop.x + crop.width, crop.y, canvas.width - crop.x - crop.width, crop.height);
    ctx.strokeStyle = "#5aa7ff";
    ctx.lineWidth = 2;
    ctx.strokeRect(crop.x, crop.y, crop.width, crop.height);
  };

  const close = (result) => {
    modal.remove();
    syncBodyScrollLock();
    resolve(result);
  };

  zoomInput.addEventListener("input", () => {
    state.zoom = Number(zoomInput.value);
    draw();
  });

  canvas.addEventListener("pointerdown", (event) => {
    state.dragging = true;
    state.lastX = event.clientX;
    state.lastY = event.clientY;
    canvas.setPointerCapture(event.pointerId);
  });

  canvas.addEventListener("pointermove", (event) => {
    if (!state.dragging) return;
    state.offsetX += event.clientX - state.lastX;
    state.offsetY += event.clientY - state.lastY;
    state.lastX = event.clientX;
    state.lastY = event.clientY;
    draw();
  });

  canvas.addEventListener("pointerup", () => {
    state.dragging = false;
  });

  modal.addEventListener("click", (event) => {
    const action = event.target.closest("[data-editor-action]")?.dataset.editorAction;
    if (!action) return;
    if (action === "cancel" || action === "keep") close(file);
    if (action === "rotate-left" || action === "rotate-right") {
      state.rotate += action === "rotate-left" ? -90 : 90;
      draw();
    }
    if (action === "apply") {
      const output = document.createElement("canvas");
      output.width = 1400;
      output.height = Math.round((crop.height / crop.width) * output.width);
      output.getContext("2d").drawImage(canvas, crop.x, crop.y, crop.width, crop.height, 0, 0, output.width, output.height);
      output.toBlob(
        (blob) => close(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" })),
        "image/jpeg",
        0.9,
      );
    }
  });

  draw();
}

function rangeBelongsToEditor(range, editor) {
  return range && editor.contains(range.commonAncestorContainer);
}

function placeCaretAfter(node) {
  const range = document.createRange();
  range.setStartAfter(node);
  range.collapse(true);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
}

async function insertImageIntoRichEditor(editor, file, insertionRange = null) {
  if (!editor) return;

  const dataUrl = await imageFromFile(file);
  const img = document.createElement("img");
  img.src = dataUrl;
  img.alt = file.name || "Imagem colada";

  const selection = window.getSelection();
  const range = rangeBelongsToEditor(insertionRange, editor)
    ? insertionRange
    : selection.rangeCount > 0 && rangeBelongsToEditor(selection.getRangeAt(0), editor)
      ? selection.getRangeAt(0)
      : null;

  if (range) {
    range.deleteContents();
    const spacer = document.createElement("br");
    range.insertNode(img);
    img.after(spacer);
    placeCaretAfter(spacer);
  } else {
    editor.appendChild(img);
    const spacer = document.createElement("br");
    editor.appendChild(spacer);
    placeCaretAfter(spacer);
  }

  editor.focus();
}

function sanitizeQuestionHeaderHtml(html) {
  const template = document.createElement("template");
  template.innerHTML = html || "";
  const allowedTags = new Set(["BR", "DIV", "P", "SPAN", "STRONG", "B", "EM", "I", "U", "UL", "OL", "LI", "IMG"]);

  const cleanNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.textContent || "");
    if (node.nodeType !== Node.ELEMENT_NODE) return document.createTextNode("");

    const tag = allowedTags.has(node.tagName) ? node.tagName.toLowerCase() : "span";
    const clone = document.createElement(tag);
    if (node.tagName === "IMG") {
      const src = node.getAttribute("src") || "";
      if (!src) return document.createTextNode("");
      clone.src = src;
      clone.alt = node.getAttribute("alt") || "Imagem";
      return clone;
    }

    Array.from(node.childNodes).forEach((child) => clone.appendChild(cleanNode(child)));
    return clone;
  };

  const fragment = document.createDocumentFragment();
  Array.from(template.content.childNodes).forEach((child) => fragment.appendChild(cleanNode(child)));
  const wrapper = document.createElement("div");
  wrapper.appendChild(fragment);
  return wrapper.innerHTML.trim();
}

function legacyQuestionHeaderToHtml(item) {
  const text = escapeHtml(item.questionHeader || "");
  const images = item.questionHeaderImages || [];
  let html = text.replace(/\[\[IMG:(\d+)\]\]/g, (_, imageIndex) => {
    const image = images[Number(imageIndex)];
    return image ? `<img src="${escapeAttribute(image.src)}" alt="${escapeAttribute(image.name || "Imagem")}">` : "";
  }).replace(/\n/g, "<br>");

  [
    { src: item.image, alt: "Imagem da questão" },
    { src: item.resolutionImage, alt: "Imagem da resolução" },
  ].forEach((image) => {
    if (!image.src) return;
    html += `${html ? "<br>" : ""}<img src="${escapeAttribute(image.src)}" alt="${image.alt}">`;
  });

  return html;
}

function renderQuestionHeaderContent(item) {
  return sanitizeQuestionHeaderHtml(item.questionHeaderHtml || legacyQuestionHeaderToHtml(item));
}

function renderRichTextContent(html, fallbackText = "") {
  return sanitizeQuestionHeaderHtml(html || escapeHtml(fallbackText || "").replace(/\n/g, "<br>"));
}

function getRichEditorHtml(selector) {
  const editor = document.querySelector(selector);
  return editor ? sanitizeQuestionHeaderHtml(editor.innerHTML) : "";
}

function getRichEditorText(selector) {
  const editor = document.querySelector(selector);
  return editor ? editor.innerText.trim() : "";
}

function setRichEditorHtml(selector, html) {
  const editor = document.querySelector(selector);
  if (editor) editor.innerHTML = sanitizeQuestionHeaderHtml(html);
}

function getQuestionHeaderHtml() {
  return getRichEditorHtml("#questionHeaderEditor");
}

function getQuestionHeaderText() {
  return getRichEditorText("#questionHeaderEditor");
}

function setQuestionHeaderHtml(html) {
  setRichEditorHtml("#questionHeaderEditor", html);
}

function setupRichTextEditor(selector) {
  const editor = document.querySelector(selector);
  if (!editor) return;

  editor.addEventListener("paste", async (event) => {
    const items = event.clipboardData?.items || [];
    const imageItems = Array.from(items).filter((item) => item.kind === "file" && item.type.startsWith("image/"));
    if (!imageItems.length) return;

    event.preventDefault();
    const selection = window.getSelection();
    let insertionRange = selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null;

    for (const item of imageItems) {
      const file = item.getAsFile();
      if (file) await insertImageIntoRichEditor(editor, file, insertionRange);
      insertionRange = null;
    }
  });

  editor.addEventListener("drop", async (event) => {
    const files = Array.from(event.dataTransfer?.files || []).filter((file) => file.type.startsWith("image/"));
    if (!files.length) return;
    event.preventDefault();

    let range = document.caretRangeFromPoint?.(event.clientX, event.clientY) || null;
    for (const file of files) {
      await insertImageIntoRichEditor(editor, file, range);
      range = null;
    }
  });

  editor.addEventListener("dragover", (event) => {
    if (Array.from(event.dataTransfer?.items || []).some((item) => item.type.startsWith("image/"))) {
      event.preventDefault();
    }
  });

  editor.addEventListener("click", (event) => {
    const image = event.target.closest("img");
    if (image) openImageModal(image.src);
  });
}

function setupQuestionHeaderEditor() {
  setupRichTextEditor("#questionHeaderEditor");
}

function setupErrorFixEditor() {
  setupRichTextEditor("#errorFixEditor");
}

/* =====================================================
   FORMULARIOS, EVENTOS E INICIALIZACAO
   Delegacao de eventos, submits, atalhos, drag/drop e bootstrap.
===================================================== */
function renderThumb(image) {
  if (!image) return "";
  return `
    <button class="image-thumb-button image-open" data-image-src="${image}" type="button" title="Abrir imagem anexada">
      <img class="table-thumb" src="${image}" alt="Imagem anexada">
    </button>
  `;
}

function handleForm(formId, callback) {
  const formElement = document.querySelector(formId);
  if (!formElement) return;
  formElement.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const preservedValues = formId === "#errorForm" ? {
      date: form.elements.date?.value || "",
      subject: form.elements.subject?.value || "",
      content: form.elements.content?.value || "",
    } : null;
    const data = Object.fromEntries(new FormData(form).entries());
    if (formId === "#errorForm") {
      data.questionHeaderHtml = getQuestionHeaderHtml();
      data.questionHeader = getQuestionHeaderText();
      data.fixHtml = getRichEditorHtml("#errorFixEditor");
      data.fix = getRichEditorText("#errorFixEditor");
      data.image = "";
      data.resolutionImage = "";
    } else {
      data.image = await namedImageFromForm(form, "image");
    }
    callback(data);
    rememberSmartForm(form, data);
    delete form.dataset.imagePasted;
    delete form.dataset.imageName;
    setQuestionHeaderHtml("");
    setRichEditorHtml("#errorFixEditor", "");
    resetSmartFormState(form);
    form.reset();
    setTodayDefaults();
    if (preservedValues) {
      Object.entries(preservedValues).forEach(([name, value]) => {
        if (value !== "" && form.elements[name]) {
          form.elements[name].value = value;
        }
      });
    }
    prepareRenderState();
    if (activeViewId() !== "dashboard") renderMetrics({ includeDashboard: false });
    renderSection(activeViewId());
    renderSectionUi(activeViewId());
  });
}

function bindQueryEvent(selector, eventName, handler) {
  const element = document.querySelector(selector);
  if (!element) return null;
  element.addEventListener(eventName, handler);
  return element;
}

function setupEssayScoreAutoFill() {
  const form = document.querySelector("#essayForm");
  if (!form) return;
  const scoreInput = form.elements.score;
  const updateScore = () => {
    const draft = {
      c1: form.elements.c1.value,
      c2: form.elements.c2.value,
      c3: form.elements.c3.value,
      c4: form.elements.c4.value,
      c5: form.elements.c5.value,
    };
    scoreInput.value = essayScoreTotal(draft);
  };

  ["c1", "c2", "c3", "c4", "c5"].forEach((field) => {
    form.elements[field]?.addEventListener("input", updateScore);
  });
  updateScore();
}

function setupForms() {
  setupEssayScoreAutoFill();
  setupQuestionHeaderEditor();
  setupErrorFixEditor();
  applySmartFieldMemory();

  handleForm("#questionForm", (data) => {
    const date = String(data.date || "").trim() || questionTodayValue();
    const item = editingQuestionId
      ? (state.questions || []).find((entry) => entry.id === editingQuestionId && !isQuestionGroup(entry))
      : null;
    if (item) {
      item.date = date;
      item.subject = canonicalSubject(String(data.subject || "").trim() || "Geral");
      item.content = String(data.content || "").trim();
      item.subcontent = String(data.subcontent || "").trim();
      item.questions = Math.max(1, number(data.questions));
      item.correct = Math.min(item.questions, Math.max(0, number(data.correct)));
      if (item.subject && item.subject !== "Geral") ensureSubjectExists(item.subject);
      rememberContent(item.subject, item.content);
      syncQuestionPerformance(item);
      if (item.questionGroupId) syncQuestionGroupState(item.questionGroupId, { removeEmpty: false });
      touchRecord("questions", item.id);
      syncPlannerLinks();
      editingQuestionId = "";
    } else {
      const groupId = pendingQuestionGroupId;
      addQuestionRecord(
        { ...data, date },
        groupId ? { extra: { questionGroupId: groupId } } : {},
      );
      if (groupId) syncQuestionGroupState(groupId, { removeEmpty: false });
    }
    closeQuestionModal();
  });

  handleForm("#questionGroupForm", (data) => {
    confirmQuestionGroupModal(
      String(data.groupName || "").trim(),
      String(data.groupSubject || "").trim(),
    );
  });

  handleForm("#errorForm", (data) => {
    const record = normalizeErrorRecord({
      id: uid(),
      ...data,
      done: false,
    });
    state.errors = [...(state.errors || []), record];
    touchRecord("errors", record.id);
    closeEntryModal("errorModal");
  });

  handleForm("#mockForm", (data) => {
    const target = data.mockTarget || "day1";
    const examDay = target === "day1" || target === "day2" ? target : "";
    const record = normalizeMockRecord({
      id: uid(),
      ...data,
      subject: examDay ? "Geral" : target,
      examDay,
      mockType: examDay || "subject",
      date: String(data.date || "").trim() || mockTodayValue(),
    });
    state.mocks = [...(state.mocks || []), record];
    touchRecord("mocks", record.id);
    closeEntryModal("mockModal");
  });

  handleForm("#essayForm", (data) => {
    const essay = {
      c1: Math.min(200, Math.max(0, number(data.c1))),
      c2: Math.min(200, Math.max(0, number(data.c2))),
      c3: Math.min(200, Math.max(0, number(data.c3))),
      c4: Math.min(200, Math.max(0, number(data.c4))),
      c5: Math.min(200, Math.max(0, number(data.c5))),
    };
    const record = {
      id: uid(),
      ...data,
      ...essay,
      score: essayScoreTotal(essay),
    };
    state.essays = [...(state.essays || []), record];
    touchRecord("essays", record.id);
    closeEntryModal("essayModal");
  });

}

function setupActions() {
  setupCloudAuth();
  setupFirstLogin();

  document.addEventListener("click", (event) => {
    if (!event.target.closest(".timer-subject-menu")) closeTimerSubjectMenu();
    if (event.target.classList?.contains("timer-subject-modal")) closeTimerSubjectModal();
    if (event.target.classList?.contains("subject-modal")) closeSubjectModal();
    if (!event.target.closest(".study-session-menu")) closeStudySessionMenu();
    if (event.target.classList?.contains("study-session-modal")) closeStudySessionModal();
    if (event.target.id === "questionModal") closeQuestionModal();
    if (event.target.id === "questionGroupModal") closeQuestionGroupModal();
    if (event.target.id === "questionGroupBuilderModal") closeQuestionGroupBuilderModal();
    if (event.target.id === "questionGroupViewModal") closeQuestionGroupView();
    if (event.target.id === "deleteConfirmModal") closeDeleteConfirmModal();
    if (event.target.id === "layoutEditExitModal") closeLayoutEditExitModal();
    if (event.target.id === "plannerCreationModal") closePlannerCreationModal();
    if (event.target.id === "plannerLaterModal") closePlannerLaterModal();
    if (event.target.id === "plannerCompletionModal") closePlannerCompletionModal();
    if (event.target.id === "subjectCycleModal") closeSubjectCycleModal();
    if (event.target.id === "cycleLessonModal") closeCycleLessonModal();
    if (event.target.id === "errorModal") closeEntryModal("errorModal");
    if (event.target.id === "mockModal") closeEntryModal("mockModal");
    if (event.target.id === "essayModal") closeEntryModal("essayModal");

    const button = event.target.closest("[data-action='toggle-password']");
    if (!button) return;

    const input = document.querySelector(`#${button.dataset.target}`);
    if (!input) return;
    const visible = input.type === "text";
    input.type = visible ? "password" : "text";
    const eyeIcon = button.querySelector(".eye-icon");
    const eyeOffIcon = button.querySelector(".eye-off-icon");
    if (eyeIcon) eyeIcon.style.display = visible ? "" : "none";
    if (eyeOffIcon) eyeOffIcon.style.display = visible ? "none" : "";
    button.setAttribute("aria-label", visible ? "Mostrar senha" : "Ocultar senha");
    button.setAttribute("title", visible ? "Mostrar senha" : "Ocultar senha");
  });

  document.addEventListener("toggle", (event) => {
    const filterMenu = event.target.closest?.(".filter-menu");
    const actionMenu = event.target.closest?.(".action-menu");
    if (filterMenu) {
      if (filterMenu.open) {
        closeActionMenu();
        closeTopbarMobileMenu();
        closeAllOverlays({ except: ["filterMenus"] });
      }
      syncFilterMenuState();
    }
    if (actionMenu) {
      if (actionMenu.open) {
        closeFilterMenus();
        closeTopbarMobileMenu();
        closeAllOverlays({ except: ["actionMenu"] });
      }
      syncActionMenuState();
    }
    syncTopbarBackdropState();
  }, true);
  syncAllDetailsPanelAccessibility();

  window.addEventListener("resize", refreshFloatingPanels);
  window.addEventListener("scroll", refreshFloatingPanels, true);

  navItems.forEach((item) => {
    item.addEventListener("click", () => {
      if (editMode) return;
      closeTopbarMobileMenu();
      switchView(item.dataset.view);
    });
  });

  document.querySelector("#dashboardPrimaryAction")?.addEventListener("click", () => {
    const target = document.querySelector("#dashboardPrimaryAction")?.dataset?.viewTarget;
    if (target) switchView(target);
  });

  document.querySelector("#dashboardStartFocusBtn")?.addEventListener("click", () => {
    const subject = document.querySelector("#dashboardStartFocusBtn")?.dataset?.subject;
    if (subject) {
      startStudyTimer(subject);
      switchView("tempo");
    }
  });

  document.querySelector("#dashboardEmptyAction")?.addEventListener("click", () => {
    switchView("materias");
  });

const METRIC_VIEW_MAP = {
"metric-questions": "questoes",
"metric-mocks": "simulados",
"metric-essays": "redacoes",
"metric-errors": "erros",
"metric-flashcards": "planner",
"metric-goal": "questoes",
"metric-study-hours": "tempo",
};

function commitMetricEdit(strong) {
  if (!strong || !strong.isContentEditable) return;
  const card = strong.closest(".metric[data-edit-block]");
  if (!card) return;
  strong.contentEditable = "false";
  strong.classList.remove("metric-value-editing");
  const raw = String(strong.textContent || "").trim();
  const num = Number(raw.replace(/[^0-9.,]/g, "").replace(",", "."));
  if (!num || num < 1) { renderSectionNow("dashboard", { save: false }); return; }
  const blockId = card.dataset.editBlock;
  if (blockId === "metric-goal") {
    touchScalar("weeklyGoal", Math.min(10000, Math.round(num)));
  } else if (blockId === "metric-study-hours") {
    touchScalar("studyWeeklyGoalHours", Math.min(168, Math.round(num * 10) / 10));
  }
  renderSectionNow("dashboard", { save: true, commit: true });
}

function enableMetricEdit(card) {
  const strong = card.querySelector("strong");
  if (!strong) return;
  strong.contentEditable = "plaintext-only";
  strong.classList.add("metric-value-editing");
  const range = document.createRange();
  range.selectNodeContents(strong);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
}

document.querySelector("#dashboard .metrics-grid")?.addEventListener("click", (e) => {
if (e.target.closest(".metric-edit-btn, .metric-value-editing")) return;
const card = e.target.closest(".metric[data-edit-block]");
if (!card) return;
const viewId = METRIC_VIEW_MAP[card.dataset.editBlock];
if (viewId) switchView(viewId);
});

document.querySelector("#dashboard .metrics-grid")?.addEventListener("click", (e) => {
const editBtn = e.target.closest(".metric-edit-btn");
if (!editBtn) return;
const card = editBtn.closest(".metric");
if (!card) return;
enableMetricEdit(card);
});

document.querySelector("#dashboard .metrics-grid")?.addEventListener("blur", (e) => {
commitMetricEdit(e.target.closest("strong"));
}, true);

document.querySelector("#dashboard .metrics-grid")?.addEventListener("keydown", (e) => {
const strong = e.target.closest("strong.metric-value-editing");
if (!strong) return;
if (e.key === "Enter") { e.preventDefault(); commitMetricEdit(strong); }
if (e.key === "Escape") { e.preventDefault(); renderSectionNow("dashboard", { save: false }); }
});

document.addEventListener("change", (event) => {
    if (event.target.closest("#chartMode")) {
      drawChartsForSection("dashboard", { force: true });
    }
  });

  document.querySelector("#topbarSectionFilterPanel")?.addEventListener("change", handleTopbarFilterChange);
  document.querySelector("#topbarSectionFilterPanel")?.addEventListener("click", (event) => {
    const resetButton = event.target.closest("#questionFilterReset, #errorFilterReset");
    if (resetButton) handleTopbarFilterChange(event);
  });

  bindQueryEvent("#editModeButton", "click", () => {
    if (editMode) {
      requestExitLayoutEditMode();
      return;
    }
    enterLayoutEditMode();
  });

  bindQueryEvent("#saveLayoutButton", "click", () => {
    saveLayoutEdits();
  });

  bindQueryEvent("#backupButton", "click", () => {
    import("./sections/backups-ui.js").then((mod) => mod.openBackupsModal({
      api: {
        _lastSyncedAt: state.syncMeta?.lastSyncedAt ? new Date(state.syncMeta.lastSyncedAt).toLocaleTimeString() : null,
        openDeleteConfirmModal,
        replaceStateSafely,
        getState: () => cloneStudyState(state),
        saveState,
        render,
        notify,
        exportNow: downloadStateBackup,
      },
    }));
  });
  bindQueryEvent("#importBackupButton", "click", openBackupImportPicker);
  document.querySelector("#backupImportInput")?.addEventListener("change", handleBackupImport);

  bindQueryEvent('#questionForm select[name="subject"]', "change", () => {
    renderContentOptions();
    const form = document.querySelector("#questionForm");
    resetSmartContextDependents(form, "subject", { clearValues: true });
    applySmartFieldMemory(form);
  });
  document.addEventListener("input", (event) => {
    const input = event.target.closest("[data-question-group-view-search]");
    if (!input) return;
    questionGroupViewSearch = input.value || "";
    applyQuestionGroupViewSearch();
  });
  bindQueryEvent('#errorForm select[name="subject"]', "change", () => {
    renderContentOptions();
    const form = document.querySelector("#errorForm");
    resetSmartContextDependents(form, "subject", { clearValues: true });
    applySmartFieldMemory(form);
  });

  document.addEventListener("click", (event) => {
    const control = event.target.closest("#prevMonth, #todayMonth, #nextMonth, #monthViewButton, #weekViewButton, #dayViewButton, #plannerTimeToggle");
    if (!control?.closest("#planner")) return;

    if (control.id === "prevMonth" || control.id === "nextMonth") {
      const direction = control.id === "prevMonth" ? -1 : 1;
      const delta = plannerMode === "week" ? direction * 7 : direction;
      calendarDate =
        plannerMode === "month"
          ? new Date(calendarDate.getFullYear(), calendarDate.getMonth() + delta, 1)
          : new Date(calendarDate.getFullYear(), calendarDate.getMonth(), calendarDate.getDate() + delta);
      renderSectionNow("planner");
      syncURLParams();
      return;
    }

    if (control.id === "todayMonth") {
      calendarDate = new Date();
      renderSectionNow("planner");
      syncURLParams();
      return;
    }

    if (control.id === "plannerTimeToggle") {
      touchScalar("plannerTimeEnabled", !state.plannerTimeEnabled);
      if (!state.plannerTimeEnabled) plannerBuilderTime = "";
      plannerMode = "week";
      saveState();
      renderSectionNow("planner", { commit: true });
      return;
    }

    if (state.plannerTimeEnabled) return;
    plannerMode = control.id === "monthViewButton" ? "month" : control.id === "dayViewButton" ? "day" : "week";
    renderSectionNow("planner");
    pushSubStateToHistory();
  });

  document.querySelector("#seedButton").addEventListener("click", seedExampleData);

  document.querySelector("#clearButton").addEventListener("click", () => {
    openDeleteConfirmModal({
      title: "Apagar dados deste navegador",
      subtitle: "Essa ação remove todos os registros locais",
      message: "Apagar todos os dados salvos neste navegador?",
      confirmLabel: "Apagar tudo",
      onConfirm: () => {
        Object.assign(state, defaultState());
        render();
      },
    });
  });

  document.querySelector("#csvImportInput").addEventListener("change", handleCsvImport);

  document.querySelector("#imageModalClose").addEventListener("click", closeImageModal);
  document.querySelector("#imageModal").addEventListener("click", (event) => {
    if (event.target.id === "imageModal") closeImageModal();
  });

  document.addEventListener("keydown", (event) => {
    const inEditableField = Boolean(event.target.closest("input, textarea, select, [contenteditable='true']"));
    if (event.key === "Escape") {
      closeAllOverlays({ except: ["focusScreen", "imageModal"] });
      if (editMode && !inEditableField) {
        event.preventDefault();
        requestExitLayoutEditMode();
        return;
      }
    }

    if (event.key === " " && !inEditableField && activeStudyTimer) {
      event.preventDefault();
      if (!focusScreenOpen) {
        focusScreenOpen = true;
      } else {
        toggleStudyTimer();
      }
      renderFocusScreen();
      renderFocusController();
      updateStudyTimerDisplay();
    }

    if ((event.key === "f" || event.key === "F") && activeStudyTimer && !focusScreenOpen && !inEditableField) {
      event.preventDefault();
      focusScreenOpen = true;
      renderFocusScreen();
      renderFocusController();
    }

    const isFlashcardTxtImport = event.ctrlKey && event.shiftKey && !event.altKey && event.key.toLowerCase() === "a";
    if (isFlashcardTxtImport && !inEditableField) {
      event.preventDefault();
      openFlashcardTxtImportPicker();
      return;
    }

    const isUndo = event.ctrlKey && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "z";
    const isRedo = event.ctrlKey && event.altKey && !event.shiftKey && event.key.toLowerCase() === "z";
    const isGroupShortcut = event.ctrlKey && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "g";
    const isUngroupShortcut = event.ctrlKey && !event.altKey && event.shiftKey && event.key.toLowerCase() === "g";

    if (isGroupShortcut && !inEditableField) {
      event.preventDefault();
      if (questionSelectedRowIds.length) {
        createQuestionGroupFromSelection();
        return;
      }
      if (createPlannerGroupFromSelection()) {
        const groupId = plannerSelectedTaskIds[0] || "";
        renderSectionNow("planner", { save: true, commit: true });
        focusPlannerGroupTitleEdit(groupId);
      }
      return;
    }

    if (isUngroupShortcut && !inEditableField) {
      event.preventDefault();
      if (canUngroupQuestionSelection()) {
        ungroupQuestionGroup(questionSelectedRowIds[0]);
        renderSectionNow("questoes", { save: true, commit: true });
        return;
      }
      if (canUngroupPlannerSelection()) {
        ungroupPlannerGroup(plannerSelectedTaskIdsInActiveView()[0]);
        renderSectionNow("planner", { save: true, commit: true });
      }
      return;
    }

    if ((isUndo || isRedo) && !event.target.closest("input, textarea, [contenteditable='true']")) {
      event.preventDefault();
      if (isUndo) undoState();
      if (isRedo) redoState();
      return;
    }

    if (event.key === "Escape") {
      if (focusScreenOpen) {
        focusScreenOpen = false;
        render();
        return;
      }
      closeImageModal();
    }
  });

  const TIMER_ACTION = {
    "start-study-timer"(btn) { startStudyTimer(btn.dataset.subject); },
    "pause-study-timer"() { pauseStudyTimer(); },
    "finish-study-timer"() { finishStudyTimer(); },
    "toggle-focus-timer"() { toggleStudyTimer(); },
    "resume-focus-screen"() { if (activeStudyTimer) focusScreenOpen = true; },
    "close-focus-screen"() { focusScreenOpen = false; },
    "toggle-focus-panel"() { focusStudyPanelCollapsed = !focusStudyPanelCollapsed; },
    "timeline-prev-day"() { selectedStudyTimelineDate = addDays(selectedStudyTimelineDate || timerOperationalValue(), -1); syncURLParams(); },
    "timeline-next-day"() { selectedStudyTimelineDate = addDays(selectedStudyTimelineDate || timerOperationalValue(), 1); syncURLParams(); },
    "timeline-today"() { selectedStudyTimelineDate = timerOperationalValue(); syncURLParams(); },
  };
  document.body.addEventListener("click", (event) => {
    if (event.target.closest("[data-edit-record][contenteditable='true'], [data-edit-key][contenteditable='true']")) {
      return;
    }

    if (!event.target.closest(".planner-card-menu, .planner-edit-popover")) closePlannerCardMenu();

    if (
      plannerSelectedTaskIds.length
      && !event.target.closest("[data-task-id]")
      && !event.target.closest(".planner-card-menu, .planner-edit-popover, .question-modal, .question-delete-modal, .modal, .question-group-view-modal")
    ) {
      plannerSelectedTaskIds = [];
      plannerSelectionAnchorId = "";
      renderSectionNow("planner");
      return;
    }

    if (
      questionSelectedRowIds.length
      && !event.target.closest("[data-question-id]")
      && !event.target.closest(".planner-card-menu, .planner-edit-popover, .question-modal, .question-delete-modal, .modal, .question-group-view-modal, .page-header-context-actions")
    ) {
      questionSelectedRowIds = [];
      renderSectionNow("questoes");
      return;
    }

    const imageButton = event.target.closest(".image-open");
    if (imageButton) {
      openImageModal(imageButton.dataset.imageSrc);
      return;
    }

    const selectionPlannerCard = (event.shiftKey || event.ctrlKey || event.metaKey) ? event.target.closest("[data-task-id]") : null;
    if (isPlannerCardInActiveView(selectionPlannerCard) && !event.target.closest("input, button")) {
      event.preventDefault();
      const plannerCardId = selectionPlannerCard.dataset.taskId || "";
      const isPlannerRange = event.shiftKey && !event.ctrlKey && !event.metaKey;
      togglePlannerTaskSelection(plannerCardId, { append: true, range: isPlannerRange });
      renderSectionNow("planner");
      return;
    }

    const selectionQuestionRow = (event.shiftKey || event.ctrlKey || event.metaKey) ? event.target.closest("[data-question-id]") : null;
    if (selectionQuestionRow && !event.target.closest("input, button")) {
      event.preventDefault();
      toggleQuestionRowSelection(selectionQuestionRow.dataset.questionId || "", true);
      renderSectionNow("questoes");
      return;
    }

    if (
      event.target.closest("[data-edit-record], [data-edit-key]")
      && !event.target.closest("button, [data-action]")
    ) {
      return;
    }

    const plannerGroupCard = event.target.closest("[data-planner-group-card]");
    if (plannerGroupCard && !plannerGroupCard.dataset.action && !event.target.closest("[data-action], input, button, label")) {
      const item = state.planner.find((entry) => entry.id === plannerGroupCard.dataset.plannerGroupCard && isPlannerGroup(entry));
      if (item) {
        item.collapsed = !item.collapsed;
        renderPlannerLight();
      }
      return;
    }

    const questionGroupRow = event.target.closest("[data-question-group-row]");
    if (questionGroupRow && !event.target.closest("input, button, label")) {
      const item = state.questions.find((entry) => entry.id === questionGroupRow.dataset.questionGroupRow && isQuestionGroup(entry));
      if (item) {
        openQuestionGroupView(item.id);
      }
      return;
    }

    const clickedCalendarDay = event.target.closest("[data-calendar-date]");
    if (clickedCalendarDay && !event.target.closest("button, [data-task-id], .planner-builder")) {
      if (clickedCalendarDay.classList.contains("builder-open")) {
        if (window.innerWidth <= 980) {
          plannerBuilderOpen = false;
          plannerBuilderType = "";
          plannerBuilderTime = "";
          renderSectionNow("planner");
          return;
        }
        return;
      }
      plannerBuilderOpen = true;
      plannerBuilderType = "";
      plannerBuilderDate = clickedCalendarDay.dataset.calendarDate || localDateValue();
      plannerBuilderTime = clickedCalendarDay.dataset.calendarTime || "";
      renderSectionNow("planner");
      return;
    }

    const materialGroupSummary = event.target.closest('summary[data-action="select-planner-material-group"]');
    if (materialGroupSummary) {
      if (event.shiftKey) {
        event.preventDefault();
        const form = materialGroupSummary.closest("form");
        const ids = (materialGroupSummary.dataset.values || "").split(",").filter(Boolean);
        if (form && ids.length) {
          const existing = new Set(plannerSelectedMaterialRefs.filter((value) => value && value !== "__new__" && value !== "__new_subject__"));
          const allSelected = ids.every((id) => existing.has(id));
          let anchor = ids.at(-1) || "";
          if (allSelected) {
            ids.forEach((id) => existing.delete(id));
            anchor = "";
          } else {
            ids.forEach((id) => existing.add(id));
          }
          plannerSelectedMaterialAnchor = anchor;
          syncPlannerMaterialSelection(form, [...existing], anchor);
        }
      }
      return;
    }

    const button = event.target.closest("button[data-action], input[data-action], [data-task-id][data-action]");
    if (!button) return;

    const { action, id } = button.dataset;
    if (runSectionModuleAction(activeViewId(), { action, id, button, event })) return;
    if (action === "update-subject-note") {
      return;
    }

    if (action === "select-planner-material") {
      event.preventDefault();
      const form = button.closest("form");
      const input = form?.querySelector('input[name="materialRef"]');
      if (!input) return;
      const value = button.dataset.value || "";
      if (event.shiftKey && value !== "__new__") {
        const rangeValues = plannerPickerRangeSelection(
          plannerPickerOptionOrder(form, '[data-action="select-planner-material"]'),
          plannerSelectedMaterialAnchor,
          value,
        );
        plannerSelectedMaterialAnchor = value;
        syncPlannerMaterialSelection(
          form,
          rangeValues.length ? rangeValues : [value],
          plannerSelectedMaterialAnchor,
        );
        return;
      }
      if ((event.ctrlKey || event.metaKey) && value !== "__new__") {
        const selected = new Set(plannerSelectedMaterialValues(form));
        if (selected.has(value)) selected.delete(value);
        else selected.add(value);
        plannerSelectedMaterialAnchor = value;
        syncPlannerMaterialSelection(form, [...selected], plannerSelectedMaterialAnchor);
        return;
      }
      plannerSelectedMaterialAnchor = "";
      syncPlannerMaterialSelection(form, [value], value);
      return;
    }

    if (action === "select-planner-question") {
      event.preventDefault();
      const form = button.closest("form");
      const input = form?.querySelector('input[name="questionRef"]');
      if (!input) return;
      const value = button.dataset.value || "";
      if (event.shiftKey && value !== "__new__") {
        const rangeValues = plannerPickerRangeSelection(
          plannerPickerOptionOrder(form, '[data-action="select-planner-question"]'),
          plannerSelectedQuestionAnchor,
          value,
        );
        plannerSelectedQuestionAnchor = value;
        syncPlannerQuestionSelection(
          form,
          rangeValues.length ? rangeValues : [value],
          plannerSelectedQuestionAnchor,
        );
        return;
      }
      if ((event.ctrlKey || event.metaKey) && value !== "__new__") {
        const selected = new Set(plannerSelectedQuestionValues(form));
        if (selected.has(value)) selected.delete(value);
        else selected.add(value);
        plannerSelectedQuestionAnchor = value;
        syncPlannerQuestionSelection(form, [...selected], plannerSelectedQuestionAnchor);
        return;
      }
      plannerSelectedQuestionAnchor = "";
      syncPlannerQuestionSelection(form, [value], value);
      return;
    }

    if (action === "show-more-list") {
      event.preventDefault();
      const listKey = button.dataset.list || activeViewId();
      increaseVirtualListLimit(listKey);
      renderSectionNow(activeViewId());
      return;
    }

    if (editMode && isNavigationAction(action)) {
      event.preventDefault();
      return;
    }

    if (action === "open-study-subject") {
      selectedStudySubject = button.dataset.subject;
      selectedStudyCycle = "";
      pushSubStateToHistory();
    }

    if (action === "add-subject") {
      openSubjectModal();
      return;
    }

    if (action === "close-subject-modal") {
      closeSubjectModal();
      return;
    }

    if (action === "open-subject-cycle-modal") {
      openSubjectCycleModal();
      return;
    }

    if (action === "close-subject-cycle-modal") {
      closeSubjectCycleModal();
      return;
    }

    if (action === "open-cycle-lesson-modal") {
      openCycleLessonModal();
      return;
    }

    if (action === "open-cycle-lesson-modal-group") {
      openCycleLessonModal(button.dataset.group || "");
      return;
    }

    if (action === "close-cycle-lesson-modal") {
      closeCycleLessonModal();
      return;
    }

    if (action === "open-error-modal") {
      openEntryModal("errorModal", 'select[name="subject"]');
      return;
    }

    if (action === "close-error-modal") {
      closeEntryModal("errorModal");
      return;
    }

    if (action === "open-mock-modal") {
      openEntryModal("mockModal", 'input[name="name"]');
      syncMockFormDateDefault();
      return;
    }

    if (action === "close-mock-modal") {
      closeEntryModal("mockModal");
      return;
    }

    if (action === "open-essay-modal") {
      openEntryModal("essayModal", 'input[name="theme"]');
      return;
    }

    if (action === "close-essay-modal") {
      closeEntryModal("essayModal");
      return;
    }

    if (action === "open-question-modal") {
      openQuestionModal({ groupId: button.dataset.groupId || "" });
      return;
    }

    if (action === "open-question-group-builder") {
      openQuestionGroupBuilder(button.dataset.subject || selectedQuestionSubject);
      return;
    }

    if (action === "close-question-group-builder") {
      closeQuestionGroupBuilderModal();
      return;
    }

    if (action === "add-question-group-builder-row") {
      addQuestionGroupBuilderRow();
      return;
    }

    if (action === "remove-question-group-builder-row") {
      removeQuestionGroupBuilderRow(Number(button.dataset.rowIndex));
      return;
    }

    if (action === "close-question-modal") {
      closeQuestionModal();
      return;
    }

    if (action === "close-question-group-modal") {
      closeQuestionGroupModal();
      return;
    }

    if (action === "close-question-delete-modal" || action === "close-delete-confirm-modal") {
      closeDeleteConfirmModal();
      return;
    }

    if (action === "close-layout-edit-exit-modal") {
      closeLayoutEditExitModal();
      return;
    }

    if (action === "save-layout-edit") {
      saveLayoutEdits();
      return;
    }

    if (action === "discard-layout-edit") {
      discardLayoutEdits();
      return;
    }

    if (action === "continue-sync-offline") {
      closeSyncDisconnectedModal();
      setSyncStatus("disconnected");
      return;
    }

    if (action === "export-sync-backup") {
      downloadStateBackup();
      return;
    }

    if (action === "focus-sync-login") {
      closeSyncDisconnectedModal();
      const menu = document.querySelector(".action-menu");
      if (menu) {
        menu.open = true;
        syncActionMenuState();
      }
      document.querySelector("#cloudEmail")?.focus();
      return;
    }

    if (action === "close-planner-creation-modal") {
      closePlannerCreationModal();
      return;
    }

    if (action === "close-planner-later-modal") {
      closePlannerLaterModal();
      return;
    }

    if (action === "confirm-question-delete" || action === "confirm-delete-confirm-modal") {
      confirmDeleteModalAction();
      return;
    }

    if (action === "create-planner-as-simple") {
      confirmPlannerCreationMode("simple");
      return;
    }

    if (action === "create-planner-as-group") {
      confirmPlannerCreationMode("group");
      return;
    }

    if (action === "open-timer-subject-modal") {
      openTimerSubjectModal("create");
    }

    if (action === "start-study-from-empty") {
      openSessionStartPicker();
      return;
    }

    if (action === "start-study-session-pick") {
      const subject = button.dataset.subject || "";
      closeTimerSubjectModal();
      startStudyTimer(subject);
      renderSectionNow("tempo", { save: true, commit: true });
      return;
    }

    if (action === "open-timer-subject-menu") {
      event.stopPropagation();
      openTimerSubjectMenu(button.dataset.subject || "", button);
      return;
    }

    if (action === "close-timer-subject-modal") {
      closeTimerSubjectModal();
    }

    if (action === "edit-timer-subject") {
      closeTimerSubjectMenu();
      openTimerSubjectModal("edit", button.dataset.subject || "");
    }

    if (action === "delete-timer-subject") {
      if (deleteTimerSubject(button.dataset.subject || "")) {
        closeTimerSubjectMenu();
        renderSectionNow("tempo", { save: true, commit: true });
        showUndoToast("Matéria do cronômetro excluída.");
      }
    }

    if (action === "close-study-session-modal") {
      closeStudySessionModal();
    }

    if (action === "edit-study-session") {
      openStudySessionModal(button.dataset.id || "");
      return;
    }

    if (action === "open-study-session-menu") {
      openStudySessionMenu(button.dataset.id || "", button);
      return;
    }

    if (action === "toggle-section") {
      toggleSection(button.dataset.section);
    }

    if (action === "toggle-timeline-group") {
      const timeline = document.querySelector("#studyTimeline");
      if (timeline) {
        const current = timeline.dataset.groupBySubject === "true";
        timeline.dataset.groupBySubject = current ? "false" : "true";
        renderStudyTimeline(timeline);
      }
      return;
    }

    if (action === "delete-subject") {
      if (deleteSubject(button.dataset.subject)) fillSubjectSelects();
    }

    if (action === "close-study-subject") {
      selectedStudySubject = "";
      selectedStudyCycle = "";
      syncURLParams();
    }

    if (action === "open-study-cycle") {
      selectedStudyCycle = button.dataset.cycle;
      pushSubStateToHistory();
    }

    if (action === "delete-cycle") {
      deleteStudyCycle(button.dataset.cycle);
    }

    if (action === "close-study-cycle") {
      selectedStudyCycle = "";
      syncURLParams();
    }

    if (action === "toggle-planner") {
      if (event.shiftKey || event.ctrlKey || event.metaKey) {
        event.preventDefault();
        togglePlannerTaskSelection(id, { append: true, range: event.shiftKey && !event.ctrlKey && !event.metaKey });
        renderPlannerLight();
        return;
      }
      const item = state.planner.find((entry) => entry.id === id);
      if (item) {
        if (!item.done && item.questionData?.questionId) {
          openPlannerCompletionModal(item);
          return;
        }
        const completedAt = reviewDayValue();
        const wasDone = Boolean(item.done);
        setPlannerDone(item, !item.done, { completedAt });
        renderPlannerLight();
        showUndoToast(wasDone ? "Tarefa marcada como pendente." : "Tarefa concluída.");
        return;
      }
    }

    if (action === "close-planner-completion-modal") {
      closePlannerCompletionModal();
      return;
    }

    if (action === "toggle-question-done") {
      const item = state.questions.find((entry) => entry.id === id);
      if (item && !isQuestionGroup(item)) {
        const completedAt = reviewDayValue();
        setQuestionDone(item, !questionRecordDone(item), { completedAt });
        if (button.closest("#questionGroupViewModal")) {
          refreshQuestionGroupViewModalState(item.id);
          saveState();
          commitHistory();
          if (activeViewId() !== "dashboard") renderMetrics({ includeDashboard: false });
          return;
        }
      }
    }

    if (action === "open-planner-edit") {
      const card = plannerCardInActiveView(id);
      if (!card) return;
      openPlannerEditPopover(id, card);
      return;
    }

    if (action === "open-planner-later-modal") {
      const activeSelectedIds = plannerSelectedTaskIdsInActiveView();
      const selectedIds = activeSelectedIds.includes(id) ? activeSelectedIds : [id].filter(Boolean);
      const itemIds = plannerIdsWithGroupChildren(selectedIds);
      const defaultItem = state.planner.find((item) => item.id === id) || state.planner.find((item) => item.id === selectedIds[0]);
      if (!itemIds.length) return;
      openPlannerLaterModal({
        ids: itemIds,
        defaultDate: plannerDateValue(defaultItem) || localDateValue(),
        selectedIds,
      });
      return;
    }

    if (action === "group-planner-selection") {
      if (createPlannerGroupFromSelection()) renderSectionNow("planner", { save: true, commit: true });
      return;
    }

    if (action === "ungroup-planner-selection") {
      if (ungroupPlannerGroup(id)) renderSectionNow("planner", { save: true, commit: true });
      return;
    }

    if (action === "delete-planner") {
      if (!removePlannerItem(id, { confirm: true, closeMenu: false })) return;
      showUndoToast("Item do Planner excluído.");
    }

    if (action === "open-planner-builder") {
      plannerSelectedMaterialRefs = [];
      plannerSelectedQuestionRefs = [];
      plannerSelectedMaterialAnchor = "";
      plannerSelectedQuestionAnchor = "";
      plannerBuilderOpen = true;
      plannerBuilderType = "";
      plannerBuilderDate = button.dataset.date || localDateValue();
      plannerBuilderTime = button.dataset.time || "";
    }

    if (action === "select-planner-block-type") {
      plannerSelectedMaterialRefs = [];
      plannerSelectedQuestionRefs = [];
      plannerSelectedMaterialAnchor = "";
      plannerSelectedQuestionAnchor = "";
      plannerBuilderOpen = true;
      plannerBuilderType = button.dataset.type || "";
    }

    if (action === "close-planner-builder") {
      plannerSelectedMaterialRefs = [];
      plannerSelectedQuestionRefs = [];
      plannerSelectedMaterialAnchor = "";
      plannerSelectedQuestionAnchor = "";
      plannerBuilderOpen = false;
      plannerBuilderType = "";
      plannerBuilderTime = "";
    }

    if (action === "close-planner-card-menu") {
      closePlannerCardMenu();
      return;
    }

    if (action === "delete-planner-from-edit") {
      if (!removePlannerItem(id, { confirm: true })) return;
      showUndoToast("Item do Planner excluído.");
    }

    if (action === "delete-question") {
      openQuestionDeleteModal(id);
      return;
    }

    if (action === "open-question-edit") {
      const item = state.questions.find((entry) => entry.id === id && !isQuestionGroup(entry));
      if (item) openQuestionModal({ item });
      closePlannerCardMenu();
      return;
    }

    if (action === "open-inline-question-edit") {
      const editable = document.querySelector(
        `[data-question-id="${CSS.escape(id)}"] [data-edit-record="questions"][data-field="content"], ` +
        `[data-question-id="${CSS.escape(id)}"] [data-edit-record="questions"][data-field="name"]`,
      );
      if (editable) startInlineEdit(editable);
      closePlannerCardMenu();
      return;
    }

    if (action === "open-question-group-view" || action === "toggle-question-group") {
      const item = state.questions.find((entry) => entry.id === id && isQuestionGroup(entry));
      if (item) {
        openQuestionGroupView(item.id);
      }
      return;
    }

    if (action === "close-question-group-view") {
      closeQuestionGroupView();
      return;
    }

    if (action === "group-question-selection") {
      createQuestionGroupFromSelection();
      return;
    }

    if (action === "add-question-to-group") {
      openAddQuestionToGroupPicker();
      return;
    }

    if (action === "pick-question-group") {
      closeTimerSubjectModal();
      if (addQuestionsToGroup(questionSelectedRowIds, id)) {
        renderSectionNow("questoes", { save: true, commit: true });
      }
      return;
    }

    if (action === "ungroup-question-selection") {
      if (ungroupQuestionGroup(id)) renderSectionNow("questoes", { save: true, commit: true });
      return;
    }

    if (action === "remove-question-from-group") {
      if (removeQuestionsFromGroup(questionSelectedRowIds)) renderSectionNow("questoes", { save: true, commit: true });
      return;
    }

    if (action === "edit-question-group") {
      const item = state.questions.find((entry) => entry.id === id && isQuestionGroup(entry));
      if (item) {
        const children = questionGroupChildren(item.id, { subjectFilter: "all" });
        const childSubjects = [...new Set(children.map((child) => canonicalSubject(child.subject || "Geral")))];
        openQuestionGroupModal({
          itemCount: children.length,
          defaultName: item.name,
          defaultSubject: childSubjects.length === 1 ? childSubjects[0] : "__mixed__",
          onConfirm: (name, subject) => {
            const nextSubject = subject && subject !== "__mixed__" ? canonicalSubject(subject) : "";
            if (nextSubject) {
              children.forEach((child) => {
                child.subject = nextSubject;
                if (nextSubject !== "Geral") ensureSubjectExists(nextSubject);
                rememberContent(nextSubject, child.content);
                syncQuestionPerformance(child);
                touchRecord("questions", child.id);
              });
            }
            item.name = String(name || "").trim() || "Grupo de listas";
            touchRecord("questions", item.id);
            syncQuestionGroupState(item.id, { removeEmpty: false });
            renderSectionNow("questoes", { save: true, commit: true });
          },
        });
      }
      closePlannerCardMenu();
      return;
    }

    if (action === "toggle-focus-planner") {
      const item = state.planner.find((entry) => entry.id === id);
      if (item) {
        if (!item.done && item.questionData?.questionId) {
          openPlannerCompletionModal(item);
          return;
        }
        setPlannerDone(item, !item.done, { flash: true });
        const updated = state.planner.find((entry) => entry.id === id);
        const ref = state.focusTodos?.find((entry) => entry.ref === `planner:${id}`);
        if (ref) ref.done = Boolean(updated?.done);
      }
    }

    if (action === "toggle-focus-lesson") {
      const subject = decodeURIComponent(button.dataset.subject || "");
      const cycle = decodeURIComponent(button.dataset.cycle || "");
      const item = getCycleLessons(subject, cycle).find((entry) => entry.id === id);
      if (item) {
        const wasDone = Boolean(item.theory && item.practice && item.review);
        const next = !(item.theory && item.practice && item.review);
        item.theory = next;
        item.practice = next;
        item.review = next;
        const ref = state.focusTodos?.find(
          (entry) => entry.ref === `lesson:${encodeURIComponent(subject)}:${encodeURIComponent(cycle)}:${id}`,
        );
        if (ref) ref.done = next;
        touchMapKey("cycleLessons", cycleKey(subject, cycle), getCycleLessons(subject, cycle));
        syncPlannerLinks();
        flashIfTaskCompleted(wasDone, next);
      }
    }

    if (action === "remove-focus-todo") {
      event.preventDefault();
      removeFocusTodoRef(button.dataset.ref);
    }

    if (action === "toggle-error") {
      const item = state.errors.find((entry) => entry.id === id);
      if (item) {
        item.done = !item.done;
        if (item.done) item.reviewedAt = reviewDayValue();
        else delete item.reviewedAt;
        touchRecord("errors", item.id);
      }
    }

    if (action === "generate-error-pdf") {
      generateErrorContentPdf();
    }

    if (action === "select-error-subject") {
      selectedErrorSubject = selectedErrorSubject === button.dataset.subject ? "" : button.dataset.subject;
      selectedErrorContent = "";
      resetVirtualListLimit("erros");
      if (selectedErrorSubject) pushSubStateToHistory(); else syncURLParams();
    }

    if (action === "close-error-subject") {
      selectedErrorSubject = "";
      selectedErrorContent = "";
      resetVirtualListLimit("erros");
      syncURLParams();
    }

    if (action === "open-error-content") {
      selectedErrorContent = button.dataset.content;
      resetVirtualListLimit("erros");
      pushSubStateToHistory();
    }

    if (action === "close-error-content") {
      selectedErrorContent = "";
      resetVirtualListLimit("erros");
      syncURLParams();
    }

    if (action === "delete-error") {
      if (!removeErrorRecord(id)) return;
      renderSectionNow("erros", { save: true, commit: true });
      showUndoToast("Questão do caderno excluída.");
      return;
    }

    if (action === "delete-study-session") {
      if (!removeStudySession(id, { confirm: true })) return;
      renderSectionNow("tempo", { save: true, commit: true });
      showUndoToast("Sessão de estudo excluída.");
      return;
    }

    if (action === "toggle-error-open") {
      const item = state.errors.find((entry) => entry.id === id);
      if (item) item.open = !item.open;
    }

    if (action === "toggle-lesson-step") {
      const lessons = activeRecords(getCycleLessons(selectedStudySubject, selectedStudyCycle));
      const item = lessons.find((entry) => entry.id === id);
      if (item) {
        const step = button.dataset.step;
        item[step] = !item[step];
        if (step === "theory" && item[step]) {
          item.theoryCompletedAt ||= reviewDayValue();
        }
        touchMapKey("cycleLessons", cycleKey(selectedStudySubject, selectedStudyCycle), getCycleLessons(selectedStudySubject, selectedStudyCycle));
        syncPlannerLinks();
      }
    }

    if (action === "add-pending-lesson") {
      openPendingLessonModal(button.dataset.subject || selectedStudySubject);
      return;
    }

    if (action === "edit-pending-lesson") {
      const item = activeRecords(state.pendingLessons || []).find((entry) => entry.id === id);
      if (!item) return;
      openPendingLessonModal(item.subject, item);
      return;
    }

    if (action === "delete-pending-lesson") {
      const item = activeRecords(state.pendingLessons || []).find((entry) => entry.id === id);
      if (!item) return;
      markDeleted("pendingLessons", item.id);
      saveState();
      if (activeViewId() === "materias") renderSectionNow("materias", { save: true, commit: true });
      if (focusScreenOpen) renderFocusScreen();
      notify("Aula pendente excluída.", "success");
      return;
    }

    if (action === "close-pending-lesson-modal") {
      closePendingLessonModal();
      return;
    }

    if (action === "toggle-pending-lesson") {
      const becomingDone = togglePendingLesson(button.dataset.id || "");
      if (activeViewId() === "materias") renderSectionNow("materias", { save: true, commit: true });
      if (focusScreenOpen) renderFocusScreen();
      notify(becomingDone ? "Aula marcada como assistida" : "Aula voltou para pendente", "success");
      return;
    }

    if (action === "delete-cycle-lesson") {
      if (lessonDependencyCount(id)) {
        alert("Essa aula está vinculada ao Planner. Remova os blocos relacionados antes de excluir.");
        return;
      }
      openDeleteConfirmModal({
        title: "Excluir aula",
        subtitle: "A aula será removida do ciclo atual",
        message: "Excluir esta aula?",
        onConfirm: () => {
          markDeleted("cycleLessons", id);
          renderSectionNow("materias", { save: true, commit: true });
          showUndoToast("Aula excluída.");
        },
      });
      return;
    }

    if (action === "toggle-lesson-group") {
      toggleLessonGroup(decodeURIComponent(button.dataset.group || ""));
    }

    if (TIMER_ACTION[action]) { TIMER_ACTION[action](button); }

    if (action === "delete-mock") {
      if (!removeMockRecord(id)) return;
      renderSectionNow("simulados", { save: true, commit: true });
      showUndoToast("Simulado excluído.");
      return;
    }

    if (action === "delete-essay") {
      if (!removeEssayRecord(id)) return;
      renderSectionNow("redacoes", { save: true, commit: true });
      showUndoToast("Redação excluída.");
      return;
    }

    prepareRenderState();
    if (activeViewId() !== "dashboard") renderMetrics({ includeDashboard: false });
    renderSection(activeViewId());
    renderSectionUi(activeViewId());
  });

document.body.addEventListener("contextmenu", (event) => {
  const card = event.target.closest("[data-task-id]");

  if (
    !card ||
    !isPlannerCardInActiveView(card) ||
    !card.querySelector('[data-edit-record="planner"][data-field="title"]')
  ) return;

  event.preventDefault();
  event.stopPropagation();

  const taskId = card.dataset.taskId || "";

  if (!plannerSelectedTaskIdsInActiveView().includes(taskId)) {
    plannerSelectedTaskIds = [taskId];
    plannerSelectionAnchorId = "";
  }

  openPlannerCardMenu(card, event);
});

document.body.addEventListener("contextmenu", (event) => {
  const row = event.target.closest("[data-question-id]");

  if (
    !row ||
    !row.querySelector('[data-edit-record="questions"], [data-question-group-row]')
  ) return;

  event.preventDefault();
  event.stopPropagation();

  const questionId = row.dataset.questionId || "";

  if (!questionSelectedRowIds.includes(questionId)) {
    questionSelectedRowIds = [questionId];
  }

  openQuestionCardMenu(row, event);
});

document.body.addEventListener("contextmenu", (event) => {
  const card = event.target.closest("[data-study-session]");

  if (!card) return;

  event.preventDefault();
  event.stopPropagation();

  openStudySessionMenu(card.dataset.studySession || "", event);
});

  document.body.addEventListener("submit", (event) => {
    const form = event.target.closest("[data-action]");
    if (!form) return;

    const action = form.dataset.action;
    if (
      action !== "add-study-cycle-form" &&
      action !== "add-cycle-lesson-form" &&
      action !== "submit-subject-cycle-modal" &&
      action !== "submit-cycle-lesson-modal" &&
      action !== "submit-pending-lesson-modal" &&
      action !== "submit-planner-later-modal" &&
      action !== "submit-planner-completion" &&
      action !== "create-planner-block" &&
      action !== "update-planner-card" &&
      action !== "add-focus-todo-form" &&
      action !== "add-pending-lesson-form" &&
      action !== "focus-question-form" &&
      action !== "submit-create-subject" &&
      action !== "submit-create-timer-subject" &&
      action !== "submit-edit-timer-subject" &&
      action !== "submit-edit-study-session" &&
      action !== "submit-question-group-builder"
    ) return;

    event.preventDefault();
    const data = Object.fromEntries(new FormData(form).entries());

    if (action === "submit-question-group-builder") {
      saveQuestionGroupBuilder(form);
      return;
    }

    if (action === "create-planner-block") {
      const created = createPlannerBlock(form, data);
      if (!created) return;
    }

    if (action === "submit-planner-later-modal") {
      if (!submitPlannerLaterModal(data.date)) return;
      return;
    }

    if (action === "submit-planner-completion") {
      submitPlannerCompletion(form);
      return;
    }

    if (action === "update-planner-card") {
      const item = state.planner.find((entry) => entry.id === form.dataset.id);
      if (!item) return;
      let title = String(data.title || "").trim();
      if (item.type === "Flash-card") {
        const deckName = String(data.deckName || "").trim();
        const reviewCards = Math.max(0, number(data.reviewCards));
        const newCards = Math.max(0, number(data.newCards));
        title = plannerFlashcardTitle(deckName);
        item.flashcardData = {
          ...(item.flashcardData || {}),
          deckName,
          totalCards: reviewCards + newCards,
          reviewCards,
          newCards,
        };
      } else if (!title) {
        alert("Digite um título para a tarefa.");
        return;
      }
      if (isPlannerGroup(item)) {
        item.title = title;
        touchRecord("planner", item.id);
        syncPlannerGroupState(item.id, { removeEmpty: false });
        closePlannerCardMenu();
        rememberSmartForm(form, data);
        renderSectionNow("planner", { save: true, commit: true });
        return;
      }
      const nextDate = data.date || item.date || localDateValue();
      markPlannerUserEdited(item);
      const hasTimeFields = Boolean(form.elements.time);
      if (hasTimeFields) {
        if (data.time) {
          const endTime = data.endTime || defaultPlannerEndTime(data.time);
          if (!isPlannerEndAfterStart(data.time, endTime)) {
            alert("O horário final deve ser depois do horário inicial.");
            return;
          }
          item.time = data.time;
          item.endTime = endTime;
        } else {
          delete item.time;
          delete item.endTime;
        }
      }
      item.date = nextDate;
      item.title = title;
      syncPlannerItemToSource(item);
      closePlannerCardMenu();
      rememberSmartForm(form, data);
    }

    if (action === "add-focus-todo-form") {
      if (!activeStudyTimer?.subject || !data.todoRef) return;
      const item = focusTodoCandidates(activeStudyTimer.subject, true).find((candidate) => candidate.ref === data.todoRef);
      if (item && !focusTodoRefs(activeStudyTimer.subject).some((entry) => entry.ref === item.ref)) {
        upsertFocusTodo(item, activeStudyTimer.subject);
      }
      rememberSmartForm(form, data);
    }

    if (action === "add-pending-lesson-form") {
      const subject = String(form.dataset.subject || activeStudyTimer?.subject || "").trim();
      const title = String(data.title || "").trim();
      if (!subject || !title) return;
      addPendingLesson(subject, title);
      rememberSmartForm(form, data);
      form.reset();
      if (focusScreenOpen) renderFocusScreen();
      return;
    }

    if (action === "focus-question-form") {
      if (!activeStudyTimer?.subject) return;
      addQuestionRecord({
        date: questionTodayValue(),
        subject: activeStudyTimer.subject,
        content: String(data.content || "").trim(),
        questions: data.questions,
        correct: data.correct,
      }, {
        extra: { studySessionId: activeStudyTimer.id || "" },
      });
      rememberSmartForm(form, data);
    }

    if (action === "add-study-cycle-form") {
      addStudyCycle(data.cycleName);
      rememberSmartForm(form, data);
    }

    if (action === "submit-create-subject") {
      addSubject(data.name);
      fillSubjectSelects();
      closeSubjectModal();
    }

    if (action === "submit-subject-cycle-modal") {
      const count = addStudyCyclesFromText(data.cycleBatch, data.subjectContext || selectedStudySubject);
      if (!count) {
        alert("Digite pelo menos um conteúdo válido, um por linha.");
        return;
      }
      rememberSmartForm(form, data);
      closeSubjectCycleModal();
    }

    if (action === "submit-cycle-lesson-modal") {
      const count = addLessonRowsFromBatch(data.group, data.topicBatch);
      if (!count) {
        alert('Preencha o conteúdo base ou use o formato "Conteúdo | Aula" em cada linha.');
        return;
      }
      rememberSmartForm(form, data);
      closeCycleLessonModal();
    }

    if (action === "submit-pending-lesson-modal") {
      const id = String(data.pendingLessonId || "").trim();
      const subject = String(data.subjectContext || "").trim();
      const lines = String(data.title || data.titles || "")
        .split(/\r?\n/)
        .map((entry) => entry.trim())
        .filter(Boolean);
      if (!subject || !lines.length) return;
      if (id) {
        const item = findRecordById("pendingLessons", id);
        if (!item) return;
        updateRecord("pendingLessons", item.id, { subject, title: lines[0] });
      } else {
        lines.forEach((title) => addPendingLesson(subject, title));
      }
      rememberSmartForm(form, data);
      closePendingLessonModal();
      if (activeViewId() === "materias") renderSectionNow("materias", { save: true, commit: true });
      if (focusScreenOpen) renderFocusScreen();
      return;
    }

    if (action === "add-cycle-lesson-form") {
      const key = cycleKey(selectedStudySubject, selectedStudyCycle);
      touchMapKey("cycleLessons", key, [...getCycleLessons(selectedStudySubject, selectedStudyCycle), {
        id: uid(),
        group: data.group.trim(),
        topic: data.topic.trim(),
        theory: false,
        practice: false,
        review: false,
      }]);
      rememberSmartForm(form, data);
    }

    if (action === "submit-create-timer-subject") {
      const created = addTimerSubject(data.name);
      if (!created) return;
      closeTimerSubjectModal();
      persistStateCache();
      saveState({ immediate: true });
      renderSectionNow("tempo", { commit: true });
      return;
    }

    if (action === "submit-edit-timer-subject") {
      const renamed = renameTimerSubject(form.dataset.subject || "", data.name);
      if (!renamed) return;
      closeTimerSubjectModal();
      persistStateCache();
      saveState({ immediate: true });
      renderSectionNow("tempo", { commit: true });
      return;
    }

    if (action === "submit-edit-study-session") {
      const item = (state.studySessions || []).find((entry) => entry.id === form.dataset.id);
      if (!item) return;
      const subject = String(data.subject || "").trim();
      if (!subject) {
        alert("Preencha a matéria.");
        return;
      }
      const dateValue = String(data.date || "").trim() || item.date || localDateValue();
      const startTime = String(data.startTime || "").trim();
      const endTime = String(data.endTime || "").trim();
      if (!startTime || !endTime) {
        alert("Preencha o horário de começo e de finalização.");
        return;
      }
      if (!isPlannerEndAfterStart(startTime, endTime)) {
        alert("O horário final deve ser depois do horário inicial.");
        return;
      }
      const durationMinutes = Math.max(1, plannerTimeToMinutes(endTime, true) - plannerTimeToMinutes(startTime));
      item.subject = subject;
      if (!getTimerSubjects().some((entry) => normalizeHeader(entry) === normalizeHeader(subject))) addTimerSubject(subject);
      item.date = dateValue;
      item.startTime = localDateTimeValue(dateValue, startTime);
      item.endTime = localDateTimeValue(dateValue, endTime);
      item.duration = durationMinutes;
      item.durationMs = durationMinutes * 60000;
      touchRecord("studySessions", item.id);
      closeStudySessionModal();
    }

    resetSmartFormState(form);
    form.reset();
    render();
  });

  document.body.addEventListener("paste", (event) => {

    const target = event.target.closest("[data-lesson-group]");
    if (!target || !selectedStudySubject || !selectedStudyCycle) return;

    const text = event.clipboardData?.getData("text/plain") || "";
    if (!text.includes("\t") && !text.includes("\n")) return;

    event.preventDefault();
    const count = importPastedLessonRows(decodeURIComponent(target.dataset.lessonGroup || ""), text);
    if (count > 0) renderSectionNow("materias", { save: true, commit: true });
  });

  document.body.addEventListener("input", (event) => {
    const studySessionField = event.target.closest('.study-session-form input[name="startTime"], .study-session-form input[name="durationMinutes"]');
    if (studySessionField) {
      syncStudySessionForm(studySessionField.closest("form"), studySessionField.name);
      return;
    }
    const flashcardInput = event.target.closest('.planner-builder-form input[name="reviewCards"], .planner-builder-form input[name="newCards"]');
    if (flashcardInput) {
      updatePlannerFlashcardTotal(flashcardInput.closest("form"));
      return;
    }
    const newMaterialTextField = event.target.closest('.new-material-fields input[name="topic"], .new-material-fields input[name="cycle"], .new-material-fields input[name="group"], .new-material-fields input[name="newSubject"]');
    if (newMaterialTextField) {
      const form = newMaterialTextField.closest("form");
      if (form && String(newMaterialTextField.value || "").trim() && !plannerSelectedMaterialValues(form).includes("__new__")) {
        syncPlannerMaterialSelection(form, ["__new__"], "__new__");
      }
      return;
    }
    const field = event.target.closest('[data-action="update-subject-note"]');
    if (!field) return;
    touchMapKey("subjectNotes", field.dataset.subject, field.value);
    scheduleSubjectNotesSave();
  });

  document.body.addEventListener("change", async (event) => {
    const studySessionField = event.target.closest('.study-session-form input[name="endTime"]');
    if (studySessionField) {
      syncStudySessionForm(studySessionField.closest("form"), studySessionField.name);
      return;
    }
    const plannerStartTime = event.target.closest('.planner-time-range select[name="time"]');
    if (plannerStartTime) {
      const range = plannerStartTime.closest(".planner-time-range");
      const endSelect = range?.querySelector('select[name="endTime"]');
      if (endSelect && !isPlannerEndAfterStart(plannerStartTime.value, endSelect.value)) {
        endSelect.value = defaultPlannerEndTime(plannerStartTime.value);
      }
      return;
    }

    const materialSelect = event.target.closest('.new-material-fields select[name="subject"], .new-material-fields select[name="cycleSelect"], .new-material-fields select[name="groupSelect"]');
    if (materialSelect) {
      const form = materialSelect.closest("form");
      if (form && !plannerSelectedMaterialValues(form).includes("__new__")) {
        syncPlannerMaterialSelection(form, ["__new__"], "__new__");
      }
      updatePlannerMaterialSelectors(form);
      return;
    }

    const plannerQuestionSubject = event.target.closest('.planner-builder-form[data-type="Questões"] select[name="subject"]');
    if (plannerQuestionSubject) {
      renderContentOptions();
      const form = plannerQuestionSubject.closest("form");
      resetSmartContextDependents(form, "subject", { clearValues: true });
      applySmartFieldMemory(form);
      return;
    }

    if (event.target.id === "studyTimelineDate") {
      selectedStudyTimelineDate = event.target.value || timerOperationalValue();
      renderSectionNow("tempo");
      return;
    }

  });

  document.body.addEventListener("contextmenu", (event) => {
    const card = event.target.closest("[data-timer-subject]");
    if (!card) return;
    event.preventDefault();
    openTimerSubjectMenu(card.dataset.timerSubject || card.getAttribute("data-timer-subject") || "", event);
  });

  document.body.addEventListener("dblclick", (event) => {
    const editableRecord = event.target.closest("[data-edit-record]");
    const editableKey = event.target.closest("[data-edit-key]");
    if (!editableRecord && !editableKey) return;

    event.preventDefault();
    event.stopPropagation();

    if (editableRecord) {
      startInlineEdit(editableRecord);
      return;
    }

    if (editableKey && isInlineEditableKey(editableKey.dataset.editKey)) {
      startCustomInlineEdit(editableKey);
    }
  });

  document.body.addEventListener("mouseover", (event) => {
    const editable = event.target.closest("[data-edit-record],[data-edit-key]");
    if (editable && !editable.hasAttribute("title")) {
      editable.title = "Clique duplo para editar";
    }
  });

  document.body.addEventListener("keydown", handleInlineEditKey);

  document.body.addEventListener(
    "blur",
    (event) => {
      if (!document.querySelector("[data-editing='true']")) return;
      const recordElement = event.target.closest("[data-edit-record][contenteditable='true']");
      if (recordElement && recordElement.dataset.editing === "true") {
        saveInlineEdit(recordElement);
        return;
      }

      const customElement = event.target.closest("[data-edit-key][contenteditable='true']");
      if (customElement && customElement.dataset.editing === "true") saveCustomInlineEdit(customElement);
    },
    true,
  );

  document.body.addEventListener("mousedown", (event) => {
    const editingRecord = document.querySelector("[data-edit-record][data-editing='true']");
    if (editingRecord && !editingRecord.contains(event.target)) {
      saveInlineEdit(editingRecord);
    }

    const editingKey = document.querySelector("[data-edit-key][data-editing='true']");
    if (editingKey && !editingKey.contains(event.target)) {
      saveCustomInlineEdit(editingKey);
    }
  });

  let longPressTimer = null;
  let longPressTarget = null;
  document.body.addEventListener("touchstart", (event) => {
    const target = event.target.closest("[data-edit-record],[data-edit-key]");
    if (!target || target.closest("[contenteditable='true']")) return;
    longPressTarget = target;
    longPressTimer = setTimeout(() => {
      longPressTimer = null;
      if (longPressTarget) {
        if (longPressTarget.matches("[data-edit-record]")) {
          startInlineEdit(longPressTarget);
        } else if (isInlineEditableKey(longPressTarget.dataset.editKey)) {
          startCustomInlineEdit(longPressTarget);
        }
      }
    }, 500);
  }, { passive: true });

  document.body.addEventListener("touchend", () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
    longPressTarget = null;
  }, { passive: true });

  document.body.addEventListener("touchmove", () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
    longPressTarget = null;
  }, { passive: true });

  document.body.addEventListener("dragstart", (event) => {
    const editBlock = event.target.closest("[data-edit-block]");
    if (editMode && editBlock) {
      activeDashboardDragBlockId = editBlock.dataset.editBlock || "";
      event.dataTransfer.setData("text/edit-block", editBlock.dataset.editBlock);
      event.dataTransfer.effectAllowed = "move";
      editBlock.classList.add("dragging");
      return;
    }

    const cycleCard = event.target.closest("[data-study-cycle-card]");
    if (cycleCard) {
      event.dataTransfer.setData("text/study-cycle", cycleCard.dataset.studyCycleCard);
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.dropEffect = "move";
      cycleCard.classList.add("dragging");
      return;
    }

    const lessonGroup = event.target.closest("[data-cycle-content-group]");
    if (lessonGroup) {
      event.dataTransfer.setData("text/lesson-group", lessonGroup.dataset.cycleContentGroup);
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.dropEffect = "move";
      lessonGroup.classList.add("dragging");
      return;
    }

    const questionRow = event.target.closest("[data-question-id]");
    if (questionRow) {
      activeQuestionDragIds = questionDragIds(questionRow.dataset.questionId);
      event.dataTransfer.setData("text/plain", questionRow.dataset.questionId);
      event.dataTransfer.setData("text/question-ids", JSON.stringify(activeQuestionDragIds));
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.dropEffect = "move";
      questionRow.classList.add("dragging");
      return;
    }

    const card = event.target.closest("[data-task-id]");
    if (!card) return;
    const dragIds = plannerDragIds(card.dataset.taskId);
    document.body.classList.add("planner-dragging");
    event.dataTransfer.setData("text/plain", card.dataset.taskId);
    event.dataTransfer.setData("text/planner-ids", JSON.stringify(dragIds));
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.dropEffect = "move";
    card.classList.add("dragging");
  });

  document.body.addEventListener("dragend", (event) => {
    activeDashboardDragBlockId = "";
    event.target.closest("[data-edit-block]")?.classList.remove("dragging");
    event.target.closest("[data-study-cycle-card]")?.classList.remove("dragging");
    event.target.closest("[data-cycle-content-group]")?.classList.remove("dragging");
    event.target.closest("[data-question-id]")?.classList.remove("dragging");
    event.target.closest("[data-task-id]")?.classList.remove("dragging");
    activeQuestionDragIds = [];
    clearDashboardDropState();
    clearPlannerDragVisualState();
    clearGenericDndState();
    document.querySelectorAll("[data-study-cycle-card].drag-over").forEach((element) => {
      element.classList.remove("drag-over");
    });
    document.querySelectorAll("[data-cycle-content-group].drag-over").forEach((element) => {
      element.classList.remove("drag-over");
    });
    document.querySelectorAll("[data-question-group-row].drag-over").forEach((element) => {
      element.classList.remove("drag-over");
    });
  });

  const handleDragOver = (event) => {
    const dashboardContainer = event.target.closest(".metrics-grid, .dashboard-grid");
    const dashboardBlockId = activeDashboardDragBlockId || event.dataTransfer.getData("text/edit-block");
    if (editMode && dashboardContainer && dashboardBlockId) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      updateDashboardDropTarget(dashboardContainer, dashboardBlockId, event.clientX, event.clientY);
      return;
    }

    const studyCycleId = event.dataTransfer.getData("text/study-cycle");
    if (studyCycleId && event.target.closest(".cycle-gallery")) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      updateStudyCycleDropTarget(event, decodeURIComponent(studyCycleId));
      return;
    }

    const lessonGroupId = event.dataTransfer.getData("text/lesson-group");
    if (lessonGroupId && event.target.closest("[data-cycle-content-group], .lesson-group")) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      updateLessonGroupDropTarget(event, decodeURIComponent(lessonGroupId));
      return;
    }

    const plannerIds = hasPlannerDragData(event) ? plannerDragDataIds(event) : [];
    if (plannerIds.length && !event.target.closest("[data-calendar-date]")) {
      const destination = updatePlannerDropTarget(event, plannerIds);
      if (destination) {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        plannerAutoScroll(event);
        return;
      }
    }

    const resolvedQuestionIds = resolveDraggedQuestionIds(event);
    if (resolvedQuestionIds.length && [...event.dataTransfer.types].includes("text/question-ids")) {
      const questionDestination = updateQuestionDropTarget(event, resolvedQuestionIds);
      if (questionDestination) {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        return;
      }
    }

    const dropzone = event.target.closest("[data-task-dropzone]");
    const calendarDay = event.target.closest("[data-calendar-date]");
    if (!dropzone && !calendarDay) return;
    if (!hasPlannerDragData(event)) return;
    if (dropzone?.dataset.taskDropzone === "overdue") return;
    event.preventDefault();
    dropzone?.classList.add("drag-over");
    calendarDay?.classList.add("drag-over");
  };

  document.body.addEventListener("dragover", (event) => {
    if (activeDashboardDragBlockId || hasPlannerDragData(event)) {
      handleDragOver(event);
      return;
    }
    const now = performance.now();
    if (now - lastDragOverAt < 50) return;
    lastDragOverAt = now;
    handleDragOver(event);
  });

  document.body.addEventListener("dragleave", (event) => {
    const dashboardContainer = event.target.closest(".metrics-grid, .dashboard-grid");
    if (dashboardContainer && !dashboardContainer.contains(event.relatedTarget)) {
      clearDashboardDropState();
    }

    if (event.target.closest(".cycle-gallery") && !event.target.closest(".cycle-gallery")?.contains(event.relatedTarget)) clearGenericDndState("cycle");

    if (hasPlannerDragData(event) && !event.target.closest("[data-task-dropzone], [data-planner-group-card], [data-planner-group-children], [data-calendar-date]")) {
      clearPlannerDragVisualState();
    }

    if (event.target.closest("[data-question-id], #questionsTable, .questions-table") && !questionTableDropzone(event.target)?.contains(event.relatedTarget)) clearGenericDndState("question");

    if (event.target.closest("[data-cycle-content-group], .lesson-group") && !event.target.closest(".split-main")?.contains(event.relatedTarget)) clearGenericDndState("lesson-group");

    const calendarDay = event.target.closest("[data-calendar-date]");
    if (calendarDay && !calendarDay.contains(event.relatedTarget)) calendarDay.classList.remove("drag-over");
  });

  document.body.addEventListener("drop", (event) => {
    const editId = activeDashboardDragBlockId || event.dataTransfer.getData("text/edit-block");
    const dashboardContainer = event.target.closest(".metrics-grid, .dashboard-grid");
    if (editMode && dashboardContainer && editId) {
      event.preventDefault();
      const placeholder = document.querySelector("[data-dashboard-drop-placeholder]");
      const dragged = dashboardContainer.querySelector(`[data-edit-block="${editId}"]`);
      if (dragged && placeholder && dragged.parentElement === dashboardContainer) {
        dashboardContainer.insertBefore(dragged, placeholder);
        saveDashboardOrder();
      }
      activeDashboardDragBlockId = "";
      clearDashboardDropState();
      return;
    }

    const cycleCardId = event.dataTransfer.getData("text/study-cycle");
    if (cycleCardId && event.target.closest(".cycle-gallery")) {
      event.preventDefault();
      const destination = updateStudyCycleDropTarget(event, decodeURIComponent(cycleCardId));
      clearGenericDndState("cycle");
      if (moveStudyCycleToPosition(decodeURIComponent(cycleCardId), destination?.beforeCycle || "")) {
        renderSectionNow("materias", { save: true, commit: true });
      }
      return;
    }

    const lessonGroupId = event.dataTransfer.getData("text/lesson-group");
    if (lessonGroupId && event.target.closest("[data-cycle-content-group], .lesson-group")) {
      event.preventDefault();
      const destination = updateLessonGroupDropTarget(event, decodeURIComponent(lessonGroupId));
      clearGenericDndState("lesson-group");
      if (moveCycleContentGroupToPosition(decodeURIComponent(lessonGroupId), destination?.beforeGroup || "")) {
        renderSectionNow("materias", { save: true, commit: true });
      }
      return;
    }

    const resolvedPlannerIds = hasPlannerDragData(event) ? plannerDragDataIds(event) : [];
    const plannerDropDestination = resolvedPlannerIds.length
      ? plannerDropDestinationFromEvent(event, resolvedPlannerIds)
      : null;
    if (plannerDropDestination && canMovePlannerItemsToDestination(resolvedPlannerIds, plannerDropDestination)) {
      event.preventDefault();
      clearPlannerDragVisualState();
      if (applyPlannerDropDestination(resolvedPlannerIds, plannerDropDestination)) {
        renderSectionNow("planner", { save: true, commit: true });
        if (plannerDropDestination.kind === "date") showUndoToast("Item do Planner reagendado.");
      }
      return;
    }

    const resolvedQuestionIds = resolveDraggedQuestionIds(event);
    const questionDestination = resolvedQuestionIds.length && [...event.dataTransfer.types].includes("text/question-ids")
      ? questionDropDestinationFromEvent(event, resolvedQuestionIds)
      : null;
    if (questionDestination && canMoveQuestionsToDestination(resolvedQuestionIds, questionDestination)) {
      event.preventDefault();
      clearGenericDndState("question");
      if (applyQuestionDropDestination(resolvedQuestionIds, questionDestination)) {
        renderSectionNow("questoes", { save: true, commit: true });
      }
      return;
    }

    if (resolvedPlannerIds.length) return;
  });

  document.addEventListener("click", (event) => {
    if (performance.now() > plannerPointerSuppressClickUntil) return;
    if (!event.target.closest("#planner [data-task-id]")) return;
    event.preventDefault();
    event.stopPropagation();
  }, true);

  document.addEventListener("pointerdown", (event) => {
    if (!["touch", "pen"].includes(event.pointerType)) return;
    const card = event.target.closest("#planner [data-task-id]");
    if (!card || event.target.closest("button, input, select, textarea, [contenteditable='true'], [data-edit-record]")) return;
    const ids = plannerDragIds(card.dataset.taskId);
    if (!ids.length) return;
    activePlannerPointerDrag = {
      pointerId: event.pointerId,
      card,
      ids,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      started: false,
    };
    card.setPointerCapture?.(event.pointerId);
  });

  document.addEventListener("pointermove", (event) => {
    if (!activePlannerPointerDrag || activePlannerPointerDrag.pointerId !== event.pointerId) return;
    const drag = activePlannerPointerDrag;
    drag.lastX = event.clientX;
    drag.lastY = event.clientY;
    const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
    if (!drag.started && distance < 8) return;
    if (!drag.started) {
      drag.started = true;
      document.body.classList.add("planner-dragging");
      drag.card.classList.add("dragging");
    }
    const target = document.elementFromPoint(event.clientX, event.clientY);
    if (target) {
      const pointerEvent = { target, clientX: event.clientX, clientY: event.clientY };
      updatePlannerDropTarget(pointerEvent, drag.ids);
      plannerAutoScroll(pointerEvent);
    }
    event.preventDefault();
  }, { passive: false });

  const finishPlannerPointerDrag = (event) => {
    if (!activePlannerPointerDrag || activePlannerPointerDrag.pointerId !== event.pointerId) return;
    const drag = activePlannerPointerDrag;
    activePlannerPointerDrag = null;
    drag.card.classList.remove("dragging");
    if (!drag.started) return;
    const target = document.elementFromPoint(drag.lastX, drag.lastY);
    const destination = target ? plannerDropDestinationFromEvent({ target, clientX: drag.lastX, clientY: drag.lastY }, drag.ids) : null;
    const changed = canMovePlannerItemsToDestination(drag.ids, destination)
      ? applyPlannerDropDestination(drag.ids, destination)
      : false;
    clearPlannerDragVisualState();
    plannerPointerSuppressClickUntil = performance.now() + 420;
    if (changed) {
      renderSectionNow("planner", { save: true, commit: true });
      if (destination.kind === "date") showUndoToast("Item do Planner reagendado.");
    }
    event.preventDefault();
    event.stopPropagation();
  };

  document.addEventListener("pointerup", finishPlannerPointerDrag);
  document.addEventListener("pointercancel", finishPlannerPointerDrag);

  document.addEventListener("click", (event) => {
    if (performance.now() > genericPointerSuppressClickUntil) return;
    if (!event.target.closest("[data-study-cycle-card], [data-cycle-content-group], [data-question-id]")) return;
    event.preventDefault();
    event.stopPropagation();
  }, true);

  document.addEventListener("pointerdown", (event) => {
    if (!["touch", "pen"].includes(event.pointerType)) return;
    if (event.target.closest("#planner [data-task-id], button, input, select, textarea, [contenteditable='true'], [data-edit-record]")) return;

    const cycleCard = event.target.closest("[data-study-cycle-card]");
    const lessonGroup = event.target.closest("[data-cycle-content-group]");
    const questionRow = event.target.closest("[data-question-id]");
    let drag = null;

    if (cycleCard) {
      drag = {
        type: "cycle",
        element: cycleCard,
        id: decodeURIComponent(cycleCard.dataset.studyCycleCard || ""),
      };
    } else if (lessonGroup) {
      drag = {
        type: "lesson-group",
        element: lessonGroup,
        id: decodeURIComponent(lessonGroup.dataset.cycleContentGroup || ""),
      };
    } else if (questionRow) {
      drag = {
        type: "question",
        element: questionRow,
        ids: questionDragIds(questionRow.dataset.questionId),
      };
    }

    if (!drag || (!drag.id && !drag.ids?.length)) return;
    activeGenericPointerDrag = {
      ...drag,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      started: false,
    };
    drag.element.setPointerCapture?.(event.pointerId);
  });

  document.addEventListener("pointermove", (event) => {
    if (!activeGenericPointerDrag || activeGenericPointerDrag.pointerId !== event.pointerId) return;
    const drag = activeGenericPointerDrag;
    drag.lastX = event.clientX;
    drag.lastY = event.clientY;
    const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
    if (!drag.started && distance < 8) return;
    if (!drag.started) {
      drag.started = true;
      drag.element.classList.add("dragging");
      if (drag.type === "question") activeQuestionDragIds = [...drag.ids];
    }
    const target = document.elementFromPoint(event.clientX, event.clientY);
    if (!target) return;
    const pointerEvent = { target, clientX: event.clientX, clientY: event.clientY };
    if (drag.type === "cycle") updateStudyCycleDropTarget(pointerEvent, drag.id);
    if (drag.type === "lesson-group") updateLessonGroupDropTarget(pointerEvent, drag.id);
    if (drag.type === "question") updateQuestionDropTarget(pointerEvent, drag.ids);
    plannerAutoScroll(pointerEvent);
    event.preventDefault();
  }, { passive: false });

  const finishGenericPointerDrag = (event) => {
    if (!activeGenericPointerDrag || activeGenericPointerDrag.pointerId !== event.pointerId) return;
    const drag = activeGenericPointerDrag;
    activeGenericPointerDrag = null;
    drag.element.classList.remove("dragging");
    if (!drag.started) return;

    const target = document.elementFromPoint(drag.lastX, drag.lastY);
    if (target && drag.type === "cycle") {
      const destination = updateStudyCycleDropTarget({ target, clientX: drag.lastX, clientY: drag.lastY }, drag.id);
      const changed = moveStudyCycleToPosition(drag.id, destination?.beforeCycle || "");
      clearGenericDndState("cycle");
      if (changed) renderSectionNow("materias", { save: true, commit: true });
    } else if (target && drag.type === "lesson-group") {
      const destination = updateLessonGroupDropTarget({ target, clientX: drag.lastX, clientY: drag.lastY }, drag.id);
      const changed = moveCycleContentGroupToPosition(drag.id, destination?.beforeGroup || "");
      clearGenericDndState("lesson-group");
      if (changed) renderSectionNow("materias", { save: true, commit: true });
    } else if (target && drag.type === "question") {
      const destination = questionDropDestinationFromEvent({ target, clientX: drag.lastX, clientY: drag.lastY }, drag.ids);
      const changed = canMoveQuestionsToDestination(drag.ids, destination)
        ? applyQuestionDropDestination(drag.ids, destination)
        : false;
      clearGenericDndState("question");
      activeQuestionDragIds = [];
      if (changed) renderSectionNow("questoes", { save: true, commit: true });
    }

    genericPointerSuppressClickUntil = performance.now() + 420;
    event.preventDefault();
    event.stopPropagation();
  };

  document.addEventListener("pointerup", finishGenericPointerDrag);
  document.addEventListener("pointercancel", finishGenericPointerDrag);

  let activeDashboardResize = null;
  let activeDashboardResizeFrame = 0;
  const stopDashboardResize = () => {
    if (!activeDashboardResize) return;
    if (activeDashboardResizeFrame) {
      cancelAnimationFrame(activeDashboardResizeFrame);
      activeDashboardResizeFrame = 0;
      applyDashboardBlockSizes();
      updateHeatMap();
    }
    document.body.classList.remove("resizing-dashboard");
    activeDashboardResize.element?.classList.remove("resizing");
    activeDashboardResize = null;
  };

  document.addEventListener("pointerdown", (event) => {
    const handle = event.target.closest(".dashboard-span-resizer");
    if (!handle || !editMode || window.innerWidth <= 980) return;
    const blockId = handle.dataset.block || "";
    const element = handle.closest('[data-edit-block]');
    const container = element?.closest(".metrics-grid, .dashboard-grid");
    if (!blockId || !element || !container) return;

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const min = Math.max(1, Math.round(number(handle.dataset.min) || 1));
    const max = Math.max(min, Math.round(number(handle.dataset.max) || DASHBOARD_GRID_COLUMNS));
    const size = getDashboardBlockSize(blockId);

    activeDashboardResize = {
      blockId,
      element,
      container,
      startX: event.clientX,
      startY: event.clientY,
      startSpan: size.span,
      startHeight: size.height || elementRect.height,
      containerWidth: containerRect.width,
      min,
      max,
    };
    element.classList.add("resizing");
    document.body.classList.add("resizing-dashboard");
    handle.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  });

  document.addEventListener("pointermove", (event) => {
    if (!activeDashboardResize) return;
    const { blockId, element, startX, startY, startSpan, startHeight, containerWidth, min, max } = activeDashboardResize;
    const unitWidth = Math.max(24, containerWidth / DASHBOARD_GRID_COLUMNS);
    const span = Math.min(max, Math.max(min, Math.round(startSpan + ((event.clientX - startX) / unitWidth))));
    const rawHeight = startHeight + (event.clientY - startY);
    const height = normalizeDashboardBlockHeight(blockId, Math.round(rawHeight / DASHBOARD_HEIGHT_GRID) * DASHBOARD_HEIGHT_GRID);
    setDashboardBlockSize(blockId, { span, height }, false);
    element.dataset.dashboardSpan = String(span);
    element.style.gridColumn = `span ${span}`;
    if (height) element.style.minHeight = `${height}px`;
    if (!activeDashboardResizeFrame) {
      activeDashboardResizeFrame = requestAnimationFrame(() => {
        activeDashboardResizeFrame = 0;
        updateHeatMap();
      });
    }
  });

  document.addEventListener("pointerup", () => {
    if (!activeDashboardResize) return;
    stopDashboardResize();
    scheduleDashboardVisualRefresh();
  });

  document.addEventListener("pointercancel", stopDashboardResize);
  window.addEventListener("blur", stopDashboardResize);

  const hideHeatMapTooltip = () => {
    const tooltip = document.querySelector("#heatMapTooltip");
    if (!tooltip) return;
    tooltip.hidden = true;
    tooltip.innerHTML = "";
    delete tooltip.dataset.date;
  };

  const positionHeatMapTooltip = (tooltip, day) => {
    const panel = tooltip.closest(".heatmap-panel");
    const rect = day.getBoundingClientRect();
    const panelRect = panel?.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const horizontalOffset = 12;
    const verticalOffset = 4;
    if (!panelRect) return;
    const panelScrollLeft = panel.scrollLeft || 0;
    const panelScrollTop = panel.scrollTop || 0;
    const placeRight = rect.right + horizontalOffset + tooltipRect.width <= panelRect.right - 8;
    const left = placeRight
      ? rect.right - panelRect.left + panelScrollLeft + horizontalOffset
      : Math.max(8, rect.left - panelRect.left + panelScrollLeft - tooltipRect.width - horizontalOffset);
    const top = Math.min(
      panelScrollTop + panelRect.height - tooltipRect.height - 8,
      Math.max(8, rect.top - panelRect.top + panelScrollTop - verticalOffset),
    );
    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  };

  document.body.addEventListener("pointermove", (event) => {
    const day = event.target.closest("[data-heatmap-day]");
    const tooltip = document.querySelector("#heatMapTooltip");
    if (!day || !tooltip) return;
    const minutes = Math.max(0, number(day.dataset.hours));
    const isFuture = day.dataset.future === "true";
    const content = isFuture
      ? [
        `<strong>${escapeHtml(formatDate(day.dataset.date || localDateValue()))}</strong>`,
        "<span>Dia futuro</span>",
      ].join("")
      : [
        `<strong>${escapeHtml(formatDate(day.dataset.date || localDateValue()))}</strong>`,
        `<span>Questões feitas: ${escapeHtml(day.dataset.questions || "0")}</span>`,
        `<span>Tarefas concluídas: ${escapeHtml(day.dataset.tasks || "0")}</span>`,
        `<span>Horas estudadas: ${escapeHtml(minutes ? formatDuration(minutes) : "0min")}</span>`,
        `<span>Cards/revisões feitos: ${escapeHtml(day.dataset.cards || "0")}</span>`,
        `<span>Pontuação total: ${escapeHtml(day.dataset.score || "0")}</span>`,
      ].join("");

    if (tooltip.dataset.date !== day.dataset.date) {
      tooltip.innerHTML = content;
      tooltip.dataset.date = day.dataset.date || "";
    }
    tooltip.hidden = false;
    positionHeatMapTooltip(tooltip, day);
  });

  document.body.addEventListener("pointerover", (event) => {
    if (event.target.closest("[data-heatmap-day]")) return;
    hideHeatMapTooltip();
  });

  document.body.addEventListener("mouseleave", hideHeatMapTooltip);
}

function setupCloudAuth() {
  updateCloudStatus();

  window.studyCloud?.onAuthChange?.((event) => {
    if (event === "SIGNED_OUT") {
      remoteSyncEnabled = false;
      setSyncStatus("disconnected");
      if (manualCloudLogout) {
        manualCloudLogout = false;
      } else {
        openSyncDisconnectedModal();
      }
      return;
    }

        hydrateRemoteState("auth-event").then(() => {
            syncPlannerLinks();
            updateCloudStatus();
            render();
        });
  });

  document.querySelector("#cloudLogoutButton")?.addEventListener("click", logoutAccount);
}

function logoutAccount() {
  manualCloudLogout = true;
  window.studyCloud?.signOut()?.catch((err) => {
    console.error("[logoutAccount]", err);
  });
  remoteSyncEnabled = false;
  cloudSessionActive = false;
  replaceState({
    ...defaultState(),
    userProfile: null,
    keepConnected: false,
  });
  setKeepConnected(false);
  clearEphemeralSyncArtifacts({ preserveStateCache: true, nextState: state }).catch((err) => {
    console.error("[logoutAccount] falhou ao limpar cache local:", err);
  });
  saveState({ immediate: true });
  setSyncStatus("disconnected");
  updateCloudStatus();
  updateFirstLoginModal();
}

function timeoutPromise(ms) {
  return new Promise((_, reject) =>
    setTimeout(() => reject(new Error("DB_TIMEOUT")), ms)
  );
}

async function authenticateCloudAccount(email, password, action, options = {}) {
  if (!window.studyCloud?.enabled) {
    alert("A sincronização online ainda não foi configurada. Seus dados continuam salvos neste dispositivo.");
    return false;
  }

  try {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    const normalizedPassword = String(password || "");
    console.log("[auth] iniciando autenticacao cloud para", normalizedEmail, "acao:", action);

    const CLOUD_AUTH_TIMEOUT = 15000;
    const authPromise =
      action === "signUp"
        ? window.studyCloud.signUp(normalizedEmail, normalizedPassword)
        : window.studyCloud.signIn(normalizedEmail, normalizedPassword);

    console.log("[auth] chamando signIn/signUp...");
    const authResult = await Promise.race([
      authPromise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("TIMEOUT")), CLOUD_AUTH_TIMEOUT)
      ),
    ]);
    console.log("[auth] resposta recebida:", authResult);

    const { data, error } = authResult;
    if (error) {
      console.log("[auth] erro na resposta:", error);
      if (options.silentExistingEmail && isExistingEmailError(error)) return false;
      alert(cloudAuthErrorMessage(error, action));
      return false;
    }

    if (action === "signUp" && data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      if (options.silentExistingEmail) return false;
      alert("E-mail ja cadastrado.");
      return false;
    }

    if (action === "signIn" && data?.user && !data?.session) {
      alert("Seu e-mail ainda não foi confirmado. Confirme o e-mail e tente entrar novamente.");
      return false;
    }

    const user = data?.user || await window.studyCloud.getUser().catch(() => null);
    if (!user) {
      alert(action === "signUp"
        ? "Cadastro criado. Confirme seu e-mail e depois entre com a mesma senha."
        : "Não foi possível entrar. Se o e-mail estiver correto, confira se ele já foi confirmado.");
      return action === "signUp" ? null : false;
    }
    cloudSessionActive = true;

    const localState = options.localState || cloneStudyState(state);
    console.log("[auth] carregando estado da nuvem...");
    const versionResult = await Promise.race([
      window.studyCloud.loadLatestVersion(),
      timeoutPromise(10000),
    ]);
    const cloudState = versionResult?.data || null;
    const versionDate = versionResult?.created_at || null;
    console.log("[auth] estado da nuvem carregado:", cloudState ? "com dados" : "vazio");

    const resolved = await Promise.race([
      resolveCloudStateAfterAuth({
        cloudState,
        localState,
        profile: options.profile,
        source: "auth",
        versionDate,
      }),
      timeoutPromise(20000),
    ]);
    if (!resolved) return false;

    console.log("[auth] login completo, finalizando...");
    if (versionDate && stateHasUserData(cloudState)) {
      notify("Restaurado de " + localDateTimeLabel(versionDate), "info");
    }
    remoteSyncEnabled = true;
    syncDisconnectedPromptShown = false;
    updateCloudStatus();
    if (!options.skipRender) render();
    saveState();
    return true;
  } catch (error) {
    remoteSyncEnabled = false;
    setSyncStatus("error");
    if (error?.message === "TIMEOUT" || error?.message === "DB_TIMEOUT") {
      console.error("Supabase não respondeu dentro do prazo. Verifique se o projeto está ativo em https://supabase.com/dashboard/project/yfrapobjycdczblcnctn");
      alert("O servidor não respondeu. Pode ser que o projeto Supabase esteja pausado ou inacessível. Acesse supabase.com, faça login e reative o projeto na dashboard.");
    } else {
      console.error("Erro de autenticação Supabase:", error);
      alert(cloudAuthErrorMessage(error, action));
    }
    return false;
  }
}

async function resolveCloudStateAfterAuth({
  cloudState = null,
  localState = null,
  profile = null,
  source = "auth",
} = {}) {
  if (isLocalSandbox() || !window.studyCloud?.enabled) return false;
  localState = cloneStudyState(localState || state);
  cloudState = cloudState ? cloneStudyState(cloudState) : null;
  const localHasData = stateHasUserData(localState);
  const cloudHasData = stateHasUserData(cloudState);

  try {
    if (!localHasData && !cloudHasData) {
      const emptyState = {
        ...defaultState(),
        userProfile: profile || localState?.userProfile || state.userProfile,
      };
      await replaceStateSafely(emptyState, "cloud-to-local");
      await finalizeRemoteSync();
      return true;
    }

    if (!localHasData && cloudHasData) {
      const cloudToLocalState = {
        ...cloudState,
        userProfile: profile || cloudState?.userProfile || state.userProfile,
      };
      await replaceStateSafely(cloudToLocalState, "cloud-to-local");
      await finalizeRemoteSync();
      return true;
    }

    if (!cloudHasData) {
      const localOnlyState = {
        ...localState,
        userProfile: profile || localState?.userProfile || state.userProfile,
      };
      await replaceStateSafely(localOnlyState, "merge");
      setPendingMergeUpload(localOnlyState);
      saveState({ immediate: true });
      return true;
    }

    if (!window.syncMerge?.mergeStates) {
      throw new Error("Modulo de merge de sincronizacao indisponivel.");
    }

    const sanitizedMergeInput = purgeArchiveFromMergeInput(localState, cloudState);
    if (sanitizedMergeInput.droppedCount > 0 && typeof window.studyCloud?.notifyArchiveTrim === "function") {
      window.studyCloud.notifyArchiveTrim(sanitizedMergeInput.droppedCount);
    }

    const mergeResult = window.syncMerge.mergeStates(sanitizedMergeInput.local, sanitizedMergeInput.cloud, {
      mergeStampsLocal: sanitizedMergeInput.local?.mergeStamps,
      mergeStampsCloud: sanitizedMergeInput.cloud?.mergeStamps,
    });
    const { conflicts = [], mergedDomains = [] } = mergeResult;
    const mergedState = {
      ...mergeResult.state,
      userProfile: profile || mergeResult.state?.userProfile || state.userProfile,
    };

    await replaceStateSafely(mergedState, "merge");
    setPendingMergeUpload(mergedState);

    if (mergedDomains.length > 0) {
      notifyConflictCount(mergedDomains.length);
    }

    if (conflicts.length > 0) {
      conflictPending = true;
      setSyncStatus("conflict");
      openSyncConflictModal({ localState, cloudState, profile, source, conflicts })
        .then((choice) => applySyncConflictChoice(choice, { cloudState, localState, profile }))
        .catch((err) => {
          console.error("[resolveCloudStateAfterAuth]", err);
          setSyncStatus("conflict");
        });
    }

    if (!conflictPending) {
      await finalizeRemoteSync();
    }
    return true;
  } catch (err) {
    console.error("[resolveCloudStateAfterAuth]", err);
    setSyncStatus("error");
    openSyncDisconnectedModal();
    return false;
  }
}

function isExistingEmailError(error) {
  const message = String(error?.message || "").toLowerCase();
  const code = String(error?.code || "").toLowerCase();
  return (
    message.includes("already registered") ||
    message.includes("already been registered") ||
    message.includes("already exists") ||
    message.includes("user already") ||
    code.includes("user_already_exists")
  );
}

function cloudAuthErrorMessage(error, action) {
  const message = String(error?.message || "").toLowerCase();
  const code = String(error?.code || "").toLowerCase();

  if (action === "signIn" && (message.includes("invalid login") || code.includes("invalid_credentials"))) {
    return "Senha incorreta.";
  }

  if (message.includes("email not confirmed") || code.includes("email_not_confirmed")) {
    return "Seu e-mail ainda não foi confirmado. Abra o e-mail de confirmação antes de entrar.";
  }

  if (message.includes("failed to fetch") || message.includes("networkerror")) {
    return "Não consegui conectar sua conta agora. Verifique a internet e tente de novo.";
  }

  if (
    action === "signUp" &&
    isExistingEmailError(error)
  ) {
    return "E-mail ja cadastrado.";
  }

  return String(error?.message || "Não foi possível concluir essa ação.").replace(/Supabase/gi, "sincronização online");
}

function syncStatusInfo(statusKey = syncStatusKey) {
  const statuses = {
    localSandbox: { label: "Modo local de testes", tone: "offline" },
    synced: { label: "Salvo na nuvem", tone: "synced" },
    syncing: { label: "Salvando...", tone: "syncing" },
    offline: { label: "Nao salvo (sem conexao)", tone: "offline" },
    pendingLocal: { label: "Sincronizacao local pendente", tone: "warning" },
    conflict: { label: "Conflito de sincronizacao", tone: "warning" },
    disconnected: { label: "Offline — alteracoes nao serao mantidas", tone: "error" },
    error: { label: "Erro ao acessar dados online", tone: "error" },
  };
  return statuses[statusKey] || statuses.offline;
}

function setSyncStatus(statusKey = "offline", detail = "") {
  syncStatusKey = statusKey;
  const status = document.querySelector("#cloudStatus");
  if (!status) return;
  const info = syncStatusInfo(statusKey);
  status.innerHTML = `
    <span class="sync-status sync-status-${info.tone}">
      <span class="sync-status-dot" aria-hidden="true"></span>
      <span></span>
    </span>
  `;
  const label = status.querySelector(".sync-status span:last-child");
  if (label) label.textContent = detail ? `${info.label} - ${detail}` : info.label;
}

async function renderBackupStatus() {
  const el = document.querySelector("#backupStatus");
  const text = document.querySelector("#backupStatusText");
  if (!el || !text) return;
  try {
    const m = await _loadBackupModules();
    const est = await m.persistence.ring.idbEstimateStorage();
    const usedKB = Math.round((est.usage || 0) / 1024);
    const qfc = m.persistence.ring.quotaFailureCount || 0;
    const parts = ["Cache: " + usedKB + "KB"];
    if (qfc > 0) parts.push("Falhas: " + qfc);
    if (state.syncMeta?.lastSyncedAt) {
      parts.push("Sync: " + new Date(state.syncMeta.lastSyncedAt).toLocaleTimeString());
    }
    text.textContent = parts.join(" | ");
    el.className = "sync-status sync-status-" + (qfc > 0 ? "warning" : "synced");
  } catch (err) {
    console.error("[renderBackupStatus] falhou ao renderizar status:", err);
    text.textContent = "---";
  }
}

async function updateCloudStatus() {
  const status = document.querySelector("#cloudStatus");
  if (!status) return;

  if (isLocalSandbox()) {
    remoteSyncEnabled = false;
    cloudSessionActive = false;
    setSyncStatus("localSandbox");
    return;
  }

  if (!window.studyCloud?.enabled) {
    setSyncStatus("offline");
    return;
  }

  if (conflictPending) {
    setSyncStatus("conflict");
    return;
  }

  try {
    const user = await window.studyCloud.getUser();
    if (user) {
      setSyncStatus(syncStatusKey === "syncing" || syncStatusKey === "conflict" || syncStatusKey === "error" ? syncStatusKey : "synced");
    } else {
      setSyncStatus("disconnected");
    }
  } catch (err) {
    console.error("[updateCloudStatus]", err);
    remoteSyncEnabled = false;
    cloudSessionActive = false;
    setSyncStatus("error");
    openSyncDisconnectedModal();
  }
}

async function handleCsvImport(event) {
  const file = event.target.files?.[0];
  if (!file) return;

  try {
    const text = await readFileText(file);
    const type = document.querySelector("#importType").value;
    let count = 0;

    if (type === "json") {
      count = importJsonPackage(JSON.parse(text));
    } else {
      const records = parseCsv(text);
      const importers = {
        questions: importQuestionsCsv,
        essays: importEssaysCsv,
        planner: importPlannerCsv,
        subjects: importSubjectsCsv,
      };
      count = importers[type](records);
    }

    saveState();
    render();
    alert(`${count} registro${count === 1 ? "" : "s"} importado${count === 1 ? "" : "s"}.`);
  } catch (error) {
    alert(`Não consegui importar esse CSV: ${error.message}`);
  } finally {
    event.target.value = "";
  }
}

function getFlashcardTxtImportInput() {
  if (flashcardTxtImportInput?.isConnected) return flashcardTxtImportInput;
  flashcardTxtImportInput = document.createElement("input");
  flashcardTxtImportInput.type = "file";
  flashcardTxtImportInput.accept = ".txt,text/plain";
  flashcardTxtImportInput.className = "hidden";
  flashcardTxtImportInput.setAttribute("aria-hidden", "true");
  flashcardTxtImportInput.addEventListener("change", handleFlashcardTxtImport);
  document.body.appendChild(flashcardTxtImportInput);
  return flashcardTxtImportInput;
}

function openFlashcardTxtImportPicker() {
  const input = getFlashcardTxtImportInput();
  input.value = "";
  input.click();
}

async function handleFlashcardTxtImport(event) {
  const file = event.target.files?.[0];
  if (!file) return;

  try {
    const text = await readFileText(file);
    const records = parseFlashcardTxtSchedule(text);
    const count = importFlashcardTxtSchedule(records);
    saveState();
    render();
    alert(`${count} dia${count === 1 ? "" : "s"} de flash-card importado${count === 1 ? "" : "s"}.`);
  } catch (error) {
    alert(`Nao consegui importar esse TXT: ${error.message}`);
  } finally {
    event.target.value = "";
  }
}

function parseFlashcardTxtSchedule(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) {
    throw new Error("O arquivo TXT esta vazio.");
  }

  const records = [];
  const startDate = plannerTodayValue();
  const endDate = addDays(startDate, 14);

  lines.forEach((line) => {
    const parsed = parseFlashcardTxtLine(line);
    if (!parsed) {
      throw new Error(`Linha invalida: "${line}". Use o formato YYYY-MM-DD R: 1200 N: 52.`);
    }
    const { date, reviewCards, newCards } = parsed;
    if (date < startDate || date > endDate) return;

    records.push({
      date,
      reviewCards,
      newCards,
    });
  });

  const deduped = new Map();
  records.forEach((record) => {
    deduped.set(record.date, record);
  });

  if (!deduped.size) {
    throw new Error("Nenhuma linha valida encontrada nos proximos 15 dias.");
  }

  return [...deduped.values()].sort((a, b) => a.date.localeCompare(b.date));
}

function parseFlashcardTxtLine(line) {
  const match = String(line).match(/^(\d{4}-\d{2}-\d{2})\s+R:\s*(\d+)\s+N:\s*(\d+)$/);
  if (!match) return null;
  return {
    date: match[1],
    reviewCards: Math.max(0, number(match[2])),
    newCards: Math.max(0, number(match[3])),
  };
}

function importFlashcardTxtSchedule(records) {
  state.planner ||= [];
  let count = 0;

  records.forEach((record) => {
    const existing = state.planner.find((item) => item.type === "Flash-card" && item.date === record.date);
    if (existing) {
      existing.flashcardData = {
        ...(existing.flashcardData || {}),
        reviewCards: record.reviewCards,
        newCards: record.newCards,
        totalCards: record.reviewCards + record.newCards,
      };
      syncPlannerItemToSource(existing);
      count += 1;
      return;
    }

    const item = {
      id: uid(),
      date: record.date,
      type: "Flash-card",
      done: false,
      status: "todo",
      subject: "Geral",
      title: plannerFlashcardTitle(""),
      flashcardData: {
        deckName: "",
        reviewCards: record.reviewCards,
        newCards: record.newCards,
        totalCards: record.reviewCards + record.newCards,
      },
    };
    syncPlannerItemToSource(item);
    state.planner = [...(state.planner || []), item];
    touchRecord("planner", item.id);
    count += 1;
  });

  return count;
}

function importJsonPackage(packageData) {
  const imported = packageData?.state || packageData;
  let count = 0;
  ["planner", "questions", "errors", "mocks", "essays", "studySessions"].forEach((key) => {
    if (Array.isArray(imported[key])) {
      if (key === "questions") {
        imported[key].forEach((item) => {
          addQuestionRecord({
            ...item,
            id: item.id || uid(),
            date: questionDateFromValue(item.date),
            subject: item.subject ? canonicalSubject(item.subject) : item.subject,
          }, {
            extra: {
              plannerId: item.plannerId || "",
              plannerDone: Boolean(item.plannerDone),
              studySessionId: item.studySessionId || "",
            },
          });
        });
      } else {
        const importedItems = imported[key].map((item) => ({
            id: item.id || uid(),
            ...item,
            subject: item.subject ? canonicalSubject(item.subject) : item.subject,
          }));
        state[key] = [...(state[key] || []), ...importedItems];
        importedItems.forEach((item) => touchRecord(key, item.id));
      }
      count += imported[key].length;
    }
  });

  ["subjectContents", "subjectCycles", "cycleLessons", "cycleContentOrder", "subjectNotes"].forEach((key) => {
    if (imported[key] && typeof imported[key] === "object") {
      if (key === "subjectContents" || key === "subjectCycles" || key === "subjectNotes") {
        Object.entries(imported[key]).forEach(([subject, value]) => {
          state[key][canonicalSubject(subject)] = value;
        });
      } else {
        Object.assign(state[key], imported[key]);
      }
      count += Object.keys(imported[key]).length;
    }
  });

  return count;
}

function importQuestionsCsv(records) {
  let count = 0;
  records.forEach((record) => {
    const subject = canonicalSubject(pick(record, ["matéria", "materia", "subject"]) || "Matemática");
    const content = pick(record, ["conteúdo", "conteudo", "content"]) || "Sem conteúdo";
    const questions = Math.max(1, parseCsvNumber(pick(record, ["Questões feitas", "questoes feitas", "questões", "questoes"])));
    const correct = Math.min(
      questions,
      Math.max(0, parseCsvNumber(pick(record, ["Acertos", "corretas", "correct"]))),
    );
    addQuestionRecord({
      date: questionDateFromValue(pick(record, ["Data", "date"])),
      subject,
      content,
      questions,
      correct,
    });
    count += 1;
  });
  return count;
}

function importEssaysCsv(records) {
  let count = 0;
  records.forEach((record) => {
    const essay = {
      c1: parseCsvNumber(pick(record, ["c1", "competência 1", "competencia 1"])),
      c2: parseCsvNumber(pick(record, ["c2", "competência 2", "competencia 2"])),
      c3: parseCsvNumber(pick(record, ["c3", "competência 3", "competencia 3"])),
      c4: parseCsvNumber(pick(record, ["c4", "competência 4", "competencia 4"])),
      c5: parseCsvNumber(pick(record, ["c5", "competência 5", "competencia 5"])),
    };
    const essayRecord = {
      id: uid(),
      date: parseFlexibleDate(pick(record, ["Data", "date"])),
      theme: pick(record, ["tema", "theme", "Nome"]) || "Redação importada",
      ...essay,
      score: essayScoreTotal(essay),
      axis: pick(record, ["eixo"]),
      status: pick(record, ["status"]),
      mirror: pick(record, ["espelho"]),
      image: "",
    };
    state.essays = [...(state.essays || []), essayRecord];
    touchRecord("essays", essayRecord.id);
    count += 1;
  });
  return count;
}

function importPlannerCsv(records) {
  let count = 0;
  records.forEach((record) => {
    const title = pick(record, ["Nome", "name", "tarefa"]);
    if (!title) return;
    const subject = canonicalSubject(pick(record, ["matéria", "materia", "subject"]) || "Geral");
    const topics = pick(record, ["Tópicos", "Topicos", "conteúdo", "conteudo"]);
    const status = pick(record, ["Status"]);
    const normalizedStatus = normalizeHeader(status);
    const done = normalizedStatus.includes("concluido") || normalizedStatus.includes("concluida");
    const plannerStatus = done ? "done" : normalizedStatus.includes("fazendo") ? "doing" : "todo";

    const plannerRecord = {
      id: uid(),
      date: parseFlexibleDate(pick(record, ["Data", "date"])),
      subject,
      title,
      type: inferPlannerType(title),
      done,
      status: plannerStatus,
    };
    state.planner = [...(state.planner || []), plannerRecord];
    touchRecord("planner", plannerRecord.id);

    extractNotionRelationNames(topics).forEach((content) => rememberContent(subject, content));
    count += 1;
  });
  return count;
}

function importSubjectsCsv(records) {
  let count = 0;
  records.forEach((record) => {
    const subject = canonicalSubject(pick(record, ["Nome", "name", "matéria", "materia"]));
    if (!subject) return;
    addSubject(subject);
    ensureSubjectState(subject);
    count += 1;
  });
  fillSubjectSelects();
  return count;
}

function inferPlannerType(title) {
  const normalized = normalizeHeader(title);
  if (normalized.includes("quest")) return "Lista";
  if (normalized.includes("revis")) return "Revisão";
  if (normalized.includes("simulado")) return "Simulado";
  if (normalized.includes("redacao")) return "Redação";
  return "Aula";
}

function extractNotionRelationNames(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.replace(/\s*\(https?:\/\/[^)]*\)/g, "").trim())
    .filter(Boolean);
}

function openImageModal(src) {
  if (!src) return;
  closeAllOverlays();
  const modal = document.querySelector("#imageModal");
  const image = document.querySelector("#imageModalImg");
  const fallback = document.querySelector("#imageModalEmpty");
  if (!modal || !image) return;
  image.classList.remove("hidden");
  if (fallback) fallback.classList.add("hidden");
  image.onerror = () => {
    image.classList.add("hidden");
    if (fallback) fallback.classList.remove("hidden");
  };
  image.src = src;
  modal.classList.remove("hidden");
  modal.setAttribute("aria-hidden", "false");
  syncBodyScrollLock();
}

function closeImageModal() {
  const modal = document.querySelector("#imageModal");
  const image = document.querySelector("#imageModalImg");
  if (!modal || modal.classList.contains("hidden")) return;
  modal.classList.add("hidden");
  modal.setAttribute("aria-hidden", "true");
  image.src = "";
  syncBodyScrollLock();
}

function drawChartsForSection(viewId = activeViewId(), options = {}) {
  const signature = chartSignatureForSection(viewId);
  if (signature && !options.force && sectionChartCache.get(viewId) === signature) return;
  runSectionModuleHook(loadedSectionModules.get(viewId), "drawCharts", viewId);
  if (signature) sectionChartCache.set(viewId, signature);
}

function dashboardQuestionChartSnapshot() {
  const mode = document.querySelector("#chartMode")?.value || "accumulated";
  const filter = dashboardSubjectFilter();
  const chartItems = dashboardQuestions();
  const timeline = [...new Set(chartItems.map((item) => String(item.date || "")).filter(Boolean))].sort();
  const grouped = chartItems.reduce((map, item) => {
    const key = `${item.subject}::${item.date}`;
    const bucket = map.get(key) || { questions: 0, correct: 0 };
    bucket.questions += Math.max(0, number(item.questions));
    bucket.correct += Math.max(0, number(item.correct));
    map.set(key, bucket);
    return map;
  }, new Map());
  const chartSubjects = filter === "all"
    ? dashboardSubjects()
    : dashboardSubjects().filter((subject) => subject === filter);
  const series = chartSubjects
    .map((subject) => {
      let runningQuestions = 0;
      let runningCorrect = 0;
      const points = timeline.map((date) => {
        const dayStats = grouped.get(`${subject}::${date}`) || { questions: 0, correct: 0 };
        runningQuestions += dayStats.questions;
        runningCorrect += dayStats.correct;
        if (mode === "daily") return dayStats.questions;
        if (mode === "accuracy") return pct(runningCorrect, runningQuestions);
        return runningQuestions;
      });
      return { label: subject, color: colors[subject], points };
    })
    .filter((serie) => serie.points.some((point) => point > 0));

  const subtitle = mode === "daily"
    ? "Questões por dia"
    : mode === "accuracy"
      ? "Acerto acumulado por dia"
      : "Evolução acumulada por dia";

  const subtitleElement = document.querySelector('[data-edit-key="chartSubtitle"]');
  if (subtitleElement && subtitleElement.dataset.defaultText !== "custom") {
    subtitleElement.textContent = subtitle;
  }

  return {
    mode,
    labels: timeline.map(formatDate),
    series,
    max: mode === "accuracy" ? 100 : undefined,
    subtitle,
    height: 360,
  };
}

function essayChartSnapshot() {
  const entries = visibleEssays();
  const sorted = [...entries]
    .filter((item) => essayScoreTotal(item) > 0)
    .sort((a, b) => a.date.localeCompare(b.date));
  return {
    labels: sorted.map((item) => formatDate(item.date)),
    points: sorted.map((item) => item.score),
    label: "Nota",
    color: "#70c69a",
    max: 1000,
    height: 280,
  };
}

function drawQuestionsChart() {
  const canvas = document.querySelector("#questionsChart");
  if (!canvas) return;

  const mode = document.querySelector("#chartMode").value;
  const filter = dashboardSubjectFilter();
  const chartItems = dashboardQuestions();
  const timeline = [...new Set(chartItems.map((item) => String(item.date || "")).filter(Boolean))].sort();
  const grouped = chartItems.reduce((map, item) => {
    const key = `${item.subject}::${item.date}`;
    const bucket = map.get(key) || { questions: 0, correct: 0 };
    bucket.questions += Math.max(0, number(item.questions));
    bucket.correct += Math.max(0, number(item.correct));
    map.set(key, bucket);
    return map;
  }, new Map());
  const chartSubjects =
    filter === "all"
      ? dashboardSubjects()
      : dashboardSubjects().filter((subject) => subject === filter);
  const series = chartSubjects
    .map((subject) => {
      let runningQuestions = 0;
      let runningCorrect = 0;
      const points = timeline.map((date) => {
        const dayStats = grouped.get(`${subject}::${date}`) || { questions: 0, correct: 0 };
        const dayQuestions = dayStats.questions;
        const dayCorrect = dayStats.correct;
        runningQuestions += dayQuestions;
        runningCorrect += dayCorrect;

        if (mode === "daily") return dayQuestions;
        if (mode === "accuracy") return pct(runningCorrect, runningQuestions);
        return runningQuestions;
      });
      return { label: subject, color: colors[subject], points };
    })
    .filter((serie) => serie.points.some((point) => point > 0));

  const subtitle = document.querySelector('[data-edit-key="chartSubtitle"]');
  if (subtitle && subtitle.dataset.defaultText !== "custom") {
    subtitle.textContent = mode === "daily"
      ? "Questões por dia"
      : mode === "accuracy"
        ? "Acerto acumulado por dia"
        : "Evolução acumulada por dia";
  }

  drawLineChart(canvas, timeline.map(formatDate), series, mode === "accuracy" ? 100 : undefined);
}

function drawLineChart(canvas, labels, series, fixedMax) {
  const rect = canvas.getBoundingClientRect();
  if (rect.width < 1) return;
  const ratio = window.devicePixelRatio || 1;
  const width = Math.max(CHART_MIN_DRAWABLE_WIDTH, Math.round(rect.width));
  const compactChart = width <= CHART_COMPACT_BREAKPOINT;
  const baseHeight = Number(canvas.dataset.baseChartHeight || canvas.getAttribute("height") || 300);
  canvas.dataset.baseChartHeight = String(baseHeight);

  const measureCtx = canvas.getContext("2d");
  measureCtx.font = "12px system-ui";
  const legendRows = buildChartLegendRows(series, measureCtx, width - CHART_LEGEND_HORIZONTAL_PADDING);
  const legendHeight = Math.max(18, legendRows.length * 18);
  const xLabelHeight = CHART_X_LABEL_HEIGHT;
  const cssHeight = Math.max(baseHeight, 210 + legendHeight + xLabelHeight);
  canvas.style.height = `${cssHeight}px`;
  canvas.width = width * ratio;
  canvas.height = cssHeight * ratio;

  const ctx = canvas.getContext("2d");
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  const height = canvas.height / ratio;
  const pad = {
    top: 24,
    right: compactChart ? 14 : 22,
    bottom: xLabelHeight + legendHeight + 14,
    left: compactChart ? 42 : 54,
  };
  const innerWidth = width - pad.left - pad.right;
  const innerHeight = height - pad.top - pad.bottom;
  const values = series.flatMap((item) => item.points);
  const max = fixedMax || Math.max(10, ...values);
  const min = 0;

  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = "#171a19";
  ctx.fillRect(0, 0, width, height);

  if (!labels.length || !series.length) {
    ctx.fillStyle = "#9ca39c";
    ctx.font = "14px system-ui";
    ctx.fillText("Adicione registros para gerar o gráfico.", pad.left, pad.top + 30);
    return;
  }

  ctx.strokeStyle = "#2d332f";
  ctx.lineWidth = 1;
  ctx.fillStyle = "#9ca39c";
  ctx.font = "12px system-ui";

  for (let i = 0; i <= 4; i += 1) {
    const y = pad.top + innerHeight - (innerHeight / 4) * i;
    const value = Math.round((max / 4) * i);
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(width - pad.right, y);
    ctx.stroke();
    ctx.fillText(String(value), 10, y + 4);
  }

  const xFor = (index) => pad.left + (labels.length === 1 ? innerWidth / 2 : (innerWidth / (labels.length - 1)) * index);
  const yFor = (value) => pad.top + innerHeight - ((value - min) / (max - min || 1)) * innerHeight;

  series.forEach((serie) => {
    ctx.strokeStyle = serie.color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    const plotted = serie.points.map((value, index) => ({
      x: xFor(index),
      y: yFor(value),
    }));
    drawSmoothPath(ctx, plotted);
    ctx.stroke();

    ctx.fillStyle = serie.color;
    serie.points.forEach((value, index) => {
      if (value <= 0) return;
      const x = xFor(index);
      const y = yFor(value);
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();
    });
  });

  ctx.fillStyle = "#9ca39c";
  ctx.textAlign = "center";
  const xLabelY = height - legendHeight - 12;
  const maxVisibleLabels = Math.min(
    CHART_MAX_VISIBLE_X_LABELS,
    Math.max(CHART_MIN_VISIBLE_X_LABELS, Math.floor(innerWidth / CHART_MIN_X_LABEL_SPACING)),
  );
  const labelStep = Math.max(1, Math.ceil(labels.length / maxVisibleLabels));
  labels.forEach((label, index) => {
    const isLastLabel = index === labels.length - 1;
    if (!isLastLabel && index % labelStep !== 0) return;
    const x = xFor(index);
    ctx.fillText(label, x, xLabelY);
  });
  ctx.textAlign = "left";

  legendRows.forEach((row, rowIndex) => {
    let legendX = pad.left;
    const legendY = height - legendHeight + rowIndex * 18 + 4;
    row.forEach((item) => {
      ctx.fillStyle = item.color;
      ctx.fillRect(legendX, legendY, 10, 3);
      ctx.fillStyle = "#dfe4de";
      ctx.fillText(item.label, legendX + 14, legendY + 4);
      legendX += item.width;
    });
  });
}

function buildChartLegendRows(series, ctx, maxWidth) {
  const rows = [];
  let currentRow = [];
  let currentWidth = 0;

  series.forEach((serie) => {
    const itemWidth = ctx.measureText(serie.label).width + 42;
    if (currentRow.length && currentWidth + itemWidth > maxWidth) {
      rows.push(currentRow);
      currentRow = [];
      currentWidth = 0;
    }
    currentRow.push({ label: serie.label, color: serie.color, width: itemWidth });
    currentWidth += itemWidth;
  });

  if (currentRow.length) rows.push(currentRow);
  return rows;
}

function drawSmoothPath(ctx, points) {
  if (!points.length) return;
  ctx.moveTo(points[0].x, points[0].y);
  if (points.length === 1) return;

  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    const midX = (previous.x + current.x) / 2;
    const midY = (previous.y + current.y) / 2;
    ctx.quadraticCurveTo(previous.x, previous.y, midX, midY);
  }

  const last = points[points.length - 1];
  ctx.lineTo(last.x, last.y);
}

function seedExampleData() {
  const sample = defaultState();
  sample.userProfile = state.userProfile;
  sample.questions = [
    syncQuestionPerformance({ id: uid(), date: "2026-03-04", subject: "Física", content: "Cinemática", subcontent: "Módulo 1", questions: 44, correct: 40 }),
    syncQuestionPerformance({ id: uid(), date: "2026-03-10", subject: "Física", content: "Cinemática", subcontent: "M.U", questions: 23, correct: 21 }),
    syncQuestionPerformance({ id: uid(), date: "2026-03-16", subject: "Biologia", content: "Ecologia", subcontent: "Lista ENEM", questions: 38, correct: 30 }),
    syncQuestionPerformance({ id: uid(), date: "2026-03-23", subject: "Matemática", content: "Funções", subcontent: "Lista base", questions: 55, correct: 42 }),
    syncQuestionPerformance({ id: uid(), date: "2026-04-02", subject: "Química", content: "Estequiometria", subcontent: "Exercícios", questions: 46, correct: 34 }),
    syncQuestionPerformance({ id: uid(), date: "2026-04-13", subject: "Matemática", content: "Geometria", subcontent: "Lista 2", questions: 64, correct: 52 }),
  ];
  sample.planner = [
    { id: uid(), date: plannerTodayValue(), subject: "Matemática", title: "Revisar funções", type: "Revisão", done: false },
    { id: uid(), date: plannerTodayValue(), subject: "Redação", title: "Escrever redação modelo ENEM", type: "Redação", done: false },
  ];
  sample.subjectNotes = {
    "Matemática": "Funções, geometria plana, estatística e probabilidade.",
    "Física": "Cinemática, dinâmica, trabalho e energia.",
    "Redação": "Repertório, projeto de texto e competência 5.",
  };
  sample.subjectContents = {
    "Matemática": ["Funções", "Geometria"],
    "Física": ["Cinemática"],
    "Química": ["Estequiometria"],
  };
  sample.subjectCycles = {
    "Matemática": ["Nivelamento", "Básico 1", "Básico 2", "Construção", "Ataque"],
    "Física": ["Nivelamento", "Básico 1"],
    "Química": ["Nivelamento", "Básico 1"],
    Biologia: ["Nivelamento", "Básico 1"],
    "Redação": ["Competências", "Modelos"],
  };
  sample.cycleLessons = {
    "Matemática::Básico 1": [
      { id: uid(), group: "Grandezas Proporcionais", topic: "Injeção teórica: conceito de razão/proporção e exemplo prático", theory: true, practice: true, review: false },
      { id: uid(), group: "Grandezas Proporcionais", topic: "Treino de consolidação: comparando razões em um gráfico", theory: true, practice: true, review: false },
      { id: uid(), group: "Grandezas Proporcionais", topic: "Injeção teórica: proporcionalidade direta e inversa", theory: true, practice: true, review: false },
      { id: uid(), group: "Regra de 3", topic: "Regra de três simples", theory: false, practice: false, review: false },
    ],
    "Matemática::Nivelamento": [
      { id: uid(), group: "Fundamentos da matemática", topic: "Operações básicas e frações", theory: false, practice: false, review: false },
    ],
  };
  sample.errors = [
    { id: uid(), date: "2026-04-13", subject: "Matemática", content: "Geometria", source: "Questão de área", reason: "Confundi unidades.", fix: "Refazer conversão antes da fórmula.", done: false },
    { id: uid(), date: "2026-04-14", subject: "Matemática", content: "Funções", source: "Questão de função afim", reason: "Errei o coeficiente angular.", fix: "Identificar variação de y sobre variação de x.", done: false },
  ];
  sample.mocks = [
    { id: uid(), date: "2026-03-10", name: "módulo 1", subject: "Física", score: null, correct: 15, totalQuestions: 20, languages: 0, humanities: 0, nature: 0, math: 0, essayScore: null },
    { id: uid(), date: "2026-03-06", name: "Simulado de carnaval", subject: "Biologia", score: null, correct: 15, totalQuestions: 24, languages: 0, humanities: 0, nature: 0, math: 0, essayScore: null },
    { id: uid(), date: "2026-03-14", name: "Simulado 2 | 2° dia | P. Assad", subject: "Geral", score: null, correct: 74, totalQuestions: 90, languages: 0, humanities: 0, nature: 33, math: 41, essayScore: null },
  ];
  sample.essays = [
    { id: uid(), date: "2026-03-18", theme: "Tecnologia e educação", score: 820, c1: 160, c2: 160, c3: 160, c4: 180, c5: 160 },
    { id: uid(), date: "2026-04-10", theme: "Desafios ambientais", score: 880, c1: 180, c2: 180, c3: 160, c4: 180, c5: 180 },
  ];
  sample.studyWeeklyGoalHours = 20;
  sample.studySessions = [
    {
      id: uid(),
      date: localDateValue(),
      subject: "Matemática",
      duration: 90,
      durationMs: 90 * 60000,
      startTime: new Date(new Date().setHours(17, 10, 0, 0)).toISOString(),
      endTime: new Date(new Date().setHours(18, 40, 0, 0)).toISOString(),
    },
    {
      id: uid(),
      date: localDateValue(),
      subject: "Biologia",
      duration: 60,
      durationMs: 60 * 60000,
      startTime: new Date(new Date().setHours(14, 0, 0, 0)).toISOString(),
      endTime: new Date(new Date().setHours(15, 0, 0, 0)).toISOString(),
    },
  ];

  Object.assign(state, stripUnusedQuestionListNames(sample));
  render();
}

let dashboardResizeFrame = 0;
let dashboardResizeTimeout = 0;
function scheduleDashboardVisualRefresh() {
  if (typeof ResizeObserver === "function") return;
  if (dashboardResizeTimeout) window.clearTimeout(dashboardResizeTimeout);
  dashboardResizeTimeout = window.setTimeout(() => {
    dashboardResizeTimeout = 0;
    if (dashboardResizeFrame) window.cancelAnimationFrame(dashboardResizeFrame);
    dashboardResizeFrame = window.requestAnimationFrame(() => {
      dashboardResizeFrame = 0;
      const viewId = activeViewId();
      if (viewId !== "dashboard" && viewId !== "redacoes") return;
      drawChartsForSection(viewId, { force: true });
    });
  }, 80);
}

window.addEventListener("resize", scheduleDashboardVisualRefresh);
window.addEventListener("resize", () => scheduleActiveTopbarNavReveal({ behavior: "auto" }));

const chartSectionRefreshFrames = new Map();
function scheduleChartSectionRefresh(viewId) {
  const currentFrame = chartSectionRefreshFrames.get(viewId);
  if (currentFrame) window.cancelAnimationFrame(currentFrame);
  const frame = window.requestAnimationFrame(() => {
    chartSectionRefreshFrames.delete(viewId);
    drawChartsForSection(viewId, { force: true });
  });
  chartSectionRefreshFrames.set(viewId, frame);
}

function syncChartResizeObserver() {
  if (typeof ResizeObserver !== "function" || syncChartResizeObserver.initialized) return;
  syncChartResizeObserver.initialized = true;
  const observedWidths = new WeakMap();
  const observer = new ResizeObserver((entries) => {
    entries.forEach((entry) => {
      const host = entry.target?.querySelector?.("#questionsChart, #essayChart") || entry.target;
      const width = Math.round(entry.contentRect?.width || 0);
      if (!host || width < 1) return;
      if (observedWidths.get(host) === width) return;
      observedWidths.set(host, width);
      if (host.id === "questionsChart") {
        scheduleDashboardVisualRefresh();
        return;
      }
      if (host.id === "essayChart") {
        scheduleChartSectionRefresh("redacoes");
      }
    });
  });

  [
    document.querySelector('#dashboard [data-edit-block="chart"]'),
    document.querySelector("#redacoes .chart-row"),
  ]
    .filter(Boolean)
    .forEach((element) => observer.observe(element));
}

syncChartResizeObserver.initialized = false;
syncChartResizeObserver();

async function handleReconnect() {
  if (reconnectInFlight || isLocalSandbox() || !window.studyCloud?.enabled) return false;
  // Sem sessao autenticada nao ha o que sincronizar: sem este guard, o uploadSnapshot
  // lancava "Sessao desconectada." e o catch abria o modal "Sem conexao com a nuvem"
  // com status de erro — falso alarme na tela de login / apos sair da conta.
  const sessionUser = await window.studyCloud.getUser?.().catch(() => null);
  if (!sessionUser) {
    remoteSyncEnabled = false;
    cloudSessionActive = false;
    setSyncStatus("disconnected");
    return false;
  }
  if (conflictPending) {
    setSyncStatus("conflict");
    return false;
  }
  const online = navigator.onLine !== false;
  const pendingWrites = await pendingWalWriteCount();
  if (!online && pendingWrites <= 0) return false;
  reconnectInFlight = true;
  try {
    setSyncStatus("syncing");
    await persistSyncRetry(state);
    // hydrateRemoteState delegates cloud payload resolution to resolveCloudStateAfterAuth.
    await hydrateRemoteState("reconnect");
    publishWalRuntimeConfig();
    if (typeof window.studyCloud.uploadSnapshot === "function") {
      await window.studyCloud.uploadSnapshot(state);
    } else {
      await window.studyCloud.saveState(state);
    }
    await finalizeRemoteSync();
    setSyncStatus(conflictPending ? "conflict" : "synced");
    return true;
  } catch (err) {
    console.error("[handleReconnect]", err);
    setSyncStatus("error");
    openSyncDisconnectedModal();
    return false;
  } finally {
    reconnectInFlight = false;
  }
}

function scheduleSyncPolling() {
  if (syncPollingTimer) return;
  syncPollingTimer = setInterval(() => {
    if (isLocalSandbox() || !window.studyCloud?.enabled || document.hidden || navigator.onLine === false) return;
    if (conflictPending) {
      setSyncStatus("conflict");
      return;
    }
    if (Date.now() - lastUserActivityAt < SYNC_INACTIVITY_DEBOUNCE_MS) return;
    handleReconnect().catch((err) => {
      console.error("[scheduleSyncPolling]", err);
    });
  }, SYNC_POLL_INTERVAL_MS);
}

function trackSyncUserActivity() {
  lastUserActivityAt = Date.now();
}

document.addEventListener("pointerdown", trackSyncUserActivity);
document.addEventListener("keydown", trackSyncUserActivity);
window.addEventListener("online", handleReconnect);
scheduleSyncPolling();

// Phase 7: Flushes garantidos + observabilidade
async function flushAll({ immediate = false } = {}) {
  if (immediate) {
    await persistStateNow();
    await drainWalWrites();
  } else {
    void persistStateNow().then(() => drainWalWrites());
  }
  if (remoteSyncEnabled && !isBootstrapping && window.studyCloud?.enabled) {
    try {
      publishWalRuntimeConfig();
      await window.studyCloud.saveState(state);
      await finalizeRemoteSync();
      setSyncStatus("synced");
        } catch (err) {
            console.error("[flushAll] remote sync failed:", err);
        }
  }
}

window.addEventListener('pagehide', () => {
  if (navigator.locks) {
    navigator.locks.request('flush', { mode: 'exclusive' }, async () => {
      await flushAll({ immediate: true });
    });
  } else {
    flushAll({ immediate: true });
  }
});

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    flushAll({ immediate: true });
    return;
  }
  updateStudyTimerDisplay();
  handleReconnect().catch((err) => {
    console.error("[visibilitychange]", err);
  });
});
window.addEventListener("beforeunload", () => {
  try {
    syncActiveStudyTimerState();
    stripUnusedQuestionListNames(state);
    persistStateNow();
    void drainWalWrites();
  } catch (err) {
    console.error("[beforeunload] persistStateNow falhou:", err);
  }
});

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("sw.js").catch((err) => {
    console.error("[serviceWorker] register falhou:", err);
  });
}

async function initApp() {
  bootLoadingUI.show();
  try {
    setupLocalSandboxBanner();
    setupFocusController({
      getTimer: () => activeStudyTimer,
      getFocusOpen: () => focusScreenOpen,
      getViewId: () => activeViewId(),
      getColors: () => colors,
      formatTimer,
      getElapsedMs: () => elapsedStudyTimerMs(),
    });
    await verifyLoadedStateIntegrity();
    selectedStudyTimelineDate = timerOperationalValue();
    migrateRenamedLabels();
    syncAllQuestionPerformance();
    syncAllMocks();
    if (state.metadata?.schemaVersion < 2) {
      try {
        const m = await _loadBackupModules();
        const migrated = await m.persistence.ring.migrateImagesToIndexedDB(state);
        if (migrated !== state) {
          Object.keys(state).forEach((k) => delete state[k]);
          Object.assign(state, migrated);
          state.metadata.schemaVersion = 2;
        }
      } catch (e) {
        console.warn("[initApp] image migration failed:", e);
      }
    }
    await applyConnectionPreference();
    saveState();
    fillSubjectSelects();
    setTodayDefaults();
    setupForms();
    setupActions();
    await hydrateRemoteState();
    syncPlannerLinks();
    initBrowserHistory();
    await ensureSectionDataLoaded(activeViewId());
    await ensureSectionModuleLoaded(activeViewId());
    restoreActiveStudyTimerState();
    selectedStudyTimelineDate ||= timerOperationalValue();
    isBootstrapping = false;
    if (window.studyIntegrity) {
      try {
        state.metadata.payloadHash = await window.studyIntegrity.sha256(state);
      } catch (e) {
        console.warn("[initApp] sha256 failed:", e);
      }
    }
    render();
    renderBackupStatus();
  } catch (err) {
    console.error("[initApp] falhou:", err);
    notify("Nao foi possivel concluir o carregamento inicial.", "error");
  } finally {
    bootLoadingUI.hide();
  }
}

initApp();

/* =====================================================
   DESIGN CUSTOM — CONTROLES NATIVOS ("The Dark Desk")
   Substitui os popups nativos (calendario de data, relogio
   de hora e lista de sugestoes do datalist) por popovers
   com a identidade visual do DESIGN.md. Usa delegacao de
   eventos para funcionar com o conteudo renderizado
   dinamicamente pela SPA.
===================================================== */

let customPopover = null; // popover ativo (elemento)
let customPopoverKind = ""; // "date" | "time" | "autocomplete"
let customPopoverInput = null; // input alvo do popover
let customPopoverState = {}; // estado (mes/ano do calendario, itens do autocomplete)

function customPopoverClose() {
  if (!customPopover) return;
  customPopover.remove();
  customPopover = null;
  customPopoverKind = "";
  customPopoverInput = null;
  customPopoverState = {};
}

function customPopoverOpen(kind, input) {
  customPopoverClose();
  if (!input || input.disabled || input.readOnly) return;
  customPopoverKind = kind;
  customPopoverInput = input;
  if (kind === "date") {
    customPopoverRenderDate(input);
  } else if (kind === "time") {
    customPopoverRenderTime(input);
  } else if (kind === "select") {
    customPopoverRenderSelect(input);
  } else {
    customPopoverRenderAutocomplete(input);
  }
}

function customPopoverPlace(pop, input) {
  const rect = input.getBoundingClientRect();
  const gap = 6;
  const margin = 10;
  let top = rect.bottom + gap;
  let left = rect.left;
  const estHeight = pop.offsetHeight || 300;
  const estWidth = pop.offsetWidth || 288;
  if (top + estHeight > window.innerHeight - margin) {
    top = Math.max(margin, rect.top - estHeight - gap);
  }
  left = Math.min(Math.max(margin, left), window.innerWidth - estWidth - margin);
  pop.style.left = `${Math.round(left)}px`;
  pop.style.top = `${Math.round(top)}px`;
}

function setCustomFieldValue(input, value) {
  if (String(input.value) === String(value)) return;
  input.value = value;
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));
}

/* ---------- Date picker ---------- */
const CUSTOM_DATEPICKER_WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];
const CUSTOM_DATEPICKER_MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function customDatePickerParts(value) {
  const parts = String(value || "").split("-").map(Number);
  if (parts.length === 3 && parts.every(Number.isFinite)) {
    return { y: parts[0], m: parts[1] - 1 };
  }
  const now = new Date();
  return { y: now.getFullYear(), m: now.getMonth() };
}

function customPopoverRenderDate(input) {
  const initial = customDatePickerParts(input.value);
  const state = { y: initial.y, m: initial.m, selected: input.value || null };
  customPopoverState = state;

  const pop = document.createElement("div");
  pop.className = "dp-popover";
  pop.setAttribute("role", "dialog");
  pop.setAttribute("aria-label", "Selecionar data");

  const dayButton = (day, iso, muted) => {
    const classes = ["dp-day"];
    if (muted) classes.push("is-muted");
    if (iso === localDateValue(new Date())) classes.push("is-today");
    if (iso && iso === state.selected) classes.push("is-selected");
    return `<button class="${classes.join(" ")}" type="button" data-dp-value="${iso}">${day}</button>`;
  };

  const render = () => {
    const first = new Date(state.y, state.m, 1);
    const startWeekday = first.getDay();
    const daysInMonth = new Date(state.y, state.m + 1, 0).getDate();
    const daysPrev = new Date(state.y, state.m, 0).getDate();
    const cells = [];
    for (let i = startWeekday - 1; i >= 0; i--) {
      const dt = new Date(state.y, state.m - 1, daysPrev - i);
      cells.push(dayButton(daysPrev - i, localDateValue(dt), true));
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const dt = new Date(state.y, state.m, day);
      cells.push(dayButton(day, localDateValue(dt), false));
    }
    let next = 1;
    while (cells.length % 7 !== 0) {
      const dt = new Date(state.y, state.m + 1, next);
      cells.push(dayButton(next, localDateValue(dt), true));
      next++;
    }
    pop.innerHTML = `
      <div class="dp-head">
        <button class="dp-nav" type="button" data-dp-action="prev" aria-label="Mês anterior">‹</button>
        <div class="dp-title">${CUSTOM_DATEPICKER_MONTHS[state.m]} ${state.y}</div>
        <button class="dp-nav" type="button" data-dp-action="next" aria-label="Próximo mês">›</button>
      </div>
      <div class="dp-weekdays">${CUSTOM_DATEPICKER_WEEKDAYS.map((w) => `<div class="dp-weekday">${w}</div>`).join("")}</div>
      <div class="dp-grid">${cells.join("")}</div>
      <div class="dp-foot">
        <button class="dp-action" type="button" data-dp-action="clear">Limpar</button>
        <button class="dp-action is-primary" type="button" data-dp-action="today">Hoje</button>
      </div>
    `;
  };

  render();
  document.body.appendChild(pop);
  customPopover = pop;
  customPopoverPlace(pop, input);

  pop.addEventListener("click", (event) => {
    const dayBtn = event.target.closest("[data-dp-value]");
    if (dayBtn) {
      setCustomFieldValue(input, dayBtn.dataset.dpValue);
      customPopoverClose();
      return;
    }
    const actionBtn = event.target.closest("[data-dp-action]");
    if (!actionBtn) return;
    const action = actionBtn.dataset.dpAction;
    if (action === "prev") {
      state.m -= 1;
      if (state.m < 0) {
        state.m = 11;
        state.y -= 1;
      }
      render();
    } else if (action === "next") {
      state.m += 1;
      if (state.m > 11) {
        state.m = 0;
        state.y += 1;
      }
      render();
    } else if (action === "today") {
      setCustomFieldValue(input, localDateValue(new Date()));
      customPopoverClose();
    } else if (action === "clear") {
      setCustomFieldValue(input, "");
      customPopoverClose();
    }
  });
}

/* ---------- Time picker ---------- */
function customPopoverRenderTime(input) {
  const pop = document.createElement("div");
  pop.className = "dp-popover";
  pop.setAttribute("role", "dialog");
  pop.setAttribute("aria-label", "Selecionar horário");

  const current = String(input.value || "");
  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (const mm of [0, 30]) {
      const iso = `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
      const selected = iso === current ? " is-selected" : "";
      slots.push(`<button class="dp-time-opt${selected}" type="button" data-dp-time="${iso}">${iso}</button>`);
    }
  }
  pop.innerHTML = `
    <div class="dp-head"><div class="dp-title">Horário</div></div>
    <div class="dp-time-list">${slots.join("")}</div>
  `;
  document.body.appendChild(pop);
  customPopover = pop;
  customPopoverPlace(pop, input);

  pop.addEventListener("click", (event) => {
    const btn = event.target.closest("[data-dp-time]");
    if (!btn) return;
    setCustomFieldValue(input, btn.dataset.dpTime);
    customPopoverClose();
  });
}

/* ---------- Autocomplete (substitui o datalist nativo) ---------- */
function customAutocompleteSuggestions(field) {
  let suggestions = [];
  if (field.dataset.smartField) {
    suggestions = smartFieldSuggestions(field);
  }
  const listId = field.dataset.customAutocompleteList || field.getAttribute("list");
  if (listId && suggestions.length === 0) {
    const list = document.getElementById(listId);
    if (list) {
      suggestions = [...list.querySelectorAll("option")]
        .map((option) => option.value)
        .filter(Boolean);
    }
  }
  const value = String(field.value || "").trim().toLowerCase();
  const filtered = value
    ? suggestions.filter((s) => String(s).toLowerCase().includes(value))
    : suggestions;
  return [...new Set(filtered)].slice(0, 12);
}

function customPopoverRenderAutocomplete(field) {
  const suggestions = customAutocompleteSuggestions(field);
  if (!suggestions.length) {
    customPopoverClose();
    return;
  }
  // Desliga o datalist nativo (popup do SO) em favor do popover custom,
  // guardando o id para continuar filtrando e marcando o campo.
  const listId = field.getAttribute("list");
  if (listId) {
    field.dataset.customAutocompleteList = listId;
    field.removeAttribute("list");
  }
  field.dataset.customAutocomplete = "1";

  const pop = document.createElement("div");
  pop.className = "ac-popover";
  pop.setAttribute("role", "listbox");
  pop.innerHTML = `
    <div class="ac-label">Sugestões</div>
    <div class="ac-list">${suggestions
      .map(
        (s, i) =>
          `<button class="ac-item${i === 0 ? " is-active" : ""}" type="button" role="option" data-ac-value="${escapeAttribute(s)}">${escapeHtml(s)}</button>`,
      )
      .join("")}</div>
  `;
  document.body.appendChild(pop);
  customPopover = pop;
  customPopoverState = { items: suggestions, activeIndex: 0 };
  customPopoverPlace(pop, field);

  // Mantem o foco no input enquanto seleciona com o mouse.
  pop.addEventListener("pointerdown", (e) => e.preventDefault());
  pop.addEventListener("click", (event) => {
    const item = event.target.closest("[data-ac-value]");
    if (!item) return;
    setCustomFieldValue(field, item.dataset.acValue);
    customPopoverClose();
  });
}

/* ---------- Select (dropdown custom no lugar do nativo) ---------- */
function customPopoverRenderSelect(selectEl) {
  const options = [...selectEl.options];
  const items = [];
  for (const option of options) {
    if (option.disabled) continue;
    const group =
      option.parentElement && option.parentElement.tagName === "OPTGROUP"
        ? option.parentElement.label
        : null;
    items.push({ value: option.value, label: option.label || option.textContent, group });
  }
  if (!items.length) {
    customPopoverClose();
    return;
  }
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.selected && !option.disabled),
  );

  const parts = [];
  let lastGroup = null;
  items.forEach((item, i) => {
    if (item.group && item.group !== lastGroup) {
      parts.push(`<div class="ac-group">${escapeHtml(item.group)}</div>`);
      lastGroup = item.group;
    }
    const active = i === selectedIndex ? " is-active" : "";
    const sel = i === selectedIndex ? " is-selected" : "";
    parts.push(
      `<button class="ac-item${active}${sel}" type="button" role="option" data-ac-value="${escapeAttribute(item.value)}">${escapeHtml(item.label)}</button>`,
    );
  });

  const pop = document.createElement("div");
  pop.className = "ac-popover";
  pop.setAttribute("role", "listbox");
  pop.innerHTML = `<div class="ac-label">Opções</div><div class="ac-list">${parts.join("")}</div>`;
  document.body.appendChild(pop);
  customPopover = pop;
  customPopoverState = { items, activeIndex: selectedIndex };
  customPopoverPlace(pop, selectEl);

  pop.addEventListener("pointerdown", (e) => e.preventDefault());
  pop.addEventListener("click", (event) => {
    const item = event.target.closest("[data-ac-value]");
    if (!item) return;
    setCustomFieldValue(selectEl, item.dataset.acValue);
    customPopoverClose();
  });
}

/* ---------- Select multiplo custom (Simulado: substitui a listbox nativa) ---------- */
function customMultiPickerSync(picker) {
  const form = picker && picker.closest("form");
  const select = form && form.querySelector('select[name="subjects"]');
  if (!select) return;
  const chipsEl = picker.querySelector("[data-ds-chips]");
  const countEl = picker.querySelector("[data-ds-count]");
  const selected = [...picker.querySelectorAll(".ds-multi-opt[aria-pressed='true']")].map(
    (btn) => btn.dataset.dsValue,
  );
  [...select.options].forEach((option) => {
    option.selected = selected.includes(option.value);
  });
  if (chipsEl) {
    chipsEl.innerHTML = selected.length
      ? selected
          .map(
            (value) =>
              `<span class="ds-multi-chip">${escapeHtml(value)}<button type="button" class="ds-multi-chip-x" data-ds-remove="${escapeAttribute(value)}" aria-label="Remover ${escapeAttribute(value)}">×</button></span>`,
          )
          .join("")
      : "";
    chipsEl.classList.toggle("has-value", selected.length > 0);
  }
  if (countEl) {
    countEl.textContent = `${selected.length} ${selected.length === 1 ? "matéria selecionada" : "matérias selecionadas"}`;
  }
}

function customApplyNoValidate(root) {
  if (!root) return;
  const forms =
    root.nodeType === 1 && root.matches && root.matches("form")
      ? [root]
      : root.querySelectorAll
        ? [...root.querySelectorAll("form")]
        : [];
  forms.forEach((form) => {
    if (!form.noValidate) form.noValidate = true;
  });
}

/* ---------- Delegacao de eventos ---------- */
function setupCustomNativeControls() {
  if (window.__customNativeControlsReady) return;
  window.__customNativeControlsReady = true;

  // Abre date/time custom no clique e impede o picker nativo do navegador.
  document.addEventListener(
    "pointerdown",
    (event) => {
      const dateInput = event.target.closest('input[type="date"], input[type="time"]');
      if (dateInput) {
        if (dateInput.disabled || dateInput.readOnly) return;
        event.preventDefault();
        dateInput.focus({ preventScroll: true });
        customPopoverOpen(dateInput.type === "date" ? "date" : "time", dateInput);
        return;
      }
      // Select unico: dropdown custom no lugar do nativo.
      const selectEl = event.target.closest("select:not([multiple]):not([size])");
      if (selectEl) {
        if (selectEl.disabled) return;
        event.preventDefault();
        selectEl.focus({ preventScroll: true });
        customPopoverOpen("select", selectEl);
        return;
      }
      // Fecha o popover quando o clique e fora dele e fora do input alvo.
      if (customPopover) {
        const insidePopover = event.target.closest(".dp-popover, .ac-popover");
        const sameInput = customPopoverInput && (event.target === customPopoverInput || customPopoverInput.contains(event.target));
        if (!insidePopover && !sameInput) {
          customPopoverClose();
        }
      }
    },
    true,
  );

  // Teclado: Esc fecha; setas/Enter navegam no autocomplete; setas/Enter abrem date/time.
  document.addEventListener(
    "keydown",
    (event) => {
      if (customPopover) {
        if (event.key === "Escape") {
          customPopoverClose();
          if (customPopoverInput) customPopoverInput.focus({ preventScroll: true });
          event.preventDefault();
          return;
        }
        if (customPopoverKind === "autocomplete" || customPopoverKind === "select") {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            const buttons = [...customPopover.querySelectorAll(".ac-item")];
            if (!buttons.length) return;
            const delta = event.key === "ArrowDown" ? 1 : -1;
            const next = (customPopoverState.activeIndex + delta + buttons.length) % buttons.length;
            customPopoverState.activeIndex = next;
            buttons.forEach((el, i) => el.classList.toggle("is-active", i === next));
            const active = buttons[next];
            if (active) active.scrollIntoView({ block: "nearest" });
            return;
          }
          if (event.key === "Enter") {
            event.preventDefault();
            const active = customPopover.querySelector(".ac-item.is-active");
            if (active) {
              setCustomFieldValue(customPopoverInput, active.dataset.acValue);
            }
            customPopoverClose();
            return;
          }
        }
        return;
      }
      const dateInput =
        event.target instanceof Element
          ? event.target.closest('input[type="date"], input[type="time"]')
          : null;
      // Enter fica livre para submeter formularios; setas/espaço abrem o picker.
      if (dateInput && (event.key === " " || event.key === "ArrowDown" || event.key === "ArrowUp")) {
        event.preventDefault();
        customPopoverOpen(dateInput.type === "date" ? "date" : "time", dateInput);
      }
      // Select unico: Enter/espaco/setas abrem o dropdown custom.
      const selectEl =
        event.target instanceof Element
          ? event.target.closest("select:not([multiple]):not([size])")
          : null;
      if (selectEl && (event.key === "Enter" || event.key === " " || event.key === "ArrowDown" || event.key === "ArrowUp")) {
        event.preventDefault();
        customPopoverOpen("select", selectEl);
      }
    },
    true,
  );

  // Autocomplete: abre no foco e atualiza ao digitar.
  document.addEventListener("focusin", (event) => {
    const field = event.target.closest("input[list], [data-smart-field], [data-custom-autocomplete]");
    if (!field || !(field instanceof HTMLInputElement)) return;
    if (field.disabled || field.readOnly) return;
    if (field.type && !["text", "search"].includes(field.type)) return;
    customPopoverOpen("autocomplete", field);
  });

  document.addEventListener("input", (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement)) return;
    const isCustomAuto =
      field.hasAttribute("data-smart-field") ||
      field.hasAttribute("list") ||
      field.hasAttribute("data-custom-autocomplete");
    if (!isCustomAuto) return;
    if (field.type && !["text", "search"].includes(field.type)) return;
    if (document.activeElement !== field) return;
    customPopoverOpen("autocomplete", field);
  });

  // Fecha ao redimensionar ou rolar (exceto rolagem interna do proprio popover).
  window.addEventListener("resize", () => {
    if (customPopover) customPopoverClose();
  });
  document.addEventListener(
    "scroll",
    (event) => {
      if (!customPopover) return;
      const target = event.target;
      if (target instanceof Element && target.closest(".dp-popover, .ac-popover")) return;
      customPopoverClose();
    },
    true,
  );

  // Select multiplo custom: alterna materias e remove chips.
  document.addEventListener("click", (event) => {
    const opt = event.target.closest(".ds-multi-opt");
    if (opt) {
      const picker = opt.closest("[data-ds-multi]");
      if (!picker) return;
      const pressed = opt.getAttribute("aria-pressed") === "true";
      opt.setAttribute("aria-pressed", String(!pressed));
      customMultiPickerSync(picker);
      return;
    }
    const remove = event.target.closest("[data-ds-remove]");
    if (remove) {
      const picker = remove.closest("[data-ds-multi]");
      if (!picker) return;
      const value = remove.dataset.dsRemove;
      const btn = [...picker.querySelectorAll(".ds-multi-opt")].find((b) => b.dataset.dsValue === value);
      if (btn) btn.setAttribute("aria-pressed", "false");
      customMultiPickerSync(picker);
    }
  });

  // Desliga a validacao nativa do navegador (bolha do SO) nos formularios do app.
  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (form && form instanceof HTMLFormElement) form.noValidate = true;
    },
    true,
  );
  const validationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) customApplyNoValidate(node);
      }
    }
  });
  validationObserver.observe(document.body, { childList: true, subtree: true });
  customApplyNoValidate(document);
}

setupCustomNativeControls();
